// React hooks from global React (loaded as vendor lib)
const { useState, useEffect, useMemo, useCallback, useRef } = React;

// ---- Ленивая подгрузка тяжёлых библиотек -------------------------------
// chart.min.js (203 КБ), hls.min.js (404 КБ) и plyr.min.js (110 КБ) висели в
// <head> синхронными тегами: грузились ВСЕМ и ВСЕГДА, хотя нужны только на
// экранах с графиками и видео. На слабом мобильном это откладывало первый
// экран. Теперь подгружаются по требованию, один раз, с кэшем промиса.
const _libs = {};
const _loadScript = (src) => _libs[src] || (_libs[src] = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.async = true;
    s.onload = resolve;
    s.onerror = () => { delete _libs[src]; reject(new Error('не загрузился ' + src)); };
    document.head.appendChild(s);
}));
const _loadCss = (href) => _libs[href] || (_libs[href] = new Promise((resolve) => {
    const l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = href;
    l.onload = resolve; l.onerror = resolve;   // без стилей плеер всё равно играет
    document.head.appendChild(l);
}));

const ensureChart = () => window.Chart
    ? Promise.resolve()
    : _loadScript('/static/vendor/chart.min.js');
const ensureMedia = () => (window.Hls && window.Plyr)
    ? Promise.resolve()
    : Promise.all([
        _loadScript('/static/vendor/hls.min.js'),
        _loadScript('/static/vendor/plyr.min.js'),
        _loadCss('/static/vendor/plyr.css'),
      ]);

// Возвращает true, когда библиотека готова. Эффекты, которым она нужна,
// обязаны держать этот флаг в зависимостях и выходить, пока он false.
const useLib = (ensure) => {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        let alive = true;
        ensure().then(() => { if (alive) setReady(true); })
                .catch((e) => console.error('lazy lib:', e));
        return () => { alive = false; };
    }, []);
    return ready;
};
// ------------------------------------------------------------------------


// IS_WEBAPP is replaced at build time by esbuild define
const IS_WEBAPP = typeof __IS_WEBAPP__ !== 'undefined' ? __IS_WEBAPP__ : true;

const tg = IS_WEBAPP ? window.Telegram?.WebApp : null;
if (tg) {
    if (parseFloat(tg.version) >= 6.1) {
        tg.expand();
        tg.setHeaderColor('#0F0F15');
        tg.setBackgroundColor('#0F0F15');
    } else {
        tg.expand();
    }
}

const initData = IS_WEBAPP ? (tg?.initData || '') : '';

// ==== AUTH HELPERS ====
const _getAuthHeaders = () => {
    if (IS_WEBAPP) return { 'X-Telegram-Init-Data': initData };
    const token = localStorage.getItem('f1hub_auth_token');
    return token ? { 'Authorization': 'TgLogin ' + token } : {};
};

// Upscale F1 CDN image params for retina screens.
const hiResImg = (url, targetW = 800) => {
    if (!url || typeof url !== 'string') return url;
    if (!url.includes('media.formula1.com/image/upload/')) return url;
    return url
        .replace(/(\b)w_\d+/g, `$1w_${targetW}`)
        .replace(/(\b)h_\d+/g, `$1h_${Math.round(targetW * 0.75)}`);
};

// ==== API CLIENT ====
// Все фото с media.formula1.com идут через наш nginx-кэш (/f1media/):
// CDN тянет по 2.5с из РФ и отдаёт Cache-Control:private — из-за этого
// «карточки грузятся каждый раз». Одна точка переписи — здесь, в клиенте.
const _f1media = (o) => {
    if (typeof o === 'string') return o.startsWith('https://media.formula1.com/') ? o.replace('https://media.formula1.com/', '/f1media/') : o;
    if (Array.isArray(o)) { for (let i = 0; i < o.length; i++) o[i] = _f1media(o[i]); return o; }
    if (o && typeof o === 'object') { for (const k in o) o[k] = _f1media(o[k]); return o; }
    return o;
};
const api = {
    get: async (url) => {
        try {
            const res = await fetch(url, { headers: _getAuthHeaders() });
            if (!IS_WEBAPP && res.status === 401) {
                window.dispatchEvent(new CustomEvent('f1:auth-expired'));
                return null;
            }
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return _f1media(await res.json());
        } catch (err) { console.error('API GET ' + url + ':', err); return null; }
    },
    post: async (url, body) => {
        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ..._getAuthHeaders() },
                body: JSON.stringify(body)
            });
            if (!IS_WEBAPP && res.status === 401) {
                window.dispatchEvent(new CustomEvent('f1:auth-expired'));
                return null;
            }
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return await res.json();
        } catch (err) { console.error('API POST ' + url + ':', err); return null; }
    },
    del: async (url) => {
        try {
            const res = await fetch(url, { method: 'DELETE', headers: _getAuthHeaders() });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return await res.json();
        } catch (err) { console.error('API DELETE ' + url + ':', err); return null; }
    }
};

// ==== HELPERS ====
const formatTime = (s) => { if(s==null) return '—'; const m=Math.floor(s/60); const sec=(s%60).toFixed(3); return m>0?`${m}:${sec.padStart(6,'0')}`:sec; };
const fmtSector = (v) => v != null ? v.toFixed(3) : '—';
const fmtLap = (v) => { if(v==null) return '—'; const m=Math.floor(v/60); const s=(v%60).toFixed(3); return m>0?`${m}:${s.padStart(6,'0')}`:s; };
const _downsample = (arr, n=80) => { if(!arr||arr.length<=n) return arr||[]; const s=arr.length/n; return Array.from({length:n},(_,i)=>arr[Math.floor(i*s)]); };

const formatCountdown = (targetDate) => {
    const now = new Date();
    const target = new Date(targetDate);
    let diff = Math.max(0, Math.floor((target - now) / 1000));
    const days = Math.floor(diff / 86400); diff %= 86400;
    const hours = Math.floor(diff / 3600); diff %= 3600;
    const minutes = Math.floor(diff / 60);
    const secs = diff % 60;
    return { days, hours, minutes, seconds: secs };
};

const flagEmoji = (code) => {
    const flags = {'NL':'🇳🇱','GB':'🇬🇧','MC':'🇲🇨','AU':'🇦🇺','ES':'🇪🇸','DE':'🇩🇪','FR':'🇫🇷','CA':'🇨🇦','TH':'🇹🇭','JP':'🇯🇵','IT':'🇮🇹','NZ':'🇳🇿','BR':'🇧🇷','AR':'🇦🇷',
        'Bahrain':'🇧🇭','Saudi Arabia':'🇸🇦','Australia':'🇦🇺','Japan':'🇯🇵','China':'🇨🇳','USA':'🇺🇸','Italy':'🇮🇹','Monaco':'🇲🇨','Spain':'🇪🇸','Canada':'🇨🇦','Austria':'🇦🇹','UK':'🇬🇧','Hungary':'🇭🇺','Belgium':'🇧🇪','Netherlands':'🇳🇱','Azerbaijan':'🇦🇿','Singapore':'🇸🇬','Mexico':'🇲🇽','Brazil':'🇧🇷','Qatar':'🇶🇦','UAE':'🇦🇪'};
    return flags[code] || '🏳️';
};

const openLink = (url) => { if (tg?.openLink) tg.openLink(url, {try_instant_view: false, try_browser: true}); else window.open(url, '_blank'); };

// ---- Аппаратная «назад» (нативная обёртка / браузер) ----
// Экраны-оверлеи регистрируются в браузерной истории: открытие = pushState,
// системная «назад» = popstate = закрыть верхний экран (а не всё приложение).
// В TG-мини-аппе не используется (там свой BackButton и нет хардварной назад).
const _backStack = [];
let _suppressPop = 0;
const _notifyNative = () => {
    try { window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'stack', depth: _backStack.length })); } catch (e) {}
};
if (!IS_WEBAPP && typeof window !== 'undefined') {
    window.addEventListener('popstate', () => {
        if (_suppressPop > 0) { _suppressPop--; return; }
        const top = _backStack.pop();
        _notifyNative();
        if (top) top.close();
    });
}
const useHistoryBack = (open, closeFn) => {
    const ref = useRef(closeFn);
    ref.current = closeFn;
    useEffect(() => {
        if (IS_WEBAPP || !open) return;
        const entry = { close: () => { if (ref.current) ref.current(); } };
        _backStack.push(entry);
        _notifyNative();
        try { history.pushState({ f1: _backStack.length }, ''); } catch (e) {}
        return () => {
            const i = _backStack.indexOf(entry);
            if (i >= 0) {
                // закрыли крестиком/кодом — синхронизируем историю
                _backStack.splice(i, 1);
                _notifyNative();
                _suppressPop++;
                try { history.back(); } catch (e) { _suppressPop--; }
            }
        };
    }, [open]);
};

// ==== PULL TO REFRESH HOOK ====
const usePullToRefresh = (onRefresh) => {
    const [pulling, setPulling] = useState(false);
    const [pullY, setPullY] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const startY = useRef(0);
    const containerRef = useRef(null);
    const threshold = 80;
    const onTouchStart = useCallback(e => {
        if (refreshing) return;
        const el = containerRef.current;
        if (el && el.scrollTop <= 0) startY.current = e.touches[0].clientY;
        else startY.current = 0;
    }, [refreshing]);
    const onTouchMove = useCallback(e => {
        if (!startY.current || refreshing) return;
        const dy = e.touches[0].clientY - startY.current;
        if (dy > 0) { setPulling(true); setPullY(Math.min(dy * 0.4, 120)); if (dy > 10) e.preventDefault(); }
    }, [refreshing]);
    const onTouchEnd = useCallback(async () => {
        if (pullY >= threshold && onRefresh && !refreshing) {
            setRefreshing(true); setPullY(50);
            try { await onRefresh(); } finally { setRefreshing(false); }
        }
        setPulling(false); setPullY(0); startY.current = 0;
    }, [pullY, onRefresh, refreshing]);
    const indicatorEl = (pullY > 0 || refreshing) ? (
        <div style={{textAlign:'center',overflow:'hidden',transition:pulling?'none':'height 0.3s',height:refreshing?50:pullY,display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
            {refreshing ? <F1MiniLoader/> : (<>
                <div style={{transform:`rotate(${pullY*3}deg)`,fontSize:24,opacity:pullY/threshold}}>🏎️</div>
                <div style={{fontSize:11,color:'rgba(255,255,255,0.4)'}}>{pullY >= threshold ? 'Отпусти для обновления!' : 'Потяни вниз'}</div>
            </>)}
        </div>
    ) : null;
    return {containerRef, onTouchStart, onTouchMove, onTouchEnd, indicatorEl};
};

// ==== SVG NAV ICONS ====
// Авторский набор пиктограмм: единый сток 1.7, скруглённые окончания,
// без библиотечных форм (Material/Hero выдают «дешёвый» интерфейс).
// Профиль — гоночный шлем, «видео» — мягкий пусковой клин, аналитика — темп-бары.
const _ic = (children) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const NavIcons = {
    home: _ic(<><path d="M4.5 11.2 12 4.8l7.5 6.4"/><path d="M6.5 10.2v8.6a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-8.6"/></>),
    calendar: _ic(<><rect x="4.2" y="6" width="15.6" height="13.6" rx="2.6"/><path d="M4.5 10.6h15M9 4v3.4M15 4v3.4"/></>),
    trophy: _ic(<><path d="M8.2 4.6h7.6v4.6a3.8 3.8 0 0 1-7.6 0z"/><path d="M8.2 6.4H6a2.2 2.2 0 0 0 2.3 3.4M15.8 6.4H18a2.2 2.2 0 0 1-2.3 3.4"/><path d="M12 13v3.6M8.8 19.4h6.4"/></>),
    play: _ic(<path d="M9.2 7.5v9c0 .95 1.05 1.53 1.86 1.03l7.1-4.5c.75-.47.75-1.59 0-2.06l-7.1-4.5C10.25 5.97 9.2 6.55 9.2 7.5z"/>),
    user: _ic(<><path d="M4.8 14.2a7.2 7.2 0 0 1 14.4 0v2.2a2.2 2.2 0 0 1-2.2 2.2H7a2.2 2.2 0 0 1-2.2-2.2z"/><path d="M11.4 12.6h7.6"/></>),
    news: _ic(<><rect x="4.2" y="5" width="15.6" height="15" rx="2.4"/><path d="M7.6 9.2h5.2M7.6 12.6h8.8M7.6 16h8.8"/></>),
    analytics: _ic(<path d="M6.2 19v-5.4M12 19V5.8M17.8 19v-8.6"/>),
}

// ==== TRACK FACTS ====
// Факты трассы из уже имеющихся данных API: кругов + базовый круг (schedule)
// и рекордсмен трассы (history/circuit, кэш 24ч на бэке). Hairline-стиль,
// без карточек — по манифесту.
const fmtBaseLap = (sec) => sec ? `${Math.floor(sec/60)}:${String(sec%60).padStart(2,'0')}` : null;
const TrackFacts = ({race}) => {
    const [rec, setRec] = useState(null);
    useEffect(() => {
        if (!race?.circuit_id) return;
        let a = true;
        api.get(`/api/history/circuit/${race.circuit_id}`).then(d => { if (a && d?.most_wins) setRec(d.most_wins); }).catch(()=>{});
        return () => { a = false; };
    }, [race?.circuit_id]);
    const bits = [];
    if (race?.laps) bits.push(`${race.laps} кругов`);
    if (race?.base_lap) bits.push(`круг ~${fmtBaseLap(race.base_lap)}`);
    if (rec) bits.push(`рекордсмен: ${rec.driver} ×${rec.wins}`);
    if (!bits.length) return null;
    return (
        <div style={{display:'flex',alignItems:'center',gap:8,margin:'10px 0 2px',fontSize:11,color:'rgba(255,255,255,0.75)',flexWrap:'wrap'}}>
            {bits.map((b,i)=>(
                <span key={i} style={{display:'flex',alignItems:'center',gap:8}}>
                    {i>0 && <span style={{width:3,height:3,borderRadius:'50%',background:'rgba(255,255,255,0.4)'}}/>}
                    <span style={{fontWeight:600,letterSpacing:0.3}}>{b}</span>
                </span>
            ))}
        </div>
    );
};

// ==== TEAM ATMOSPHERES ====
// Не сырой team_color, а художественная палитра: глубокий многостоповый
// градиент (как на постерах команд) + цвет «света» за пилотом.
// Ролики этапов: ключ — circuit_id. Показываем, пока этап ближайший.
const RACE_CLIP = {
    madring: {mobile:'/static/video/madrid-mobile.mp4', poster:'/static/video/madrid-poster.jpg'},
};

const TEAM_ATMOS = {
    'Ferrari':         {bg:'linear-gradient(165deg,#3a060d 0%,#7a0f1d 34%,#2b0509 72%,#120409 100%)', glow:'rgba(255,70,60,0.30)'},
    'Mercedes':        {bg:'linear-gradient(165deg,#032220 0%,#0b4f46 36%,#02201c 74%,#050c0b 100%)', glow:'rgba(0,235,200,0.22)'},
    'McLaren':         {bg:'linear-gradient(165deg,#1d0e03 0%,#8a4a00 36%,#3a1c04 72%,#140a03 100%)', glow:'rgba(255,150,20,0.26)'},
    'Red Bull Racing': {bg:'linear-gradient(165deg,#050c26 0%,#1e2f6e 38%,#080e2c 74%,#04071a 100%)', glow:'rgba(90,130,255,0.24)'},
    'Aston Martin':    {bg:'linear-gradient(165deg,#02231c 0%,#0b5c46 38%,#032018 74%,#020f0b 100%)', glow:'rgba(0,210,160,0.20)'},
    'Alpine':          {bg:'linear-gradient(165deg,#04122e 0%,#14418f 38%,#071531 74%,#040a1a 100%)', glow:'rgba(255,110,180,0.16)'},
    'Williams':        {bg:'linear-gradient(165deg,#03102b 0%,#0e3f8f 38%,#051433 74%,#030a1c 100%)', glow:'rgba(70,150,255,0.24)'},
    'Haas':            {bg:'linear-gradient(165deg,#141419 0%,#4a4f5a 38%,#1a1a20 74%,#0d0d11 100%)', glow:'rgba(235,235,245,0.16)'},
    'Audi':            {bg:'linear-gradient(165deg,#0a0f0d 0%,#1e3d2d 38%,#0b1510 74%,#070a08 100%)', glow:'rgba(60,255,150,0.16)'},
    'Racing Bulls':    {bg:'linear-gradient(165deg,#0a0e2a 0%,#32418f 38%,#0d1233 74%,#060920 100%)', glow:'rgba(130,150,255,0.20)'},
    'Cadillac':        {bg:'linear-gradient(165deg,#0d0d10 0%,#3d3524 38%,#15130d 74%,#0a0a0c 100%)', glow:'rgba(215,180,80,0.18)'},
};
const teamAtmos = (team, color) => TEAM_ATMOS[team] || {bg:`linear-gradient(165deg, ${color||'#333'}66 0%, ${color||'#333'}22 40%, var(--f1-dark) 88%)`, glow:'rgba(255,255,255,0.10)'};
// Зерно плёнки: лёгкий SVG-шум для глубины фона (mix-blend overlay).
const NOISE_URI = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='140' height='140' filter='url(%23n)' opacity='0.55'/></svg>\")";

// ==== SKELETON ====
const Skeleton = ({w='100%',h='16px',r='8px',className=''}) => <div className={`skeleton ${className}`} style={{width:w,height:h,borderRadius:r}}/>;
const CardSkeleton = () => <div className="card" style={{display:'flex',flexDirection:'column',gap:12}}><Skeleton w="60%" h="20px"/><Skeleton w="100%" h="14px"/><Skeleton w="80%" h="14px"/></div>;

// ==== F1 LOADERS ====
const F1Loader = ({text = 'Загрузка...'}) => {
    const [activeLight, setActiveLight] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => setActiveLight(prev => (prev + 1) % 6), 400);
        return () => clearInterval(interval);
    }, []);
    return (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'60px 20px',gap:20}}>
            <div style={{display:'flex',gap:12}}>
                {[0,1,2,3,4].map(i => (
                    <div key={i} style={{width:28,height:28,borderRadius:'50%',background:activeLight===5?'#333':i<=activeLight?'#e10600':'#333',boxShadow:(activeLight!==5&&i<=activeLight)?'0 0 15px rgba(225,6,0,0.6)':'none',transition:'all 0.3s ease',border:'2px solid #555'}}/>
                ))}
            </div>
            <div style={{fontSize:13,color:'rgba(255,255,255,0.4)',fontWeight:600,letterSpacing:1,animation:'pulse 1.5s ease-in-out infinite'}}>
                {activeLight === 5 ? 'GO!' : text}
            </div>
        </div>
    );
};
const F1MiniLoader = () => (
    <div style={{display:'flex',gap:6,justifyContent:'center',padding:'20px 0'}}>
        {[0,1,2].map(i => (
            <div key={i} style={{width:8,height:8,borderRadius:'50%',background:'#e10600',animation:`f1pulse 1.2s ease-in-out ${i*0.2}s infinite`}}/>
        ))}
    </div>
);
const TyreLoader = ({text = 'Анализ данных...'}) => (
    <div style={{textAlign:'center',padding:'40px 20px'}}>
        <div style={{width:50,height:50,margin:'0 auto 16px',border:'4px solid rgba(255,255,255,0.1)',borderTop:'4px solid #e10600',borderRadius:'50%',animation:'tyreSpin 0.8s linear infinite'}}/>
        <div style={{fontSize:13,color:'rgba(255,255,255,0.4)',fontWeight:600}}>{text}</div>
    </div>
);

// ==== BOTTOM NAV ====
const BottomNav = ({active, onChange, isLive}) => {
    const tabs = [
        {id:'home', icon:NavIcons.home, label:'Главная'},
        {id:'schedule', icon:NavIcons.calendar, label:'Календарь'},
        {id:'predict', icon:NavIcons.trophy, label:'Прогнозы'},
        {id:'videos', icon:NavIcons.play, label:'Видео'},
        {id:'profile', icon:NavIcons.user, label:'Профиль'},
    ];
    return (
        <div className="bottom-nav">
            <div style={{display:'flex',justifyContent:'space-around',maxWidth:500,margin:'0 auto'}}>
                {tabs.map(tab => (
                    <div key={tab.id} className={`nav-item ${active===tab.id?'active':''}`} onClick={()=>onChange(tab.id)}>
                        <span className="nav-icon" style={{position:'relative'}}>
                            {tab.icon}
                            {tab.id==='live' && isLive && <span className="live-dot" style={{position:'absolute',top:-2,right:-6}}/>}
                        </span>
                        <span>{tab.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ==== COUNTDOWN ====
const Countdown = ({targetDate}) => {
    const [time, setTime] = useState(formatCountdown(targetDate));
    useEffect(() => {
        const iv = setInterval(() => setTime(formatCountdown(targetDate)), 1000);
        return () => clearInterval(iv);
    }, [targetDate]);
    const cells = [{val:time.days,label:'ДНЕЙ'},{val:time.hours,label:'ЧАСОВ'},{val:time.minutes,label:'МИНУТ'},{val:time.seconds,label:'СЕКУНД'}];
    return (
        <div className="cd-card">
            {cells.map((item,i)=>(
                <React.Fragment key={item.label}>
                    <div className="cd-cell">
                        <div className="cd-num">{String(item.val).padStart(2,'0')}</div>
                        <div className="cd-label">{item.label}</div>
                    </div>
                    {i<3 && <div className="cd-slash"><i/><i/></div>}
                </React.Fragment>
            ))}
        </div>
    );
};

const PosBadge = ({pos}) => <div className={`pos-badge ${pos<=3?`pos-${pos}`:'pos-n'}`}>{pos}</div>;
const TyreIcon = ({compound}) => <span className={`tyre-dot tyre-${compound||'UNKNOWN'}`} title={compound}/>;
const SectorBar = ({s1,s2,s3}) => <div className="sector-bar"><div className={`sb-${s1||'normal'}`}/><div className={`sb-${s2||'normal'}`}/><div className={`sb-${s3||'normal'}`}/></div>;
const FlagIcon = ({flag}) => {
    const map = {'GREEN':{cls:'flag-green',text:'G'},'YELLOW':{cls:'flag-yellow',text:'!'},'DOUBLE YELLOW':{cls:'flag-yellow',text:'!!'},'RED':{cls:'flag-red',text:'R'},'CHEQUERED':{cls:'flag-chequered',text:'🏁'}};
    const f = map[flag]||{cls:'',text:'•'};
    return <span className={`flag-icon ${f.cls}`}>{f.text}</span>;
};

const DriverPhoto = ({url, size=32, style:s={}}) => (
    <img src={size >= 40 ? hiResImg(url, Math.max(200, size * 4)) : url} alt="" className="driver-avatar"
         style={{width:size,height:size,...s}}
         onError={e=>{e.target.style.display='none'}}
         loading="lazy"/>
);

// ==== HOME PAGE ====
// «В этот день в F1» + история трассы (Ergast, вечнозелёный контент).
// Самонаполняется: если сегодня в истории гонок не было — показываем, кто
// побеждал на трассе следующего ГП, так карточка не бывает пустой.
const F1History = ({circuitId}) => {
    const [today, setToday] = useState(null);
    const [circ, setCirc] = useState(null);
    useEffect(() => {
        let a = true;
        api.get('/api/history/on-this-day').then(d => { if (a) setToday(d || {events:[]}); }).catch(() => { if (a) setToday({events:[]}); });
        return () => { a = false; };
    }, []);
    useEffect(() => {
        if (!circuitId) return;
        let a = true;
        api.get('/api/history/circuit/' + circuitId).then(d => { if (a) setCirc(d); }).catch(() => {});
        return () => { a = false; };
    }, [circuitId]);
    if (!today) return null;
    const events = today.events || [];
    const useToday = events.length > 0;
    const rows = useToday
        ? events.slice(0,5).map(e => ({year:e.season, who:e.winner, label:e.race}))
        : (circ && circ.winners ? circ.winners : []).slice(0,5).map(w => ({year:w.season, who:w.winner, label:w.race}));
    if (!rows.length) return null;
    const title = useToday ? 'В этот день в F1' : ('Побеждали на трассе' + (circ && circ.circuit ? ' · ' + circ.circuit : ''));
    const sub = (!useToday && circ && circ.most_wins) ? `Рекорд: ${circ.most_wins.driver} — ${circ.most_wins.wins} побед` : null;
    return (
        <div className="card" style={{padding:'16px 12px'}}>
            <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:sub?4:10}}>{title}</div>
            {sub && <div style={{fontSize:12,color:'var(--f1-red)',fontWeight:700,marginBottom:10}}>{sub}</div>}
            <div style={{display:'flex',flexDirection:'column'}}>
                {rows.map((r,i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 4px',borderBottom:i<rows.length-1?'1px solid var(--f1-border)':'none'}}>
                        <div style={{color:'var(--f1-red)',fontSize:18,fontWeight:900,minWidth:52,fontVariantNumeric:'tabular-nums'}}>{r.year}</div>
                        <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:14,fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.who}</div>
                            <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.label}</div>
                        </div>
                        <span style={{fontSize:18}}>{'🏆'}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const HomePage = ({nextRace, lastRace, standings, user, streams, seasonResults, schedule, onChange, season, onSeasonChange, spoilerFree, onToggleSpoiler, onRaceClick, onRefresh, liveBroadcasts}) => {
    const ptr = usePullToRefresh(onRefresh);
    const [homeNews, setHomeNews] = useState(null);
    useEffect(() => { api.get('/api/news').then(d => setHomeNews(((d && d.posts) || []).slice(0, 3))).catch(()=>{}); }, []);
    if (!nextRace && !lastRace) return (
        <div className="page-container fade-in" style={{padding:16}}>
            <div style={{textAlign:'center',padding:'8px 0'}}>
                <img src="/static/logo-f1hub-720.webp?v=1" alt="F1 Hub" style={{width:'min(94%,360px)',height:'auto',margin:'0 auto',display:'block'}}/>
                <div style={{display:'flex',gap:4,justifyContent:'center',marginTop:8}}>
                    {[2026].map(y=><button key={y} onClick={()=>onSeasonChange(y)} style={{background:'var(--f1-red)',border:'1px solid var(--f1-red)',borderRadius:8,padding:'6px 18px',color:'white',fontSize:13,fontWeight:700,cursor:'default',fontFamily:'inherit'}}>{y}</button>)}
                </div>
            </div>
            <F1Loader text="Разогрев моторов..."/>
        </div>
    );
    return (
        <div className="page-container fade-in" ref={ptr.containerRef} onTouchStart={ptr.onTouchStart} onTouchMove={ptr.onTouchMove} onTouchEnd={ptr.onTouchEnd} style={{padding:16,display:'flex',flexDirection:'column',gap:16}}>
            {ptr.indicatorEl}
            <div style={{textAlign:'center',padding:'8px 0'}}>
                <img src="/static/logo-f1hub-720.webp?v=1" alt="F1 Hub" style={{width:'min(94%,360px)',height:'auto',margin:'0 auto',display:'block'}}/>
                <div style={{display:'flex',gap:4,justifyContent:'center',marginTop:8}}>
                    {[2026].map(y=><button key={y} onClick={()=>onSeasonChange(y)} style={{background:'var(--f1-red)',border:'1px solid var(--f1-red)',borderRadius:8,padding:'6px 18px',color:'white',fontSize:13,fontWeight:700,cursor:'default',fontFamily:'inherit'}}>{y}</button>)}
                </div>
                <button onClick={onToggleSpoiler} style={{marginTop:8,display:'flex',alignItems:'center',gap:6,margin:'8px auto 0',background:spoilerFree?'rgba(225,6,0,0.15)':'rgba(255,255,255,0.06)',border:'1px solid '+(spoilerFree?'rgba(225,6,0,0.3)':'var(--f1-border)'),borderRadius:8,padding:'5px 14px',color:spoilerFree?'var(--f1-red)':'var(--f1-text-muted)',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',transition:'all 0.2s'}}>
                    <img src="/static/antispoiler.webp?v=1" alt="" style={{width:19,height:19,borderRadius:4,objectFit:'cover',verticalAlign:'middle',marginRight:2,opacity:spoilerFree?1:0.5}}/> Антиспойлер {spoilerFree ? 'вкл' : 'выкл'}
                </button>
            </div>

            {nextRace && nextRace.name && (
                <>
                    <div className="eyebrow-row"><span className="eyebrow-dot"/><span className="eyebrow-text">СТАРТ ЧЕРЕЗ</span></div>
                    {nextRace.race_datetime && <Countdown targetDate={nextRace.race_datetime}/>}
                    <div className="hero-card">
                        {/* У новых трасс своих кадров нет: у Мадрида circuit_image пустой,
                            и карточка оставалась просто тёмной. Там показываем ролик. */}
                        {RACE_CLIP[nextRace.circuit_id]
                            ? <div className="hero-clip-box">
                                  <video src={RACE_CLIP[nextRace.circuit_id].mobile}
                                         poster={RACE_CLIP[nextRace.circuit_id].poster}
                                         muted autoPlay loop playsInline preload="metadata" aria-hidden="true"/>
                              </div>
                            : nextRace.circuit_image && <div className="hero-bg" style={{backgroundImage:`url(${hiResImg(nextRace.circuit_image, 1200)})`}}/>}
                        <div className="hero-shade"/>
                        <img className="hero-car" src="/static/leclerc.webp?v=3" alt="" loading="lazy"/>
                        <div className="hero-inner">
                            <div className="hero-badge">СЛЕДУЮЩИЙ ГРАН-ПРИ</div>
                            <div>
                                <div className="hero-name">{(() => { const m = nextRace.name.match(/^Гран[- ]при\s+(.+)$/i); return m ? <>Гран-при<br/>{m[1]}</> : nextRace.name; })()}</div>
                                <div className="hero-place"><FlagImg code={nextRace.country_code} size={20}/><span>{nextRace.locality ? `${nextRace.locality}${nextRace.country ? ', ' + nextRace.country : ''}` : (nextRace.circuit || '')}</span></div>
                                <div className="hero-round">РАУНД {String(nextRace.round).padStart(2,'0')} · {season}</div>
                                <TrackFacts race={nextRace}/>
                                <button className="hero-btn" onClick={()=>onRaceClick(nextRace.round)}>ОТКРЫТЬ ГРАН-ПРИ <span style={{fontSize:16,lineHeight:1}}>›</span></button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {homeNews && homeNews.length > 0 && (
                <div>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',margin:'6px 4px 12px'}}>
                        <div style={{fontSize:22,fontWeight:800,fontStyle:'italic'}}>НОВОСТИ</div>
                        <div onClick={()=>onChange('news')} style={{display:'flex',alignItems:'center',gap:4,color:'var(--f1-red)',fontSize:11,fontWeight:800,letterSpacing:1.5,cursor:'pointer'}}>ВСЕ НОВОСТИ <span style={{fontSize:14}}>{'\u203a'}</span></div>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:10}}>
                        {homeNews.map((p,i)=>(
                            <div key={i} className="news-row" onClick={()=>onChange('news')}>
                                <div className="news-thumb">{(p.image||p.photo) ? <img src={p.image||p.photo} alt="" loading="lazy" onError={e=>{e.target.style.display='none'}}/> : <span style={{fontSize:22}}>{'\ud83d\udcf0'}</span>}</div>
                                <div style={{flex:1,minWidth:0,padding:'12px 14px'}}>
                                    <div style={{color:'var(--f1-red)',fontSize:10,fontWeight:800,letterSpacing:2}}>{(p.source||'НОВОСТЬ').toUpperCase()}</div>
                                    <div style={{fontSize:14,fontWeight:700,lineHeight:'18px',marginTop:5,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{p.title}</div>
                                </div>
                                <div style={{paddingRight:14,color:'var(--f1-red)',fontSize:18}}>{'\u203a'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* В этот день в F1 / история трассы */}
            <F1History circuitId={nextRace?.circuit_id}/>

            {/* Upcoming races */}
            {schedule?.races && (() => {
                const now = new Date();
                const upcoming = schedule.races.filter(r => r.race_datetime && new Date(r.race_datetime.replace('Z','')) > now).slice(0, 5);
                if (!upcoming.length) return null;
                return (
                    <div className="card" style={{padding:'16px 12px'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                            <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5}}>Ближайшие гонки</div>
                            <button onClick={()=>onChange('schedule')} style={{background:'none',border:'none',color:'var(--f1-red)',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',padding:0}}>Все гонки ›</button>
                        </div>
                        {upcoming.map((race, i) => {
                            const rd = new Date(race.race_datetime.replace('Z',''));
                            const msk = new Date(rd.getTime() + 3*3600000);
                            const dateStr = msk.toLocaleDateString('ru-RU',{day:'numeric',month:'short'});
                            const timeStr = msk.toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'});
                            return (
                                <div key={race.round} onClick={()=>onRaceClick(race.round)} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0',borderBottom:i<upcoming.length-1?'1px solid var(--f1-border)':'none',cursor:'pointer'}}>
                                    <div style={{width:26,textAlign:'center',fontWeight:800,fontSize:13,color:'var(--f1-text-muted)'}}>{race.round}</div>
                                    <FlagImg code={race.country_code} size={18}/>
                                    <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontWeight:600,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{race.name}</div>
                                        <div style={{fontSize:11,color:'var(--f1-text-muted)'}}>
                                            {dateStr} · {timeStr} МСК
                                            {race.sprint && <span style={{color:'#FF8000',marginLeft:6,fontWeight:700}}>СПРИНТ</span>}
                                        </div>
                                    </div>
                                    <div style={{color:'var(--f1-text-muted)',fontSize:16}}>›</div>
                                </div>
                            );
                        })}
                    </div>
                );
            })()}

            {/* Last race — podium layout */}
            {lastRace && lastRace.results && !spoilerFree && (
                <div className="card" style={{padding:'16px 12px'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                        <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5}}>Последний результат · {lastRace.name?.replace('Grand Prix','ГП')}</div>
                        <div onClick={()=>onChange('standings')} style={{color:'var(--f1-red)',fontSize:11,fontWeight:800,letterSpacing:1,cursor:'pointer',whiteSpace:'nowrap'}}>ЧЕМПИОНАТ {'\u203a'}</div>
                    </div>
                    {/* Podium — positions 2, 1, 3 */}
                    <div style={{display:'flex',justifyContent:'center',alignItems:'flex-end',gap:8,marginBottom:16}}>
                        {[1,0,2].map(idx => {
                            const r = lastRace.results[idx]; if(!r) return null;
                            const heights = [120,90,80]; const photoSizes = [56,44,44];
                            const colors = ['#FFD700','#C0C0C0','#CD7F32'];
                            return (
                                <div key={idx} style={{flex:1,maxWidth:110,textAlign:'center'}}>
                                    <div style={{position:'relative',marginBottom:6}}>
                                        {r.photo_url && <img src={hiResImg(r.photo_url, 256)} alt="" style={{width:photoSizes[idx],height:photoSizes[idx],borderRadius:'50%',objectFit:'cover',border:`3px solid ${colors[idx]}`,background:'var(--f1-gray)',margin:'0 auto'}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                    </div>
                                    <div style={{fontWeight:900,fontSize:idx===0?15:13}}>{r.code || r.name?.split(' ').pop()}</div>
                                    <div style={{fontSize:10,color:'var(--f1-text-muted)'}}>{r.team?.split(' ')[0]}</div>
                                    <div style={{background:`${colors[idx]}22`,borderRadius:'8px 8px 0 0',padding:'10px 0',marginTop:6,height:heights[idx],display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-start',paddingTop:10}}>
                                        <div style={{fontSize:24,fontWeight:900,color:colors[idx]}}>{r.position}</div>
                                        <div style={{fontSize:10,color:'var(--f1-text-muted)',marginTop:2}}>{r.time||`${r.points} очк.`}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {/* Rest of results */}
                    {lastRace.results.slice(3,8).map((r,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'7px 0',borderBottom:i<4?'1px solid var(--f1-border)':'none'}}>
                            <div style={{width:24,fontWeight:700,fontSize:13,color:'var(--f1-text-muted)',textAlign:'center'}}>{r.position}</div>
                            <div style={{background:r.team_color,width:3,height:28,borderRadius:2}}/>
                            {r.photo_url && <DriverPhoto url={r.photo_url} size={24}/>}
                            <div style={{flex:1,minWidth:0}}>
                                <span style={{fontWeight:700,fontSize:13}}>{r.code || r.name?.split(' ').pop()}</span>
                                <span style={{fontSize:11,color:'var(--f1-text-muted)',marginLeft:6}}>{r.team?.split(' ')[0]}</span>
                            </div>
                            <div style={{fontWeight:600,fontSize:12,color:'var(--f1-text-secondary)',fontVariantNumeric:'tabular-nums'}}>{r.time||''}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Championship top-3 — bigger photos */}
            {standings?.not_started && (
                <div className="card" style={{padding:'20px 16px',textAlign:'center'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:12}}>Чемпионат пилотов</div>
                    <div style={{fontSize:32,marginBottom:8}}>🏁</div>
                    <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Сезон {season} ещё не начался</div>
                    <div style={{fontSize:13,color:'var(--f1-text-secondary)'}}>Результаты появятся после первой гонки</div>
                </div>
            )}
            {standings?.standings && !spoilerFree && (
                <div className="card" style={{padding:'16px 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:12}}>Чемпионат пилотов</div>
                    {standings.standings.slice(0,3).map((s,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0',borderBottom:i<2?'1px solid var(--f1-border)':'none'}}>
                            <div style={{fontSize:20,fontWeight:900,color:['#FFD700','#C0C0C0','#CD7F32'][i],minWidth:24,textAlign:'center'}}>{s.position}</div>
                            {s.photo_url && <img src={hiResImg(s.photo_url, 256)} alt="" style={{width:48,height:48,borderRadius:'50%',objectFit:'cover',background:'var(--f1-gray)',border:`3px solid ${s.team_color}`,flexShrink:0}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                            <div style={{flex:1,minWidth:0}}>
                                <div style={{fontWeight:900,fontSize:15}}>{s.name}</div>
                                <div style={{fontSize:11,color:s.team_color,fontWeight:600}}>{s.team}</div>
                            </div>
                            <div style={{textAlign:'right'}}>
                                <div style={{fontWeight:900,fontSize:18,fontVariantNumeric:'tabular-nums'}}>{s.points}</div>
                                <div style={{fontSize:9,color:'var(--f1-text-muted)'}}>очков</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {spoilerFree && (lastRace?.results || standings?.standings) && (
                <div className="card" style={{padding:'20px 16px',textAlign:'center'}}>
                    <img src="/static/antispoiler.webp?v=1" alt="" style={{width:58,height:58,borderRadius:14,objectFit:'cover',margin:'0 auto 8px',display:'block'}}/>
                    <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Антиспойлер включён</div>
                    <div style={{fontSize:13,color:'var(--f1-text-secondary)'}}>Результаты и чемпионат скрыты</div>
                </div>
            )}

            {/* Live broadcast banner */}
            {liveBroadcasts && liveBroadcasts.length > 0 && (
                <div className="card" onClick={()=>{const b=liveBroadcasts[0];onRaceClick(b.race_round,'broadcast');}} style={{cursor:'pointer',padding:'14px 16px',background:'linear-gradient(135deg,rgba(225,6,0,0.15),var(--f1-card))',border:'1px solid rgba(225,6,0,0.3)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div style={{position:'relative'}}>
                            <div style={{width:10,height:10,borderRadius:'50%',background:'var(--f1-red)',animation:'pulse 1.5s infinite'}}/>
                        </div>
                        <div style={{flex:1}}>
                            <div style={{fontSize:11,color:'var(--f1-red)',fontWeight:700,textTransform:'uppercase',letterSpacing:1}}>В эфире</div>
                            <div style={{fontSize:14,fontWeight:700}}>{liveBroadcasts[0].title || 'Трансляция F1'}</div>
                        </div>
                        <div style={{color:'var(--f1-text-muted)',fontSize:16}}>›</div>
                    </div>
                </div>
            )}

            {/* Быстрые плитки — грамматика f1.com: акцент-полоска + Russo-лейбл,
                без эмодзи (они читались «несерьёзно») */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                {[
                    {id:'predict', color:'var(--f1-red)', label:'Прогнозы', sub:'Угадай победителя'},
                    {id:'games',   color:'#FF8000',       label:'Игры',     sub:'10 игр: пилот дня, дурак, квиз'},
                    {id:'schedule',color:'#6692FF',       label:'Календарь',sub:'Все гонки'},
                ].map(t => (
                    <div key={t.id} className="gradient-card" onClick={()=>onChange(t.id)}
                         style={{position:'relative',cursor:'pointer',padding:'16px 12px 12px',background:'var(--f1-card-solid)',overflow:'hidden'}}>
                        <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:t.color}}/>
                        <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:13,textTransform:'uppercase',letterSpacing:0.5,lineHeight:1.2}}>{t.label}</div>
                        <div style={{fontSize:9.5,color:'var(--f1-text-muted)',marginTop:3,lineHeight:1.3}}>{t.sub}</div>
                        <div style={{marginTop:8,color:t.color,fontSize:14,fontWeight:700}}>{'→'}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ==== LIVE PAGE ====
const DemoBanner = ({session, onPickSession}) => {
    if (!session?.is_demo) return null;
    const demoInfo = session.demo_session;
    return (
        <div style={{margin:'0 12px 8px',padding:'8px 12px',borderRadius:10,background:'linear-gradient(135deg,rgba(255,165,0,0.15),rgba(255,200,0,0.08))',border:'1px solid rgba(255,165,0,0.3)',display:'flex',alignItems:'center',gap:8,fontSize:12}}>
            <span style={{fontSize:16}}>{'📺'}</span>
            <div style={{flex:1}}>
                <div style={{fontWeight:700,color:'#FFA500'}}>Демо-режим</div>
                <div style={{color:'var(--f1-text-secondary)',fontSize:11}}>{demoInfo?.meeting_name || session.meeting_name || 'Историческая сессия'}</div>
            </div>
            <button onClick={onPickSession} style={{background:'rgba(255,165,0,0.2)',border:'1px solid rgba(255,165,0,0.4)',borderRadius:8,padding:'4px 10px',color:'#FFA500',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>Выбрать</button>
        </div>
    );
};

const DemoSessionPicker = ({show, onClose, onSelect}) => {
    const [sessions, setSessions] = useState(null);
    useEffect(() => {
        if (show && !sessions) api.get('/api/demo/sessions').then(d => { if (d) setSessions(d.sessions); });
    }, [show]);
    if (!show) return null;
    return (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:9999,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'flex-end',justifyContent:'center'}} onClick={onClose}>
            <div style={{width:'100%',maxWidth:480,maxHeight:'70vh',background:'var(--f1-card-solid)',borderRadius:'20px 20px 0 0',padding:'16px',overflow:'auto'}} onClick={e=>e.stopPropagation()}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                    <h3 style={{fontSize:16,fontWeight:900}}>Выбрать сессию</h3>
                    <button onClick={async()=>{await api.get('/api/demo/clear');onClose();window.location.reload();}} style={{background:'rgba(255,255,255,0.06)',border:'1px solid var(--f1-border)',borderRadius:8,padding:'4px 10px',color:'var(--f1-text-secondary)',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Авто</button>
                </div>
                {!sessions ? <div className="skeleton" style={{height:200}}/> : sessions.map(s => (
                    <div key={s.session_key} onClick={async()=>{await api.get('/api/demo/set?session_key='+s.session_key);onSelect(s);onClose();window.location.reload();}} style={{padding:'10px 12px',borderRadius:10,marginBottom:6,background:'rgba(255,255,255,0.04)',border:'1px solid var(--f1-border)',cursor:'pointer',display:'flex',alignItems:'center',gap:10}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:'rgba(255,165,0,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,color:'#FFA500'}}>R{s.round}</div>
                        <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:700}}>{s.name}</div>
                            <div style={{fontSize:11,color:'var(--f1-text-muted)'}}>{s.date} · {s.laps} кругов</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const LivePage = ({session, positions, timing, weather, raceControl, onChange, onRefresh}) => {
    const [liveTab, setLiveTab] = useState('positions');
    const [expandedRow, setExpandedRow] = useState(null);
    const [radioData, setRadioData] = useState(null);
    const [pitData, setPitData] = useState(null);
    const [audioPlaying, setAudioPlaying] = useState(null);
    const [trackMapData, setTrackMapData] = useState(null);
    const [selectedCar, setSelectedCar] = useState(null);
    const [showDemoPicker, setShowDemoPicker] = useState(false);
    const [transcripts, setTranscripts] = useState({});
    const [transcribing, setTranscribing] = useState({});
    const [speedoData, setSpeedoData] = useState(null);
    const [speedoDriver, setSpeedoDriver] = useState(null);
    const audioRef = useRef(null);
    const ptr = usePullToRefresh(onRefresh);

    const handleTranscribe = async (audioUrl) => {
        if (transcripts[audioUrl] || transcribing[audioUrl]) return;
        setTranscribing(p => ({...p, [audioUrl]: true}));
        try {
            const data = await api.get(`/api/radio/transcribe?url=${encodeURIComponent(audioUrl)}`);
            if (data) setTranscripts(p => ({...p, [audioUrl]: data}));
        } catch(e) {
            setTranscripts(p => ({...p, [audioUrl]: {error: 'Ошибка транскрипции'}}));
        }
        setTranscribing(p => ({...p, [audioUrl]: false}));
    };
    const isLive = session?.is_live;

    useEffect(() => {
        if (liveTab === 'pits' && !pitData) api.get('/api/live/pit-stops').then(setPitData);
    }, [liveTab]);

    // Radio polling (15s live, 30s demo)
    useEffect(() => {
        if (liveTab !== 'radio') return;
        const fetchRadio = () => api.get('/api/live/radio').then(d => { if (d) setRadioData(d); });
        fetchRadio();
        const iv = setInterval(fetchRadio, session?.is_demo ? 30000 : 15000);
        return () => clearInterval(iv);
    }, [liveTab, session?.is_demo]);

    // Track map polling (1.5s live, 30s demo — data is cached anyway)
    useEffect(() => {
        if (liveTab !== 'track') return;
        const fetchTrack = () => api.get('/api/live/track-map').then(d => { if (d) setTrackMapData(d); });
        fetchTrack();
        const iv = setInterval(fetchTrack, session?.is_demo ? 30000 : 1500);
        return () => clearInterval(iv);
    }, [liveTab, session?.is_demo]);

    // Speedometer polling (2s when tab active)
    useEffect(() => {
        if (liveTab !== 'speedo') return;
        const dn = speedoDriver || (positions?.positions?.[0]?.driver_number) || 1;
        if (!speedoDriver && positions?.positions?.[0]?.driver_number) setSpeedoDriver(positions.positions[0].driver_number);
        const fetchSpeedo = () => api.get(`/api/live/car-data?driver=${dn}`).then(d => { if (d) setSpeedoData(d); });
        fetchSpeedo();
        const iv = setInterval(fetchSpeedo, 2000);
        return () => clearInterval(iv);
    }, [liveTab, speedoDriver]);

    const playRadio = (url) => {
        if (audioRef.current) audioRef.current.pause();
        if (audioPlaying === url) { setAudioPlaying(null); return; }
        const audio = new Audio(url); audioRef.current = audio;
        audio.play().catch(()=>{}); audio.onended = () => setAudioPlaying(null); setAudioPlaying(url);
    };
    useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

    if (!session) return <div className="page-container fade-in" style={{padding:16}}><F1Loader text="Подключение к трассе..."/></div>;

    const timingMap = {};
    if (timing?.timing) timing.timing.forEach(t => { timingMap[t.driver_number] = t; });

    return (
        <div className="page-container fade-in" ref={ptr.containerRef} onTouchStart={ptr.onTouchStart} onTouchMove={ptr.onTouchMove} onTouchEnd={ptr.onTouchEnd} style={{padding:0}}>
            {ptr.indicatorEl}
            <div style={{padding:'10px 16px 0'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:2}}>
                    {isLive && <span className="live-dot"/>}
                    <span style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,color:isLive?'var(--f1-red)':session?.is_demo?'#FFA500':'var(--f1-text-muted)'}}>{isLive?'LIVE':session?.is_demo?'ДЕМО':'Последняя сессия'}</span>
                    <div style={{marginLeft:'auto'}}>
                        {onChange && <button onClick={()=>onChange('analytics')} style={{background:'rgba(255,255,255,0.06)',border:'1px solid var(--f1-border)',borderRadius:8,padding:'5px 10px',color:'var(--f1-text-secondary)',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:4}}>
                            <span style={{width:14,height:14,display:'inline-flex'}}>{NavIcons.analytics}</span> Аналитика
                        </button>}
                    </div>
                </div>
                <h2 style={{fontSize:18,fontWeight:900,lineHeight:1.2}}>{session.meeting_name || 'Гран-при'}</h2>
                <div style={{fontSize:12,color:'var(--f1-text-secondary)',marginBottom:4}}>{session.session_name || session.session_type || ''} · {session.circuit_short_name}</div>
            </div>

            <DemoBanner session={session} onPickSession={()=>setShowDemoPicker(true)}/>
            <DemoSessionPicker show={showDemoPicker} onClose={()=>setShowDemoPicker(false)} onSelect={()=>{}}/>

            {(weather?.weather || timing?.session_best?.lap) && (
                <div className="weather-strip">
                    {timing?.session_best?.lap && <div className="weather-pill" style={{background:'rgba(160,32,240,0.15)',border:'1px solid rgba(160,32,240,0.3)'}}><span style={{fontSize:14}}>⚡</span><span className="weather-pill-val" style={{color:'#A020F0'}}>{fmtLap(timing.session_best.lap)}</span></div>}
                    {weather?.weather && <>
                        <div className={`weather-pill ${weather.weather.is_raining?'rain-active':''}`}><span style={{fontSize:14}}>{weather.weather.is_raining?'🌧️':'☀️'}</span><span className="weather-pill-val">{weather.weather.air_temperature||'—'}°C</span></div>
                        <div className="weather-pill"><span style={{fontSize:14}}>🏁</span><span>Трасса </span><span className="weather-pill-val">{weather.weather.track_temperature||'—'}°C</span></div>
                        <div className="weather-pill"><span style={{fontSize:14}}>💧</span><span className="weather-pill-val">{weather.weather.humidity||'—'}%</span></div>
                        <div className="weather-pill"><span style={{fontSize:14}}>💨</span><span className="weather-pill-val">{weather.weather.wind_speed||'—'} м/с</span></div>
                    </>}
                </div>
            )}

            {raceControl?.messages?.length > 0 && (() => {
                const last = raceControl.messages[raceControl.messages.length-1];
                const isFlag = last.flag==='RED'||last.flag==='YELLOW'||last.flag==='DOUBLE YELLOW';
                if (!isFlag && last.category !== 'SafetyCar') return null;
                return (
                    <div style={{margin:'4px 12px 8px',padding:'8px 12px',borderRadius:10,display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:600,
                        background:last.flag==='RED'?'rgba(225,6,0,0.2)':last.flag==='YELLOW'||last.flag==='DOUBLE YELLOW'?'rgba(255,215,0,0.12)':'rgba(255,128,0,0.12)',
                        border:`1px solid ${last.flag==='RED'?'rgba(225,6,0,0.4)':last.flag==='YELLOW'||last.flag==='DOUBLE YELLOW'?'rgba(255,215,0,0.3)':'rgba(255,128,0,0.3)'}`,
                        animation:'fadeIn 0.3s ease-out'}}>
                        <FlagIcon flag={last.flag||'YELLOW'}/><span style={{flex:1}}>{last.message}</span>
                        {last.lap_number && <span style={{color:'var(--f1-text-muted)'}}>L{last.lap_number}</span>}
                    </div>
                );
            })()}

            <div className="live-tabs">
                {[{id:'positions',label:'Позиции'},{id:'track',label:'Трасса'},{id:'speedo',label:'Спидометр'},{id:'sectors',label:'Секторы'},{id:'pits',label:'Пит-стопы'},{id:'radio',label:'Радио'},{id:'messages',label:'Дирекция'}].map(t=>(
                    <button key={t.id} className={`live-tab ${liveTab===t.id?'active':''}`} onClick={()=>setLiveTab(t.id)}>{t.label}</button>
                ))}
            </div>

            {liveTab==='positions' && (positions?.positions?.length>0 ? (
                <div>
                    <div className="live-row" style={{fontSize:10,color:'var(--f1-text-muted)',fontWeight:600,textTransform:'uppercase',letterSpacing:0.5,minHeight:28,padding:'4px 12px'}}>
                        <span>P</span><span></span><span></span><span>Пилот</span><span style={{textAlign:'center'}}>Время</span><span style={{textAlign:'center'}}>Ш</span><span style={{textAlign:'right'}}>Отст.</span><span style={{textAlign:'center'}}>🔧</span>
                    </div>
                    {positions.positions.map((p,i) => {
                        const t = timingMap[p.driver_number]; const isExp = expandedRow===p.driver_number;
                        return (
                            <div key={p.driver_number}>
                                <div className={`live-row ${isExp?'live-row-expanded':''}`} onClick={()=>setExpandedRow(isExp?null:p.driver_number)}
                                     style={{background:i%2===0?'transparent':`${p.team_color}08`,cursor:'pointer'}}>
                                    <PosBadge pos={p.position}/>
                                    <div style={{background:p.team_color,width:5,height:34,borderRadius:2,alignSelf:'center'}}/>
                                    <div style={{width:28,height:28,borderRadius:'50%',background:p.team_color+'22',flexShrink:0,position:'relative',overflow:'hidden'}}>
                                        {p.photo_url && <img src={p.photo_url} alt="" style={{width:28,height:28,borderRadius:'50%',objectFit:'cover',position:'absolute',top:0,left:0}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                        <div style={{width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:p.team_color}}>{(p.code||'?')[0]}</div>
                                    </div>
                                    <div style={{minWidth:0}}><div style={{fontWeight:700,fontSize:14,lineHeight:1.2}}>{p.code||p.name?.split(' ').pop()}</div>{t&&<SectorBar s1={t.s1_status} s2={t.s2_status} s3={t.s3_status}/>}</div>
                                    <div style={{textAlign:'center',fontSize:12,fontWeight:600,color:t?.is_best_lap?'#A020F0':t?.is_personal_best?'#39B54A':'var(--f1-text-secondary)',fontVariantNumeric:'tabular-nums'}}>{t?fmtLap(t.lap_duration):'—'}</div>
                                    <div style={{textAlign:'center',position:'relative'}}><TyreIcon compound={p.tyre}/>{p.tyre_age>0&&<div style={{fontSize:8,color:'var(--f1-text-muted)',position:'absolute',bottom:-4,left:'50%',transform:'translateX(-50%)',fontWeight:600}}>{p.tyre_age}L</div>}</div>
                                    <div style={{textAlign:'right',fontSize:11,fontWeight:600,color:'var(--f1-text-secondary)',fontVariantNumeric:'tabular-nums'}}>{i===0?<span style={{color:'var(--f1-text-muted)',fontSize:10}}>INT</span>:t?.interval?`+${t.interval}`:t?.gap_to_leader?`+${t.gap_to_leader}`:'—'}</div>
                                    <div style={{textAlign:'center',fontSize:12,color:'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums'}}>{p.pit_stops||0}</div>
                                </div>
                                {isExp && t && (
                                    <div style={{padding:'4px 12px 12px',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6,background:'rgba(255,255,255,0.015)',borderBottom:'1px solid var(--f1-border)',animation:'fadeIn 0.2s ease-out'}}>
                                        <div className={`sector-time st-${t.s1_status}`}><div style={{fontSize:9,opacity:0.6,marginBottom:2}}>S1</div>{fmtSector(t.sector_1)}</div>
                                        <div className={`sector-time st-${t.s2_status}`}><div style={{fontSize:9,opacity:0.6,marginBottom:2}}>S2</div>{fmtSector(t.sector_2)}</div>
                                        <div className={`sector-time st-${t.s3_status}`}><div style={{fontSize:9,opacity:0.6,marginBottom:2}}>S3</div>{fmtSector(t.sector_3)}</div>
                                        <div style={{gridColumn:'1/-1',display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--f1-text-muted)',padding:'4px 4px 0'}}>
                                            <span>Круг {t.lap_number}</span><span>Стинт #{p.stint_number} · {p.tyre} {p.tyre_age>0?`(${p.tyre_age} кругов)`:''}</span>{t.gap_to_leader&&<span>Отст.: +{t.gap_to_leader}</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{textAlign:'center',padding:'50px 20px',color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🏎️</div><div style={{fontSize:16,fontWeight:700,marginBottom:4}}>{isLive?'Загрузка данных...':'Нет активной сессии'}</div><div style={{fontSize:13}}>Данные появятся когда начнётся сессия</div></div>
            ))}

            {/* TRACK MAP TAB */}
            {liveTab==='track' && (trackMapData?.track?.points?.length > 0 ? (() => {
                const pts = trackMapData.track.points;
                const cars = trackMapData.cars || [];
                const allPts = [...pts, ...cars.filter(c=>c.x!=null&&c.y!=null)];
                const xs = allPts.map(p=>p.x), ys = allPts.map(p=>p.y);
                const minX=Math.min(...xs), maxX=Math.max(...xs), minY=Math.min(...ys), maxY=Math.max(...ys);
                const rangeX=maxX-minX||1, rangeY=maxY-minY||1;
                const padX=rangeX*0.1||50, padY=rangeY*0.1||50;
                const vbX=minX-padX, vbY=minY-padY, vbW=rangeX+padX*2, vbH=rangeY+padY*2;
                const strokeScale = Math.max(rangeX,rangeY)/400;
                const outlineStr = pts.map(p=>`${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
                const leader = cars.length > 0 ? cars[0] : null;
                // Check if outline forms a closed loop (first/last points close enough)
                const f=pts[0], l=pts[pts.length-1];
                const gapDist=Math.sqrt((f.x-l.x)**2+(f.y-l.y)**2);
                const isClosed = gapDist / Math.max(rangeX,rangeY) < 0.15;
                const TrackLine = isClosed ? 'polygon' : 'polyline';
                return (
                    <div style={{padding:'8px 12px'}}>
                        <div style={{width:'100%',height:380,position:'relative'}}>
                        <svg viewBox={`${vbX.toFixed(0)} ${vbY.toFixed(0)} ${vbW.toFixed(0)} ${vbH.toFixed(0)}`} preserveAspectRatio="xMidYMid meet" style={{width:'100%',height:'100%',display:'block'}}>
                            {/* Track glow */}
                            <TrackLine points={outlineStr} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={Math.max(16*strokeScale,4)} strokeLinejoin="round" strokeLinecap="round"/>
                            {/* Track outline */}
                            <TrackLine points={outlineStr} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={Math.max(8*strokeScale,2)} strokeLinejoin="round" strokeLinecap="round"/>
                            {/* Car dots */}
                            {cars.map(car => {
                                const isLeader = car.driver_number === leader?.driver_number;
                                const isSel = selectedCar === car.driver_number;
                                const r = Math.max(6*strokeScale,2);
                                return (
                                    <g key={car.driver_number} style={{cursor:'pointer'}} onClick={()=>setSelectedCar(isSel?null:car.driver_number)}>
                                        <circle cx={car.x} cy={car.y} r={r} fill={car.team_color||'#888'}/>
                                        {isLeader && <circle cx={car.x} cy={car.y} r={r*1.7} fill="none" stroke={car.team_color||'#888'} strokeWidth={strokeScale*1.5} opacity="0.6">
                                            <animate attributeName="r" values={`${r*1.7};${r*2.3};${r*1.7}`} dur="1.5s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0.6;0.1;0.6" dur="1.5s" repeatCount="indefinite"/>
                                        </circle>}
                                        <text x={car.x} y={car.y-r*1.8} textAnchor="middle" fill="white" fontSize={Math.max(8*strokeScale,3)} fontWeight="700" fontFamily="Titillium Web,sans-serif">{car.code}</text>
                                    </g>
                                );
                            })}
                        </svg>
                        </div>
                        {/* Selected car popup */}
                        {selectedCar && (() => {
                            const car = cars.find(c=>c.driver_number===selectedCar);
                            if (!car) return null;
                            return (
                                <div className="card" onClick={()=>setSelectedCar(null)} style={{margin:'12px 0',padding:12,display:'flex',alignItems:'center',gap:10,animation:'fadeIn 0.2s ease-out'}}>
                                    <div style={{background:car.team_color,width:4,height:36,borderRadius:2}}/>
                                    {car.photo_url && <DriverPhoto url={car.photo_url} size={36}/>}
                                    <div style={{flex:1}}>
                                        <div style={{fontWeight:900,fontSize:15}}>{car.code} <span style={{fontWeight:400,color:'var(--f1-text-secondary)',fontSize:13}}>{car.name}</span></div>
                                        <div style={{fontSize:11,color:car.team_color,fontWeight:600}}>{car.team}</div>
                                    </div>
                                    <div style={{textAlign:'right'}}>
                                        <PosBadge pos={car.position||0}/>
                                    </div>
                                </div>
                            );
                        })()}
                        {/* Driver legend */}
                        <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:8}}>
                            {cars.map(c=>(
                                <div key={c.driver_number} onClick={()=>setSelectedCar(selectedCar===c.driver_number?null:c.driver_number)}
                                     style={{display:'flex',alignItems:'center',gap:3,fontSize:10,padding:'2px 6px',borderRadius:4,background:selectedCar===c.driver_number?`${c.team_color}33`:`${c.team_color}11`,color:c.team_color,fontWeight:600,cursor:'pointer'}}>
                                    <span style={{width:6,height:6,borderRadius:'50%',background:c.team_color}}/>{c.code}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })() : (
                <div style={{textAlign:'center',padding:'50px 20px',color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🗺️</div><div style={{fontSize:16,fontWeight:700,marginBottom:4}}>Карта трассы загружается...</div><div style={{fontSize:13}}>Данные о положении машин появятся во время сессии</div></div>
            ))}

            {/* SPEEDOMETER TAB */}
            {liveTab==='speedo' && (
                <div style={{padding:'12px'}}>
                    {/* Driver selector */}
                    {positions?.positions?.length > 0 && (
                        <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:12}}>
                            {positions.positions.slice(0, 20).map(p => {
                                const sel = speedoDriver === p.driver_number;
                                return (
                                    <button key={p.driver_number} onClick={()=>{setSpeedoDriver(p.driver_number);setSpeedoData(null);}}
                                        style={{fontSize:11,padding:'4px 8px',borderRadius:6,border:`1.5px solid ${sel?p.team_color:p.team_color+'44'}`,background:sel?`${p.team_color}33`:'transparent',color:sel?'#fff':'var(--f1-text-muted)',fontWeight:600,cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                                        {p.code}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {speedoData && !speedoData.error ? (() => {
                        const speed = speedoData.speed || 0;
                        const maxSpeed = 370;
                        const angle = -135 + (speed / maxSpeed) * 270;
                        const throttle = speedoData.throttle || 0;
                        const brake = speedoData.brake || 0;
                        const gear = speedoData.gear || 0;
                        const drs = speedoData.drs || 0;
                        const rpm = speedoData.rpm || 0;
                        const drsOpen = drs >= 10 && drs <= 14;
                        const tc = speedoData.team_color || '#E10600';
                        return (
                            <div>
                                {/* Driver info */}
                                <div style={{textAlign:'center',marginBottom:8}}>
                                    <span style={{fontSize:20,fontWeight:900,color:tc}}>{speedoData.code}</span>
                                    <span style={{fontSize:13,color:'var(--f1-text-muted)',marginLeft:8}}>{speedoData.team}</span>
                                </div>
                                {/* SVG Gauge */}
                                <div style={{display:'flex',justifyContent:'center',marginBottom:12}}>
                                    <svg viewBox="0 0 260 200" style={{width:'100%',maxWidth:320}}>
                                        {/* Background arc */}
                                        <path d="M 30 170 A 100 100 0 1 1 230 170" fill="none" stroke="#38383F" strokeWidth="12" strokeLinecap="round"/>
                                        {/* Speed arc */}
                                        <path d="M 30 170 A 100 100 0 1 1 230 170" fill="none" stroke={tc} strokeWidth="12" strokeLinecap="round"
                                            strokeDasharray={`${(speed/maxSpeed)*471} 471`} style={{transition:'stroke-dasharray 0.3s ease'}}/>
                                        {/* Speed ticks */}
                                        {[0,50,100,150,200,250,300,350].map(v => {
                                            const a = (-135 + (v/maxSpeed)*270) * Math.PI/180;
                                            const x1 = 130 + 85*Math.cos(a), y1 = 130 + 85*Math.sin(a);
                                            const x2 = 130 + 95*Math.cos(a), y2 = 130 + 95*Math.sin(a);
                                            const tx = 130 + 74*Math.cos(a), ty = 130 + 74*Math.sin(a);
                                            return <g key={v}>
                                                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#6B6B80" strokeWidth="1.5"/>
                                                <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fill="#6B6B80" fontSize="8" fontFamily="Titillium Web">{v}</text>
                                            </g>;
                                        })}
                                        {/* Needle */}
                                        {(() => {
                                            const a = angle * Math.PI/180;
                                            const nx = 130 + 80*Math.cos(a), ny = 130 + 80*Math.sin(a);
                                            return <line x1="130" y1="130" x2={nx} y2={ny} stroke={tc} strokeWidth="2.5" strokeLinecap="round" style={{transition:'x2 0.3s ease, y2 0.3s ease'}}/>;
                                        })()}
                                        <circle cx="130" cy="130" r="5" fill={tc}/>
                                        {/* Speed text */}
                                        <text x="130" y="165" textAnchor="middle" fill="white" fontSize="36" fontWeight="900" fontFamily="Titillium Web" style={{fontVariantNumeric:'tabular-nums'}}>{speed}</text>
                                        <text x="130" y="180" textAnchor="middle" fill="#6B6B80" fontSize="10" fontFamily="Titillium Web">км/ч</text>
                                    </svg>
                                </div>
                                {/* Gear + DRS */}
                                <div style={{display:'flex',justifyContent:'center',gap:16,marginBottom:16}}>
                                    <div className="card" style={{padding:'12px 24px',textAlign:'center',minWidth:80}}>
                                        <div style={{fontSize:36,fontWeight:900,color:tc,fontVariantNumeric:'tabular-nums',lineHeight:1}}>{gear === 0 ? 'N' : gear}</div>
                                        <div style={{fontSize:10,color:'var(--f1-text-muted)',marginTop:2}}>Передача</div>
                                    </div>
                                    <div className="card" style={{padding:'12px 24px',textAlign:'center',minWidth:80,border:drsOpen?'1.5px solid #39B54A':'1px solid var(--f1-border)'}}>
                                        <div style={{fontSize:20,fontWeight:900,color:drsOpen?'#39B54A':'var(--f1-text-muted)',lineHeight:1.6}}>DRS</div>
                                        <div style={{fontSize:10,color:drsOpen?'#39B54A':'var(--f1-text-muted)',marginTop:2}}>{drsOpen?'ОТКРЫТО':'ЗАКРЫТО'}</div>
                                    </div>
                                    <div className="card" style={{padding:'12px 16px',textAlign:'center',minWidth:80}}>
                                        <div style={{fontSize:16,fontWeight:900,fontVariantNumeric:'tabular-nums',lineHeight:1.6}}>{rpm > 0 ? Math.round(rpm) : '—'}</div>
                                        <div style={{fontSize:10,color:'var(--f1-text-muted)',marginTop:2}}>RPM</div>
                                    </div>
                                </div>
                                {/* Throttle bar */}
                                <div style={{marginBottom:8}}>
                                    <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'var(--f1-text-muted)',marginBottom:3}}>
                                        <span>Газ</span><span style={{fontWeight:700,color:'#39B54A'}}>{throttle}%</span>
                                    </div>
                                    <div style={{height:14,background:'var(--f1-gray)',borderRadius:7,overflow:'hidden'}}>
                                        <div style={{width:`${throttle}%`,height:'100%',background:'linear-gradient(90deg,#39B54A88,#39B54A)',borderRadius:7,transition:'width 0.2s ease'}}/>
                                    </div>
                                </div>
                                {/* Brake bar */}
                                <div>
                                    <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'var(--f1-text-muted)',marginBottom:3}}>
                                        <span>Тормоз</span><span style={{fontWeight:700,color:'#FF3333'}}>{brake}%</span>
                                    </div>
                                    <div style={{height:14,background:'var(--f1-gray)',borderRadius:7,overflow:'hidden'}}>
                                        <div style={{width:`${brake}%`,height:'100%',background:'linear-gradient(90deg,#FF333388,#FF3333)',borderRadius:7,transition:'width 0.2s ease'}}/>
                                    </div>
                                </div>
                            </div>
                        );
                    })() : (
                        <div style={{textAlign:'center',padding:'40px 20px',color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🏎️</div><div style={{fontSize:15,fontWeight:700}}>Загрузка телеметрии...</div><div style={{fontSize:13}}>Выберите пилота для просмотра данных</div></div>
                    )}
                </div>
            )}

            {liveTab==='sectors' && (timing?.timing?.length>0 ? (
                <div style={{padding:'0 8px'}}>
                    <div style={{display:'grid',gridTemplateColumns:'40px 1fr 64px 64px 64px 72px',gap:4,padding:'6px 8px',fontSize:10,color:'var(--f1-text-muted)',fontWeight:600,textTransform:'uppercase',borderBottom:'1px solid var(--f1-border)'}}>
                        <span>Код</span><span></span><span style={{textAlign:'center'}}>S1</span><span style={{textAlign:'center'}}>S2</span><span style={{textAlign:'center'}}>S3</span><span style={{textAlign:'center'}}>Круг</span>
                    </div>
                    {(positions?.positions||[]).map((p,i) => {
                        const t = timingMap[p.driver_number]; if(!t) return null;
                        return (
                            <div key={p.driver_number} style={{display:'grid',gridTemplateColumns:'40px 1fr 64px 64px 64px 72px',gap:4,padding:'7px 8px',alignItems:'center',borderBottom:'1px solid var(--f1-border)',fontSize:12,background:i%2?`${p.team_color}06`:'transparent'}}>
                                <span style={{fontWeight:700,color:p.team_color}}>{p.code}</span>
                                <div style={{width:4,height:20,background:p.team_color,borderRadius:2}}/>
                                <div className={`sector-time st-${t.s1_status}`}>{fmtSector(t.sector_1)}</div>
                                <div className={`sector-time st-${t.s2_status}`}>{fmtSector(t.sector_2)}</div>
                                <div className={`sector-time st-${t.s3_status}`}>{fmtSector(t.sector_3)}</div>
                                <div style={{textAlign:'center',fontWeight:700,color:t.is_best_lap?'#A020F0':t.is_personal_best?'#39B54A':'var(--f1-text-secondary)',fontVariantNumeric:'tabular-nums'}}>{fmtLap(t.lap_duration)}</div>
                            </div>
                        );
                    })}
                    {timing.session_best && (
                        <div style={{padding:'12px 8px',borderTop:'2px solid var(--f1-border)',marginTop:4}}>
                            <div style={{fontSize:10,color:'#A020F0',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8}}>Лучшие секторы сессии</div>
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:6}}>
                                {[{label:'S1',val:timing.session_best.sector_1},{label:'S2',val:timing.session_best.sector_2},{label:'S3',val:timing.session_best.sector_3},{label:'Круг',val:timing.session_best.lap}].map((s,i)=>(
                                    <div key={i} className="sector-time st-best" style={{padding:8}}><div style={{fontSize:9,opacity:0.6,marginBottom:2}}>{s.label}</div>{i<3?fmtSector(s.val):fmtLap(s.val)}</div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)',fontSize:13}}>Нет данных по секторам</div>)}

            {liveTab==='pits' && (pitData?.pit_stops?.length>0 ? (
                <div>{pitData.pit_stops.slice().reverse().map((p,i)=>(
                    <div key={i} className="pit-entry">
                        <div style={{width:36,height:36,borderRadius:10,background:`${p.team_color}22`,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:900,fontSize:13,color:p.team_color,flexShrink:0}}>{p.code||p.driver_number}</div>
                        <div style={{flex:1}}><div style={{fontWeight:700,fontSize:14}}>{p.name}</div><div style={{fontSize:11,color:'var(--f1-text-muted)'}}>Круг {p.lap_number||'?'} · {p.team}</div></div>
                        <div className="pit-time" style={{color:p.pit_duration&&p.pit_duration<25?'#39B54A':p.pit_duration&&p.pit_duration<30?'var(--f1-text)':'#FFD700'}}>{p.pit_duration?`${p.pit_duration.toFixed(1)}s`:'—'}</div>
                    </div>
                ))}</div>
            ) : <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)',fontSize:13}}><div style={{fontSize:32,marginBottom:8}}>🔧</div>Нет пит-стопов</div>)}

            {liveTab==='radio' && (radioData?.radio?.length>0 ? (
                <div><audio ref={audioRef}/>
                    {radioData.radio.slice().reverse().map((r,i)=>{
                        const t = transcripts[r.recording_url];
                        const isLoading = transcribing[r.recording_url];
                        return (
                        <div key={i} style={{padding:'12px 16px',borderBottom:'1px solid var(--f1-border)',animation:i<2?'fadeIn 0.3s ease-out':'none'}}>
                            <div style={{display:'flex',alignItems:'center',gap:10}}>
                                <button className="radio-play-btn" onClick={()=>r.recording_url&&playRadio(r.recording_url)}>{audioPlaying===r.recording_url?'⏸':'▶'}</button>
                                <div style={{flex:1,minWidth:0}}>
                                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:2}}>
                                        <div style={{width:4,height:20,borderRadius:2,background:r.team_color}}/>
                                        <span style={{fontWeight:700,fontSize:14}}>{r.code||r.name}</span>
                                        <span style={{fontSize:11,color:'var(--f1-text-muted)'}}>{r.team}</span>
                                        <span style={{fontSize:10,color:'var(--f1-text-muted)',marginLeft:'auto'}}>{r.date?new Date(r.date).toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit',second:'2-digit'}):''}</span>
                                    </div>
                                </div>
                                {radioData.transcription_available && r.recording_url && !t && (
                                    <button onClick={()=>handleTranscribe(r.recording_url)} disabled={isLoading} style={{background:'rgba(255,255,255,0.06)',border:'1px solid var(--f1-border)',borderRadius:8,padding:'4px 8px',color:isLoading?'var(--f1-text-muted)':'var(--f1-text-secondary)',fontSize:10,fontWeight:600,cursor:isLoading?'wait':'pointer',fontFamily:'inherit',flexShrink:0,transition:'all 0.2s'}}>
                                        {isLoading ? '⏳' : '📝'}
                                    </button>
                                )}
                            </div>
                            {t && !t.error && (
                                <div style={{marginTop:8,marginLeft:46,padding:'8px 10px',background:'rgba(255,255,255,0.03)',borderRadius:8,borderLeft:'3px solid var(--f1-text-muted)'}}>
                                    {t.text_en && <div style={{fontSize:12,color:'var(--f1-text-secondary)',lineHeight:1.4}}>🇬🇧 {t.text_en}</div>}
                                    {t.text_ru && <div style={{fontSize:13,color:'var(--f1-text)',fontWeight:600,marginTop:4,lineHeight:1.4}}>🇷🇺 {t.text_ru}</div>}
                                    {!t.text_en && <div style={{fontSize:12,color:'var(--f1-text-muted)',fontStyle:'italic'}}>🎙️ [неразборчиво]</div>}
                                </div>
                            )}
                            {t?.error && <div style={{marginTop:6,marginLeft:46,fontSize:11,color:'var(--f1-red)'}}>{t.error}</div>}
                        </div>
                    );})}
                </div>
            ) : <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)',fontSize:13}}><div style={{fontSize:32,marginBottom:8}}>📻</div>Нет радиопереговоров</div>)}

            {liveTab==='messages' && (raceControl?.messages?.length>0 ? (
                <div style={{padding:'4px 0'}}>{raceControl.messages.slice().reverse().map((msg,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 16px',borderBottom:'1px solid var(--f1-border)'}}>
                        <FlagIcon flag={msg.flag||'GREEN'}/>
                        <div style={{flex:1}}>
                            <div style={{fontSize:13,lineHeight:1.4}}>{msg.message_ru && msg.message_ru !== msg.message ? msg.message_ru : msg.message}</div>
                            {msg.message_ru && msg.message_ru !== msg.message && <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:2,fontStyle:'italic'}}>{msg.message}</div>}
                            <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:3,display:'flex',gap:8}}>
                                {msg.lap_number&&<span>Круг {msg.lap_number}</span>}{msg.driver_number&&<span>#{msg.driver_number}</span>}{msg.category&&<span>{{Other:'Другое',Flag:'Флаг',SafetyCar:'Сейфти-кар',VirtualSafetyCar:'Виртуальный СК',DRS:'DRS',SessionStatus:'Статус'}[msg.category]||msg.category}</span>}{msg.date&&<span>{new Date(msg.date).toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'})}</span>}
                            </div>
                        </div>
                    </div>
                ))}</div>
            ) : <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)',fontSize:13}}><div style={{fontSize:32,marginBottom:8}}>📋</div>Нет сообщений дирекции</div>)}
        </div>
    );
};

// ==== ARTICLE PAGE ====
const ArticlePage = ({ articleUrl, onBack }) => {
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!articleUrl) return;
        api.get('/api/news/article?url=' + encodeURIComponent(articleUrl))
            .then(d => { setArticle(d); setLoading(false); })
            .catch(() => setLoading(false));
    }, [articleUrl]);

    if (loading) return <div className="page-container fade-in" style={{padding:16}}><TyreLoader text="Загрузка статьи..."/></div>;
    if (!article || article.error) return (
        <div className="page-container fade-in" style={{padding:20,textAlign:'center'}}>
            <div style={{fontSize:48,marginBottom:12}}>😕</div>
            <div style={{fontSize:15,fontWeight:700,marginBottom:8}}>Не удалось загрузить статью</div>
            <button className="btn-primary" onClick={()=>openLink(articleUrl)}>Открыть на сайте ↗</button>
        </div>
    );

    const fmtDate = article.date ? new Date(article.date).toLocaleDateString('ru-RU', {day:'numeric',month:'long',hour:'2-digit',minute:'2-digit'}) : '';

    return (
        <div className="page-container fade-in">
            {article.image && <img src={article.image} style={{width:'100%',maxHeight:250,objectFit:'cover',borderRadius:'0 0 12px 12px'}} onError={e=>{e.target.style.display='none'}}/>}
            <div style={{padding:'16px 16px 0'}}>
                <div style={{fontSize:11,color:'var(--f1-text-muted)',marginBottom:8}}>{article.source} · {fmtDate}</div>
                <h1 style={{fontSize:22,fontWeight:800,lineHeight:1.3,margin:'0 0 16px 0'}}>{article.title}</h1>
                <div style={{fontSize:15,lineHeight:1.7,color:'rgba(255,255,255,0.85)'}}>
                    {article.paragraphs?.map((p,i) => (
                        <p key={i} style={{margin:'0 0 14px 0'}} dangerouslySetInnerHTML={{__html:p}}/>
                    ))}
                </div>
                {article.quotes?.length > 0 && article.quotes.map((q,i) => (
                    <div key={'q'+i} style={{borderLeft:'3px solid var(--f1-red)',padding:'8px 14px',margin:'12px 0',background:'rgba(255,255,255,0.03)',borderRadius:'0 8px 8px 0',fontSize:14,fontStyle:'italic',color:'rgba(255,255,255,0.7)',lineHeight:1.6}}>{q}</div>
                ))}
                <div style={{marginTop:24,padding:16,background:'rgba(255,255,255,0.05)',borderRadius:12,textAlign:'center'}}>
                    <div style={{fontSize:12,color:'var(--f1-text-muted)',marginBottom:8}}>Источник</div>
                    <a href={article.source_url} onClick={e=>{e.preventDefault();openLink(article.source_url);}} style={{color:'var(--f1-red)',fontSize:14,fontWeight:600,textDecoration:'none'}}>{'Открыть на ' + ((article.source_url||'').replace(/^https?:\/\//,'').split('/')[0].replace(/^www\./,'') || 'сайте') + ' ↗'}</a>
                </div>
            </div>
        </div>
    );
};

// ==== NEWS PAGE ====
const NewsPage = ({ onArticleClick }) => {
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => { api.get('/api/news').then(d => { setNews(d); setLoading(false); }); }, []);
    const ptr = usePullToRefresh(async()=>{const d=await api.get('/api/news');if(d)setNews(d);});

    if (loading) return <div className="page-container fade-in" style={{padding:16}}><F1MiniLoader/></div>;

    return (
        <div className="page-container fade-in" ref={ptr.containerRef} onTouchStart={ptr.onTouchStart} onTouchMove={ptr.onTouchMove} onTouchEnd={ptr.onTouchEnd} style={{padding:'12px 16px'}}>
            {ptr.indicatorEl}
            <h2 style={{fontSize:30,fontWeight:800,fontStyle:'italic',letterSpacing:-0.5,textTransform:'uppercase',marginBottom:4}}>НОВОСТИ</h2>
            <div style={{fontSize:13,color:'var(--f1-text-secondary)',marginBottom:16,display:'flex',alignItems:'center',gap:6}}>
                <span style={{width:6,height:6,borderRadius:'50%',background:'var(--f1-red)',display:'inline-block'}}/>
                {news?.source || 'championat.com'}
            </div>

            {news?.posts?.length > 0 ? (
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {news.posts.map((post,i) => (
                    <div key={i} className="news-row" onClick={()=>{if(post.url && onArticleClick) onArticleClick(post.url); else if(post.url) openLink(post.url);}}>
                        <div className="news-thumb">{(post.photo||post.image) ? <img src={post.photo||post.image} alt="" loading="lazy" onError={e=>{e.target.style.display='none'}}/> : <span style={{fontSize:22}}>{'\ud83d\udcf0'}</span>}</div>
                        <div style={{flex:1,minWidth:0,padding:'12px 14px'}}>
                            <div style={{color:'var(--f1-red)',fontSize:10,fontWeight:800,letterSpacing:2}}>{(post.source||'НОВОСТЬ').toUpperCase()}</div>
                            <div style={{fontSize:14,fontWeight:700,lineHeight:'18px',marginTop:5,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{post.title}</div>
                            {post.date_text && <div style={{fontSize:10,color:'var(--f1-text-muted)',fontWeight:700,letterSpacing:1,marginTop:5,textTransform:'uppercase'}}>{post.date_text}</div>}
                        </div>
                        <div style={{paddingRight:14,color:'var(--f1-red)',fontSize:18}}>{'\u203a'}</div>
                    </div>
                ))}
                </div>
            ) : (
                <div className="card" style={{textAlign:'center',padding:'40px 20px'}}>
                    <div style={{fontSize:48,marginBottom:12}}>📰</div>
                    <div style={{fontSize:15,fontWeight:700,marginBottom:8}}>Новости загружаются...</div>
                    <div style={{fontSize:13,color:'var(--f1-text-muted)',marginBottom:16}}>Последние новости Формулы-1</div>
                    <button className="btn-primary" onClick={()=>openLink('https://www.championat.com/auto/_f1.html')}>Открыть championat.com</button>
                </div>
            )}
        </div>
    );
};

// ==== SCHEDULE PAGE ====
const circuitFlags = {
    'albert_park':'🇦🇺','shanghai':'🇨🇳','suzuka':'🇯🇵','bahrain':'🇧🇭',
    'jeddah':'🇸🇦','miami':'🇺🇸','imola':'🇮🇹','monaco':'🇲🇨',
    'catalunya':'🇪🇸','villeneuve':'🇨🇦','red_bull_ring':'🇦🇹','silverstone':'🇬🇧',
    'spa':'🇧🇪','hungaroring':'🇭🇺','zandvoort':'🇳🇱','monza':'🇮🇹',
    'baku':'🇦🇿','marina_bay':'🇸🇬','cota':'🇺🇸','americas':'🇺🇸','rodriguez':'🇲🇽',
    'interlagos':'🇧🇷','las_vegas':'🇺🇸','vegas':'🇺🇸','lusail':'🇶🇦','losail':'🇶🇦','yas_marina':'🇦🇪',
    'madring':'🇪🇸'
};

const countryFlag = (code) => {
    if (!code) return '';
    return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
};
const FlagImg = ({code, size}) => {
    if (!code || code.length !== 2) return null;
    const s = size || 20;
    // flagcdn отдаёт только свой набор размеров: 44x33 и 52x39 (size 22 и 26)
    // возвращали 404, флаги в календаре и на карточке этапа не грузились вовсе.
    // Формат w<ширина> валиден всегда; берём с запасом под ретину, CSS ужмёт.
    const fw = s <= 20 ? 40 : s <= 40 ? 80 : 160;
    return <img src={`/flagcdn/w${fw}/${code.toLowerCase()}.png`} alt={code} style={{width:s,height:Math.round(s*0.75),objectFit:'cover',verticalAlign:'middle',borderRadius:2}} onError={e=>{e.target.style.display='none'}}/>;
};
const toMSK = (utcTime) => {
    if (!utcTime) return '';
    const [h, m] = utcTime.replace('Z','').split(':');
    const msk = (parseInt(h) + 3) % 24;
    return `${String(msk).padStart(2,'0')}:${m} МСК`;
};

// Рукописное зачёркивание номера пройденной гонки (по мотивам Норриса).
const RaceStrike = ({color = 'var(--f1-red)'}) => (
    <svg viewBox="0 0 100 44" preserveAspectRatio="none"
         style={{position:'absolute',left:'-10%',top:'50%',width:'120%',height:'80%',
                 transform:'translateY(-50%) rotate(-5deg)',pointerEvents:'none',overflow:'visible'}}>
        <path d="M3 27 C 22 13, 40 31, 60 17 S 90 23, 98 11" fill="none"
              stroke={color} strokeWidth="5.5" strokeLinecap="round" opacity="0.92"/>
        <path d="M5 15 C 27 27, 45 11, 67 25 S 91 13, 97 23" fill="none"
              stroke={color} strokeWidth="3.5" strokeLinecap="round" opacity="0.7"/>
    </svg>
);

const SchedulePage = ({seasonResults, schedule, season, onRaceClick, spoilerFree}) => {
    const [filter, setFilter] = useState('all');
    const resultsByRound = {};
    if (seasonResults && seasonResults.races) seasonResults.races.forEach(r => { resultsByRound[r.round] = r; });
    const races = (seasonResults && seasonResults.races && seasonResults.races.length ? seasonResults.races : null) || (schedule && schedule.races) || [];
    if (!races.length) return <div className="page-container fade-in" style={{padding:16}}><F1Loader text="Загрузка расписания..."/></div>;

    const now = new Date();
    const raceTime = (r) => new Date((r.race_datetime || r.date || '').replace('Z','')).getTime();
    const future = races.filter(r => raceTime(r) > now.getTime());
    const nextRound = future.length ? future[0].round : null;
    const filtered = filter === 'up' ? future : filter === 'past' ? races.filter(r => raceTime(r) <= now.getTime()) : races;
    const SERIF = "Georgia, 'Times New Roman', serif";

    const winnerOf = (race) => {
        const res = resultsByRound[race.round] || race;
        const p = res.podium && res.podium[0];
        return race.winner || res.winner || (p && (p.driver || p.name || p.driver_name)) || null;
    };

    return (
        <div className="page-container fade-in" style={{padding:'14px 16px 20px'}}>
            {/* Двухслойный заголовок: Russo + serif-водяной знак сезона + росчерк */}
            <div style={{position:'relative', marginBottom:20, paddingTop:8, overflow:'hidden'}}>
                <div style={{position:'absolute', right:-6, top:-14, fontFamily:SERIF, fontStyle:'italic',
                             fontSize:'clamp(90px,30vw,150px)', lineHeight:1, color:'rgba(255,255,255,0.05)',
                             pointerEvents:'none', userSelect:'none', letterSpacing:-4}}>{season}</div>
                <div style={{position:'relative', fontFamily:"'Russo One','Exo 2',sans-serif", fontWeight:400,
                             fontSize:'clamp(34px,11vw,50px)', textTransform:'uppercase', lineHeight:0.94, letterSpacing:-1}}>
                    Календарь
                </div>
                <div style={{position:'relative', fontFamily:SERIF, fontStyle:'italic', fontSize:'clamp(15px,4.6vw,20px)',
                             color:'rgba(255,255,255,0.34)', marginTop:2}}>сезон {season}</div>
                <svg viewBox="0 0 220 20" preserveAspectRatio="none" style={{display:'block',width:150,height:13,marginTop:6,overflow:'visible'}}>
                    <path d="M2 12 C 50 3, 100 17, 150 7 S 205 10, 218 5" fill="none" stroke="var(--f1-red)" strokeWidth="4" strokeLinecap="round"/>
                </svg>
            </div>

            <div className="tab-switch" style={{marginBottom:6}}>
                {[['all','ВСЕ'],['up','БЛИЖАЙШИЕ'],['past','ПРОШЕДШИЕ']].map(([id,label]) => (
                    <button key={id} className={`tab-switch-btn ${filter===id?'active':''}`} style={{letterSpacing:2,fontSize:11,fontWeight:800}} onClick={()=>setFilter(id)}>{label}</button>
                ))}
            </div>

            {/* Протокол-таблица: строка = гонка, гигантский № (зачёркнут у пройденных) */}
            <div>
                {filtered.map((race, i) => {
                    const isPast = raceTime(race) <= now.getTime();
                    const isNext = race.round === nextRound;
                    const d = new Date((race.race_datetime || race.date || '').replace('Z',''));
                    const dateRu = d.toLocaleDateString('ru-RU',{day:'numeric',month:'short'}).replace('.','').toUpperCase();
                    const country = (race.country || race.name || '').replace(/^Гран[- ]при\s+/i,'').toUpperCase();
                    const winner = isPast ? winnerOf(race) : null;
                    return (
                        <div key={race.round} onClick={()=>onRaceClick(race.round)}
                             style={{display:'flex',alignItems:'center',gap:12,padding:'15px 2px',cursor:'pointer',position:'relative',
                                     borderBottom:i<filtered.length-1?'1px solid rgba(255,255,255,0.07)':'none',
                                     background:isNext?'linear-gradient(90deg, rgba(225,6,0,0.10), transparent 70%)':'transparent'}}>
                            {isNext && <div style={{position:'absolute',left:-16,top:0,bottom:0,width:3,background:'var(--f1-red)',boxShadow:'0 0 10px rgba(225,6,0,0.8)'}}/>}
                            <div style={{position:'relative',width:58,flexShrink:0,textAlign:'left'}}>
                                <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:40,lineHeight:1,letterSpacing:-2,
                                             color:isPast?'rgba(255,255,255,0.32)':'#fff'}}>{String(race.round).padStart(2,'0')}</div>
                                {isPast && <RaceStrike/>}
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                                <div style={{display:'flex',alignItems:'center',gap:9}}>
                                    {race.country_code ? <FlagImg code={race.country_code} size={22}/> : <span style={{fontSize:20}}>{circuitFlags[race.circuit_id]||''}</span>}
                                    <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:'clamp(17px,5.4vw,23px)',
                                                 textTransform:'uppercase',letterSpacing:-0.4,lineHeight:1,whiteSpace:'nowrap',
                                                 overflow:'hidden',textOverflow:'ellipsis',opacity:isPast?0.6:1}}>{country}</div>
                                    {race.sprint && <span style={{background:'rgba(255,128,0,0.18)',color:'#FF8000',fontSize:8,fontWeight:800,padding:'2px 5px',borderRadius:4,letterSpacing:1,flexShrink:0}}>SPR</span>}
                                </div>
                                <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                    {winner ? <span style={{color:'var(--f1-text-secondary)'}}>{'\U0001F3C6'} {winner}</span> : (race.circuit || race.locality || '')}
                                </div>
                            </div>
                            <div style={{textAlign:'right',flexShrink:0,display:'flex',alignItems:'center',gap:8}}>
                                <div>
                                    <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:15,
                                                 color:isPast?'var(--f1-text-muted)':'var(--f1-red)',lineHeight:1,fontVariantNumeric:'tabular-nums'}}>{dateRu}</div>
                                    <div style={{fontSize:9.5,color:'var(--f1-text-muted)',marginTop:4,fontVariantNumeric:'tabular-nums'}}>{race.laps ? `${race.laps} кр.` : (isPast?'завершён':'')}</div>
                                </div>
                                <div style={{color:'var(--f1-text-muted)',fontSize:16}}>{'›'}</div>
                            </div>
                        </div>
                    );
                })}
                {filtered.length === 0 && <div style={{textAlign:'center',color:'var(--f1-text-muted)',padding:24}}>Гонок не найдено</div>}
            </div>
        </div>
    );
};

// ==== STANDINGS PAGE ====
const StandingsPage = ({driversStandings, constructorsStandings, season, onRefresh, spoilerFree, onBack}) => {
    const chartReady = useLib(ensureChart);
    const [tab, setTab] = useState('drivers');
    const [spoilerDismissed, setSpoilerDismissed] = useState(() => localStorage.getItem('f1hub_spoiler_standings_skip') === 'true');
    const showSpoilerWarning = spoilerFree && !spoilerDismissed;
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [driverDetail, setDriverDetail] = useState(null);
    const [allDrivers, setAllDrivers] = useState(null);
    const [teamsData, setTeamsData] = useState(null);
    const [h2hData, setH2hData] = useState(null);
    const [progressData, setProgressData] = useState(null);
    const progressChartRef = useRef(null);
    const progressChartInstance = useRef(null);
    const ptr = usePullToRefresh(onRefresh);
    useEffect(() => {
        setAllDrivers(null); setTeamsData(null); setSelectedDriver(null); setDriverDetail(null); setH2hData(null); setProgressData(null);
        if (tab==='cards') api.get(`/api/drivers?season=${season}`).then(d=>setAllDrivers(d?.drivers||[]));
        if (tab==='teams') api.get(`/api/teams?season=${season}`).then(d=>setTeamsData(d?.teams||[]));
        if (tab==='h2h') api.get(`/api/head-to-head?season=${season}`).then(d=>setH2hData(d));
        if (tab==='progress') api.get(`/api/standings/points-progression?season=${season}`).then(d=>setProgressData(d));
    }, [tab, season]);

    // Points progression chart rendering
    useEffect(() => {
        if (!chartReady) return;   // ждём ленивую библиотеку
        if (tab !== 'progress' || !progressData?.drivers?.length || !progressChartRef.current) return;
        if (progressChartInstance.current) progressChartInstance.current.destroy();
        const ctx = progressChartRef.current.getContext('2d');
        const datasets = progressData.drivers.map(d => ({
            label: d.code,
            data: d.progression.map(p => ({x: p.round, y: p.cumulative})),
            borderColor: d.team_color || '#888',
            backgroundColor: d.team_color || '#888',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.1,
            fill: false,
        }));
        progressChartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {datasets},
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: {
                    legend: {display: true, labels: {color: '#9A9AAF', boxWidth: 12, font: {size: 10, family: 'Titillium Web'}}},
                    tooltip: {
                        mode: 'nearest', intersect: false,
                        backgroundColor: '#1E1E2C', titleColor: '#fff', bodyColor: '#9A9AAF',
                        borderColor: '#38383F', borderWidth: 1,
                        callbacks: {
                            title: (items) => items[0]?.dataset?.label || '',
                            label: (item) => `Раунд ${item.parsed.x}: ${item.parsed.y} очков`,
                        }
                    },
                },
                scales: {
                    x: {type: 'linear', title: {display: true, text: 'Раунд', color: '#6B6B80', font: {family: 'Titillium Web'}}, ticks: {color: '#6B6B80', stepSize: 1}, grid: {color: '#38383F33'}},
                    y: {title: {display: true, text: 'Очки', color: '#6B6B80', font: {family: 'Titillium Web'}}, ticks: {color: '#6B6B80'}, grid: {color: '#38383F33'}, min: 0},
                },
                interaction: {mode: 'nearest', axis: 'x', intersect: false},
            },
        });
        return () => { if (progressChartInstance.current) progressChartInstance.current.destroy(); };
    }, [progressData, tab, chartReady]);

    useHistoryBack(selectedDriver != null, () => { setSelectedDriver(null); setDriverDetail(null); });
    const openDriver = async (num) => { setSelectedDriver(num); setDriverDetail(await api.get(`/api/driver/${num}?season=${season}`)); };

    if (selectedDriver && driverDetail) {
        const d = driverDetail, stats = d.season_stats;
        const champIdx = driversStandings?.standings?.findIndex(x=>x.driver_number===d.driver_number);
        const champPos = (champIdx != null && champIdx >= 0) ? champIdx + 1 : null;
        const ptsInt = stats ? Math.trunc(stats.points) : 0;
        const atmos = teamAtmos(d.team, d.team_color);
        // Крупный кроп по пояс: пилот занимает почти весь экран
        const heroPhoto = (d.photo_url && d.photo_url.includes('c_fill,g_north,ar_1:1,w_200'))
            ? d.photo_url.replace('c_fill,g_north,ar_1:1,w_200', 'c_fill,g_north,w_720,h_1000')
            : (d.photo_url_large || (d.card_photo_url ? hiResImg(d.card_photo_url, 800) : null));
        // Стат-лента интегрирована в постер: числа + hairline-сепараторы, НИКАКИХ карточек
        const heroStats = stats ? [
            {v: ptsInt, l: 'Очки'},
            champPos ? {v: <span><span style={{color:d.team_color}}>P</span>{champPos}</span>, l: 'В зачёте'} : null,
            {v: stats.wins, l: 'Побед'},
            {v: stats.podiums, l: 'Подиумов'},
        ].filter(Boolean) : [];
        return (
            <div className="page-container fade-in" style={{padding:0}}>
                {/* ПОСТЕР: экран построен вокруг пилота, данные — поверх композиции */}
                <div style={{position:'relative',minHeight:'86vh',overflow:'hidden',background:atmos.bg,display:'flex',flexDirection:'column'}}>
                    <div style={{position:'absolute',inset:0,background:`radial-gradient(70% 52% at 62% 26%, ${atmos.glow}, transparent 74%)`,pointerEvents:'none'}}/>
                    {/* Номер — обрезается краем экрана: графика, не текст */}
                    <div style={{position:'absolute',top:-54,left:-30,fontSize:300,fontWeight:400,fontFamily:"'Russo One','Exo 2',sans-serif",lineHeight:1,color:'rgba(255,255,255,0.075)',pointerEvents:'none',letterSpacing:-8}}>{d.driver_number}</div>
                    {/* Пилот — почти весь экран, уходит за правый край */}
                    {heroPhoto && <img src={heroPhoto} alt="" onError={e=>{if(d.card_photo_url&&e.target.src!==hiResImg(d.card_photo_url,800)){e.target.src=hiResImg(d.card_photo_url,800);}else{e.target.style.display='none';}}}
                        style={{position:'absolute',right:'-14%',bottom:0,width:'98%',maxWidth:470,objectFit:'contain',objectPosition:'bottom right',filter:'drop-shadow(0 26px 50px rgba(0,0,0,0.6)) saturate(1.06) contrast(1.04)',pointerEvents:'none',zIndex:1}}/>}
                    <div style={{position:'absolute',inset:0,backgroundImage:NOISE_URI,opacity:0.05,mixBlendMode:'overlay',pointerEvents:'none',zIndex:2}}/>
                    <div style={{position:'absolute',inset:0,background:'radial-gradient(135% 100% at 50% 30%, transparent 50%, rgba(0,0,0,0.5) 100%)',pointerEvents:'none',zIndex:2}}/>
                    <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg, transparent 52%, rgba(5,5,10,0.55) 78%, var(--f1-dark) 100%)',pointerEvents:'none',zIndex:2}}/>
                    <button onClick={()=>{setSelectedDriver(null);setDriverDetail(null);}} style={{position:'absolute',top:14,left:16,zIndex:4,background:'rgba(0,0,0,0.35)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:999,padding:'8px 16px',color:'#fff',fontSize:13,fontWeight:700,fontFamily:'inherit',cursor:'pointer'}}>← Назад</button>
                    {/* Композиция низа: команда → имя (поверх пилота) → интегрированная стата */}
                    <div style={{position:'relative',zIndex:3,marginTop:'auto',padding:'0 0 18px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:10,padding:'0 20px'}}>
                            <div style={{width:26,height:3,background:d.team_color,borderRadius:2}}/>
                            <div style={{fontSize:12,fontWeight:800,letterSpacing:3,textTransform:'uppercase',color:'rgba(255,255,255,0.85)'}}>{d.team}</div>
                        </div>
                        <div style={{padding:'10px 20px 0',fontSize:18,fontWeight:300,letterSpacing:6,textTransform:'uppercase',opacity:0.8}}>{d.first_name}</div>
                        <div style={{padding:'2px 0 0 20px',fontSize:'clamp(44px, 13.5vw, 62px)',fontWeight:400,fontFamily:"'Russo One','Exo 2',sans-serif",textTransform:'uppercase',lineHeight:1.0,whiteSpace:'nowrap',textShadow:'0 6px 34px rgba(0,0,0,0.5)'}}>{d.last_name}</div>
                        {heroStats.length > 0 && (
                            <div style={{display:'flex',margin:'26px 20px 0',borderTop:'1px solid rgba(255,255,255,0.16)'}}>
                                {heroStats.map((x,i)=>(
                                    <div key={i} style={{flex:1,padding:'12px 0 0',borderLeft:i?'1px solid rgba(255,255,255,0.12)':'none',textAlign:i?'center':'left',paddingLeft:i?0:2}}>
                                        <div style={{fontSize:26,fontWeight:400,fontFamily:"'Russo One','Exo 2',sans-serif",lineHeight:1}}>{x.v}</div>
                                        <div style={{fontSize:8.5,fontWeight:700,letterSpacing:1.6,textTransform:'uppercase',color:'rgba(255,255,255,0.5)',marginTop:5}}>{x.l}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <CareerStrip ergastId={driversStandings?.standings?.[champIdx]?.ergast_id}/>

                {/* Результаты — лента, не карточка */}
                {stats?.results?.length > 0 && (
                    <div style={{padding:'26px 20px 6px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:6}}>
                            <div style={{fontSize:12,fontFamily:"'Russo One','Exo 2',sans-serif",letterSpacing:2,textTransform:'uppercase',color:'var(--f1-text-muted)'}}>Сезон {season}</div>
                            <div style={{flex:1,height:1,background:'rgba(255,255,255,0.08)'}}/>
                        </div>
                        {stats.results.map((r,i)=>(
                            <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'11px 0',borderBottom:i<stats.results.length-1?'1px solid rgba(255,255,255,0.06)':'none'}}>
                                <span style={{width:34,fontSize:11,color:'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums'}}>R{r.round}</span>
                                <span style={{flex:1,fontSize:13.5,fontWeight:600}}>{r.race?.replace('Grand Prix','ГП')}</span>
                                <PosBadge pos={r.position}/>
                                <span style={{width:40,textAlign:'right',fontSize:13,fontWeight:700,color:r.points>0?'var(--f1-text)':'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums'}}>{r.points>0?`+${r.points}`:'0'}</span>
                            </div>
                        ))}
                    </div>
                )}
                {/* Напарник — тонкая строка, не блок */}
                {d.teammate && (
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'14px 20px 28px',opacity:0.85}}>
                        {d.teammate.photo_url && <DriverPhoto url={d.teammate.photo_url} size={26}/>}
                        <span style={{fontSize:10,fontWeight:800,letterSpacing:2,textTransform:'uppercase',color:'var(--f1-text-muted)'}}>Напарник</span>
                        <span style={{fontSize:13,fontWeight:700}}>{d.teammate.name}</span>
                    </div>
                )}
            </div>
        );
    }

    const notStarted = (tab==='drivers' && driversStandings?.not_started) || (tab==='constructors' && constructorsStandings?.not_started);
    const data = tab==='drivers'?driversStandings:tab==='constructors'?constructorsStandings:null;
    const leaderPts = data?.standings?.[0]?.points || 1;

    // Build large photo URL from regular photo URL
    const largePhoto = (url) => url ? url.replace('/2col/','/4col/').replace('/1col/','/4col/') : null;

    return (
        <div className="page-container fade-in flex-layout" style={{display:'flex',flexDirection:'column',overflow:'hidden',paddingBottom:0}}>
            {showSpoilerWarning && <SpoilerWarningOverlay onDismiss={()=>setSpoilerDismissed(true)} onBack={()=>onBack?onBack():null}/>}
            {/* Sticky header + tabs */}
            <div style={{flexShrink:0,padding:'12px 16px 0',background:'var(--f1-dark)'}}>
                {ptr.indicatorEl}
                <h2 style={{fontSize:22,fontWeight:900,marginBottom:12}}>Чемпионат</h2>
                <div className="tab-switch" style={{marginBottom:12}}>
                    <button className={`tab-switch-btn ${tab==='drivers'?'active':''}`} style={{padding:'9px 4px',fontSize:12,letterSpacing:0}} onClick={()=>setTab('drivers')}>Пилоты</button>
                    <button className={`tab-switch-btn ${tab==='constructors'?'active':''}`} style={{padding:'9px 4px',fontSize:12,letterSpacing:0}} onClick={()=>setTab('constructors')}>Кубок</button>
                    <button className={`tab-switch-btn ${tab==='h2h'?'active':''}`} style={{padding:'9px 4px',fontSize:12,letterSpacing:0}} onClick={()=>setTab('h2h')}>H2H</button>
                    <button className={`tab-switch-btn ${tab==='cards'?'active':''}`} style={{padding:'9px 4px',fontSize:12,letterSpacing:0}} onClick={()=>setTab('cards')}>Карточки</button>
                    <button className={`tab-switch-btn ${tab==='teams'?'active':''}`} style={{padding:'9px 4px',fontSize:12,letterSpacing:0}} onClick={()=>setTab('teams')}>Команды</button>
                </div>
            </div>

            {/* Scrollable content */}
            <div ref={ptr.containerRef} onTouchStart={ptr.onTouchStart} onTouchMove={ptr.onTouchMove} onTouchEnd={ptr.onTouchEnd} style={{flex:1,overflowY:'auto',WebkitOverflowScrolling:'touch',paddingBottom:'calc(92px + env(safe-area-inset-bottom, 12px))'}}>

            {/* Not started placeholder */}
            {notStarted && (
                <div style={{textAlign:'center',padding:'50px 20px',color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🏁</div><div style={{fontSize:16,fontWeight:700,marginBottom:4}}>Сезон {season} ещё не начался</div><div style={{fontSize:13}}>Результаты появятся после первой гонки</div></div>
            )}

            {/* DRIVERS STANDINGS */}
            {tab==='drivers' && !notStarted && (!data?.standings ? (
                <div style={{padding:16}}><TyreLoader text="Загрузка..."/></div>
            ) : (
                <div style={{padding:'0 12px'}}>
                    {/* Top 3 podium cards (app design) */}
                    {(() => {
                        const top3 = data.standings.slice(0,3);
                        if (top3.length < 3) return null;
                        const METAL = ['#FFCB05','#C0C0C0','#CD7F32'];
                        const orderIdx = [1,0,2]; // 2nd, 1st, 3rd
                        const P = ({s, place, winner}) => {
                            const portrait = s.card_photo_url || s.photo_url;
                            const metal = METAL[place-1];
                            return (
                                <div onClick={()=>s.driver_number&&openDriver(s.driver_number)}
                                     style={{flex:winner?1.18:1,background:winner?'#1A1505':'var(--f1-card-solid)',borderRadius:20,
                                             border:`1.5px solid ${metal}${winner?'CC':'55'}`,overflow:'hidden',cursor:'pointer',
                                             boxShadow:winner?`0 6px 22px ${metal}40`:`0 4px 12px ${metal}20`,alignSelf:'flex-end',flexShrink:0,minWidth:0}}>
                                    <div style={{width:'100%',height:winner?122:106,background:'#1A1A24',position:'relative'}}>
                                        {portrait && <img src={hiResImg(portrait, 400)} alt="" loading="lazy" style={{width:'100%',height:'100%',objectFit:'cover',objectPosition:'top center'}} onError={e=>{e.target.style.display='none'}}/>}
                                        <div style={{position:'absolute',top:0,left:10,fontSize:winner?42:34,fontWeight:800,letterSpacing:-1.5,color:'#FAFAFA',textShadow:'0 2px 8px rgba(0,0,0,0.75)'}}>{place}</div>
                                    </div>
                                    <div style={{padding:'10px 10px 12px'}}>
                                        <div style={{fontSize:12,fontWeight:700,lineHeight:'14px'}}>{s.first_name || (s.name||'').split(' ')[0]}</div>
                                        <div style={{fontSize:12,fontWeight:700,lineHeight:'14px'}}>{s.last_name || (s.name||'').split(' ').slice(1).join(' ')}</div>
                                        <div style={{display:'flex',alignItems:'baseline',gap:3,marginTop:8}}>
                                            <span style={{color:winner?'#FFCB05':(s.team_color||'#FAFAFA'),fontWeight:800,fontSize:winner?18:16,letterSpacing:-0.3}}>{s.points}</span>
                                            <span style={{color:winner?'#FFCB05':(s.team_color||'#FAFAFA'),fontWeight:700,fontSize:9,letterSpacing:0.8}}>PTS</span>
                                        </div>
                                        {s.wins > 0 && <div style={{color:'var(--f1-text-secondary)',fontSize:10,fontWeight:700,marginTop:2}}>{s.wins} побед{s.wins===1?'а':''}</div>}
                                    </div>
                                </div>
                            );
                        };
                        return (
                            <div style={{display:'flex',gap:8,alignItems:'flex-end',margin:'6px 0 18px'}}>
                                {orderIdx.map(i => <P key={i} s={top3[i]} place={i+1} winner={i===0}/>)}
                            </div>
                        );
                    })()}

                    <div className="divider-line" style={{margin:'8px 4px'}}/>

                    {/* Rest of grid */}
                    {data.standings.slice(3).map((s,i)=>(
                        <React.Fragment key={i}>
                            {i===7 && <div className="divider-line" style={{margin:'4px 4px'}}/>}
                            <div className="standings-row" onClick={()=>s.driver_number&&openDriver(s.driver_number)} style={{cursor:'pointer',padding:'13px 8px',gap:12}}>
                                <div style={{width:30,fontWeight:900,fontSize:17,color:'var(--f1-text-muted)',textAlign:'center',fontVariantNumeric:'tabular-nums'}}>{s.position}</div>
                                <div style={{background:s.team_color,width:4,height:52,borderRadius:2}}/>
                                {s.photo_url && <img src={hiResImg(s.photo_url, 320)} alt="" style={{width:56,height:56,borderRadius:'50%',objectFit:'cover',background:'var(--f1-gray)',border:`2px solid ${s.team_color}`,flexShrink:0}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontWeight:700,fontSize:16.5}}>{s.name}</div>
                                    <div style={{fontSize:12.5,color:'var(--f1-text-muted)',marginTop:2}}>{s.team}</div>
                                </div>
                                <div style={{textAlign:'right'}}>
                                    <div style={{fontWeight:900,fontSize:19,fontVariantNumeric:'tabular-nums'}}>{s.points}</div>
                                    {s.gap_to_leader>0 && <div style={{fontSize:11,color:'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums'}}>-{s.gap_to_leader}</div>}
                                </div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            ))}

            {/* CONSTRUCTORS STANDINGS */}
            {tab==='constructors' && !notStarted && (!constructorsStandings?.standings ? (
                <div style={{padding:16}}><TyreLoader text="Загрузка..."/></div>
            ) : (
                <div style={{padding:'0 12px'}}>
                    {constructorsStandings.standings.map((s,i)=>{
                        const cLeader = constructorsStandings.standings[0]?.points || 1;
                        const driverPoints = s.drivers?.map(d => {
                            const ds = driversStandings?.standings?.find(x => x.driver_number === d.driver_number);
                            return {code: d.code, photo_url: d.photo_url, points: ds?.points || 0, driver_number: d.driver_number};
                        }) || [];
                        return (
                            <div key={i} className="card" style={{marginBottom:8,padding:12,borderLeft:`4px solid ${s.team_color}`}}>
                                <div style={{display:'flex',alignItems:'center',gap:10}}>
                                    <div style={{fontSize:20,fontWeight:900,color:i<3?['#FFD700','#C0C0C0','#CD7F32'][i]:'var(--f1-text-muted)',minWidth:28,textAlign:'center'}}>{s.position}</div>
                                    {s.logo_url && <img src={hiResImg(s.logo_url, 200)} alt="" style={{width:36,height:36,objectFit:'contain',flexShrink:0}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                    <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontWeight:700,fontSize:15}}>{s.team}</div>
                                        <div style={{display:'flex',gap:8,marginTop:6,alignItems:'center'}}>
                                            {driverPoints.map((d,j)=>(
                                                <div key={j} onClick={(e)=>{e.stopPropagation();d.driver_number&&openDriver(d.driver_number);}} style={{display:'flex',alignItems:'center',gap:4,cursor:'pointer',padding:'3px 8px',background:'rgba(255,255,255,0.03)',borderRadius:8,border:'1px solid var(--f1-border)'}}>
                                                    {d.photo_url && <img src={hiResImg(d.photo_url, 200)} alt="" style={{width:32,height:32,borderRadius:'50%',objectFit:'cover',background:'var(--f1-gray)'}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                                    <span style={{fontSize:11,fontWeight:700}}>{d.code}</span>
                                                    <span style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:600,fontVariantNumeric:'tabular-nums'}}>{d.points}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {s.points > 0 && (
                                            <div style={{display:'flex',height:6,borderRadius:3,overflow:'hidden',marginTop:6,background:'var(--f1-gray)',gap:1}}>
                                                {driverPoints.map((d,j)=>{
                                                    const w = Math.max(3, (d.points / s.points) * 100);
                                                    return <div key={j} style={{width:`${w}%`,background:j===0?s.team_color:`${s.team_color}99`,borderRadius:3,transition:'width 0.5s ease'}}/>;
                                                })}
                                            </div>
                                        )}
                                        {s.points === 0 && (
                                            <div className="progress-bar-bg" style={{marginTop:6,maxWidth:140}}>
                                                <div className="progress-bar-fill" style={{width:'3%',background:s.team_color}}/>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{textAlign:'right'}}>
                                        <div style={{fontWeight:900,fontSize:18,fontVariantNumeric:'tabular-nums'}}>{s.points}</div>
                                        {i>0 && <div style={{fontSize:10,color:'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums'}}>-{constructorsStandings.standings[0].points-s.points}</div>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}

            {/* POINTS PROGRESSION */}
            {tab==='progress' && (
                <div style={{padding:'0 12px'}}>
                    {!progressData ? (
                        <div style={{padding:16}}><TyreLoader text="Загрузка прогресса..."/></div>
                    ) : progressData.not_started ? (
                        <div style={{textAlign:'center',padding:'50px 20px',color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>📈</div><div style={{fontSize:16,fontWeight:700,marginBottom:4}}>Сезон {season} ещё не начался</div><div style={{fontSize:13}}>График появится после первой гонки</div></div>
                    ) : progressData.drivers?.length > 0 ? (
                        <div>
                            <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>Прогресс очков · Топ-10 · Сезон {season}</div>
                            <div className="card" style={{padding:8,height:350,marginBottom:12}}>
                                <canvas ref={progressChartRef}/>
                            </div>
                            {/* Final standings mini-table */}
                            {progressData.drivers.map((d, i) => (
                                <div key={d.driver_number} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 4px',borderBottom:'1px solid var(--f1-border)'}}>
                                    <div style={{width:20,fontWeight:900,fontSize:13,color:i<3?['#FFD700','#C0C0C0','#CD7F32'][i]:'var(--f1-text-muted)',textAlign:'center'}}>{i+1}</div>
                                    <div style={{background:d.team_color,width:3,height:20,borderRadius:2}}/>
                                    <span style={{fontWeight:700,fontSize:13,color:d.team_color,width:36}}>{d.code}</span>
                                    <span style={{flex:1,fontSize:12,color:'var(--f1-text-muted)'}}>{d.team?.split(' ')[0]}</span>
                                    <span style={{fontWeight:900,fontSize:14,fontVariantNumeric:'tabular-nums'}}>{d.total_points}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>📈</div><div style={{fontSize:15,fontWeight:700}}>Нет данных</div></div>
                    )}
                </div>
            )}

            {/* DRIVER CARDS — redesigned with big photos */}
            {tab==='cards' && (
                <div style={{padding:'0 12px'}}>
                    {!allDrivers ? (
                        <div style={{padding:16}}><TyreLoader text="Загрузка карточек..."/></div>
                    ) : (
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                            {allDrivers.map(d=>{
                                const bigPhoto = hiResImg(d.card_photo_url || d.photo_url_large || largePhoto(d.photo_url), 800);
                                return (
                                    <div key={d.driver_number} onClick={()=>openDriver(d.driver_number)}
                                         style={{cursor:'pointer',borderRadius:16,overflow:'hidden',position:'relative',background:'var(--f1-card)',border:'1px solid var(--f1-border)',boxShadow:'0 4px 24px rgba(0,0,0,0.2)',transition:'transform 0.15s'}}>
                                        <div style={{position:'absolute',top:10,right:10,fontSize:42,fontWeight:900,color:d.team_color,opacity:0.2,lineHeight:1,zIndex:1}}>{d.driver_number}</div>
                                        <div style={{width:'100%',height:180,background:`linear-gradient(180deg,${d.team_color}15,${d.team_color}05)`,position:'relative',overflow:'hidden'}}>
                                            {bigPhoto && <img src={bigPhoto} alt="" style={{width:'100%',height:'100%',objectFit:'cover',objectPosition:d.card_photo_position||'top center'}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                            <div style={{position:'absolute',bottom:0,left:0,right:0,height:'60%',background:`linear-gradient(transparent,${d.team_color}CC)`}}/>
                                            <div style={{position:'absolute',bottom:8,left:10,right:10,zIndex:1}}>
                                                <div style={{fontSize:11,color:'rgba(255,255,255,0.8)'}}>{flagEmoji(d.country)} {d.first_name||d.name?.split(' ')[0]}</div>
                                                <div style={{fontSize:16,fontWeight:900,color:'#fff',lineHeight:1.2,textShadow:'0 1px 4px rgba(0,0,0,0.5)'}}>{d.last_name||d.name?.split(' ').slice(1).join(' ')}</div>
                                            </div>
                                        </div>
                                        <div style={{padding:'8px 10px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                            <div style={{fontSize:11,color:d.team_color,fontWeight:600}}>{d.team?.replace('F1 Team','').trim()}</div>
                                            {d.points != null && <div style={{background:`${d.team_color}22`,color:d.team_color,padding:'2px 8px',borderRadius:6,fontSize:12,fontWeight:700}}>{d.points} очк.</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* HEAD-TO-HEAD */}
            {tab==='h2h' && (
                <div style={{padding:'0 12px'}}>
                    {!h2hData ? (
                        <div style={{padding:16}}><TyreLoader text="Загрузка H2H..."/></div>
                    ) : h2hData.head_to_head?.length > 0 ? (
                        h2hData.head_to_head.map((pair, i) => {
                            const d1 = pair.driver1, d2 = pair.driver2;
                            const totalPts = (d1.points + d2.points) || 1;
                            const d1Pct = (d1.points / totalPts) * 100;
                            const d2Pct = (d2.points / totalPts) * 100;
                            const totalWins = (d1.wins + d2.wins) || 1;
                            const d1WPct = (d1.wins / totalWins) * 100;
                            return (
                                <div key={i} className="card" style={{marginBottom:10,padding:0,overflow:'hidden',borderLeft:`4px solid ${pair.color}`}}>
                                    <div style={{padding:'10px 12px',borderBottom:'1px solid var(--f1-border)',display:'flex',alignItems:'center',gap:8}}>
                                        <span style={{fontSize:13,fontWeight:700,color:pair.color}}>{pair.team}</span>
                                    </div>
                                    <div style={{padding:'12px'}}>
                                        {/* Driver headers */}
                                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                                                {d1.photo_url && <img src={hiResImg(d1.photo_url, 200)} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',border:`2px solid ${pair.color}`,background:'var(--f1-gray)'}} onError={e=>{e.target.style.display='none'}}/>}
                                                <div>
                                                    <div style={{fontWeight:800,fontSize:16}}>{d1.name}</div>
                                                    <div style={{fontSize:11,color:'var(--f1-text-muted)'}}>{d1.full_name}</div>
                                                </div>
                                            </div>
                                            <div style={{display:'flex',alignItems:'center',gap:8,textAlign:'right'}}>
                                                <div>
                                                    <div style={{fontWeight:800,fontSize:16}}>{d2.name}</div>
                                                    <div style={{fontSize:11,color:'var(--f1-text-muted)'}}>{d2.full_name}</div>
                                                </div>
                                                {d2.photo_url && <img src={hiResImg(d2.photo_url, 200)} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',border:`2px solid ${pair.color}`,background:'var(--f1-gray)'}} onError={e=>{e.target.style.display='none'}}/>}
                                            </div>
                                        </div>
                                        {/* Points bar */}
                                        <div style={{marginBottom:8}}>
                                            <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--f1-text-muted)',marginBottom:2}}>
                                                <span>{d1.points} очков</span><span>Очки</span><span>{d2.points} очков</span>
                                            </div>
                                            <div style={{display:'flex',height:8,borderRadius:4,overflow:'hidden',gap:2}}>
                                                <div style={{width:`${d1Pct}%`,background:pair.color,borderRadius:4,transition:'width 0.5s'}}/>
                                                <div style={{width:`${d2Pct}%`,background:`${pair.color}66`,borderRadius:4,transition:'width 0.5s'}}/>
                                            </div>
                                        </div>
                                        {/* Wins bar */}
                                        {(d1.wins > 0 || d2.wins > 0) && (
                                            <div>
                                                <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--f1-text-muted)',marginBottom:2}}>
                                                    <span>{d1.wins} побед</span><span>Победы</span><span>{d2.wins} побед</span>
                                                </div>
                                                <div style={{display:'flex',height:8,borderRadius:4,overflow:'hidden',gap:2}}>
                                                    <div style={{width:`${d1WPct}%`,background:'#FFD700',borderRadius:4,transition:'width 0.5s'}}/>
                                                    <div style={{width:`${100-d1WPct}%`,background:'#FFD70044',borderRadius:4,transition:'width 0.5s'}}/>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🤝</div><div style={{fontSize:15,fontWeight:700}}>Нет данных H2H</div><div style={{fontSize:13}}>Данные появятся после первой гонки</div></div>
                    )}
                </div>
            )}

            {/* TEAMS — full team cards */}
            {tab==='teams' && (
                <div style={{padding:'0 12px'}}>
                    {!constructorsStandings?.standings ? (
                        <div style={{padding:16}}><TyreLoader text="Загрузка команд..."/></div>
                    ) : (
                        constructorsStandings.standings.map((team,i)=>(
                            <div key={i} className="card" style={{marginBottom:12,padding:0,overflow:'hidden'}}>
                                {team.car_url && (
                                    <div style={{width:'100%',height:140,background:`linear-gradient(135deg,${team.team_color}15,var(--f1-card-solid))`,position:'relative',overflow:'hidden'}}>
                                        <img src={hiResImg(team.car_url, 1200)} alt="" style={{width:'100%',height:'100%',objectFit:'contain',padding:12}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>
                                    </div>
                                )}
                                <div style={{padding:'12px 14px'}}>
                                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                                        {team.logo_url && <img src={hiResImg(team.logo_url, 200)} alt="" style={{width:28,height:28,objectFit:'contain'}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                        <div style={{flex:1}}>
                                            <div style={{fontWeight:700,fontSize:15,color:team.team_color}}>{team.team}</div>
                                        </div>
                                        <div style={{textAlign:'right'}}>
                                            <div style={{fontWeight:900,fontSize:18,fontVariantNumeric:'tabular-nums'}}>{team.points||'—'}</div>
                                            <div style={{fontSize:10,color:'var(--f1-text-muted)'}}>P{team.position||i+1}</div>
                                        </div>
                                    </div>
                                    <div style={{display:'flex',gap:12}}>
                                        {team.drivers?.map((d,j)=>(
                                            <div key={j} onClick={(e)=>{e.stopPropagation();d.driver_number&&openDriver(d.driver_number);}} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',flex:1,padding:'6px 8px',background:'rgba(255,255,255,0.03)',borderRadius:10,border:'1px solid var(--f1-border)'}}>
                                                {d.photo_url && <img src={hiResImg(d.photo_url, 200)} alt="" style={{width:32,height:32,borderRadius:'50%',objectFit:'cover',background:'var(--f1-gray)',border:`2px solid ${team.team_color}`}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                                <div>
                                                    <div style={{fontWeight:700,fontSize:13}}>{d.code}</div>
                                                    <div style={{fontSize:10,color:'var(--f1-text-muted)'}}>{d.name?.split(' ').pop()}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
            </div>{/* end scrollable content */}
        </div>
    );
};

// ==== PREDICTIONS PAGE ====
const PRED_ICON_V = 1;
const PredIcon = ({type, size}) => ['winner','podium','fastest_lap','dnf_count','safety_car','points'].includes(type)
    ? <img src={'/static/img/pred/'+type+'.webp?v='+PRED_ICON_V} alt="" width={size} height={size} draggable={false} style={{width:size,height:size,objectFit:'contain',filter:'drop-shadow(0 3px 6px rgba(0,0,0,0.5))',pointerEvents:'none'}}/>
    : '•';
const PRED_ICON_BOX = 'radial-gradient(circle at 50% 38%, rgba(255,255,255,0.09), rgba(255,255,255,0.02) 70%)';
const PRED_LABELS = {winner:'Победитель', podium:'Подиум', fastest_lap:'Быстрый круг', dnf_count:'Сходы', safety_car:'Safety Car'};
const PODIUM_COLORS = ['#FFCB05','#C0C0C0','#CD7F32'];

const fmtPredValue = (type, value, drivers) => {
    const byNum = {}; (drivers||[]).forEach(d=>{byNum[d.driver_number]=d;});
    const nm = (n) => byNum[n] ? (byNum[n].last_name || byNum[n].name || ('#'+n)) : ('#'+n);
    let v = value;
    if (typeof v === 'string' && (v.startsWith('[') || /^\d+$/.test(v))) { try { v = JSON.parse(v); } catch(e){} }
    if (type === 'safety_car') return [true,'yes','true',1,'1'].includes(v) ? 'Да' : 'Нет';
    if (type === 'dnf_count') return v + ' сходов';
    if (type === 'podium' && Array.isArray(v)) return v.map(nm).join(' → ');
    if (type === 'winner' || type === 'fastest_lap') return nm(v);
    return String(v ?? '');
};

// Строка пилота — как DriverPickerList в приложении
const DriverPickRow = ({d, isSelected, rank, onClick, disabled}) => {
    const tc = d.team_color || '#666';
    return (
        <div onClick={()=>!disabled&&onClick()} style={{background:'var(--f1-card-solid)',borderRadius:18,border:'1.5px solid '+(isSelected?'#E10600':'var(--f1-border)'),display:'flex',alignItems:'center',padding:'10px 14px 10px 0',cursor:disabled?'default':'pointer',opacity:disabled?0.45:1,boxShadow:isSelected?'0 4px 14px rgba(225,6,0,0.25)':'none',flexShrink:0}}>
            <div style={{width:4,height:44,borderRadius:2,background:tc,marginLeft:14}}/>
            <div style={{width:46,textAlign:'center',marginLeft:4,fontSize:26,fontWeight:800,color:tc,flexShrink:0}}>{d.driver_number}</div>
            {d.photo_url && <img src={d.photo_url} alt="" loading="lazy" style={{width:48,height:48,borderRadius:'50%',objectFit:'cover',background:'var(--f1-card-hover)',marginLeft:4,flexShrink:0}} onError={e=>{e.target.style.display='none'}}/>}
            <div style={{flex:1,minWidth:0,marginLeft:12}}>
                <div style={{fontSize:16,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</div>
                <div style={{fontSize:12,color:'var(--f1-text-muted)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    <span style={{color:tc,fontWeight:700}}>{d.team}</span>
                </div>
            </div>
            {rank ? (
                <div style={{width:28,height:28,borderRadius:14,background:'#E10600',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:13,flexShrink:0}}>{rank}</div>
            ) : (
                <div style={{width:22,height:22,borderRadius:11,border:'2px solid '+(isSelected?'#E10600':'#3A3A4A'),background:isSelected?'#E10600':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    {isSelected && <div style={{width:8,height:8,borderRadius:4,background:'#fff'}}/>}
                </div>
            )}
        </div>
    );
};

// Секция «Трасса» в карточке ГП — постерно, по манифесту: схема трассы как
// арт-объект со свечением, факты hairline-лентой (без карточек), рекордсмен
// и последние победители из нашей истории (кэш 24ч на бэке).
// 3D-неоновая схема трассы (по мотивам landonorris.com/calendar) — чистый
// контур из телеметрии (эндпоинт /api/circuit/{id}/track), псевдо-3D пол в
// перспективе + светящаяся лента + «горячий круг» бегущей кометой.
const Track3D = ({circuitId, accent, onState, big}) => {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const [pts, setPts] = useState(null);
    const [failed, setFailed] = useState(false);
    const rafRef = useRef(0);
    const visRef = useRef(true);
    const col = accent || '#E10600';

    useEffect(() => {
        if (!circuitId) return;
        let a = true;
        api.get(`/api/circuit/${circuitId}/track`).then(d => {
            if (!a) return;
            if (d?.available && d.points?.length > 20) { setPts(d.points); onState && onState(true); }
            else { setFailed(true); onState && onState(false); }
        }).catch(() => { if (a) { setFailed(true); onState && onState(false); } });
        return () => { a = false; };
    }, [circuitId]);

    useEffect(() => {
        if (!pts || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const io = new IntersectionObserver(
            (e) => { visRef.current = e[0].isIntersecting; },
            { threshold: 0.05 });
        io.observe(canvas);

        // проекция «пол в перспективе»: далёкий край сильно уже и выше,
        // ближний — шире и ниже (наклонная плоскость, как у Норриса).
        const PERSP = 0.62;   // насколько сужается дальний край
        let W = 0, H = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);
        const resize = () => {
            const cw = wrapRef.current ? wrapRef.current.clientWidth : 340;
            W = cw; H = Math.round(cw * (big ? 1.02 : 0.74));
            canvas.width = W * dpr; canvas.height = H * dpr;
            canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener('resize', resize);

        const project = (nx, ny) => {
            // nx,ny в 0..1000. depth: 0 (далеко/верх) .. 1 (близко/низ).
            // Вертикаль тоже перспективная: дальние ряды сжаты (нелинейно).
            const u = nx / 1000 - 0.5;
            const depth = ny / 1000;
            const s = 1 - PERSP * (1 - depth);        // гориз. сжатие вдаль
            const vy = Math.pow(depth, 1.35);          // верт. перспектива
            const padX = W * 0.10, topY = H * 0.10, botY = H * 0.94;
            const px = W / 2 + u * (W - padX * 2) * s;
            const py = topY + vy * (botY - topY);
            return [px, py, s];
        };

        const proj = pts.map(p => project(p.x, p.y));
        const n = proj.length;

        const drawPath = (lw, style, blur) => {
            ctx.beginPath();
            for (let i = 0; i < n; i++) {
                const [px, py] = proj[i];
                i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
            }
            ctx.closePath();
            ctx.lineJoin = 'round'; ctx.lineCap = 'round';
            ctx.lineWidth = lw; ctx.strokeStyle = style;
            ctx.shadowColor = col; ctx.shadowBlur = blur || 0;
            ctx.stroke();
            ctx.shadowBlur = 0;
        };

        let head = 0;
        const TRAIL = Math.round(n * 0.16);
        const render = () => {
            rafRef.current = requestAnimationFrame(render);
            if (!visRef.current) return;
            ctx.clearRect(0, 0, W, H);
            // фон-пол: радиальное свечение снизу
            const g = ctx.createRadialGradient(W/2, H*0.72, 10, W/2, H*0.72, H*0.9);
            g.addColorStop(0, 'rgba(255,255,255,0.04)');
            g.addColorStop(0.4, hexA(col, 0.06));
            g.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
            // пол-сетка в перспективе: горизонтальные ряды вдаль + вертикали,
            // сходящиеся к дальнему краю (читается наклонная плоскость)
            ctx.lineWidth = 1;
            for (let d = 0; d <= 1.0001; d += 0.125) {
                const [lx, ly] = project(-90, d * 1000);
                const [rx, ry] = project(1090, d * 1000);
                ctx.strokeStyle = `rgba(255,255,255,${0.02 + d * 0.05})`;
                ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(rx, ry); ctx.stroke();
            }
            for (let ux = -90; ux <= 1090; ux += 147.5) {
                const [tx, ty] = project(ux, 0);
                const [bx, by] = project(ux, 1000);
                ctx.strokeStyle = 'rgba(255,255,255,0.035)';
                ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(bx, by); ctx.stroke();
            }
            // подложка-«асфальт»
            drawPath(9, 'rgba(0,0,0,0.85)', 0);
            drawPath(6, 'rgba(255,255,255,0.06)', 0);
            // неон: широкий мягкий + тонкий яркий кор
            drawPath(4.5, hexA(col, 0.9), 16);
            drawPath(1.6, 'rgba(255,255,255,0.85)', 6);
            // старт/финиш — клетчатая метка у points[0]
            const [sx, sy] = proj[0];
            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.shadowColor = '#fff'; ctx.shadowBlur = 10;
            ctx.beginPath(); ctx.arc(sx, sy, 4, 0, Math.PI*2); ctx.fill();
            ctx.restore();
            // «горячий круг» — комета с хвостом
            head = (head + 0.6) % n;
            for (let t = 0; t < TRAIL; t++) {
                const idx = (Math.floor(head) - t + n) % n;
                const [px, py] = proj[idx];
                const a = (1 - t / TRAIL);
                ctx.beginPath();
                ctx.arc(px, py, 1 + a * 2.2, 0, Math.PI*2);
                ctx.fillStyle = t === 0 ? '#fff' : hexA(col, a * 0.8);
                ctx.shadowColor = t === 0 ? '#fff' : col;
                ctx.shadowBlur = t === 0 ? 14 : 8 * a;
                ctx.fill();
            }
            ctx.shadowBlur = 0;
        };
        render();
        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', resize);
            io.disconnect();
        };
    }, [pts, col]);

    if (failed) return null;   // фолбэк на плоский PNG делает TrackSection
    return (
        <div ref={wrapRef} style={{position:'relative', width:'100%', margin:'2px 0 4px'}}>
            <canvas ref={canvasRef} style={{display:'block', width:'100%'}}/>
            {!pts && <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,color:'var(--f1-text-muted)'}}>схема трассы…</div>}
        </div>
    );
};

// helpers для Track3D
function hexA(hex, a) {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0,2),16), g = parseInt(h.slice(2,4),16), b = parseInt(h.slice(4,6),16);
    return `rgba(${r},${g},${b},${a})`;
}
const TrackSection = ({race}) => {
    const [hist, setHist] = useState(null);
    const [has3d, setHas3d] = useState(null);   // null=грузится, true=3D, false=PNG-фолбэк
    const [expanded, setExpanded] = useState(false);
    useHistoryBack(expanded, () => setExpanded(false));
    useEffect(() => {
        if (!race?.circuit_id) return;
        let a = true;
        api.get(`/api/history/circuit/${race.circuit_id}`).then(d => { if (a) setHist(d); }).catch(()=>{});
        return () => { a = false; };
    }, [race?.circuit_id]);
    if (!race || (!race.circuit && !race.circuit_outline)) return null;
    const facts = [
        race.laps ? {v: race.laps, l: 'Кругов'} : null,
        race.base_lap ? {v: `~${fmtBaseLap(race.base_lap)}`, l: 'Круг'} : null,
        hist?.total_races ? {v: hist.total_races, l: 'Гонок в истории'} : null,
    ].filter(Boolean);
    return (
        <div style={{margin:'4px 0 20px'}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
                <div style={{fontSize:12,fontFamily:"'Russo One','Exo 2',sans-serif",letterSpacing:2,textTransform:'uppercase',color:'var(--f1-text-muted)'}}>Трасса</div>
                <div style={{flex:1,height:1,background:'rgba(255,255,255,0.08)'}}/>
            </div>
            {/* 3D-неоновая схема трассы — герой; тап = на весь экран */}
            {race.circuit_id && has3d !== false && (
                <div style={{position:'relative',cursor:'pointer'}} onClick={()=>has3d && setExpanded(true)}>
                    <Track3D circuitId={race.circuit_id} onState={setHas3d}/>
                    {has3d && (
                        <div style={{position:'absolute',right:6,top:6,display:'flex',alignItems:'center',gap:5,
                                     padding:'5px 9px',borderRadius:999,background:'rgba(0,0,0,0.45)',backdropFilter:'blur(4px)',
                                     border:'1px solid rgba(255,255,255,0.14)',fontSize:9.5,fontWeight:700,letterSpacing:1,
                                     textTransform:'uppercase',color:'rgba(255,255,255,0.8)',pointerEvents:'none'}}>
                            <span style={{fontSize:12,lineHeight:1}}>⤢</span> Развернуть
                        </div>
                    )}
                </div>
            )}
            {race.circuit_outline && has3d === false && (
                <div style={{position:'relative',display:'flex',justifyContent:'center',padding:'10px 0 2px'}}>
                    <div style={{position:'absolute',inset:'-10% 0',background:'radial-gradient(55% 65% at 50% 50%, rgba(225,6,0,0.10), transparent 75%)',pointerEvents:'none'}}/>
                    <img src={race.circuit_outline} alt="" loading="lazy"
                         style={{width:'84%',maxWidth:400,filter:'brightness(1.15) drop-shadow(0 0 22px rgba(225,6,0,0.30))'}}
                         onError={e=>{e.target.style.display='none'}}/>
                </div>
            )}
            <div style={{textAlign:'center',marginTop:10}}>
                {race.circuit && <div style={{fontSize:19,fontFamily:"'Russo One','Exo 2',sans-serif",textTransform:'uppercase',letterSpacing:0.5,lineHeight:1.15}}>{race.circuit}</div>}
                {(race.locality || race.country) && (
                    <div style={{fontSize:12,color:'var(--f1-text-secondary)',marginTop:5}}>
                        {flagEmoji(race.country_code)} {race.locality}{race.country ? `, ${race.country}` : ''}
                    </div>
                )}
            </div>
            {facts.length > 0 && (
                <div style={{display:'flex',margin:'18px 0 0',borderTop:'1px solid rgba(255,255,255,0.14)'}}>
                    {facts.map((x,i)=>(
                        <div key={i} style={{flex:1,padding:'11px 0 0',borderLeft:i?'1px solid rgba(255,255,255,0.10)':'none',textAlign:'center'}}>
                            <div style={{fontSize:22,fontWeight:400,fontFamily:"'Russo One','Exo 2',sans-serif",lineHeight:1}}>{x.v}</div>
                            <div style={{fontSize:8.5,fontWeight:700,letterSpacing:1.6,textTransform:'uppercase',color:'rgba(255,255,255,0.5)',marginTop:5}}>{x.l}</div>
                        </div>
                    ))}
                </div>
            )}
            {hist?.most_wins && (
                <div style={{textAlign:'center',marginTop:16,fontSize:12.5,color:'var(--f1-text-secondary)'}}>
                    Рекордсмен трассы — <span style={{fontWeight:800,color:'var(--f1-text)'}}>{hist.most_wins.driver}</span> · {hist.most_wins.wins} побед
                </div>
            )}
            {hist?.winners?.length > 0 && (
                <div style={{marginTop:14}}>
                    <div style={{fontSize:9.5,fontWeight:800,letterSpacing:1.8,textTransform:'uppercase',color:'var(--f1-text-muted)',marginBottom:6}}>Победители здесь</div>
                    {hist.winners.slice(0,5).map((w,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:i<Math.min(hist.winners.length,5)-1?'1px solid rgba(255,255,255,0.05)':'none'}}>
                            <span style={{width:46,fontSize:13,fontFamily:"'Russo One','Exo 2',sans-serif",color:'var(--f1-red)'}}>{w.season}</span>
                            <span style={{flex:1,fontSize:13,fontWeight:600}}>{w.winner}</span>
                            <span style={{fontSize:11,color:'var(--f1-text-muted)'}}>{w.constructor}</span>
                        </div>
                    ))}
                </div>
            )}
            {/* Полноэкранный режим 3D-трассы */}
            {expanded && (
                <BodyPortal>
                    <div style={{position:'fixed',inset:0,zIndex:3200,background:'var(--f1-darker)',display:'flex',flexDirection:'column'}}>
                        <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:14,flexShrink:0}}>
                            <button onClick={()=>setExpanded(false)} style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.14)',borderRadius:999,padding:'8px 15px',color:'#fff',fontSize:13,fontWeight:700,fontFamily:'inherit',cursor:'pointer'}}>✕ Закрыть</button>
                            <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:15,textTransform:'uppercase',letterSpacing:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{race.circuit || race.name}</div>
                        </div>
                        <div style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'center',padding:'0 10px 30px'}}>
                            <Track3D circuitId={race.circuit_id} big/>
                            <div style={{textAlign:'center',marginTop:16}}>
                                {(race.locality || race.country) && <div style={{fontSize:13,color:'var(--f1-text-secondary)'}}>{flagEmoji(race.country_code)} {race.locality}{race.country ? `, ${race.country}` : ''}</div>}
                                <div style={{display:'flex',justifyContent:'center',gap:22,marginTop:12}}>
                                    {race.laps && <div style={{textAlign:'center'}}><div style={{fontSize:24,fontFamily:"'Russo One','Exo 2',sans-serif"}}>{race.laps}</div><div style={{fontSize:8.5,letterSpacing:1.5,color:'var(--f1-text-muted)',textTransform:'uppercase',marginTop:3}}>Кругов</div></div>}
                                    {race.base_lap && <div style={{textAlign:'center'}}><div style={{fontSize:24,fontFamily:"'Russo One','Exo 2',sans-serif"}}>~{fmtBaseLap(race.base_lap)}</div><div style={{fontSize:8.5,letterSpacing:1.5,color:'var(--f1-text-muted)',textTransform:'uppercase',marginTop:3}}>Круг</div></div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </BodyPortal>
            )}
        </div>
    );
};

// «За карьеру» — вторая hairline-лента под постером пилота (наш /api/driver/career).
const CareerStrip = ({ergastId}) => {
    const [c, setC] = useState(null);
    useEffect(() => {
        if (!ergastId) return; let a = true;
        api.get('/api/driver/career/' + ergastId).then(d => { if (a && d?.career) setC(d.career); }).catch(()=>{});
        return () => { a = false; };
    }, [ergastId]);
    if (!c) return null;
    const items = [
        {v: c.championships, l: 'Титулов'}, {v: c.wins, l: 'Побед'}, {v: c.podiums, l: 'Подиумов'},
        {v: c.poles, l: 'Поулов'}, {v: c.races, l: 'Гонок'},
    ].filter(x => x.v != null);
    if (!items.length) return null;
    return (
        <div style={{padding:'18px 20px 0'}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:2}}>
                <div style={{fontSize:12,fontFamily:"'Russo One','Exo 2',sans-serif",letterSpacing:2,textTransform:'uppercase',color:'var(--f1-text-muted)'}}>За карьеру{c.debut_season ? ` · с ${c.debut_season}` : ''}</div>
                <div style={{flex:1,height:1,background:'rgba(255,255,255,0.08)'}}/>
            </div>
            <div style={{display:'flex'}}>
                {items.map((x,i)=>(
                    <div key={i} style={{flex:1,padding:'9px 0 0',borderLeft:i?'1px solid rgba(255,255,255,0.08)':'none',textAlign:i?'center':'left'}}>
                        <div style={{fontSize:20,fontWeight:400,fontFamily:"'Russo One','Exo 2',sans-serif",lineHeight:1}}>{x.v}</div>
                        <div style={{fontSize:8,fontWeight:700,letterSpacing:1.4,textTransform:'uppercase',color:'rgba(255,255,255,0.45)',marginTop:4}}>{x.l}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Зал славы — вход в профиле + полноэкранная хроника чемпионов (1950 -> ...).
const HallOfFame = () => {
    const [open, setOpen] = useState(false);
    useHistoryBack(open, () => setOpen(false));
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);
    const [tick, setTick] = useState(0);
    useEffect(() => {
        if (!open || data) return;
        let a = true; setErr(false);
        api.get('/api/history/champions').then(d => {
            if (!a) return;
            if (d && d.champions && d.champions.length) setData(d); else setErr(true);
        }).catch(() => { if (a) setErr(true); });
        return () => { a = false; };
    }, [open, tick]);
    return (<>
        <div onClick={()=>setOpen(true)} style={{display:'flex',alignItems:'center',gap:12,padding:'16px 18px',borderRadius:16,background:'linear-gradient(135deg, rgba(212,175,55,0.13), var(--f1-card-solid))',border:'1px solid rgba(212,175,55,0.28)',cursor:'pointer',marginBottom:16}}>
            <div style={{flex:1}}>
                <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:15,textTransform:'uppercase',letterSpacing:1}}>Зал славы</div>
                <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:3}}>Все чемпионы мира с 1950 года</div>
            </div>
            <div style={{color:'#D4AF37',fontSize:17,fontWeight:700}}>{'→'}</div>
        </div>
        {open && (
            <BodyPortal>
                <div style={{position:'fixed',inset:0,zIndex:3000,background:'var(--f1-darker)',display:'flex',flexDirection:'column'}}>
                    <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:14,borderBottom:'1px solid rgba(255,255,255,0.08)',flexShrink:0}}>
                        <button onClick={()=>setOpen(false)} style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.14)',borderRadius:999,padding:'7px 14px',color:'#fff',fontSize:12,fontWeight:700,fontFamily:'inherit',cursor:'pointer'}}>← Назад</button>
                        <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:15,textTransform:'uppercase',letterSpacing:1.5}}>Зал славы</div>
                    </div>
                    <div style={{flex:1,overflowY:'auto',WebkitOverflowScrolling:'touch',padding:'6px 18px 40px'}}>
                        {!data && err ? (
                            <div style={{textAlign:'center',padding:'44px 20px'}}>
                                <div style={{fontSize:14,color:'var(--f1-text-secondary)',marginBottom:16}}>Не удалось загрузить чемпионов</div>
                                <button onClick={()=>{setErr(false);setTick(t=>t+1);}} style={{background:'var(--f1-red)',border:'none',borderRadius:999,padding:'10px 22px',color:'#fff',fontSize:13,fontWeight:700,fontFamily:'inherit',cursor:'pointer'}}>Повторить</button>
                            </div>
                        ) : !data ? <F1Loader text="Загрузка чемпионов..."/> : (data.champions||[]).map((c)=>(
                            <div key={c.season} style={{display:'flex',alignItems:'baseline',gap:14,padding:'10px 0',borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                                <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:21,color:'#D4AF37',width:62,flexShrink:0}}>{c.season}</div>
                                <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:14.5,fontWeight:700}}>{c.driver}</div>
                                    <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:1}}>{c.constructor}{c.wins ? ` · ${c.wins} побед` : ''}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </BodyPortal>
        )}
    </>);
};

const RacePitstops = ({round, season}) => {
    const [d, setD] = useState(null);
    useEffect(() => {
        if (round == null) return; let a = true;
        api.get(`/api/race/${round}/pitstops?season=${season}`).then(x => { if (a) setD(x); }).catch(()=>{});
        return () => { a = false; };
    }, [round, season]);
    if (!d?.available || !d.fastest) return null;
    const rows = (d.top || []).slice(0, 5);
    const fast = d.fastest.duration;
    const slow = rows.length ? rows[rows.length - 1].duration : fast;
    const span = Math.max(0.001, slow - fast);
    const MEDAL = ['#FFCB05', '#C0C0C0', '#CD7F32'];
    return (
        <div style={{margin:'4px 0 22px'}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:3}}>
                <div style={{fontSize:12,fontFamily:"'Russo One','Exo 2',sans-serif",letterSpacing:2,textTransform:'uppercase',color:'var(--f1-text-muted)'}}>Пит-стопы</div>
                <div style={{flex:1,height:1,background:'rgba(255,255,255,0.08)'}}/>
            </div>
            <div style={{fontSize:10.5,color:'var(--f1-text-muted)',marginBottom:12}}>Быстрейший проезд пит-лейна · {d.total_stops} остановок за гонку</div>
            {rows.map((t,i)=>{
                // бар: чем ближе к лучшему времени — тем длиннее (визуальный отрыв)
                const frac = 1 - (t.duration - fast) / span;   // 1 у лидера, 0 у последнего
                const w = 30 + frac * 70;
                return (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:11,padding:'9px 0',borderBottom:i<rows.length-1?'1px solid rgba(255,255,255,0.05)':'none'}}>
                        <div style={{width:22,textAlign:'center',fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:15,color:i<3?MEDAL[i]:'var(--f1-text-muted)',flexShrink:0}}>{i+1}</div>
                        <div style={{width:3,height:26,borderRadius:2,background:t.team_color||'#666',flexShrink:0}}/>
                        <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13.5,fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{t.name}</div>
                            <div style={{marginTop:4,height:4,borderRadius:2,background:'rgba(255,255,255,0.06)',overflow:'hidden'}}>
                                <div style={{width:`${w}%`,height:'100%',borderRadius:2,background:i===0?'var(--f1-red)':(t.team_color||'#888'),transition:'width .4s ease'}}/>
                            </div>
                        </div>
                        <div style={{fontFamily:"'Russo One','Exo 2',sans-serif",fontSize:17,fontVariantNumeric:'tabular-nums',flexShrink:0}}>{t.duration.toFixed(1)}<span style={{fontSize:11,color:'var(--f1-text-muted)'}}>с</span></div>
                    </div>
                );
            })}
        </div>
    );
};

// Оверлей, вынесенный прямо в <body>.
//
// Зачем: .page-container — это скроллер с -webkit-overflow-scrolling:touch.
// На iOS такой скроллер уезжает в собственный слой композитинга, и любой
// position:fixed ВНУТРИ него начинает считаться от контейнера, а не от экрана:
// оверлей перестаёт накрывать .bottom-nav, и нижняя часть (кнопка отправки)
// оказывается под навбаром и не нажимается. В Chrome/Android этого не видно.
// Тот же приём уже используется для полноэкранного плеера ниже по файлу.
const BodyPortal = ({children}) => {
    const elRef = useRef(null);
    if (!elRef.current) elRef.current = document.createElement('div');
    useEffect(() => {
        const el = elRef.current;
        document.body.appendChild(el);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';   // фон не скроллится под модалкой
        return () => {
            document.body.style.overflow = prevOverflow;
            if (el.parentNode) el.parentNode.removeChild(el);
        };
    }, []);
    return ReactDOM.createPortal(children, elRef.current);
};

// Высота РЕАЛЬНО видимой области в Telegram.
// На iOS window.innerHeight (и 100dvh) бывает больше видимой части — низ
// перекрыт панелью/зоной жестов Telegram. Тогда нижний край fixed-оверлея
// уезжает за экран и кнопка «не всплывает». Telegram отдаёт стабильную
// высоту — берём её, если она есть, иначе остаёмся на прежнем поведении.
const useTgViewportHeight = () => {
    const [h, setH] = useState(() => (tg && tg.viewportStableHeight) || null);
    useEffect(() => {
        if (!tg || !tg.onEvent) return;
        const upd = () => setH(tg.viewportStableHeight || null);
        tg.onEvent('viewportChanged', upd);
        upd();
        return () => { try { tg.offEvent('viewportChanged', upd); } catch (e) {} };
    }, []);
    return h;
};

// Полноэкранная форма прогноза — как Modal в приложении
const PredictForm = ({info, drivers, raceRound, onClose, onDone}) => {
    const tgViewportH = useTgViewportHeight();
    const [pick, setPick] = useState(null);
    const [podium, setPodium] = useState([]);
    const [dnf, setDnf] = useState('');
    const [sc, setSc] = useState(null);
    const [busy, setBusy] = useState(false);

    const canSubmit = (info.type==='winner'||info.type==='fastest_lap') ? pick!=null
        : info.type==='podium' ? podium.length===3
        : info.type==='dnf_count' ? /^\d+$/.test(dnf.trim())
        : info.type==='safety_car' ? sc!=null : false;

    const submit = async () => {
        let value = null;
        if (info.type==='winner'||info.type==='fastest_lap') value = pick;
        else if (info.type==='podium') value = podium;
        else if (info.type==='dnf_count') value = parseInt(dnf.trim(),10);
        else if (info.type==='safety_car') value = sc ? 'yes' : 'no';
        setBusy(true);
        const r = await api.post('/api/predictions/make', {race_round: raceRound, prediction_type: info.type, prediction_value: value});
        setBusy(false);
        if (r) onDone(); else alert('Не удалось отправить прогноз');
    };

    const togglePodium = (n) => {
        setPodium(p => p.includes(n) ? p.filter(x=>x!==n) : (p.length<3 ? [...p,n] : p));
    };

    return (
        <BodyPortal>
        <div style={{position:'fixed',top:0,left:0,right:0,
                     ...(tgViewportH ? {height:tgViewportH} : {bottom:0}),
                     zIndex:2000,background:'var(--f1-darker)',display:'flex',flexDirection:'column'}}>
            <div style={{display:'flex',alignItems:'center',padding:'14px 12px 6px'}}>
                <button onClick={onClose} style={{width:44,height:44,background:'none',border:'none',color:'var(--f1-text)',fontSize:24,cursor:'pointer',fontFamily:'inherit'}}>{'‹'}</button>
                <div style={{flex:1,textAlign:'center',fontSize:18,fontWeight:700}}>{info.label}</div>
                <div style={{width:44}}/>
            </div>
            <div style={{padding:'0 20px 14px',fontSize:13,color:'var(--f1-text-muted)'}}>
                {info.description} <span style={{color:'#3A3A4A'}}>•</span> до <span style={{color:'#E10600',fontWeight:800}}>+{info.max_points} очков</span>
            </div>

            <div style={{flex:1,overflowY:'auto',WebkitOverflowScrolling:'touch',paddingBottom:20}}>
                {(info.type==='winner'||info.type==='fastest_lap') && (
                    <div style={{display:'flex',flexDirection:'column',gap:10,padding:'0 16px'}}>
                        {drivers.map(d=>(<DriverPickRow key={d.driver_number} d={d} isSelected={pick===d.driver_number} onClick={()=>setPick(d.driver_number)}/>))}
                    </div>
                )}

                {info.type==='podium' && (<>
                    <div style={{display:'flex',gap:10,padding:'0 16px',marginBottom:14}}>
                        {[0,1,2].map(i=>{
                            const n = podium[i];
                            const d = n!=null ? drivers.find(x=>x.driver_number===n) : null;
                            return (
                                <div key={i} style={{flex:1,background:'var(--f1-card-solid)',borderRadius:18,border:'1.5px solid '+(d?PODIUM_COLORS[i]:'var(--f1-border)'),padding:'12px 8px',textAlign:'center',minHeight:132}}>
                                    <div style={{width:34,height:34,borderRadius:17,margin:'0 auto',background:PODIUM_COLORS[i]+'22',border:'1.5px solid '+PODIUM_COLORS[i],display:'flex',alignItems:'center',justifyContent:'center',color:PODIUM_COLORS[i],fontWeight:800,fontSize:13}}>P{i+1}</div>
                                    {d ? (<>
                                        {d.photo_url && <img src={d.photo_url} alt="" style={{width:54,height:54,borderRadius:'50%',objectFit:'cover',marginTop:10,background:'var(--f1-card-hover)'}} onError={e=>{e.target.style.display='none'}}/>}
                                        <div style={{fontSize:12,fontWeight:700,marginTop:8,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.last_name||d.name}</div>
                                    </>) : (
                                        <div style={{fontSize:10,color:'var(--f1-text-muted)',marginTop:44}}>Не выбран</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div style={{padding:'0 20px 12px',fontSize:12,color:'var(--f1-text-muted)'}}>Первый тап {'→'} P1, второй {'→'} P2, третий {'→'} P3. Тап повторно — убрать.</div>
                    <div style={{display:'flex',flexDirection:'column',gap:10,padding:'0 16px'}}>
                        {drivers.map(d=>{
                            const idx = podium.indexOf(d.driver_number);
                            return <DriverPickRow key={d.driver_number} d={d} isSelected={idx>=0} rank={idx>=0?idx+1:null} disabled={idx<0&&podium.length>=3} onClick={()=>togglePodium(d.driver_number)}/>;
                        })}
                    </div>
                </>)}

                {info.type==='dnf_count' && (
                    <div style={{padding:'30px 16px',textAlign:'center'}}>
                        <div style={{fontSize:13,color:'var(--f1-text-muted)',marginBottom:24}}>Сколько пилотов не финишируют?</div>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:16}}>
                            <button onClick={()=>setDnf(String(Math.max(0,(parseInt(dnf||'0',10)||0)-1)))} style={{width:56,height:56,borderRadius:28,background:'var(--f1-card-solid)',border:'1.5px solid rgba(225,6,0,0.3)',color:'var(--f1-text)',fontSize:26,cursor:'pointer',fontFamily:'inherit'}}>{'−'}</button>
                            <div style={{background:'var(--f1-card-solid)',border:'1.5px solid rgba(225,6,0,0.35)',borderRadius:22,padding:'8px 26px',boxShadow:'0 6px 18px rgba(225,6,0,0.15)'}}>
                                <input value={dnf} onChange={e=>setDnf(e.target.value.replace(/[^0-9]/g,'').slice(0,2))} placeholder="0" inputMode="numeric"
                                       style={{width:110,background:'none',border:'none',outline:'none',color:'var(--f1-text)',fontSize:54,fontWeight:800,textAlign:'center',fontFamily:'inherit'}}/>
                            </div>
                            <button onClick={()=>setDnf(String(Math.min(22,(parseInt(dnf||'0',10)||0)+1)))} style={{width:56,height:56,borderRadius:28,background:'var(--f1-card-solid)',border:'1.5px solid rgba(225,6,0,0.3)',color:'var(--f1-text)',fontSize:26,cursor:'pointer',fontFamily:'inherit'}}>+</button>
                        </div>
                        <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:22}}>Точное попадание +40 · {'±'}1 сход +15</div>
                    </div>
                )}

                {info.type==='safety_car' && (
                    <div style={{padding:'30px 16px',textAlign:'center'}}>
                        <div style={{fontSize:13,color:'var(--f1-text-muted)',marginBottom:22}}>Выедет ли машина безопасности во время гонки?</div>
                        <div style={{display:'flex',gap:12}}>
                            {[[true,'Да'],[false,'Нет']].map(([val,label])=>(
                                <button key={label} onClick={()=>setSc(val)} style={{flex:1,padding:'22px 0',borderRadius:20,border:'1.5px solid '+(sc===val?'#E10600':'var(--f1-border)'),background:sc===val?'#E10600':'var(--f1-card-solid)',color:sc===val?'#fff':'var(--f1-text-secondary)',fontWeight:800,fontSize:16,cursor:'pointer',fontFamily:'inherit',boxShadow:sc===val?'0 6px 18px rgba(225,6,0,0.35)':'none',transition:'all 0.15s'}}>{label}</button>
                            ))}
                        </div>
                        <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:22}}>Учитывается полный Safety Car (не VSC)</div>
                    </div>
                )}
            </div>

            <div style={{padding:'12px 16px calc(16px + env(safe-area-inset-bottom, 0px))'}}>
                <button onClick={submit} disabled={!canSubmit||busy}
                        style={{width:'100%',padding:'17px 0',borderRadius:18,border:'none',background:canSubmit&&!busy?'#E10600':'rgba(225,6,0,0.25)',color:'#fff',fontWeight:800,fontSize:16,cursor:canSubmit?'pointer':'default',fontFamily:'inherit',boxShadow:canSubmit&&!busy?'0 6px 18px rgba(225,6,0,0.45)':'none'}}>
                    {busy ? 'Отправка...' : 'Отправить прогноз'}
                </button>
            </div>
        </div>
        </BodyPortal>
    );
};

const PredictionsPage = ({user}) => {
    const [available, setAvailable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [myPredictions, setMyPredictions] = useState([]);
    const [activeForm, setActiveForm] = useState(null);
    const [view, setView] = useState('make'); // make | mine
    const [mineTab, setMineTab] = useState('done'); // wait | done
    const [now, setNow] = useState(Date.now());

    const reload = () => {
        api.get('/api/predictions/available').then(d=>{setAvailable(d);setLoading(false);});
        api.get('/api/user/predictions').then(d=>setMyPredictions(d?.predictions||[]));
    };
    useEffect(reload, []);
    useEffect(() => { const iv = setInterval(()=>setNow(Date.now()),1000); return ()=>clearInterval(iv); }, []);

    if (loading) return <div className="page-container fade-in" style={{padding:16}}><F1Loader text="Загрузка прогнозов..."/></div>;

    const race = available?.race;
    const raceName = race?.name || '';
    const cd = (() => {
        if (!race?.race_datetime) return null;
        const left = Math.max(0, new Date(race.race_datetime.replace('Z','')).getTime() + 0 - (now - new Date().getTimezoneOffset()*0) ) ;
        const ms = new Date(race.race_datetime).getTime() - now;
        if (ms <= 0) return {d:0,h:0,m:0,s:0};
        return {d:Math.floor(ms/86400000), h:Math.floor(ms/3600000)%24, m:Math.floor(ms/60000)%60, s:Math.floor(ms/1000)%60};
    })();

    // ---- Мои прогнозы (view=mine) ----
    const donePreds = myPredictions.filter(p=>p.status!=='pending');
    const waitPreds = myPredictions.filter(p=>p.status==='pending');
    const totalEarned = myPredictions.reduce((s,p)=>s+(p.points_won||0),0);
    const list = mineTab==='wait' ? waitPreds : donePreds;
    const grouped = {};
    list.forEach(p=>{ (grouped[p.race_round+'_'+(p.season||'')] = grouped[p.race_round+'_'+(p.season||'')] || []).push(p); });
    const groups = Object.values(grouped).sort((a,b)=>(b[0].race_round-a[0].race_round));

    return (
        <div className="page-container fade-in" style={{padding:'12px 16px'}}>
            {/* Header + маскот-ставка справа (интегрирован по размеру) */}
            <div style={{position:'relative',minHeight:150,marginBottom:8}}>
                <div style={{position:'relative',zIndex:1,maxWidth:'58%'}}>
                    <div style={{fontSize:30,fontWeight:800,fontStyle:'italic',letterSpacing:-0.5,textTransform:'uppercase'}}>ПРОГНОЗЫ</div>
                    {raceName && <div style={{fontSize:13,color:'var(--f1-text-muted)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{raceName}</div>}
                    <button onClick={()=>setView(view==='make'?'mine':'make')} style={{marginTop:14,display:'inline-flex',alignItems:'center',gap:7,background:view==='mine'?'#E10600':'var(--f1-card-solid)',border:'1px solid '+(view==='mine'?'#E10600':'var(--f1-border)'),borderRadius:999,padding:'9px 16px',color:view==='mine'?'#fff':'var(--f1-text)',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="4" width="14" height="17" rx="2.5"/><path d="M9 9.5h6M9 13h6M9 16.5h3.5"/></svg> Мои
                    </button>
                </div>
                <img src="/static/predict-leclerc-2x.webp?v=1" alt="" style={{position:'absolute',right:-8,bottom:0,height:152,width:'auto',objectFit:'contain',pointerEvents:'none',zIndex:0,filter:'drop-shadow(0 6px 16px rgba(0,0,0,0.45))'}}/>
            </div>

            {!user && (
                <div className="card" style={{textAlign:'center',padding:'26px 20px'}}>
                    <div style={{fontSize:30,marginBottom:10}}>{'🔒'}</div>
                    <div style={{fontSize:15,fontWeight:700}}>Войдите, чтобы делать прогнозы</div>
                </div>
            )}

            {user && view==='mine' && (<>
                {/* Сводка */}
                <div className="card" style={{display:'flex',alignItems:'center',gap:14,padding:16,marginBottom:12,border:'1px solid rgba(225,6,0,0.18)'}}>
                    <div style={{width:48,height:48,borderRadius:14,background:PRED_ICON_BOX,border:'1px solid var(--f1-border)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><PredIcon type="points" size={42}/></div>
                    <div style={{flex:1}}>
                        <div style={{fontSize:24,fontWeight:800,letterSpacing:-0.5}}>{totalEarned}</div>
                        <div style={{fontSize:11,color:'var(--f1-text-muted)'}}>Очков заработано</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                        <div style={{fontSize:16,fontWeight:800}}>{donePreds.length} / {myPredictions.length}</div>
                        <div style={{fontSize:11,color:'var(--f1-text-muted)'}}>завершено</div>
                    </div>
                </div>
                {/* Переключатель */}
                <div className="tab-switch" style={{marginBottom:14}}>
                    <button className={'tab-switch-btn '+(mineTab==='wait'?'active':'')} onClick={()=>setMineTab('wait')}>Ожидают</button>
                    <button className={'tab-switch-btn '+(mineTab==='done'?'active':'')} onClick={()=>setMineTab('done')}>Завершены</button>
                </div>
                {groups.length===0 && <div className="card" style={{textAlign:'center',color:'var(--f1-text-muted)',padding:'26px 16px'}}>{mineTab==='wait'?'Нет ожидающих прогнозов':'Пока нет завершённых прогнозов'}</div>}
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                    {groups.map((items,gi)=>{
                        const totalPts = items.reduce((s,p)=>s+(p.points_won||0),0);
                        const rn = items[0].race_name || ('Раунд '+items[0].race_round);
                        return (
                            <div key={gi} className="card" style={{padding:0,overflow:'hidden'}}>
                                <div style={{display:'flex',alignItems:'center',padding:'12px 16px',borderBottom:'1px solid var(--f1-border)'}}>
                                    <div style={{width:4,height:26,borderRadius:2,background:'#E10600',marginRight:12}}/>
                                    <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontSize:10,color:'var(--f1-red)',fontWeight:800,letterSpacing:1.5}}>ГРАН-ПРИ · РАУНД {String(items[0].race_round).padStart(2,'0')}</div>
                                        <div style={{fontSize:15,fontWeight:800,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{rn.replace(/^Гран[- ]при\s+/i,'')}</div>
                                    </div>
                                    {totalPts>0 && <div style={{background:'rgba(225,6,0,0.15)',color:'#E10600',fontWeight:800,fontSize:12,padding:'4px 10px',borderRadius:8}}>+{totalPts} оч.</div>}
                                </div>
                                {items.map((p,i)=>{
                                    const st = p.status;
                                    const stColor = st==='correct'?'#39B54A':st==='partial'?'#FFD700':st==='incorrect'?'#E10600':'var(--f1-text-muted)';
                                    const stLabel = st==='correct'?'ВЕРНО':st==='partial'?'ЧАСТИЧНО':st==='incorrect'?'НЕВЕРНО':'⏳';
                                    return (
                                        <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 16px',borderBottom:i<items.length-1?'1px solid var(--f1-border)':'none'}}>
                                            <div style={{width:38,height:38,borderRadius:11,background:PRED_ICON_BOX,border:'1px solid var(--f1-border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,flexShrink:0}}><PredIcon type={p.prediction_type} size={32}/></div>
                                            <div style={{flex:1,minWidth:0}}>
                                                <div style={{fontSize:13,fontWeight:800}}>{PRED_LABELS[p.prediction_type]||p.prediction_type}</div>
                                                <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fmtPredValue(p.prediction_type, p.prediction_value, available?.drivers)}</div>
                                            </div>
                                            <div style={{textAlign:'right',flexShrink:0}}>
                                                <div style={{fontSize:10,fontWeight:800,letterSpacing:0.8,color:stColor}}>{stLabel}</div>
                                                {st!=='pending' && <div style={{fontSize:13,fontWeight:800,color:(p.points_won||0)>0?'#39B54A':'var(--f1-text-muted)',marginTop:2}}>{(p.points_won||0)>0?'+'+p.points_won:'0'}</div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </>)}

            {user && view==='make' && (available?.available && race ? (<>
                {/* Race info card: флаг + город + дата + машинка + countdown */}
                <div style={{background:'var(--f1-card-solid)',border:'1px solid rgba(225,6,0,0.22)',borderRadius:22,overflow:'hidden',boxShadow:'0 6px 18px rgba(225,6,0,0.15)',marginBottom:20}}>
                    <div style={{display:'flex',alignItems:'center',padding:'16px 18px 12px',position:'relative'}}>
                        <div style={{marginRight:12}}>{race.country_code ? <FlagImg code={race.country_code} size={30}/> : null}</div>
                        <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:17,fontWeight:800,letterSpacing:1,textTransform:'uppercase',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{(race.locality||race.country||'').toUpperCase()}</div>
                            <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:2}}>{race.race_datetime ? new Date(race.race_datetime.replace('Z','')).toLocaleDateString('ru-RU',{day:'numeric',month:'long',year:'numeric'}) : ''}</div>
                        </div>
                    </div>
                    <div style={{height:1,background:'rgba(225,6,0,0.18)',margin:'0 14px'}}/>
                    {cd && (
                        <div style={{display:'flex',alignItems:'center',padding:'13px 8px'}}>
                            {[[cd.d,'ДНЕЙ',true],[cd.h,'ЧАСОВ'],[cd.m,'МИНУТ'],[cd.s,'СЕКУНД']].map(([v,l,hi],i)=>(
                                <React.Fragment key={l}>
                                    <div style={{flex:1,textAlign:'center'}}>
                                        <div style={{fontSize:27,lineHeight:'29px',fontWeight:800,letterSpacing:-0.5,color:hi?'#E10600':'var(--f1-text)',fontVariantNumeric:'tabular-nums'}}>{String(v).padStart(2,'0')}</div>
                                        <div style={{fontSize:9,color:'var(--f1-text-muted)',marginTop:3,letterSpacing:1.8,fontWeight:700}}>{l}</div>
                                    </div>
                                    {i<3 && <div style={{width:1,height:28,background:'rgba(225,6,0,0.35)'}}/>}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>

                {/* Секция */}
                <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:14}}>
                    <div>
                        <div style={{fontSize:20,fontWeight:800,fontStyle:'italic',letterSpacing:-0.3,textTransform:'uppercase'}}>СДЕЛАЙ ПРОГНОЗЫ</div>
                        <div style={{fontSize:12,color:'var(--f1-text-muted)',marginTop:2}}>До закрытия — старт гонки</div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:6}}>
                        <div style={{width:20,height:2,background:'#3A3A4A',borderRadius:1}}/>
                        <div style={{width:28,height:2,background:'#E10600',borderRadius:1}}/>
                        <div style={{width:12,height:2,background:'rgba(225,6,0,0.5)',borderRadius:1}}/>
                    </div>
                </div>

                {/* Карточки типов */}
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {(available.predictions||[]).map(p=>(
                        <div key={p.type} onClick={()=>!p.already_predicted&&setActiveForm(p)}
                             style={{background:'var(--f1-card-solid)',borderRadius:20,border:'1px solid '+(p.already_predicted?'var(--f1-border)':'rgba(225,6,0,0.18)'),display:'flex',alignItems:'center',padding:16,opacity:p.already_predicted?0.55:1,cursor:p.already_predicted?'default':'pointer',flexShrink:0}}>
                            <div style={{width:56,height:56,borderRadius:16,background:PRED_ICON_BOX,border:'1px solid var(--f1-border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}><PredIcon type={p.type} size={48}/></div>
                            <div style={{flex:1,minWidth:0,marginLeft:14}}>
                                <div style={{fontSize:14,fontWeight:800,textTransform:'uppercase',letterSpacing:0.5}}>{p.label}</div>
                                <div style={{fontSize:12,color:'var(--f1-text-muted)',marginTop:4}}>{p.description}</div>
                            </div>
                            <div style={{textAlign:'right',marginRight:8,flexShrink:0}}>
                                {p.already_predicted ? (
                                    <span style={{background:'rgba(255,255,255,0.06)',padding:'4px 8px',borderRadius:6,fontSize:10,fontWeight:800,letterSpacing:1.5,color:'var(--f1-text-muted)'}}>СДЕЛАН</span>
                                ) : (<>
                                    <div style={{fontSize:22,fontWeight:800,color:'#E10600',lineHeight:'24px'}}>+{p.max_points}</div>
                                    <div style={{fontSize:9,color:'var(--f1-text-muted)',fontWeight:700,letterSpacing:1.5,marginTop:1}}>ОЧ.</div>
                                </>)}
                            </div>
                            <div style={{color:'var(--f1-text-muted)',fontSize:16}}>{'›'}</div>
                        </div>
                    ))}
                </div>

                {/* Инфо-футер */}
                <div className="card" style={{display:'flex',alignItems:'center',gap:12,padding:14,marginTop:18}}>
                    <div style={{width:28,height:28,borderRadius:14,background:'rgba(255,255,255,0.06)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" style={{color:'var(--f1-text-muted)'}}><circle cx="12" cy="12" r="9"/><path d="M12 11v5.2"/><circle cx="12" cy="7.8" r="0.6" fill="currentColor"/></svg></div>
                    <div style={{fontSize:11.5,color:'var(--f1-text-muted)',lineHeight:'17px'}}>Очки начисляются после расчёта результатов гонки. Один прогноз — один способ заработать больше очков!</div>
                </div>
            </>) : (
                <div className="card">
                    <div style={{fontWeight:700}}>Нет доступных гонок</div>
                    <div style={{fontSize:13,color:'var(--f1-text-muted)',marginTop:4}}>Прогнозы открываются перед стартом Гран-при.</div>
                </div>
            ))}

            {activeForm && race && (
                <PredictForm info={activeForm} drivers={available.drivers||[]} raceRound={race.round}
                             onClose={()=>setActiveForm(null)}
                             onDone={()=>{setActiveForm(null);reload();}}/>
            )}
        </div>
    );
};

// ==== PROFILE PAGE ====

// ==== BROADCAST VIEW (app design: player + likes + comments) ====
const _relTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.replace(' ','T') + (iso.includes('Z')||iso.includes('+')?'':'Z'));
    const s = Math.max(0, (Date.now() - d.getTime())/1000);
    if (s < 60) return 'только что';
    if (s < 3600) return Math.floor(s/60) + ' мин';
    if (s < 86400) return Math.floor(s/3600) + ' ч';
    return Math.floor(s/86400) + ' дн';
};

const BroadcastViewPage = ({broadcast, raceName, onBack, user, spoilerFree}) => {
    const [vkPlay, setVkPlay] = useState(false);
    const b = broadcast;
    const [social, setSocial] = useState(null);
    const [comments, setComments] = useState(null);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [reveal, setReveal] = useState(!spoilerFree);
    const [playerFs, setPlayerFs] = useState(false);
    const isAdmin = !!(user && window.__F1_ADMIN_IDS && window.__F1_ADMIN_IDS.includes(user.user_id));
    const names = {race:'Гонка', qualifying:'Квалификация', sprint:'Спринт', sprint_qualifying:'Спринт-квалификация', fp1:'Практика 1', fp2:'Практика 2', fp3:'Практика 3', review:'Обзор'};
    const title = (names[b.session_type]||b.session_type) + ' · ' + (raceName||'');

    const loadSocial = () => api.get(`/api/broadcast/${b.id}/social`).then(setSocial);
    const loadComments = () => api.get(`/api/broadcast/${b.id}/comments?sort=new&limit=50`).then(d=>setComments(d?d.comments:[]));
    useEffect(() => { loadSocial(); loadComments(); }, [b.id]);

    const toggleLike = async () => {
        if (!user) return;
        const r = await api.post(`/api/broadcast/${b.id}/like`, {});
        if (r) setSocial(s => ({...(s||{}), likes_count: r.likes_count, my_liked: r.my_liked}));
    };
    const share = () => {
        const url = 'https://f1-hub.ru/';   // делимся адресом сайта, а не служебным поддоменом
        if (navigator.share) navigator.share({title, url}).catch(()=>{});
        else { try { navigator.clipboard.writeText(url); } catch(e){} }
    };
    const send = async () => {
        const t = text.trim();
        if (!t || !user) return;
        setBusy(true);
        const r = await api.post(`/api/broadcast/${b.id}/comment`, {text: t});
        setBusy(false);
        if (r) { setText(''); loadComments(); loadSocial(); }
    };
    const likeComment = async (id) => { if (!user) return; const r = await api.post(`/api/comment/${id}/like`, {}); if (r) loadComments(); };
    const delComment = async (id) => { if (!confirm('Удалить комментарий?')) return; const r = await api.del(`/api/comment/${id}`); if (r) { loadComments(); loadSocial(); } };

    return (
        <div className="page-container fade-in" style={{padding:'12px 16px'}}>
            <button onClick={onBack} style={{background:'none',border:'none',color:'var(--f1-text-muted)',fontSize:13,fontFamily:'inherit',cursor:'pointer',marginBottom:12,display:'flex',alignItems:'center',gap:4}}>{'\u2190'} Видео</button>

            {(() => {
                // Rutube — РОДНОЙ плеер Rutube (iframe play/embed) + наш CSS-фулскрин
                // с поворотом (RutubeEmbedPlayer). Свой Plyr/HLS для Rutube больше не используем.
                // YouTube — нативный embed. Остальное — iframe c CSS-фулскрином.
                const u = b.video_url || b.embed_url || '';
                const rtOwn = u.match(/rutube\.ru\/(?:video|play\/embed)\/([a-f0-9]+)/);
                if (rtOwn) {
                    return <RutubeEmbedPlayer url={u} id={rtOwn[1]} title={title} directUrl={b.video_url || u} radius={18} mb={14}/>;
                }
                // VK: сразу грузить iframe нельзя — ВК требует пройти проверку
                // «вы не робот» и анониму видео не отдаёт (особенно vksport).
                // Показываем обложку и выбор: попробовать здесь или открыть в ВК.
                const vk = /vk\.com|vkvideo\.ru|vk\.ru|vksport/.test(u);
                if (vk && !vkPlay) {
                    return (
                        <>
                            <div style={{position:'relative',borderRadius:18,overflow:'hidden',marginBottom:10,border:'1px solid var(--f1-border)',background:'#111',paddingTop:'56.25%'}}>
                                {b.thumbnail_url
                                    ? <img src={b.thumbnail_url} alt="" loading="lazy" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',opacity:0.9}}/>
                                    : <div style={{position:'absolute',inset:0,background:'linear-gradient(135deg,#0077FF,#0055BB)'}}/>}
                                <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,transparent 45%,rgba(0,0,0,0.78))'}}/>
                                <div onClick={()=>setVkPlay(true)} style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:64,height:64,borderRadius:'50%',background:'rgba(225,6,0,0.92)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer'}}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M9.2 7.5v9c0 .95 1.05 1.53 1.86 1.03l7.1-4.5c.75-.47.75-1.59 0-2.06l-7.1-4.5C10.25 5.97 9.2 6.55 9.2 7.5z"/></svg>
                                </div>
                                <div style={{position:'absolute',bottom:10,left:14,fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.85)'}}>VK Видео</div>
                            </div>
                            <div style={{display:'flex',gap:8,marginBottom:8}}>
                                <button onClick={()=>setVkPlay(true)} style={{flex:1,padding:'10px 0',borderRadius:12,border:'1px solid var(--f1-border)',background:'var(--f1-card-solid)',color:'var(--f1-text)',fontFamily:'inherit',fontWeight:700,fontSize:12,cursor:'pointer'}}>{'\u25b6'} Смотреть здесь</button>
                                <button onClick={()=>openLink(b.video_url || u)} style={{flex:1,padding:'10px 0',borderRadius:12,border:'none',background:'linear-gradient(135deg,#0077FF,#0055BB)',color:'#fff',fontFamily:'inherit',fontWeight:700,fontSize:12,cursor:'pointer'}}>{'\u2197'} Открыть в ВК</button>
                            </div>
                            <div style={{fontSize:11,color:'var(--f1-text-muted)',textAlign:'center',marginBottom:14}}>ВКонтакте не всегда пускает свои записи в чужой плеер — тогда открывайте в ВК</div>
                        </>
                    );
                }
                const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
                const rt = u.match(/rutube\.ru\/(?:video|play\/embed)\/([a-f0-9]+)/);
                const src2 = yt ? ('https://www.youtube.com/embed/'+yt[1]+'?rel=0&playsinline=1')
                          : rt ? ('https://rutube.ru/play/embed/'+rt[1])
                          : (b.embed_url || u);
                const provName = yt ? 'YouTube' : rt ? 'Rutube' : 'источнике';
                const wrapStyle = playerFs
                    ? {position:'fixed',inset:0,zIndex:9999,background:'#000',display:'flex',alignItems:'center',justifyContent:'center',
                       paddingBottom:'max(env(safe-area-inset-bottom, 0px), 14px)',paddingTop:'env(safe-area-inset-top, 0px)',boxSizing:'border-box'}
                    : {position:'relative',borderRadius:18,overflow:'hidden',marginBottom:10,border:'1px solid var(--f1-border)',background:'#000',paddingTop:'56.25%'};
                // на телефоне полноэкранный режим у Rutube/YouTube — их собственный,
                // поэтому наш оверлей не мешает системным кнопкам
                return (
                    <>
                        <div style={wrapStyle}>
                            <iframe src={src2} title={title} frameBorder="0" allowFullScreen
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; screen-wake-lock"
                                    style={playerFs?{width:'100%',height:'100%',border:0,maxHeight:'100dvh'}:{position:'absolute',inset:0,width:'100%',height:'100%',border:0}}/>
                            {playerFs && (
                                <button onClick={()=>setPlayerFs(false)} style={{position:'fixed',top:'calc(env(safe-area-inset-top, 8px) + 8px)',right:12,zIndex:10000,width:38,height:38,borderRadius:'50%',border:'none',background:'rgba(0,0,0,0.65)',color:'#fff',fontSize:17,cursor:'pointer',fontFamily:'inherit'}}>{'\u2715'}</button>
                            )}
                        </div>
                        {!playerFs && (
                            <div style={{display:'flex',gap:8,marginBottom:14}}>
                                <button onClick={()=>setPlayerFs(true)} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:7,padding:'10px 0',borderRadius:12,border:'1px solid var(--f1-border)',background:'var(--f1-card-solid)',color:'var(--f1-text)',fontFamily:'inherit',fontWeight:700,fontSize:12,cursor:'pointer'}}>{'\u26f6'} Во весь экран</button>
                                <button onClick={()=>openLink(b.video_url || u)} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:7,padding:'10px 0',borderRadius:12,border:'1px solid var(--f1-border)',background:'var(--f1-card-solid)',color:'var(--f1-text)',fontFamily:'inherit',fontWeight:700,fontSize:12,cursor:'pointer'}}>{'\u2197'} Открыть в {provName}</button>
                            </div>
                        )}
                    </>
                );
            })()}

            <div style={{margin:'0 2px 14px'}}>
                <div style={{fontSize:19,fontWeight:800,letterSpacing:-0.3}}>{names[b.session_type]||b.session_type}</div>
                <div style={{fontSize:12,color:'var(--f1-text-muted)',marginTop:3}}>{raceName}{b.started_at ? ' · ' + new Date(b.started_at.replace(' ','T')).toLocaleDateString('ru-RU',{day:'numeric',month:'long'}) : ''}</div>
            </div>

            {/* Actions: like / comments / share */}
            <div style={{display:'flex',gap:8,marginBottom:16}}>
                <button onClick={toggleLike} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:7,padding:'12px 0',borderRadius:14,border:'1px solid '+(social&&social.my_liked?'rgba(225,6,0,0.5)':'var(--f1-border)'),background:social&&social.my_liked?'rgba(225,6,0,0.15)':'var(--f1-card-solid)',color:social&&social.my_liked?'var(--f1-red)':'var(--f1-text)',fontFamily:'inherit',fontWeight:800,fontSize:13,cursor:'pointer'}}>
                    {social&&social.my_liked?'\u2764\ufe0f':'\ud83e\udd0d'} {social?social.likes_count:'—'}
                </button>
                <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:7,padding:'12px 0',borderRadius:14,border:'1px solid var(--f1-border)',background:'var(--f1-card-solid)',fontWeight:800,fontSize:13}}>
                    {'\ud83d\udcac'} {social?social.comments_count:'—'}
                </div>
                <button onClick={share} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:7,padding:'12px 0',borderRadius:14,border:'1px solid var(--f1-border)',background:'var(--f1-card-solid)',color:'var(--f1-text)',fontFamily:'inherit',fontWeight:800,fontSize:13,cursor:'pointer'}}>
                    {'\ud83d\udce4'} Поделиться
                </button>
            </div>

            {/* Comments */}
            <div style={{fontSize:16,fontWeight:800,margin:'0 2px 12px'}}>Комментарии{social?` (${social.comments_count})`:''}</div>

            {!reveal ? (
                <div className="card" onClick={()=>setReveal(true)} style={{textAlign:'center',padding:'24px 16px',cursor:'pointer',border:'1px solid rgba(225,6,0,0.2)',marginBottom:16}}>
                    <img src="/static/antispoiler.webp?v=1" alt="" style={{width:52,height:52,borderRadius:12,objectFit:'cover',margin:'0 auto 8px',display:'block'}}/>
                    <div style={{fontSize:14,fontWeight:800,color:'var(--f1-red)'}}>Комментарии скрыты</div>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:4}}>Могут содержать спойлеры результатов. Нажми, чтобы показать.</div>
                </div>
            ) : (<>
                {/* Input */}
                {user ? (
                    <div style={{display:'flex',gap:8,marginBottom:16}}>
                        <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')send();}} placeholder="Написать комментарий..." maxLength={1000}
                               style={{flex:1,background:'var(--f1-card-solid)',border:'1px solid var(--f1-border)',borderRadius:14,padding:'12px 14px',color:'var(--f1-text)',fontSize:14,fontFamily:'inherit',outline:'none'}}/>
                        <button onClick={send} disabled={busy||!text.trim()} style={{width:46,borderRadius:14,border:'none',background:text.trim()?'var(--f1-red)':'var(--f1-card-solid)',color:'#fff',fontSize:16,cursor:'pointer',fontFamily:'inherit',opacity:busy?0.5:1}}>{'\u27a4'}</button>
                    </div>
                ) : (
                    <div style={{fontSize:12,color:'var(--f1-text-muted)',marginBottom:14,textAlign:'center'}}>Войди, чтобы комментировать</div>
                )}

                <div style={{display:'flex',flexDirection:'column',gap:14,paddingBottom:20}}>
                    {comments === null && <F1MiniLoader/>}
                    {comments && comments.length === 0 && <div style={{textAlign:'center',color:'var(--f1-text-muted)',fontSize:13,padding:'20px 0'}}>Будь первым {'\ud83d\udcac'}</div>}
                    {(comments||[]).map(c => {
                        const canDel = c.is_mine || isAdmin;
                        return (
                            <div key={c.id} style={{display:'flex',gap:10}}>
                                {c.user_photo_url ? <img src={c.user_photo_url} alt="" style={{width:34,height:34,borderRadius:'50%',objectFit:'cover',flexShrink:0,background:'var(--f1-card-hover)'}} onError={e=>{e.target.style.display='none'}}/> : <div style={{width:34,height:34,borderRadius:'50%',background:'var(--f1-card-hover)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0}}>{'\ud83d\udc64'}</div>}
                                <div style={{flex:1,minWidth:0}}>
                                    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                                        <span style={{fontSize:13,fontWeight:800}}>{c.user_name}</span>
                                        {c.is_mine && <span style={{background:'rgba(225,6,0,0.15)',color:'var(--f1-red)',fontSize:9,fontWeight:800,letterSpacing:0.5,padding:'1px 6px',borderRadius:4}}>ВЫ</span>}
                                        <span style={{fontSize:11,color:'var(--f1-text-muted)'}}>{_relTime(c.created_at)}</span>
                                    </div>
                                    <div style={{fontSize:13.5,lineHeight:'18px',marginTop:4,wordBreak:'break-word'}}>{c.text}</div>
                                    <div style={{display:'flex',alignItems:'center',gap:14,marginTop:6}}>
                                        <span onClick={()=>likeComment(c.id)} style={{cursor:'pointer',fontSize:11,fontWeight:700,color:c.my_liked?'var(--f1-red)':'var(--f1-text-muted)'}}>{c.my_liked?'\u2764\ufe0f':'\ud83e\udd0d'}{c.likes_count>0?' '+c.likes_count:''}</span>
                                        {canDel && <span onClick={()=>delComment(c.id)} style={{cursor:'pointer',fontSize:11,fontWeight:700,color:'var(--f1-red)'}}>{c.is_mine?'Удалить':'Удалить (модер.)'}</span>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </>)}
        </div>
    );
};

// ==== VIDEOS PAGE (app design) ====
const VIDEO_SESSION_LABELS = {race:'Гонка', qualifying:'Квалификация', sprint:'Спринт', sprint_qualifying:'Спринт-квалификация', fp1:'Практика 1', fp2:'Практика 2', fp3:'Практика 3', review:'Обзор'};
const VIDEO_SESSION_COLOR = {race:'#E10600', qualifying:'#27F4D2', sprint:'#FF8000', sprint_qualifying:'#FFB800', review:'#3B9BFF'};
const VIDEO_SESSION_BADGE = {race:'ГОНКА', qualifying:'QUALI', sprint:'SPRINT', sprint_qualifying:'SQ', fp1:'FP1', fp2:'FP2', fp3:'FP3', review:'ОБЗОР'};
const VideosPage = ({schedule, onOpen}) => {
    const [broadcasts, setBroadcasts] = useState(null);
    const [openRounds, setOpenRounds] = useState(() => new Set());
    useEffect(() => { api.get('/api/broadcasts').then(d => setBroadcasts((d && d.broadcasts) || [])).catch(()=>setBroadcasts([])); }, []);
    if (broadcasts === null) return <div className="page-container fade-in" style={{padding:16}}><F1Loader text="Загрузка записей..."/></div>;
    const raceByRound = {};
    (schedule && schedule.races || []).forEach(r => { raceByRound[r.round] = r; });
    const groups = {};
    broadcasts.filter(b => b.season === 2026 || !b.season).forEach(b => { (groups[b.race_round] = groups[b.race_round] || []).push(b); });
    const rounds = Object.keys(groups).map(Number).sort((a,b)=>b-a);
    const order = {review:-1, race:0, sprint:1, qualifying:2, sprint_qualifying:3, fp3:4, fp2:5, fp1:6};
    return (
        <div className="page-container fade-in" style={{padding:16,display:'flex',flexDirection:'column',gap:18}}>
            <div style={{position:'relative',margin:'4px 4px 0',minHeight:104}}>
                <div style={{fontSize:26,fontWeight:800,fontStyle:'italic'}}>ВИДЕО</div>
                <img src="/static/popcorn-anim.webp?v=1" alt="" style={{position:'absolute',right:-6,top:-16,height:140,width:'auto',objectFit:'contain',pointerEvents:'none',filter:'drop-shadow(0 6px 14px rgba(0,0,0,0.5))'}}/>
            </div>
            {rounds.length === 0 && <div className="card" style={{textAlign:'center',color:'var(--f1-text-muted)'}}>Записей пока нет</div>}
            {rounds.map(round => {
                const race = raceByRound[round];
                const items = groups[round].sort((a,b)=>((order[a.session_type]!=null?order[a.session_type]:9)-(order[b.session_type]!=null?order[b.session_type]:9)));
                const raceName = race ? race.name : ('Раунд ' + round);
                const cnt = items.length;
                const cntWord = cnt===1 ? 'ЗАПИСЬ' : cnt<5 ? 'ЗАПИСИ' : 'ЗАПИСЕЙ';
                return (
                    <div key={round}>
                        <div style={{display:'flex',alignItems:'center',gap:10,margin:'0 4px 10px'}}>
                            <div style={{width:4,height:34,borderRadius:2,background:'var(--f1-red)',flexShrink:0}}/>
                            <div>
                                <div style={{color:'var(--f1-red)',fontSize:10,fontWeight:800,letterSpacing:2}}>ГРАН-ПРИ</div>
                                <div style={{fontSize:19,fontWeight:800,lineHeight:'22px'}}>{raceName.replace(/^Гран[- ]при\s+/i,'')}</div>
                                <div style={{color:'var(--f1-text-muted)',fontSize:10,fontWeight:700,letterSpacing:1,marginTop:1}}>РАУНД {String(round).padStart(2,'0')} · 2026 · {cnt} {cntWord}</div>
                            </div>
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:10}}>
                            {(openRounds.has(round) ? items : items.slice(0, 4)).map(b => {
                                const color = VIDEO_SESSION_COLOR[b.session_type] || '#E10600';
                                return (
                                    <div key={b.id} className="vid-card" onClick={()=>onOpen(b, raceName)} style={{borderColor:b.session_type==='race'?color+'55':'var(--f1-border)'}}>
                                        <div className="vid-thumb">
                                            {b.thumbnail_url ? <img src={b.thumbnail_url} alt="" referrerPolicy="no-referrer" loading="lazy" onError={e=>{e.target.style.display='none'}}/> : <span style={{fontSize:22,opacity:0.4}}>{'\ud83c\udfac'}</span>}
                                            <span className="vid-badge" style={{background:color}}>{VIDEO_SESSION_BADGE[b.session_type] || (b.session_type||'').toUpperCase()}</span>
                                            <span className="vid-play" style={{borderColor:color}}>{'\u25b6'}</span>
                                        </div>
                                        <div style={{flex:1,minWidth:0,padding:'12px 14px'}}>
                                            <div style={{fontSize:14,fontWeight:800}}>{VIDEO_SESSION_LABELS[b.session_type] || b.session_type}</div>
                                            <div style={{color:'var(--f1-text-muted)',fontSize:11,marginTop:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.title || raceName}</div>
                                            {b.started_at && <div style={{color:'var(--f1-text-muted)',fontSize:10,fontWeight:700,letterSpacing:1,marginTop:5}}>{new Date(b.started_at).toLocaleDateString('ru-RU',{day:'numeric',month:'long'}).toUpperCase()}</div>}
                                        </div>
                                        <div style={{paddingRight:14,color:'var(--f1-text-muted)',fontSize:18}}>{'\u203a'}</div>
                                    </div>
                                );
                            })}
                            {items.length > 4 && !openRounds.has(round) && (
                                <button onClick={()=>setOpenRounds(prev=>{const s2=new Set(prev);s2.add(round);return s2;})}
                                    style={{background:'var(--f1-card-solid)',border:'1px solid var(--f1-border)',borderRadius:12,padding:'11px',color:'var(--f1-text-secondary)',fontSize:12.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                                    Ещё {items.length - 4} · показать все
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const ProfilePage = ({user, onNavigate, spoilerFree, onToggleSpoiler, onLogout}) => {
    const [leaderboard, setLeaderboard] = useState(null);
    const [lbAll, setLbAll] = useState(false);
    const [achievements, setAchievements] = useState(null);
    useEffect(() => { api.get('/api/leaderboard').then(setLeaderboard); api.get('/api/user/achievements').then(setAchievements); }, []);
    if (!user) return <div className="page-container fade-in" style={{padding:16}}><F1MiniLoader/></div>;
    return (
        <div className="page-container fade-in" style={{padding:'12px 16px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',margin:'2px 4px 18px'}}>
                <div style={{fontSize:30,fontWeight:800,letterSpacing:-0.5}}>Профиль</div>
            </div>

            {/* User row: avatar glow + name + car (app design) */}
            <div style={{display:'flex',alignItems:'center',gap:18,margin:'0 4px 14px',position:'relative'}}>
                <div style={{position:'relative',flexShrink:0}}>
                    <div style={{width:92,height:92,borderRadius:'50%',border:'2px solid var(--f1-red)',padding:3,boxShadow:'0 0 18px rgba(225,6,0,0.45)'}}>
                        {user.photo_url ? (
                            <img src={hiResImg(user.photo_url, 256)} alt="" style={{width:'100%',height:'100%',borderRadius:'50%',objectFit:'cover',display:'block'}} onError={e=>{e.target.style.display='none'}}/>
                        ) : (
                            <div style={{width:'100%',height:'100%',borderRadius:'50%',background:'linear-gradient(135deg,var(--f1-red),#FF6B00)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:34,fontWeight:900}}>{(user.first_name||'?')[0]}</div>
                        )}
                    </div>
                </div>
                <div style={{flex:1,minWidth:0,overflow:'hidden',paddingRight:4}}>
                    <div style={{fontSize:24,fontWeight:800,letterSpacing:-0.3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user.first_name}</div>
                    {user.username && <div style={{fontSize:13,color:'var(--f1-text-muted)',marginTop:2}}>@{user.username}</div>}
                </div>
                <img src="/static/merc-duo.webp?v=2" alt="" style={{position:'absolute',right:0,top:'50%',transform:'translateY(-50%)',height:96,width:'auto',maxWidth:154,objectFit:'contain',opacity:0.95,pointerEvents:'none'}}/>
            </div>

            {/* Bio */}
            <div style={{fontSize:13,lineHeight:'18px',color:'var(--f1-text-muted)',margin:'0 4px 16px'}}>Фанат Формулы-1 и скорости. Аналитика, прогнозы и гонки! {'\ud83c\udfc1'}</div>

            {/* Stats: Очков / Прогнозов / Точность (app design) */}
            {(() => {
                const total = user.predictions_total || 0;
                const correct = user.predictions_correct || 0;
                const acc = total ? Math.round(correct/total*100) : 0;
                const cells = [[user.points,'Очков'],[total,'Прогнозов'],[acc+'%','Точность']];
                return (
                    <div className="card" style={{display:'flex',padding:'16px 0',marginBottom:10}}>
                        {cells.map((s,i)=>(
                            <div key={i} style={{flex:1,textAlign:'center',borderLeft:i>0?'1px solid var(--f1-border)':'none'}}>
                                <div style={{fontSize:22,fontWeight:800,letterSpacing:-0.3}}>{s[0]}</div>
                                <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:4}}>{s[1]}</div>
                            </div>
                        ))}
                    </div>
                );
            })()}

            {/* Premium banner */}
            <div className="card" style={{display:'flex',alignItems:'center',gap:14,padding:16,marginBottom:10,border:'1px solid rgba(225,6,0,0.18)'}}>
                <div style={{width:40,height:40,borderRadius:10,background:'rgba(225,6,0,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>{'\ud83d\udee1\ufe0f'}</div>
                <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:15,fontWeight:800}}>Премиум статус</div>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:2}}>Больше возможностей и эксклюзивный контент</div>
                </div>
                <div style={{color:'var(--f1-text-muted)',fontSize:16}}>{'\u203a'}</div>
            </div>

            {/* Achievements: hex badges + anti-spoiler toggle (app design) */}
            {(() => {
                const total = user.predictions_total || 0;
                const correct = user.predictions_correct || 0;
                const acc = total ? Math.round(correct/total*100) : 0;
                const badges = [
                    {ic:'\ud83c\udfc1', label:'Первые шаги', desc:'Сделал 1 прогноз', ok: total>=1},
                    {ic:'\ud83c\udfc6', label:'Профи', desc:'Сделал 10 прогнозов', ok: total>=10},
                    {ic:'\ud83c\udfaf', label:'Точный глаз', desc:'Точность 70%+', ok: acc>=70 && total>=5},
                    {ic:'\ud83c\udf96\ufe0f', label:'Эксперт', desc:'Сделал 50 прогнозов', ok: total>=50},
                ];
                const unlocked = badges.filter(b=>b.ok).length;
                const Hex = ({b}) => (
                    <div style={{flex:1,textAlign:'center',minWidth:0}}>
                        <div style={{position:'relative',width:58,height:64,margin:'0 auto',display:'flex',alignItems:'center',justifyContent:'center',filter:b.ok?'drop-shadow(0 0 8px rgba(225,6,0,0.4))':'none'}}>
                            <svg width="58" height="64" viewBox="0 0 60 66" style={{position:'absolute',inset:0}}>
                                <polygon points="30,2 56,16 56,50 30,64 4,50 4,16" fill="var(--f1-card-solid)" stroke={b.ok?'var(--f1-red)':'#3A3A4A'} strokeWidth="2"/>
                            </svg>
                            <span style={{fontSize:20,position:'relative',opacity:b.ok?1:0.3}}>{b.ic}</span>
                        </div>
                        <div style={{fontSize:11,fontWeight:800,marginTop:8,color:b.ok?'var(--f1-text)':'var(--f1-text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.label}</div>
                        <div style={{fontSize:9,color:'var(--f1-text-muted)',marginTop:2,lineHeight:'11px'}}>{b.desc}</div>
                    </div>
                );
                return (
                    <div className="card" style={{padding:16,marginBottom:10}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                            <div style={{fontSize:18,fontWeight:800}}>Достижения</div>
                            <div style={{color:'var(--f1-red)',fontSize:13,fontWeight:800}}>{unlocked} / {badges.length} {'\u203a'}</div>
                        </div>
                        <div style={{display:'flex',gap:8}}>{badges.map((b,i)=><Hex key={i} b={b}/>)}</div>
                        <div style={{height:1,background:'var(--f1-border)',margin:'16px 0 4px'}}/>
                        <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 0 2px'}}>
                            <div style={{width:36,height:36,borderRadius:10,overflow:'hidden',flexShrink:0}}><img src="/static/antispoiler.webp?v=1" alt="" style={{width:'100%',height:'100%',objectFit:'cover',opacity:spoilerFree?1:0.55}}/></div>
                            <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:14,fontWeight:800}}>Антиспойлер</div>
                                <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:2}}>Скрывает результаты гонок и таблицу сезона</div>
                            </div>
                            <div onClick={onToggleSpoiler} style={{width:46,height:26,borderRadius:13,background:spoilerFree?'var(--f1-red)':'#2A2A38',position:'relative',cursor:'pointer',transition:'background 0.2s',flexShrink:0}}>
                                <div style={{position:'absolute',top:2,left:spoilerFree?22:2,width:22,height:22,borderRadius:'50%',background:'#fff',transition:'left 0.2s'}}/>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Menu (app design) */}
                <HallOfFame/>
            <div className="card" style={{padding:0,overflow:'hidden',marginBottom:16}}>
                {[['predict',NavIcons.trophy,'Мои прогнозы'],['standings',NavIcons.analytics,'Чемпионат'],['news',NavIcons.news,'Новости'],['schedule',NavIcons.calendar,'Календарь'],['videos',NavIcons.play,'Видео']].map(([id,ic,label],i,arr)=>(
                    <div key={id} onClick={()=>onNavigate && onNavigate(id)} style={{display:'flex',alignItems:'center',gap:14,padding:'13px 16px',borderBottom:i<arr.length-1?'1px solid var(--f1-border)':'none',cursor:'pointer'}}>
                        <div style={{width:30,height:30,borderRadius:8,background:'rgba(225,6,0,0.12)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--f1-red)'}}><span style={{width:17,height:17,display:'inline-flex'}}>{ic}</span></div>
                        <div style={{flex:1,fontSize:15}}>{label}</div>
                        <div style={{color:'var(--f1-text-muted)',fontSize:16}}>{'\u203a'}</div>
                    </div>
                ))}
            </div>
            {onLogout && (
                <button onClick={onLogout} style={{width:'100%',background:'transparent',border:'1px solid rgba(225,6,0,0.35)',borderRadius:14,padding:'13px',color:'var(--f1-red)',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit',marginBottom:16}}>
                    Выйти из аккаунта
                </button>
            )}

            <div className="card" style={{marginBottom:16}}>
                <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:12}}>Статистика</div>
                {[{label:'Прогнозов сделано',value:user.predictions_total||0,max:50,color:'var(--f1-red)'},
                  {label:'Прогнозов угадано',value:user.predictions_correct||0,max:user.predictions_total||1,color:'#39B54A'},
                  {label:'Точность',value:user.predictions_total?Math.round(user.predictions_correct/user.predictions_total*100)+'%':'—'},
                  {label:'Макс. серия',value:user.max_streak||0},
                  {label:'Игр сыграно',value:user.games_played||0,max:50,color:'#6692FF'}
                ].map((stat,i)=>(
                    <div key={i} style={{padding:'8px 0',borderBottom:i<4?'1px solid var(--f1-border)':'none'}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:stat.max?4:0}}>
                            <span style={{color:'var(--f1-text-secondary)',fontSize:14}}>{stat.label}</span>
                            <span style={{fontWeight:700,fontSize:14,fontVariantNumeric:'tabular-nums'}}>{stat.value}</span>
                        </div>
                        {stat.max && <div className="progress-bar-bg"><div className="progress-bar-fill" style={{width:`${Math.min(100,(typeof stat.value==='number'?stat.value:0)/stat.max*100)}%`,background:stat.color}}/></div>}
                    </div>
                ))}
            </div>

            {achievements && (
                <div style={{marginBottom:16}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:10}}>Достижения ({achievements.achievements?.length||0}/{achievements.total||0})</div>
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {achievements.all_achievements && Object.entries(achievements.all_achievements).map(([key,ach])=>{
                            const unlocked = achievements.achievements?.some(a=>a.key===key);
                            return (
                                <div key={key} className={`achievement-badge ${unlocked?'':'achievement-locked'}`}>
                                    <span className="achievement-icon">{ach.icon}</span>
                                    <div style={{flex:1}}>
                                        <div style={{fontWeight:700,fontSize:14}}>{ach.name}</div>
                                        <div style={{fontSize:12,color:'var(--f1-text-muted)'}}>{ach.desc}</div>
                                    </div>
                                    {unlocked && <span style={{fontSize:16}}>✅</span>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {window.__F1_ADMIN_IDS && window.__F1_ADMIN_IDS.includes(user.user_id) && (
                <AdminBroadcastPanel userId={user.user_id}/>
            )}

            {leaderboard?.leaderboard && (() => {
                // Раньше показывали первые 20 — новичок себя не находил. Теперь топ-10,
                // своя строка с местом и кнопка «вся таблица».
                const all = leaderboard.leaderboard;
                const me = leaderboard.me;
                const myIdx = all.findIndex(e => e.user_id === user.user_id);
                const list = lbAll ? all : all.slice(0, 10);
                const showMe = me && !list.some(e => e.user_id === me.user_id);
                const medal = (r) => r <= 3 ? ['#FFD700','#C0C0C0','#CD7F32'][r - 1] : null;
                const Row = ({entry}) => {
                    const mine = entry.user_id === user.user_id;
                    const m = medal(entry.rank);
                    return (
                        <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 16px',borderBottom:'1px solid var(--f1-border)',background:mine?'rgba(225,6,0,0.14)':'transparent'}}>
                            <div style={{width:28,fontWeight:900,fontSize:14,color:m||'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums'}}>{entry.rank}</div>
                            {entry.photo_url ? (
                                <img src={entry.photo_url} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',border:m?`2px solid ${m}`:'2px solid transparent'}} onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>
                            ) : null}
                            <div style={{width:36,height:36,borderRadius:'50%',background:m?`linear-gradient(135deg,${m}33,var(--f1-gray))`:'var(--f1-gray)',display:entry.photo_url?'none':'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:14,color:'rgba(255,255,255,0.5)'}}>{(entry.first_name||entry.username||'?')[0].toUpperCase()}</div>
                            <div style={{flex:1,minWidth:0}}><div style={{fontWeight:700,fontSize:14}}>{entry.first_name}{mine && <span style={{marginLeft:6,fontSize:10,fontWeight:800,color:'#fff',background:'var(--f1-red)',borderRadius:4,padding:'1px 5px'}}>ТЫ</span>}</div>{entry.username&&<div style={{fontSize:11,color:'var(--f1-text-muted)'}}>@{entry.username}</div>}</div>
                            <div style={{fontWeight:900,fontSize:16,fontVariantNumeric:'tabular-nums'}}>{entry.total_points}</div>
                        </div>
                    );
                };
                return (
                    <div>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:10}}>
                            <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5}}>Лидерборд</div>
                            {me && <div style={{fontSize:12,color:'var(--f1-text-secondary)'}}>Ты <b style={{color:'#fff'}}>{me.rank}-й</b> из {leaderboard.total}</div>}
                        </div>
                        <div className="card" style={{padding:0,overflow:'hidden'}}>
                            {list.map(entry => <Row key={entry.user_id} entry={entry}/>)}
                            {showMe && <><div style={{textAlign:'center',color:'var(--f1-text-muted)',fontSize:12,padding:'4px 0',borderBottom:'1px solid var(--f1-border)'}}>• • •</div><Row entry={me}/></>}
                            {all.length > 10 && (
                                <div onClick={() => setLbAll(v => !v)} style={{textAlign:'center',padding:'12px 16px',fontSize:13,fontWeight:700,color:'var(--f1-text-secondary)',cursor:'pointer'}}>
                                    {lbAll ? 'Свернуть' : 'Вся таблица · ' + all.length}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

// ==== ANALYTICS PAGE ====
const AnalyticsPage = () => {
    const chartReady = useLib(ensureChart);
    const [aTab, setATab] = useState('strategy');
    const [strategy, setStrategy] = useState(null);
    const [posChart, setPosChart] = useState(null);
    const [lapTimes, setLapTimes] = useState(null);
    const [degradation, setDegradation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedDrivers, setSelectedDrivers] = useState([]);
    const [degDrivers, setDegDrivers] = useState([]);
    const posChartRef = useRef(null);
    const lapChartRef = useRef(null);
    const degChartRef = useRef(null);
    const posChartInstance = useRef(null);
    const lapChartInstance = useRef(null);
    const degChartInstance = useRef(null);

    // Telemetry state
    const [telDriver1, setTelDriver1] = useState(null);
    const [telDriver2, setTelDriver2] = useState(null);
    const [telData, setTelData] = useState(null);
    const [telLoading, setTelLoading] = useState(false);
    const [telDriverList, setTelDriverList] = useState([]);
    const telSpeedRef = useRef(null);
    const telSpeedInstance = useRef(null);

    // Strategy prediction state
    const [stratPred, setStratPred] = useState(null);

    // Weather radar state
    const [weatherRadar, setWeatherRadar] = useState(null);
    const [radarFrame, setRadarFrame] = useState(0);
    const radarIntervalRef = useRef(null);

    // Race trace state
    const [raceTrace, setRaceTrace] = useState(null);
    const [traceDrivers, setTraceDrivers] = useState([]);
    const traceChartRef = useRef(null);
    const traceChartInstance = useRef(null);

    // Speed traps state
    const [speedTraps, setSpeedTraps] = useState(null);

    // Lap time series state
    const [lapSeries, setLapSeries] = useState(null);
    const [seriesDrivers, setSeriesDrivers] = useState([]);

    const tyreColors = {SOFT:'#FF3333',MEDIUM:'#FFD700',HARD:'#CCCCCC',INTERMEDIATE:'#39B54A',WET:'#0067FF',UNKNOWN:'#888888'};
    const tyreLabels = {SOFT:'Софт',MEDIUM:'Медиум',HARD:'Хард'};

    useEffect(() => {
        setLoading(true);
        if (aTab === 'strategy' && !strategy) {
            api.get('/api/analytics/strategy').then(d => { setStrategy(d); setLoading(false); });
        } else if (aTab === 'positions' && !posChart) {
            api.get('/api/analytics/positions').then(d => { setPosChart(d); setLoading(false); });
        } else if (aTab === 'laptimes' && !lapTimes) {
            api.get('/api/analytics/laptimes').then(d => {
                setLapTimes(d);
                if (d?.drivers?.length > 0) setSelectedDrivers(d.drivers.slice(0, 3).map(dr => dr.driver_number));
                setLoading(false);
            });
        } else if (aTab === 'degradation' && !degradation) {
            api.get('/api/analytics/degradation').then(d => {
                setDegradation(d);
                if (d?.drivers?.length > 0) setDegDrivers(d.drivers.slice(0, 3).map(dr => dr.driver_number));
                setLoading(false);
            });
        } else if (aTab === 'telemetry' && !telDriverList.length) {
            api.get('/api/analytics/telemetry/drivers').then(d => {
                if (d?.drivers?.length > 0) {
                    setTelDriverList(d.drivers);
                    setTelDriver1(d.drivers[0]?.driver_number);
                    setTelDriver2(d.drivers[1]?.driver_number);
                }
                setLoading(false);
            });
        } else if (aTab === 'stratpred' && !stratPred) {
            api.get('/api/analytics/strategy-prediction').then(d => { setStratPred(d); setLoading(false); });
        } else if (aTab === 'weather' && !weatherRadar) {
            api.get('/api/analytics/weather-radar').then(d => { setWeatherRadar(d); setLoading(false); });
        } else if (aTab === 'racetrace' && !raceTrace) {
            api.get('/api/analytics/race-trace').then(d => {
                setRaceTrace(d);
                if (d?.drivers) {
                    const drvs = Object.values(d.drivers);
                    setTraceDrivers(drvs.slice(0, 5).map(dr => dr.driver_number));
                }
                setLoading(false);
            });
        } else if (aTab === 'speedtraps' && !speedTraps) {
            api.get('/api/analytics/speed-traps').then(d => { setSpeedTraps(d); setLoading(false); });
        } else if (aTab === 'lapseries' && !lapSeries) {
            api.get('/api/analytics/lap-time-series').then(d => {
                setLapSeries(d);
                if (d?.drivers?.length > 0) setSeriesDrivers(d.drivers.slice(0, 3).map(dr => dr.driver_number));
                setLoading(false);
            });
        } else setLoading(false);
    }, [aTab]);

    // Position chart rendering
    useEffect(() => {
        if (!chartReady) return;   // ждём ленивую библиотеку
        if (aTab !== 'positions' || !posChart?.drivers?.length || !posChartRef.current) return;
        if (posChartInstance.current) posChartInstance.current.destroy();
        const ctx = posChartRef.current.getContext('2d');
        const datasets = posChart.drivers.map(d => ({
            label: d.code || d.name,
            data: d.positions.map(p => ({x: p.lap, y: p.position})),
            borderColor: d.team_color || '#888',
            backgroundColor: d.team_color || '#888',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.1,
            fill: false,
        }));
        posChartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {datasets},
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        mode: 'nearest', intersect: false,
                        backgroundColor: '#1E1E2C', titleColor: '#fff', bodyColor: '#9A9AAF',
                        borderColor: '#38383F', borderWidth: 1,
                        callbacks: {
                            title: (items) => items[0]?.dataset?.label || '',
                            label: (item) => `Круг ${item.parsed.x}: P${item.parsed.y}`,
                        }
                    },
                },
                scales: {
                    x: {type:'linear', title:{display:true,text:'Круг',color:'#6B6B80',font:{family:'Titillium Web'}}, ticks:{color:'#6B6B80'}, grid:{color:'#38383F33'}},
                    y: {reverse:true, title:{display:true,text:'Позиция',color:'#6B6B80',font:{family:'Titillium Web'}}, ticks:{color:'#6B6B80',stepSize:1}, grid:{color:'#38383F33'}, min:1, max:20},
                },
                interaction: {mode:'nearest',axis:'x',intersect:false},
            },
        });
        return () => { if (posChartInstance.current) posChartInstance.current.destroy(); };
    }, [posChart, aTab, chartReady]);

    // Lap times chart rendering
    useEffect(() => {
        if (!chartReady) return;   // ждём ленивую библиотеку
        if (aTab !== 'laptimes' || !lapTimes?.drivers?.length || !lapChartRef.current || !selectedDrivers.length) return;
        if (lapChartInstance.current) lapChartInstance.current.destroy();
        const ctx = lapChartRef.current.getContext('2d');
        const filtered = lapTimes.drivers.filter(d => selectedDrivers.includes(d.driver_number));
        const datasets = filtered.map(d => ({
            label: d.code || d.name,
            data: d.laps.filter(l => l.time && !l.is_pit_out).map(l => ({x: l.lap, y: l.time})),
            borderColor: d.team_color || '#888',
            backgroundColor: (context) => {
                const idx = context.dataIndex;
                const lap = d.laps.filter(l => l.time && !l.is_pit_out)[idx];
                return lap ? (tyreColors[lap.compound] || '#888') : d.team_color;
            },
            borderWidth: 1.5,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0,
            fill: false,
            showLine: true,
        }));
        // Calculate y axis range (exclude outlier laps)
        const allTimes = datasets.flatMap(ds => ds.data.map(p => p.y)).filter(Boolean);
        const medianTime = allTimes.sort((a,b) => a-b)[Math.floor(allTimes.length/2)] || 90;
        const yMin = Math.max(0, medianTime * 0.95);
        const yMax = medianTime * 1.12;

        lapChartInstance.current = new Chart(ctx, {
            type: 'scatter',
            data: {datasets},
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        backgroundColor: '#1E1E2C', titleColor: '#fff', bodyColor: '#9A9AAF',
                        borderColor: '#38383F', borderWidth: 1,
                        callbacks: {
                            title: (items) => items[0]?.dataset?.label || '',
                            label: (item) => {
                                const t = item.parsed.y;
                                const m = Math.floor(t/60);
                                const s = (t%60).toFixed(3);
                                return `Круг ${item.parsed.x}: ${m>0?m+':'+s.padStart(6,'0'):s}`;
                            },
                        }
                    },
                },
                scales: {
                    x: {type:'linear', title:{display:true,text:'Круг',color:'#6B6B80',font:{family:'Titillium Web'}}, ticks:{color:'#6B6B80'}, grid:{color:'#38383F33'}},
                    y: {title:{display:true,text:'Время (сек)',color:'#6B6B80',font:{family:'Titillium Web'}}, ticks:{color:'#6B6B80',callback:v=>{const m=Math.floor(v/60);const s=(v%60).toFixed(1);return m>0?m+':'+s.padStart(4,'0'):s;}}, grid:{color:'#38383F33'}, min:yMin, max:yMax},
                },
                interaction: {mode:'nearest',intersect:false},
            },
        });
        return () => { if (lapChartInstance.current) lapChartInstance.current.destroy(); };
    }, [lapTimes, aTab, selectedDrivers, chartReady]);

    // Degradation chart rendering
    useEffect(() => {
        if (!chartReady) return;   // ждём ленивую библиотеку
        if (aTab !== 'degradation' || !degradation?.drivers?.length || !degChartRef.current || !degDrivers.length) return;
        if (degChartInstance.current) degChartInstance.current.destroy();
        const ctx = degChartRef.current.getContext('2d');
        const filtered = degradation.drivers.filter(d => degDrivers.includes(d.driver_number));
        const datasets = [];
        filtered.forEach(d => {
            d.stints.forEach((stint, si) => {
                // Scatter points colored by compound
                datasets.push({
                    label: `${d.code} S${stint.stint_number} (${stint.compound})`,
                    data: stint.laps.map(l => ({x: l.lap, y: l.corrected_time})),
                    backgroundColor: tyreColors[stint.compound] || '#888',
                    borderColor: 'transparent',
                    pointRadius: 3,
                    pointHoverRadius: 5,
                    showLine: false,
                    type: 'scatter',
                });
                // Trend line
                if (stint.trend_line?.length === 2) {
                    datasets.push({
                        label: `${d.code} trend S${stint.stint_number}`,
                        data: stint.trend_line.map(t => ({x: t.lap, y: t.time})),
                        borderColor: d.team_color || '#888',
                        borderWidth: 1.5,
                        borderDash: [6, 3],
                        pointRadius: 0,
                        showLine: true,
                        fill: false,
                        type: 'scatter',
                    });
                }
            });
        });
        const allTimes = datasets.flatMap(ds => ds.data.map(p => p.y)).filter(Boolean);
        const medTime = allTimes.sort((a,b) => a-b)[Math.floor(allTimes.length/2)] || 90;
        const yMin = Math.max(0, medTime * 0.97);
        const yMax = medTime * 1.06;
        degChartInstance.current = new Chart(ctx, {
            type: 'scatter',
            data: {datasets},
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        backgroundColor: '#1E1E2C', titleColor: '#fff', bodyColor: '#9A9AAF',
                        borderColor: '#38383F', borderWidth: 1,
                        filter: (item) => !item.dataset.label?.includes('trend'),
                        callbacks: {
                            title: (items) => items[0]?.dataset?.label || '',
                            label: (item) => {
                                const t = item.parsed.y;
                                const m = Math.floor(t/60), s = (t%60).toFixed(3);
                                return `Круг ${item.parsed.x}: ${m>0?m+':'+s.padStart(6,'0'):s}`;
                            },
                        }
                    },
                },
                scales: {
                    x: {type:'linear',title:{display:true,text:'Круг',color:'#6B6B80',font:{family:'Titillium Web'}},ticks:{color:'#6B6B80'},grid:{color:'#38383F33'}},
                    y: {title:{display:true,text:'Время (скорр.)',color:'#6B6B80',font:{family:'Titillium Web'}},ticks:{color:'#6B6B80',callback:v=>{const m=Math.floor(v/60);const s=(v%60).toFixed(1);return m>0?m+':'+s.padStart(4,'0'):s;}},grid:{color:'#38383F33'},min:yMin,max:yMax},
                },
                interaction: {mode:'nearest',intersect:false},
            },
        });
        return () => { if (degChartInstance.current) degChartInstance.current.destroy(); };
    }, [degradation, aTab, degDrivers, chartReady]);

    // Race trace chart rendering
    useEffect(() => {
        if (!chartReady) return;   // ждём ленивую библиотеку
        if (aTab !== 'racetrace' || !raceTrace?.drivers || !traceChartRef.current || !traceDrivers.length) return;
        if (traceChartInstance.current) traceChartInstance.current.destroy();
        const ctx = traceChartRef.current.getContext('2d');
        const allDrivers = Object.values(raceTrace.drivers);
        const filtered = allDrivers.filter(d => traceDrivers.includes(d.driver_number));
        const datasets = filtered.map(d => ({
            label: d.name_acronym || String(d.driver_number),
            data: d.trace.filter(p => p.gap !== null).map(p => ({x: p.lap, y: p.gap})),
            borderColor: d.color || '#888',
            backgroundColor: d.color || '#888',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.1,
            fill: false,
        }));
        const maxGap = Math.max(5, ...datasets.flatMap(ds => ds.data.map(p => p.y)));
        traceChartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {datasets},
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        mode: 'nearest', intersect: false,
                        backgroundColor: '#1E1E2C', titleColor: '#fff', bodyColor: '#9A9AAF',
                        borderColor: '#38383F', borderWidth: 1,
                        callbacks: {
                            title: (items) => items[0]?.dataset?.label || '',
                            label: (item) => `Круг ${item.parsed.x}: +${item.parsed.y.toFixed(1)}с`,
                        }
                    },
                },
                scales: {
                    x: {type:'linear', title:{display:true,text:'Круг',color:'#6B6B80',font:{family:'Titillium Web'}}, ticks:{color:'#6B6B80'}, grid:{color:'#38383F33'}},
                    y: {title:{display:true,text:'Отставание (сек)',color:'#6B6B80',font:{family:'Titillium Web'}}, ticks:{color:'#6B6B80',callback:v=>`+${v.toFixed(0)}с`}, grid:{color:'#38383F33'}, min:0, max:maxGap*1.1},
                },
                interaction: {mode:'nearest',axis:'x',intersect:false},
            },
        });
        return () => { if (traceChartInstance.current) traceChartInstance.current.destroy(); };
    }, [raceTrace, aTab, traceDrivers, chartReady]);

    const toggleDriver = (dn) => {
        setSelectedDrivers(prev => prev.includes(dn) ? prev.filter(n => n !== dn) : [...prev, dn].slice(-5));
    };
    const toggleDegDriver = (dn) => {
        setDegDrivers(prev => prev.includes(dn) ? prev.filter(n => n !== dn) : [...prev, dn].slice(-5));
    };
    const toggleTraceDriver = (dn) => {
        setTraceDrivers(prev => prev.includes(dn) ? prev.filter(n => n !== dn) : [...prev, dn].slice(-8));
    };
    const toggleSeriesDriver = (dn) => {
        setSeriesDrivers(prev => prev.includes(dn) ? prev.filter(n => n !== dn) : [...prev, dn].slice(-3));
    };

    // Telemetry comparison handler
    const compareTelemetry = () => {
        if (!telDriver1 || !telDriver2 || telDriver1 === telDriver2) return;
        setTelLoading(true); setTelData(null);
        api.get(`/api/analytics/telemetry?driver1=${telDriver1}&driver2=${telDriver2}`).then(d => { setTelData(d); setTelLoading(false); });
    };

    // Telemetry speed chart
    useEffect(() => {
        if (!chartReady) return;   // ждём ленивую библиотеку
        if (aTab !== 'telemetry' || !telData?.driver1?.telemetry?.length || !telSpeedRef.current) return;
        if (telSpeedInstance.current) telSpeedInstance.current.destroy();
        const ctx = telSpeedRef.current.getContext('2d');
        const d1 = telData.driver1, d2 = telData.driver2;
        telSpeedInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
                datasets: [
                    {label:d1.code, data:d1.telemetry.map(t=>({x:t.pct,y:t.speed})), borderColor:d1.team_color||'#FF3333', borderWidth:1.5, pointRadius:0, tension:0.1},
                    {label:d2.code, data:d2.telemetry.map(t=>({x:t.pct,y:t.speed})), borderColor:d2.team_color||'#0077FF', borderWidth:1.5, pointRadius:0, tension:0.1},
                ],
            },
            options: {
                responsive:true, maintainAspectRatio:false,
                plugins: {legend:{display:true,labels:{color:'#9A9AAF',boxWidth:12,font:{size:10,family:'Titillium Web'}}}, tooltip:{backgroundColor:'#1E1E2C',titleColor:'#fff',bodyColor:'#9A9AAF',borderColor:'#38383F',borderWidth:1}},
                scales: {
                    x: {type:'linear',title:{display:true,text:'Дистанция %',color:'#6B6B80',font:{family:'Titillium Web',size:10}},ticks:{color:'#6B6B80',font:{size:9}},grid:{color:'#38383F33'},min:0,max:100},
                    y: {title:{display:true,text:'Скорость км/ч',color:'#6B6B80',font:{family:'Titillium Web',size:10}},ticks:{color:'#6B6B80',font:{size:9}},grid:{color:'#38383F33'}},
                },
                interaction:{mode:'index',intersect:false},
            },
        });
        return () => { if (telSpeedInstance.current) telSpeedInstance.current.destroy(); };
    }, [telData, aTab, chartReady]);

    // Weather radar animation
    useEffect(() => {
        if (aTab !== 'weather' || !weatherRadar?.frames?.length) { if(radarIntervalRef.current) clearInterval(radarIntervalRef.current); return; }
        radarIntervalRef.current = setInterval(() => {
            setRadarFrame(prev => (prev + 1) % weatherRadar.frames.length);
        }, 1200);
        return () => { if(radarIntervalRef.current) clearInterval(radarIntervalRef.current); };
    }, [aTab, weatherRadar]);

    return (
        <div className="page-container fade-in flex-layout" style={{display:'flex',flexDirection:'column',overflow:'hidden',paddingBottom:0}}>
            {/* Sticky header + tabs */}
            <div style={{flexShrink:0,padding:'12px 16px 0',background:'var(--f1-dark)'}}>
                <h2 style={{fontSize:22,fontWeight:900,marginBottom:12}}>Аналитика</h2>
                <div style={{display:'flex',gap:4,marginBottom:12,overflowX:'auto',paddingBottom:4}}>
                    {[{id:'strategy',label:'Шины'},{id:'positions',label:'Позиции'},{id:'laptimes',label:'Темпы'},{id:'racetrace',label:'Трейс'},{id:'lapseries',label:'Таблица'},{id:'speedtraps',label:'Скорость'},{id:'degradation',label:'Деград.'},{id:'telemetry',label:'Телемет.'},{id:'stratpred',label:'Стратегия'},{id:'weather',label:'Погода'}].map(t=>(
                        <button key={t.id} className={`tab-switch-btn ${aTab===t.id?'active':''}`} onClick={()=>setATab(t.id)} style={{fontSize:11,padding:'6px 10px',whiteSpace:'nowrap',flexShrink:0}}>{t.label}</button>
                    ))}
                </div>
            </div>

            {/* Scrollable content */}
            <div style={{flex:1,overflowY:'auto',WebkitOverflowScrolling:'touch',paddingBottom:'calc(92px + env(safe-area-inset-bottom, 12px))'}}>

            {loading && <div style={{padding:16}}><TyreLoader text="Анализ телеметрии..."/></div>}

            {/* TYRE STRATEGY */}
            {aTab==='strategy' && !loading && (strategy?.drivers?.length > 0 ? (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:4,paddingLeft:4}}>Стратегия шин · {strategy.total_laps} кругов</div>
                    <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',paddingLeft:4}}>
                        {['SOFT','MEDIUM','HARD','INTERMEDIATE','WET'].map(c=>(
                            <div key={c} style={{display:'flex',alignItems:'center',gap:4,fontSize:11}}>
                                <span style={{width:10,height:10,borderRadius:'50%',background:tyreColors[c],display:'inline-block'}}/>{c}
                            </div>
                        ))}
                    </div>
                    {strategy.drivers.map((d,i)=>(
                        <div key={d.driver_number} style={{display:'flex',alignItems:'center',gap:6,marginBottom:4,padding:'4px 0'}}>
                            <div style={{width:30,fontSize:11,fontWeight:700,color:d.team_color,textAlign:'right',flexShrink:0}}>{d.code}</div>
                            <div style={{flex:1,display:'flex',height:22,borderRadius:4,overflow:'hidden',background:'var(--f1-gray)',gap:1}}>
                                {d.stints.map((s,j)=>{
                                    const lapStart = s.lap_start || 1;
                                    const lapEnd = s.lap_end || strategy.total_laps;
                                    const widthPct = ((lapEnd - lapStart + 1) / strategy.total_laps) * 100;
                                    return (
                                        <div key={j} style={{width:`${widthPct}%`,background:tyreColors[s.compound]||'#888',minWidth:2,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,color:s.compound==='HARD'||s.compound==='MEDIUM'?'#000':'#fff',overflow:'hidden',whiteSpace:'nowrap'}}>
                                            {widthPct>8?`${lapEnd-lapStart+1}L`:''}
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{width:20,fontSize:10,color:'var(--f1-text-muted)',textAlign:'center',flexShrink:0}}>P{d.finish_position}</div>
                        </div>
                    ))}
                </div>
            ) : !loading && (
                <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🏎️</div><div style={{fontSize:15,fontWeight:700}}>Нет данных о стратегии</div><div style={{fontSize:13}}>Данные появятся во время/после гонки</div></div>
            ))}

            {/* POSITION CHART */}
            {aTab==='positions' && !loading && (posChart?.drivers?.length > 0 ? (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>Изменение позиций по кругам</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:8,paddingLeft:4}}>
                        {posChart.drivers.map(d=>(
                            <div key={d.driver_number} style={{display:'flex',alignItems:'center',gap:3,fontSize:10,padding:'2px 6px',borderRadius:4,background:`${d.team_color}22`,color:d.team_color,fontWeight:600}}>
                                <span style={{width:6,height:6,borderRadius:'50%',background:d.team_color}}/>{d.code}
                            </div>
                        ))}
                    </div>
                    <div className="card" style={{padding:8,height:350}}>
                        <canvas ref={posChartRef}/>
                    </div>
                </div>
            ) : !loading && (
                <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>📈</div><div style={{fontSize:15,fontWeight:700}}>Нет данных</div><div style={{fontSize:13}}>График появится во время/после сессии</div></div>
            ))}

            {/* LAP TIMES */}
            {aTab==='laptimes' && !loading && (lapTimes?.drivers?.length > 0 ? (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>Сравнение темпа · Выбери пилотов</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:10,paddingLeft:4}}>
                        {lapTimes.drivers.map(d=>{
                            const sel = selectedDrivers.includes(d.driver_number);
                            return (
                                <button key={d.driver_number} onClick={()=>toggleDriver(d.driver_number)}
                                    style={{display:'flex',alignItems:'center',gap:3,fontSize:11,padding:'4px 8px',borderRadius:6,border:`1.5px solid ${sel?d.team_color:d.team_color+'44'}`,background:sel?`${d.team_color}33`:'transparent',color:sel?'#fff':'var(--f1-text-muted)',fontWeight:600,cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                                    {d.code}
                                </button>
                            );
                        })}
                    </div>
                    {lapTimes.session_best && (
                        <div style={{fontSize:12,color:'#A020F0',fontWeight:600,paddingLeft:4,marginBottom:8}}>Лучший круг: {fmtLap(lapTimes.session_best)}</div>
                    )}
                    <div className="card" style={{padding:8,height:350}}>
                        <canvas ref={lapChartRef}/>
                    </div>
                </div>
            ) : !loading && (
                <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>⏱️</div><div style={{fontSize:15,fontWeight:700}}>Нет данных о кругах</div><div style={{fontSize:13}}>Данные появятся во время/после сессии</div></div>
            ))}

            {/* TYRE DEGRADATION */}
            {aTab==='degradation' && !loading && (degradation?.drivers?.length > 0 ? (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>Деградация шин · Выбери пилотов</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:10,paddingLeft:4}}>
                        {degradation.drivers.map(d=>{
                            const sel = degDrivers.includes(d.driver_number);
                            return (
                                <button key={d.driver_number} onClick={()=>toggleDegDriver(d.driver_number)}
                                    style={{display:'flex',alignItems:'center',gap:3,fontSize:11,padding:'4px 8px',borderRadius:6,border:`1.5px solid ${sel?d.team_color:d.team_color+'44'}`,background:sel?`${d.team_color}33`:'transparent',color:sel?'#fff':'var(--f1-text-muted)',fontWeight:600,cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                                    {d.code}
                                </button>
                            );
                        })}
                    </div>

                    {/* Deg rate cards */}
                    <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:12,paddingLeft:4}}>
                        {degradation.drivers.filter(d=>degDrivers.includes(d.driver_number)).map(d=>(
                            d.stints.map((stint,si)=>{
                                const rate = stint.deg_rate;
                                const color = rate===null?'var(--f1-text-muted)':rate<=0.02?'#39B54A':rate<=0.05?'#FFD700':'#FF3333';
                                return (
                                    <div key={`${d.driver_number}-${si}`} style={{background:'var(--f1-card-solid)',border:'1px solid var(--f1-border)',borderRadius:8,padding:'6px 10px',minWidth:80}}>
                                        <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:2}}>
                                            <span style={{width:8,height:8,borderRadius:'50%',background:tyreColors[stint.compound]||'#888'}}/>
                                            <span style={{fontSize:11,fontWeight:700,color:d.team_color}}>{d.code}</span>
                                            <span style={{fontSize:9,color:'var(--f1-text-muted)'}}>S{stint.stint_number}</span>
                                        </div>
                                        <div style={{fontSize:16,fontWeight:900,color,fontVariantNumeric:'tabular-nums'}}>{rate!==null?`${rate>0?'+':''}${rate.toFixed(3)}`:'-'}</div>
                                        <div style={{fontSize:9,color:'var(--f1-text-muted)'}}>с/круг</div>
                                    </div>
                                );
                            })
                        ))}
                    </div>

                    {/* Legend */}
                    <div style={{display:'flex',gap:8,marginBottom:8,paddingLeft:4,fontSize:10,color:'var(--f1-text-muted)'}}>
                        <span><span style={{color:'#39B54A',fontWeight:700}}>●</span> ≤0.02 отл.</span>
                        <span><span style={{color:'#FFD700',fontWeight:700}}>●</span> ≤0.05 норм.</span>
                        <span><span style={{color:'#FF3333',fontWeight:700}}>●</span> &gt;0.05 выс.</span>
                        <span style={{marginLeft:8}}>--- тренд</span>
                    </div>

                    <div className="card" style={{padding:8,height:350}}>
                        <canvas ref={degChartRef}/>
                    </div>
                </div>
            ) : !loading && (
                <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>📉</div><div style={{fontSize:15,fontWeight:700}}>Нет данных о деградации</div><div style={{fontSize:13}}>Данные появятся во время/после гонки</div></div>
            ))}

            {/* TELEMETRY COMPARISON */}
            {aTab==='telemetry' && !loading && (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>Сравнение телеметрии · Лучший круг</div>
                    {telDriverList.length > 0 ? (
                        <div>
                            <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap',paddingLeft:4}}>
                                <select value={telDriver1||''} onChange={e=>setTelDriver1(Number(e.target.value))} style={{background:'var(--f1-card-solid)',color:'var(--f1-text)',border:'1px solid var(--f1-border)',borderRadius:8,padding:'6px 10px',fontSize:12,fontFamily:'inherit',fontWeight:600}}>
                                    {telDriverList.map(d=><option key={d.driver_number} value={d.driver_number}>{d.code} — {d.name}</option>)}
                                </select>
                                <span style={{fontSize:13,fontWeight:700,color:'var(--f1-text-muted)'}}>vs</span>
                                <select value={telDriver2||''} onChange={e=>setTelDriver2(Number(e.target.value))} style={{background:'var(--f1-card-solid)',color:'var(--f1-text)',border:'1px solid var(--f1-border)',borderRadius:8,padding:'6px 10px',fontSize:12,fontFamily:'inherit',fontWeight:600}}>
                                    {telDriverList.map(d=><option key={d.driver_number} value={d.driver_number}>{d.code} — {d.name}</option>)}
                                </select>
                                <button onClick={compareTelemetry} disabled={telLoading||!telDriver1||!telDriver2||telDriver1===telDriver2} style={{background:'var(--f1-red)',color:'#fff',border:'none',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',opacity:telLoading?0.5:1}}>{telLoading?'Загрузка...':'Сравнить'}</button>
                            </div>

                            {telData?.error && <div className="card" style={{padding:16,textAlign:'center',color:'var(--f1-text-muted)',fontSize:13}}>{telData.error==='no_lap_data'?'Нет данных о кругах':telData.error==='no_car_data'?'Нет телеметрии':'Ошибка загрузки'}</div>}

                            {telData?.driver1 && telData?.driver2 && (
                                <div>
                                    {/* Lap time cards */}
                                    <div style={{display:'flex',gap:8,marginBottom:12}}>
                                        {[telData.driver1, telData.driver2].map((d,i)=>(
                                            <div key={i} className="card" style={{flex:1,padding:'10px 12px',borderLeft:`3px solid ${d.team_color}`}}>
                                                <div style={{fontSize:11,fontWeight:700,color:d.team_color}}>{d.code}</div>
                                                <div style={{fontSize:20,fontWeight:900,fontVariantNumeric:'tabular-nums'}}>{fmtLap(d.best_lap)}</div>
                                                <div style={{fontSize:10,color:'var(--f1-text-muted)'}}>Круг {d.lap_number}</div>
                                                {d.sectors && <div style={{display:'flex',gap:4,marginTop:4}}>{d.sectors.map((s,si)=><span key={si} style={{fontSize:9,color:'var(--f1-text-muted)',background:'rgba(255,255,255,0.05)',padding:'1px 4px',borderRadius:3}}>S{si+1}: {s?s.toFixed(3):'-'}</span>)}</div>}
                                            </div>
                                        ))}
                                    </div>
                                    {/* Delta badge */}
                                    <div style={{textAlign:'center',marginBottom:12}}>
                                        <span style={{background:telData.delta>0?`${telData.driver2.team_color}33`:`${telData.driver1.team_color}33`,color:telData.delta>0?telData.driver2.team_color:telData.driver1.team_color,padding:'4px 12px',borderRadius:20,fontSize:13,fontWeight:900,fontVariantNumeric:'tabular-nums'}}>
                                            {telData.delta>0?'+':''}{telData.delta.toFixed(3)}с {telData.delta>0?telData.driver2.code:telData.driver1.code} быстрее
                                        </span>
                                    </div>
                                    {/* Speed chart */}
                                    {(telData.driver1.telemetry?.length>0 || telData.driver2.telemetry?.length>0) && (
                                        <div>
                                            <div style={{fontSize:10,color:'var(--f1-text-muted)',fontWeight:700,marginBottom:4,paddingLeft:4}}>СКОРОСТЬ (км/ч)</div>
                                            <div className="card" style={{padding:8,height:200,marginBottom:8}}>
                                                <canvas ref={telSpeedRef}/>
                                            </div>
                                        </div>
                                    )}
                                    {/* Throttle/Brake bars */}
                                    {telData.driver1.telemetry?.length>0 && (
                                        <div>
                                            <div style={{fontSize:10,color:'var(--f1-text-muted)',fontWeight:700,marginBottom:4,paddingLeft:4}}>ГАЗ / ТОРМОЗ</div>
                                            {[telData.driver1, telData.driver2].map((d,di)=>(
                                                <div key={di} style={{marginBottom:6}}>
                                                    <div style={{fontSize:9,fontWeight:700,color:d.team_color,marginBottom:2,paddingLeft:4}}>{d.code}</div>
                                                    <div style={{display:'flex',height:12,borderRadius:3,overflow:'hidden',background:'rgba(0,0,0,0.3)'}}>
                                                        {_downsample(d.telemetry,80).map((t,i)=>(
                                                            <div key={i} style={{flex:1,background:t.brake>0?'#FF3333':t.throttle>50?`rgba(57,181,74,${t.throttle/100})`:'rgba(255,255,255,0.05)'}}/>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                            <div style={{display:'flex',gap:10,fontSize:9,color:'var(--f1-text-muted)',paddingLeft:4,marginTop:4,marginBottom:8}}>
                                                <span><span style={{color:'#39B54A'}}>■</span> Газ</span>
                                                <span><span style={{color:'#FF3333'}}>■</span> Тормоз</span>
                                            </div>
                                        </div>
                                    )}
                                    {/* Gear comparison */}
                                    {telData.driver1.telemetry?.length>0 && (
                                        <div>
                                            <div style={{fontSize:10,color:'var(--f1-text-muted)',fontWeight:700,marginBottom:4,paddingLeft:4}}>ПЕРЕДАЧИ</div>
                                            {[telData.driver1, telData.driver2].map((d,di)=>(
                                                <div key={di} style={{marginBottom:4}}>
                                                    <div style={{fontSize:9,fontWeight:700,color:d.team_color,marginBottom:1,paddingLeft:4}}>{d.code}</div>
                                                    <div style={{display:'flex',height:16,borderRadius:3,overflow:'hidden',background:'rgba(0,0,0,0.3)'}}>
                                                        {_downsample(d.telemetry,80).map((t,i)=>{
                                                            const gearColors = ['#333','#2D5BA0','#3D7AC0','#4DA3FF','#66B2FF','#99CCFF','#CCE5FF','#E6F0FF','#FFF'];
                                                            return <div key={i} style={{flex:1,background:gearColors[t.gear]||'#333',display:'flex',alignItems:'center',justifyContent:'center',fontSize:6,color:'#fff',fontWeight:700}}>{t.gear||''}</div>;
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>📊</div><div style={{fontSize:15,fontWeight:700}}>Нет данных пилотов</div></div>}
                </div>
            )}

            {/* STRATEGY PREDICTION */}
            {aTab==='stratpred' && !loading && (stratPred?.strategies?.length > 0 ? (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>AI Стратегия · {stratPred.total_laps} кругов</div>
                    {/* Info cards */}
                    <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap',paddingLeft:4}}>
                        <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid var(--f1-border)',borderRadius:8,padding:'4px 10px',fontSize:10}}>
                            <span style={{color:'var(--f1-text-muted)'}}>Пит-стоп: </span><span style={{fontWeight:700}}>{stratPred.pit_loss}с</span>
                        </div>
                        {Object.entries(stratPred.wear_rates||{}).map(([c,r])=>(
                            <div key={c} style={{background:'rgba(255,255,255,0.04)',border:'1px solid var(--f1-border)',borderRadius:8,padding:'4px 10px',fontSize:10,display:'flex',alignItems:'center',gap:4}}>
                                <span style={{width:8,height:8,borderRadius:'50%',background:tyreColors[c]||'#888',display:'inline-block'}}/><span style={{fontWeight:700}}>{r.toFixed(3)}с/кр</span>
                            </div>
                        ))}
                    </div>
                    {/* Strategy cards */}
                    {stratPred.strategies.map((s,i)=>(
                        <div key={i} className="card" style={{padding:'12px',marginBottom:8,border:s.optimal?'1.5px solid var(--f1-red)':'1px solid var(--f1-border)'}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                                <div style={{display:'flex',alignItems:'center',gap:6}}>
                                    {s.optimal && <span style={{background:'var(--f1-red)',color:'#fff',fontSize:9,fontWeight:800,padding:'2px 6px',borderRadius:4}}>ЛУЧШАЯ</span>}
                                    <span style={{fontSize:12,fontWeight:700}}>{s.stops === 1 ? '1 пит-стоп' : '2 пит-стопа'}</span>
                                </div>
                                <span style={{fontSize:12,fontWeight:700,color:s.optimal?'var(--f1-red)':'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums'}}>
                                    {s.optimal ? 'Оптимальная' : `+${s.delta}с`}
                                </span>
                            </div>
                            {/* Strategy bar */}
                            <div style={{display:'flex',height:32,borderRadius:8,overflow:'hidden',gap:2,marginBottom:6}}>
                                {s.stints.map((st,j)=>{
                                    const w = (st.laps / stratPred.total_laps) * 100;
                                    const isDark = st.compound==='HARD'||st.compound==='MEDIUM';
                                    return <div key={j} style={{width:`${w}%`,background:tyreColors[st.compound]||'#888',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:isDark?'#000':'#fff',minWidth:20}}>
                                        {w>15?`${st.compound[0]} L${st.start}-${st.end}`:`${st.compound[0]}`}
                                    </div>;
                                })}
                            </div>
                            <div style={{display:'flex',gap:12,fontSize:10,color:'var(--f1-text-muted)'}}>
                                {s.stints.map((st,j)=><span key={j}><span style={{color:tyreColors[st.compound]}}>●</span> {tyreLabels[st.compound]||st.compound}: {st.laps} кр.</span>)}
                                <span style={{marginLeft:'auto'}}>Пит: {s.pit_laps.map(p=>`L${p}`).join(', ')}</span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : !loading && (
                <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🧠</div><div style={{fontSize:15,fontWeight:700}}>Нет данных для расчёта</div><div style={{fontSize:13}}>Данные появятся во время/после гонки</div></div>
            ))}

            {/* WEATHER RADAR */}
            {aTab==='weather' && !loading && (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>Радар осадков · {weatherRadar?.circuit || 'Трасса'}</div>
                    {weatherRadar?.error ? (
                        <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🌤️</div><div style={{fontSize:15,fontWeight:700}}>{weatherRadar.error==='no_coordinates'?'Координаты трассы не найдены':'Радар недоступен'}</div></div>
                    ) : weatherRadar ? (
                        <div>
                            {/* Rain probability card */}
                            <div className="card" style={{padding:'14px 16px',marginBottom:12,display:'flex',alignItems:'center',gap:12}}>
                                <div style={{fontSize:36}}>
                                    {weatherRadar.rain_probability==='raining'?'☔':weatherRadar.rain_probability==='high'?'🌧️':weatherRadar.rain_probability==='medium'?'🌦️':'☀️'}
                                </div>
                                <div style={{flex:1}}>
                                    <div style={{fontSize:15,fontWeight:700}}>
                                        {{low:'Дождь маловероятен',medium:'Возможен дождь',high:'Высокая вероятность дождя',raining:'Идёт дождь'}[weatherRadar.rain_probability]}
                                    </div>
                                    <div style={{display:'flex',gap:12,fontSize:12,color:'var(--f1-text-muted)',marginTop:4}}>
                                        {weatherRadar.weather.temperature!=null && <span>🌡 {weatherRadar.weather.temperature}°C</span>}
                                        {weatherRadar.weather.humidity!=null && <span>💧 {weatherRadar.weather.humidity}%</span>}
                                        {weatherRadar.weather.wind_speed!=null && <span>💨 {weatherRadar.weather.wind_speed} км/ч</span>}
                                    </div>
                                </div>
                            </div>
                            {/* Radar embed */}
                            {weatherRadar.radar_url && (
                                <div className="card" style={{padding:12,marginBottom:12}}>
                                    <div style={{width:'100%',paddingBottom:'100%',position:'relative',borderRadius:8,overflow:'hidden'}}>
                                        <iframe src={weatherRadar.radar_url} style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',border:'none',borderRadius:8}} allowFullScreen/>
                                    </div>
                                    <div style={{marginTop:10,textAlign:'center'}}>
                                        <button onClick={()=>openLink(weatherRadar.radar_url)} style={{background:'var(--f1-red)',color:'#fff',border:'none',borderRadius:8,padding:'8px 20px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Открыть радар ↗</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🌤️</div><div style={{fontSize:15,fontWeight:700}}>Загрузка данных погоды...</div></div>}
                </div>
            )}

            {/* RACE TRACE */}
            {aTab==='racetrace' && !loading && (() => {
                const allDrivers = raceTrace?.drivers ? Object.values(raceTrace.drivers) : [];
                return allDrivers.length > 0 ? (
                    <div style={{padding:'0 12px'}}>
                        <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>Race Trace · Отставание от лидера · {raceTrace.total_laps} кругов</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:10,paddingLeft:4}}>
                            {allDrivers.map(d => {
                                const sel = traceDrivers.includes(d.driver_number);
                                return (
                                    <button key={d.driver_number} onClick={()=>toggleTraceDriver(d.driver_number)}
                                        style={{display:'flex',alignItems:'center',gap:3,fontSize:11,padding:'4px 8px',borderRadius:6,border:`1.5px solid ${sel?d.color:d.color+'44'}`,background:sel?`${d.color}33`:'transparent',color:sel?'#fff':'var(--f1-text-muted)',fontWeight:600,cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                                        {d.name_acronym}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="card" style={{padding:8,height:350}}>
                            <canvas ref={traceChartRef}/>
                        </div>
                    </div>
                ) : (
                    <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>📊</div><div style={{fontSize:15,fontWeight:700}}>Нет данных Race Trace</div><div style={{fontSize:13}}>Данные появятся во время/после гонки</div></div>
                );
            })()}

            {/* SPEED TRAPS */}
            {aTab==='speedtraps' && !loading && (speedTraps?.speeds?.length > 0 ? (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:12,paddingLeft:4}}>Рейтинг скорости · Ловушка скорости</div>
                    {speedTraps.speeds.map((d, i) => {
                        const maxSpeed = speedTraps.speeds[0]?.speed || 340;
                        const pct = (d.speed / maxSpeed) * 100;
                        const isTop3 = i < 3;
                        return (
                            <div key={d.driver_number} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,padding:'6px 4px'}}>
                                <div style={{width:24,fontWeight:900,fontSize:14,color:isTop3?['#FFD700','#C0C0C0','#CD7F32'][i]:'var(--f1-text-muted)',textAlign:'center'}}>{i+1}</div>
                                <div style={{background:d.color,width:3,height:28,borderRadius:2,flexShrink:0}}/>
                                <div style={{width:36,fontWeight:700,fontSize:13,color:d.color}}>{d.name_acronym}</div>
                                <div style={{flex:1,height:24,background:'var(--f1-gray)',borderRadius:4,overflow:'hidden',position:'relative'}}>
                                    <div style={{width:`${pct}%`,height:'100%',background:`linear-gradient(90deg,${d.color}88,${d.color})`,borderRadius:4,transition:'width 0.5s ease',display:'flex',alignItems:'center',justifyContent:'flex-end',paddingRight:6}}>
                                        <span style={{fontSize:12,fontWeight:900,color:'#fff',fontVariantNumeric:'tabular-nums',textShadow:'0 1px 2px rgba(0,0,0,0.5)'}}>{d.speed} км/ч</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : !loading && (
                <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>⚡</div><div style={{fontSize:15,fontWeight:700}}>Нет данных скорости</div><div style={{fontSize:13}}>Данные появятся во время/после сессии</div></div>
            ))}

            {/* LAP TIME SERIES TABLE */}
            {aTab==='lapseries' && !loading && (lapSeries?.drivers?.length > 0 ? (
                <div style={{padding:'0 12px'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,paddingLeft:4}}>Все круги · Таблица · {lapSeries.total_laps} кругов</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:10,paddingLeft:4}}>
                        {lapSeries.drivers.map(d => {
                            const sel = seriesDrivers.includes(d.driver_number);
                            return (
                                <button key={d.driver_number} onClick={()=>toggleSeriesDriver(d.driver_number)}
                                    style={{display:'flex',alignItems:'center',gap:3,fontSize:11,padding:'4px 8px',borderRadius:6,border:`1.5px solid ${sel?d.team_color:d.team_color+'44'}`,background:sel?`${d.team_color}33`:'transparent',color:sel?'#fff':'var(--f1-text-muted)',fontWeight:600,cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                                    {d.code} {d.best_lap ? fmtLap(d.best_lap) : ''}
                                </button>
                            );
                        })}
                    </div>
                    {lapSeries.drivers.filter(d => seriesDrivers.includes(d.driver_number)).map(d => (
                        <div key={d.driver_number} className="card" style={{marginBottom:10,padding:0,overflow:'hidden'}}>
                            <div style={{padding:'10px 12px',borderBottom:'1px solid var(--f1-border)',display:'flex',alignItems:'center',gap:8}}>
                                <div style={{background:d.team_color,width:4,height:24,borderRadius:2}}/>
                                <span style={{fontWeight:700,fontSize:14,color:d.team_color}}>{d.code}</span>
                                <span style={{fontSize:11,color:'var(--f1-text-muted)'}}>{d.team}</span>
                                {d.best_lap && <span style={{marginLeft:'auto',fontSize:12,fontWeight:700,color:'#A020F0',fontVariantNumeric:'tabular-nums'}}>{fmtLap(d.best_lap)}</span>}
                            </div>
                            <div style={{maxHeight:300,overflowY:'auto'}}>
                                <div style={{display:'grid',gridTemplateColumns:'40px 1fr 50px 56px',gap:0,fontSize:11}}>
                                    <div style={{padding:'4px 6px',fontWeight:700,color:'var(--f1-text-muted)',borderBottom:'1px solid var(--f1-border)'}}>Кр.</div>
                                    <div style={{padding:'4px 6px',fontWeight:700,color:'var(--f1-text-muted)',borderBottom:'1px solid var(--f1-border)'}}>Время</div>
                                    <div style={{padding:'4px 6px',fontWeight:700,color:'var(--f1-text-muted)',borderBottom:'1px solid var(--f1-border)',textAlign:'center'}}>Шина</div>
                                    <div style={{padding:'4px 6px',fontWeight:700,color:'var(--f1-text-muted)',borderBottom:'1px solid var(--f1-border)',textAlign:'right'}}>Скор.</div>
                                    {d.laps.map(l => {
                                        const isBest = l.time && d.best_lap && Math.abs(l.time - d.best_lap) < 0.001;
                                        return (
                                            <React.Fragment key={l.lap}>
                                                <div style={{padding:'3px 6px',color:'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums',borderBottom:'1px solid var(--f1-border)'}}>{l.lap}</div>
                                                <div style={{padding:'3px 6px',fontWeight:isBest?800:500,color:l.is_pit_out?'#FFA500':isBest?'#A020F0':'var(--f1-text)',fontVariantNumeric:'tabular-nums',borderBottom:'1px solid var(--f1-border)'}}>
                                                    {l.time ? fmtLap(l.time) : '—'}{l.is_pit_out ? ' P' : ''}
                                                </div>
                                                <div style={{padding:'3px 6px',textAlign:'center',borderBottom:'1px solid var(--f1-border)'}}>
                                                    <span style={{width:10,height:10,borderRadius:'50%',background:tyreColors[l.compound]||'#888',display:'inline-block',verticalAlign:'middle'}}/>
                                                </div>
                                                <div style={{padding:'3px 6px',textAlign:'right',fontSize:10,color:'var(--f1-text-muted)',fontVariantNumeric:'tabular-nums',borderBottom:'1px solid var(--f1-border)'}}>{l.st_speed||'—'}</div>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : !loading && (
                <div style={{textAlign:'center',padding:40,color:'var(--f1-text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>📋</div><div style={{fontSize:15,fontWeight:700}}>Нет данных о кругах</div><div style={{fontSize:13}}>Данные появятся во время/после сессии</div></div>
            ))}
            </div>{/* end scrollable content */}
        </div>
    );
};

// ==== IFRAME FULLSCREEN PLAYER (mobile only, uses portal to body) ====
const IframeFullscreenPlayer = ({src, title}) => {
    const [isFS, setIsFS] = useState(false);
    const tgApp = window.Telegram?.WebApp;
    const portalRef = useRef(null);

    // Create portal container once
    if (!portalRef.current) {
        portalRef.current = document.createElement('div');
    }

    const exitFS = useCallback(() => {
        setIsFS(false);
        document.body.style.overflow = '';
        try { tgApp?.exitFullscreen?.(); } catch(e) {}
    }, []);

    useEffect(() => {
        if (!isFS) return;
        // Mount portal div directly on body
        document.body.appendChild(portalRef.current);
        document.body.style.overflow = 'hidden';
        try { tgApp?.requestFullscreen?.(); } catch(e) {}

        const onKey = (e) => { if (e.key === 'Escape') exitFS(); };
        document.addEventListener('keydown', onKey);
        tgApp?.BackButton?.onClick?.(exitFS);

        return () => {
            document.removeEventListener('keydown', onKey);
            tgApp?.BackButton?.offClick?.(exitFS);
            document.body.style.overflow = '';
            if (portalRef.current.parentNode) portalRef.current.parentNode.removeChild(portalRef.current);
        };
    }, [isFS, exitFS]);

    const fsOverlay = isFS ? ReactDOM.createPortal(
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,width:'100vw',height:'100dvh',zIndex:2147483647,background:'#000',
                     paddingBottom:'env(safe-area-inset-bottom, 0px)',boxSizing:'border-box'}}>
            <iframe src={src} style={{width:'100%',height:'100%',border:'none',display:'block'}} allow="autoplay; fullscreen; encrypted-media; picture-in-picture; accelerometer; gyroscope" allowFullScreen title={title||'Video'}/>
            <div onClick={exitFS} style={{position:'fixed',top:'max(12px, env(safe-area-inset-top, 12px))',left:'max(12px, env(safe-area-inset-left, 12px))',padding:'8px 16px',borderRadius:12,background:'rgba(0,0,0,0.7)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',fontSize:14,fontWeight:700,color:'#fff',border:'1px solid rgba(255,255,255,0.15)',cursor:'pointer',zIndex:2147483647}}>{'← Назад'}</div>
        </div>,
        portalRef.current
    ) : null;

    return (
        <>
            {fsOverlay}
            {!isFS && <div style={{position:'relative',width:'100%',paddingTop:'56.25%',borderRadius:12,overflow:'hidden',background:'#000',marginBottom:12}}>
                <iframe src={src} style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',border:'none'}} allow="autoplay; encrypted-media; picture-in-picture" title={title||'Video'}/>
                <div onClick={() => setIsFS(true)} style={{position:'absolute',bottom:'max(12px, env(safe-area-inset-bottom, 12px))',right:12,padding:'6px 12px',borderRadius:8,background:'rgba(0,0,0,0.7)',backdropFilter:'blur(4px)',WebkitBackdropFilter:'blur(4px)',fontSize:12,fontWeight:600,color:'#fff',border:'1px solid rgba(255,255,255,0.15)',cursor:'pointer',display:'flex',alignItems:'center',gap:4,zIndex:10}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>
                    {'На весь экран'}
                </div>
            </div>}
        </>
    );
};

// ==== VIDEO PLAYER ====
const F1VideoPlayer = ({src, title, poster, streamType}) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const hlsRef = useRef(null);

    const mediaReady = useLib(ensureMedia);
    useEffect(() => {
        if (!mediaReady) return;   // ждём ленивую библиотеку
        const video = videoRef.current;
        if (!video || !src) return;
        const tgApp = window.Telegram?.WebApp;

        const plyrOpts = {
            controls: ['play-large','play','progress','current-time','mute','volume','settings','fullscreen'],
            settings: ['quality','speed'],
            speed: { selected:1, options:[0.5,0.75,1,1.25,1.5,2] },
            fullscreen: { enabled:true, fallback:true, iosNative:true },
            invertTime: false,
            i18n: { speed:'\u0421\u043a\u043e\u0440\u043e\u0441\u0442\u044c', quality:'\u041a\u0430\u0447\u0435\u0441\u0442\u0432\u043e', normal:'\u041e\u0431\u044b\u0447\u043d\u0430\u044f' },
        };

        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const hookFS = (p) => {
            const plyrEl = p.elements.container;
            let origParent = null, origNext = null;

            if (isMobile) {
                // Mobile: auto-fullscreen on play + move to body (CSS fallback)
                p.on('play', () => {
                    if (!p.fullscreen.active) {
                        p.fullscreen.enter();
                    }
                });

                p.on('enterfullscreen', () => {
                    origParent = plyrEl.parentNode;
                    origNext = plyrEl.nextSibling;
                    document.body.appendChild(plyrEl);
                    document.body.style.overflow = 'hidden';
                    try { tgApp?.requestFullscreen?.(); } catch(e) {}
                    // RuStore-обёртка: app.json держит портрет, поворот разрешает
                    // нативка по этому сообщению (Plyr идёт по CSS-фолбэку, так что
                    // document.fullscreenchange здесь не выстрелит).
                    try { window.ReactNativeWebView?.postMessage(JSON.stringify({type:'fullscreen',on:true})); } catch(e) {}
                    // TG лочит портрет в fullscreen — разрешаем поворот и просим landscape
                    try { tgApp?.unlockOrientation?.(); } catch(e) {}
                    try { screen.orientation && screen.orientation.lock && screen.orientation.lock('landscape').catch(()=>{}); } catch(e) {}
                });

                p.on('exitfullscreen', () => {
                    if (origParent) {
                        origParent.insertBefore(plyrEl, origNext);
                    }
                    document.body.style.overflow = '';
                    try { window.ReactNativeWebView?.postMessage(JSON.stringify({type:'fullscreen',on:false})); } catch(e) {}
                    try { screen.orientation && screen.orientation.unlock && screen.orientation.unlock(); } catch(e) {}
                    try { tgApp?.lockOrientation?.(); } catch(e) {}
                    try { tgApp?.exitFullscreen?.(); } catch(e) {}
                });
            } else {
                // Desktop: native fullscreen via Plyr (no auto-fullscreen)
                p.on('enterfullscreen', () => {
                    try { tgApp?.requestFullscreen?.(); } catch(e) {}
                    try { window.ReactNativeWebView?.postMessage(JSON.stringify({type:'fullscreen',on:true})); } catch(e) {}
                });
                p.on('exitfullscreen', () => {
                    try { tgApp?.exitFullscreen?.(); } catch(e) {}
                    try { window.ReactNativeWebView?.postMessage(JSON.stringify({type:'fullscreen',on:false})); } catch(e) {}
                });
            }
        };

        if (streamType === 'hls' && typeof Hls !== 'undefined' && Hls.isSupported()) {
            const hls = new Hls({
                maxBufferLength:30, maxMaxBufferLength:60,
                // Сегменты Rutube идут через наш /api/proxy-stream, то есть канал
                // сервера. Раньше плеер сразу брал 1080p (6.7 Мбит/с) — сегменты
                // приходили с нарастающей задержкой и видео вставало на буфер.
                capLevelToPlayerSize: true,   // не грузить 1080p в окно шириной 360
                startLevel: -1,               // старт по замеру скорости, не с топа
                abrEwmaDefaultEstimate: 1500000,
                maxStarvationDelay: 2,
            });
            hlsRef.current = hls;
            hls.loadSource(src);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                const lvls = hls.levels.map(l => l.height);
                plyrOpts.quality = {
                    default: 0, options: [0, ...lvls], forced: true,
                    onChange: (q) => { hls.currentLevel = q === 0 ? -1 : hls.levels.findIndex(l => l.height === q); },
                };
                plyrOpts.i18n.qualityLabel = { 0: '\u0410\u0432\u0442\u043e' };
                const p = new Plyr(video, plyrOpts);
                playerRef.current = p;
                hookFS(p);
            });
            hls.on(Hls.Events.ERROR, (_, d) => {
                if (d.fatal) { if (d.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad(); else if (d.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError(); }
            });
            return () => { playerRef.current?.destroy?.(); hls.destroy(); };
        } else {
            video.src = src;
            const p = new Plyr(video, plyrOpts);
            playerRef.current = p;
            hookFS(p);
            return () => { playerRef.current?.destroy?.(); };
        }
    }, [src, streamType, mediaReady]);

    return (
        <div style={{width:'100%',marginBottom:12}}>
            {/* размер 16:9 сразу: до инициализации Plyr голый <video> брал размер обложки и на секунду раздувался на весь экран */}
            <video ref={videoRef} controls playsInline poster={poster||''} preload="metadata"
                   style={{display:'block',width:'100%',aspectRatio:'16 / 9',objectFit:'contain',background:'#000',borderRadius:12}}/>
        </div>
    );
};

// ==== RUTUBE: родной плеер Rutube в iframe ====
// Вместо нашего Plyr/HLS показываем официальный embed Rutube.
// Фулскрин делаем сами (CSS position:fixed на той же обёртке — iframe не
// перемещается по DOM, поэтому не перезагружается и не теряет позицию):
//  - RuStore-приложение: поворот разрешает нативка по сообщению {type:'fullscreen'};
//  - Telegram: requestFullscreen + unlockOrientation (TG лочит портрет).
// Кнопка «Во весь экран» стоит ПОД плеером, чтобы не перекрывать панель Rutube.
const RutubeEmbedPlayer = ({url, id, title, directUrl, radius = 12, mb = 12}) => {
    const [isFS, setIsFS] = useState(false);
    const boxRef = useRef(null);
    // размер окна: в фулскрине при вертикальном телефоне поворачиваем плеер сами.
    // Telegram и обёртка RuStore только РАЗРЕШАЮТ поворот, а при выключенном
    // автоповороте телефон так и оставался вертикальным (жалоба Егора 09.10).
    const [vp, setVp] = useState({w: window.innerWidth, h: window.innerHeight});
    useEffect(() => {
        if (!isFS) return;
        const upd = () => setVp({w: window.innerWidth, h: window.innerHeight});
        upd();
        window.addEventListener('resize', upd);
        window.addEventListener('orientationchange', upd);
        return () => { window.removeEventListener('resize', upd); window.removeEventListener('orientationchange', upd); };
    }, [isFS]);
    const rotate = isFS && vp.h > vp.w;
    const tgApp = window.Telegram?.WebApp;
    // приватные ролики Rutube открываются только с ключом ?p=...
    const pKey = (url || '').match(/[?&]p=([\w-]+)/);
    const src = 'https://rutube.ru/play/embed/' + id + (pKey ? '?p=' + pKey[1] : '');

    const sendNativeFS = (on) => {
        try { window.ReactNativeWebView?.postMessage(JSON.stringify({type:'fullscreen',on})); } catch(e) {}
    };
    const enterFS = useCallback(() => {
        setIsFS(true);
        document.body.style.overflow = 'hidden';
        // Настоящий фулскрин элемента: Android WebView (RuStore, allowsFullscreenVideo)
        // и Telegram на Android прячут строку состояния и кнопки навигации — без этого
        // они оставались поверх видео (жалоба Егора 09.10). На iOS не поддерживается —
        // там остаётся CSS-режим.
        try {
            const el = boxRef.current;
            const rq = el && (el.requestFullscreen || el.webkitRequestFullscreen);
            if (rq) { const r = rq.call(el); if (r && r.catch) r.catch(() => {}); }
        } catch(e) {}
        try { tgApp?.requestFullscreen?.(); } catch(e) {}
        sendNativeFS(true);
        try { tgApp?.unlockOrientation?.(); } catch(e) {}
        try { screen.orientation && screen.orientation.lock && screen.orientation.lock('landscape').catch(()=>{}); } catch(e) {}
    }, []);
    const exitFS = useCallback(() => {
        setIsFS(false);
        document.body.style.overflow = '';
        try {
            const fe = document.fullscreenElement || document.webkitFullscreenElement;
            if (fe) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
        } catch(e) {}
        sendNativeFS(false);
        try { screen.orientation && screen.orientation.unlock && screen.orientation.unlock(); } catch(e) {}
        try { tgApp?.lockOrientation?.(); } catch(e) {}
        try { tgApp?.exitFullscreen?.(); } catch(e) {}
    }, []);

    // Esc и кнопка «Назад» Telegram выходят из полноэкранного режима
    useEffect(() => {
        if (!isFS) return;
        const onKey = (e) => { if (e.key === 'Escape') exitFS(); };
        document.addEventListener('keydown', onKey);
        try { tgApp?.BackButton?.onClick?.(exitFS); } catch(e) {}
        return () => {
            document.removeEventListener('keydown', onKey);
            try { tgApp?.BackButton?.offClick?.(exitFS); } catch(e) {}
        };
    }, [isFS, exitFS]);

    // Если сработал собственный фулскрин Rutube (кнопка внутри iframe, там где
    // WebView его поддерживает) — тоже просим нативку/TG разрешить поворот.
    useEffect(() => {
        const onFsChange = () => {
            const el = document.fullscreenElement || document.webkitFullscreenElement;
            const ours = !!el && el.tagName === 'IFRAME' && /rutube\.ru/.test(el.src || '');
            if (ours) { sendNativeFS(true); try { tgApp?.unlockOrientation?.(); } catch(e) {} }
            else if (!el) { if (fsRef.current) exitFS(); else sendNativeFS(false); }
        };
        document.addEventListener('fullscreenchange', onFsChange);
        document.addEventListener('webkitfullscreenchange', onFsChange);
        return () => {
            document.removeEventListener('fullscreenchange', onFsChange);
            document.removeEventListener('webkitfullscreenchange', onFsChange);
        };
    }, []);

    // ушли со страницы, не выйдя из фулскрина — вернуть всё как было
    const fsRef = useRef(false);
    fsRef.current = isFS;
    useEffect(() => () => { if (fsRef.current) exitFS(); }, []);

    // Внешняя обёртка уходит в настоящий фулскрин (ей браузер запрещает transform),
    // поэтому поворачиваем внутренний слой. iframe не пересоздаётся — видео не сбрасывается.
    const outerStyle = isFS
        ? {position:'fixed',top:0,left:0,width:'100vw',height:'100dvh',zIndex:2147483647,background:'#000'}
        : {position:'relative',width:'100%',height:0,paddingTop:'56.25%',borderRadius:radius,overflow:'hidden',background:'#000'};
    const innerStyle = rotate
        ? {position:'absolute',top:'50%',left:'50%',width:vp.h,height:vp.w,transform:'translate(-50%,-50%) rotate(90deg)'}
        : {position:'absolute',top:0,left:0,width:'100%',height:'100%'};
    const frameStyle = {display:'block',width:'100%',height:'100%',border:0};
    const btn = {flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:7,padding:'10px 0',borderRadius:12,border:'1px solid var(--f1-border)',background:'var(--f1-card-solid)',color:'var(--f1-text)',fontFamily:'inherit',fontWeight:700,fontSize:12,cursor:'pointer'};
    return (
        <div style={{marginBottom:mb}}>
            <div ref={boxRef} style={outerStyle}>
                <div style={innerStyle}>
                    <iframe src={src} title={title || 'Rutube'} frameBorder="0" allowFullScreen
                            allow="autoplay; fullscreen; picture-in-picture; encrypted-media; clipboard-write; screen-wake-lock"
                            style={frameStyle}/>
                    {isFS && (
                        // маленький ✕ в углу — большая кнопка «Назад» закрывала название и панель Rutube
                        <div onClick={exitFS} aria-label="Закрыть" style={{position:'absolute',top:10,left:10,width:34,height:34,borderRadius:'50%',background:'rgba(0,0,0,0.45)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,lineHeight:1,cursor:'pointer',opacity:0.75,zIndex:2}}>{'\u2715'}</div>
                    )}
                </div>
            </div>
            {!isFS && (
                <div style={{display:'flex',gap:8,marginTop:8}}>
                    <button onClick={enterFS} style={btn}>{'\u26f6'} Во весь экран</button>
                    <button onClick={() => openLink(directUrl || ('https://rutube.ru/video/' + id + '/'))} style={btn}>{'\u2197'} Открыть в Rutube</button>
                </div>
            )}
        </div>
    );
};

const VideoPlayer = ({embedUrl, videoUrl, title, sessionType, poster}) => {
    const url = embedUrl || videoUrl || '';
    const directUrl = videoUrl || embedUrl || '';
    const isMob = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
    const rtMatch = url.match(/rutube\.ru\/(?:video|play\/embed)\/([a-f0-9]+)/);

    // YouTube: lite-embed pattern — show thumbnail, load iframe on click for instant render
    const [ytPlay, setYtPlay] = useState(false);
    if (ytMatch) {
        if (ytPlay) {
            if (isMob) {
                const ytSrc = 'https://www.youtube.com/embed/' + ytMatch[1] + '?rel=0&playsinline=1&fs=0&autoplay=1';
                return <IframeFullscreenPlayer src={ytSrc} title={title}/>;
            }
            return (
                <div style={{position:'relative',width:'100%',paddingTop:'56.25%',borderRadius:12,overflow:'hidden',background:'#000',marginBottom:12}}>
                    <iframe src={'https://www.youtube.com/embed/' + ytMatch[1] + '?rel=0&playsinline=1&autoplay=1'} style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',border:'none'}} allow="autoplay; fullscreen; encrypted-media; picture-in-picture; accelerometer; gyroscope" allowFullScreen title={title||'Video'}/>
                </div>
            );
        }
        const thumb = 'https://i.ytimg.com/vi/' + ytMatch[1] + '/hqdefault.jpg';
        return (
            <div onClick={() => setYtPlay(true)} style={{position:'relative',width:'100%',paddingTop:'56.25%',borderRadius:12,overflow:'hidden',background:'#000',marginBottom:12,cursor:'pointer'}}>
                <img src={thumb} alt="" loading="lazy" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',opacity:0.92}}/>
                <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,transparent 50%,rgba(0,0,0,0.5))'}}/>
                <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:64,height:64,borderRadius:'50%',background:'rgba(225,6,0,0.92)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 8px 24px rgba(0,0,0,0.5)'}}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M9.2 7.5v9c0 .95 1.05 1.53 1.86 1.03l7.1-4.5c.75-.47.75-1.59 0-2.06l-7.1-4.5C10.25 5.97 9.2 6.55 9.2 7.5z"/></svg>
                </div>
                <div style={{position:'absolute',bottom:8,left:10,right:10,display:'flex',alignItems:'center',justifyContent:'space-between',color:'#fff'}}>
                    <span style={{fontSize:11,fontWeight:700,opacity:0.85}}>YouTube</span>
                    {title && <span style={{fontSize:11,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',marginLeft:8}}>{title}</span>}
                </div>
            </div>
        );
    }

    const [vkPlay, setVkPlay] = useState(false);
    // Rutube: родной плеер Rutube (iframe play/embed). Наш Plyr/HLS через
    // /api/rutube-stream для Rutube больше не используется.
    if (rtMatch) {
        return <RutubeEmbedPlayer url={url} id={rtMatch[1]} title={title} directUrl={directUrl}/>;
    }

    // VK: часть роликов (особенно трансляции VK Спорт) не отдаётся анониму
    // во встроенном плеере — ВК показывает свою заглушку. Поэтому сначала
    // обложка и выбор: попробовать здесь или сразу открыть в ВК, где человек авторизован.
    const vkMatch = directUrl.match(/(?:video|live|clip)(-?\d+)_(\d+)/);
    const isVkHost = /vk\.com|vkvideo\.ru|vk\.ru|vksport/.test(directUrl);
    if (isVkHost && vkMatch) {
        const vkSrc = embedUrl && embedUrl.indexOf('video_ext') !== -1
            ? embedUrl
            : 'https://vk.com/video_ext.php?oid=' + vkMatch[1] + '&id=' + vkMatch[2] + '&hd=2&js_api=1';
        if (vkPlay) return <IframeFullscreenPlayer src={vkSrc} title={title} isMobile={isMob}/>;
        return (
            <div style={{marginBottom:12}}>
                <div style={{position:'relative',width:'100%',paddingTop:'56.25%',borderRadius:12,overflow:'hidden',background:'#111'}}>
                    {poster
                        ? <img src={poster} alt="" loading="lazy" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',opacity:0.9}}/>
                        : <div style={{position:'absolute',inset:0,background:'linear-gradient(135deg,#0077FF,#0055BB)'}}/>}
                    <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,transparent 45%,rgba(0,0,0,0.75))'}}/>
                    <div onClick={() => setVkPlay(true)} style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:64,height:64,borderRadius:'50%',background:'rgba(225,6,0,0.92)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer'}}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M9.2 7.5v9c0 .95 1.05 1.53 1.86 1.03l7.1-4.5c.75-.47.75-1.59 0-2.06l-7.1-4.5C10.25 5.97 9.2 6.55 9.2 7.5z"/></svg>
                    </div>
                    <div style={{position:'absolute',bottom:10,left:12,fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.85)'}}>VK Видео</div>
                </div>
                <div style={{display:'flex',gap:8,marginTop:8}}>
                    <div onClick={() => setVkPlay(true)} style={{flex:1,textAlign:'center',padding:'11px 12px',borderRadius:10,background:'rgba(255,255,255,0.07)',border:'1px solid var(--f1-border)',fontSize:13,fontWeight:700,cursor:'pointer'}}>Смотреть здесь</div>
                    <div onClick={() => openLink(directUrl)} style={{flex:1,textAlign:'center',padding:'11px 12px',borderRadius:10,background:'linear-gradient(135deg,#0077FF,#0055BB)',fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer'}}>Открыть в ВК</div>
                </div>
                <div style={{marginTop:6,fontSize:11,color:'var(--f1-text-muted)',textAlign:'center'}}>Если запись не проигрывается здесь — она откроется в ВК</div>
            </div>
        );
    }

    // прочие ссылки — открываем во внешнем приложении
    const isVk = isVkHost;
    return (
        <div onClick={() => openLink(directUrl)} style={{cursor:'pointer',marginBottom:12,borderRadius:14,overflow:'hidden',background:isVk?'linear-gradient(135deg,#0077FF,#0055BB)':'linear-gradient(135deg,#333,#222)'}}>
            <div style={{padding:'18px 20px',display:'flex',alignItems:'center',gap:14}}>
                <div style={{width:48,height:48,borderRadius:12,background:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M9.2 7.5v9c0 .95 1.05 1.53 1.86 1.03l7.1-4.5c.75-.47.75-1.59 0-2.06l-7.1-4.5C10.25 5.97 9.2 6.55 9.2 7.5z"/></svg>
                </div>
                <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:700,color:'white',marginBottom:2}}>\u0421\u043c\u043e\u0442\u0440\u0435\u0442\u044c \u0437\u0430\u043f\u0438\u0441\u044c</div>
                    <div style={{fontSize:11,color:'rgba(255,255,255,0.7)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{title||(isVk?'VK Video':'\u0412\u0438\u0434\u0435\u043e')}</div>
                </div>
                <div style={{flexShrink:0,color:'rgba(255,255,255,0.8)',fontSize:18}}>\u2197</div>
            </div>
        </div>
    );
};


        // ==== BROADCAST TAB CONTENT ====
const BroadcastTab = ({broadcasts, raceName, hasSprint}) => {
    const SESSION_ORDER = ['sprint_qualifying','sprint','qualifying','race','review'];
    const SESSION_LABELS = {race:'Гонка',qualifying:'Квалификация',sprint:'Спринт',sprint_qualifying:'Спринт-квалификация',review:'Обзор'};
    const SESSION_ICONS = {sprint_qualifying:'⚡',sprint:'🏁',qualifying:'⏱️',race:'🏆',review:'📺'};
    const types = hasSprint ? SESSION_ORDER : SESSION_ORDER.filter(t => t !== 'sprint_qualifying' && t !== 'sprint');
    const byType = {};
    (broadcasts || []).forEach(b => { byType[b.session_type] = b; });
    const items = types.map(t => ({type:t, broadcast: byType[t] || null}));
    const total = items.filter(i => i.broadcast).length;
    const [openType, setOpenType] = useState(null);
    useEffect(() => {
        if (openType !== null) return;
        const first = items.find(i => i.broadcast);
        if (first) setOpenType(first.type);
    }, [broadcasts]);

    if (total === 0) {
        return (
            <div className="card" style={{padding:'24px 16px',textAlign:'center'}}>
                <div style={{fontSize:32,marginBottom:8}}>📺</div>
                <div style={{fontSize:15,fontWeight:700}}>Записей пока нет</div>
                <div style={{fontSize:13,color:'var(--f1-text-secondary)'}}>Записи появятся после трансляции</div>
            </div>
        );
    }
    return (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {items.map(it => {
                const b = it.broadcast;
                const open = openType === it.type;
                return (
                    <div key={it.type} className="card" style={{padding:0,opacity:b?1:0.5,overflow:'hidden'}}>
                        <div onClick={() => b && setOpenType(open ? null : it.type)}
                            style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',cursor:b?'pointer':'default'}}>
                            <span style={{fontSize:18}}>{SESSION_ICONS[it.type]}</span>
                            <div style={{flex:1}}>
                                <div style={{fontWeight:700,fontSize:14}}>{SESSION_LABELS[it.type]}</div>
                                {b?.title && b.title !== SESSION_LABELS[it.type] && <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:2}}>{b.title}</div>}
                            </div>
                            {!!b?.is_live && <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <div style={{width:8,height:8,borderRadius:'50%',background:'var(--f1-red)',animation:'pulse 1.5s infinite'}}/>
                                <span style={{color:'var(--f1-red)',fontWeight:700,fontSize:10,textTransform:'uppercase'}}>Live</span>
                            </div>}
                            {!b && <span style={{fontSize:11,color:'var(--f1-text-muted)'}}>Не добавлено</span>}
                            {b && <span style={{color:'var(--f1-text-muted)',fontSize:12}}>{open ? '▲' : '▼'}</span>}
                        </div>
                        {b && open && (b.embed_url || b.video_url) && (
                            <div style={{padding:'0 14px 14px'}}>
                                <VideoPlayer embedUrl={b.embed_url} videoUrl={b.video_url} title={b.title} sessionType={b.session_type}/>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ==== SPOILER WARNING DIALOG ====
const SpoilerWarningOverlay = ({onDismiss, onBack}) => {
    const [dontShow, setDontShow] = useState(false);
    const handleDismiss = () => {
        if (dontShow) localStorage.setItem('f1hub_spoiler_standings_skip', 'true');
        onDismiss();
    };
    return (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'#000',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
            <div className="card" style={{maxWidth:340,width:'100%',textAlign:'center',padding:'28px 24px'}}>
                <img src="/static/antispoiler.webp?v=1" alt="" style={{width:64,height:64,borderRadius:16,objectFit:'cover',margin:'0 auto 12px',display:'block'}}/>
                <div style={{fontSize:18,fontWeight:900,marginBottom:8}}>Антиспойлер включён</div>
                <div style={{fontSize:14,color:'var(--f1-text-secondary)',marginBottom:20,lineHeight:1.5}}>
                    Таблица чемпионата содержит спойлеры последних гонок. Хотите всё равно посмотреть?
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    <button onClick={handleDismiss} style={{padding:'12px 0',borderRadius:10,border:'none',fontFamily:'inherit',fontWeight:700,fontSize:14,cursor:'pointer',background:'var(--f1-red)',color:'white'}}>
                        Всё равно показать
                    </button>
                    <button onClick={onBack || (()=>{})} style={{padding:'12px 0',borderRadius:10,border:'none',fontFamily:'inherit',fontWeight:700,fontSize:14,cursor:'pointer',background:'rgba(255,255,255,0.08)',color:'var(--f1-text-muted)'}}>
                        Назад
                    </button>
                </div>
                <label style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,marginTop:14,fontSize:12,color:'var(--f1-text-muted)',cursor:'pointer'}}>
                    <input type="checkbox" checked={dontShow} onChange={e=>setDontShow(e.target.checked)} style={{accentColor:'var(--f1-red)'}}/>
                    Не показывать это предупреждение
                </label>
            </div>
        </div>
    );
};

// ==== ADMIN BROADCAST PANEL ====
const AdminBroadcastPanel = ({userId}) => {
    const [broadcasts, setBroadcasts] = useState([]);
    const [form, setForm] = useState({race_round:'',session_type:'race',video_url:'',title:'',is_live:false});
    const [saving, setSaving] = useState(false);
    const [expandedRound, setExpandedRound] = useState(null);
    const [schedule, setSchedule] = useState([]);

    useEffect(() => {
        loadBroadcasts();
        api.get('/api/schedule').then(d => {
            if (d?.races) setSchedule(d.races.filter(r=>r.round));
        });
    }, []);

    const loadBroadcasts = () => api.get('/api/admin/broadcasts').then(d => { if(d?.broadcasts) setBroadcasts(d.broadcasts); });

    const handleSave = async () => {
        if (!form.race_round || !form.video_url) return;
        setSaving(true);
        await api.post('/api/admin/broadcast', {...form, race_round: parseInt(form.race_round)});
        setForm({race_round:'',session_type:'race',video_url:'',title:'',is_live:false});
        await loadBroadcasts();
        setSaving(false);
    };

    const handleEnd = async (id) => {
        await api.post(`/api/admin/broadcast/${id}/end`);
        await loadBroadcasts();
    };

    const handleDelete = async (id) => {
        if (!confirm('Удалить трансляцию?')) return;
        await fetch(`/api/admin/broadcast/${id}`, {method:'DELETE', headers:{'X-Telegram-Init-Data':window.Telegram?.WebApp?.initData||''}});
        await loadBroadcasts();
    };

    const sessionLabels = {race:'Гонка',qualifying:'Квалификация',sprint:'Спринт',sprint_qualifying:'Спринт-квали',review:'Обзор'};

    return (
        <div className="card" style={{marginBottom:16}}>
            <div style={{fontSize:11,color:'var(--f1-red)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:12}}>Управление трансляциями</div>

            {/* Add form */}
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16,padding:12,background:'rgba(255,255,255,0.04)',borderRadius:10}}>
                <select value={form.race_round} onChange={e=>setForm(f=>({...f,race_round:e.target.value}))} style={{padding:'8px 12px',borderRadius:8,border:'1px solid var(--f1-border)',background:'var(--f1-gray)',color:'var(--f1-text)',fontSize:13,fontFamily:'inherit'}}>
                    <option value="">Выберите гран-при...</option>
                    {schedule.map(r=><option key={r.round} value={r.round}>R{r.round} — {r.name}</option>)}
                </select>
                <div style={{display:'flex',gap:6}}>
                    {Object.entries(sessionLabels).map(([k,v])=>(
                        <button key={k} onClick={()=>setForm(f=>({...f,session_type:k}))} style={{flex:1,padding:'6px 0',borderRadius:6,border:'none',fontFamily:'inherit',fontSize:11,fontWeight:700,cursor:'pointer',background:form.session_type===k?'var(--f1-red)':'rgba(255,255,255,0.08)',color:form.session_type===k?'white':'var(--f1-text-muted)'}}>
                            {v}
                        </button>
                    ))}
                </div>
                <input value={form.video_url} onChange={e=>setForm(f=>({...f,video_url:e.target.value}))} placeholder="VK Video URL (vk.com/video...)" style={{padding:'8px 12px',borderRadius:8,border:'1px solid var(--f1-border)',background:'var(--f1-gray)',color:'var(--f1-text)',fontSize:13,fontFamily:'inherit'}}/>
                <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Название (необязательно)" style={{padding:'8px 12px',borderRadius:8,border:'1px solid var(--f1-border)',background:'var(--f1-gray)',color:'var(--f1-text)',fontSize:13,fontFamily:'inherit'}}/>
                <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--f1-text-secondary)'}}>
                    <input type="checkbox" checked={form.is_live} onChange={e=>setForm(f=>({...f,is_live:e.target.checked}))}/>
                    Сейчас в эфире (Live)
                </label>
                <button onClick={handleSave} disabled={saving||!form.race_round||!form.video_url} style={{padding:'10px 0',borderRadius:8,border:'none',fontFamily:'inherit',fontWeight:700,fontSize:13,cursor:'pointer',background:form.race_round&&form.video_url?'var(--f1-red)':'rgba(255,255,255,0.08)',color:form.race_round&&form.video_url?'white':'var(--f1-text-muted)'}}>
                    {saving ? 'Сохранение...' : 'Добавить / Обновить'}
                </button>
            </div>

            {/* List grouped by GP */}
            {(() => {
                if (broadcasts.length === 0) return <div style={{textAlign:'center',padding:12,color:'var(--f1-text-muted)',fontSize:13}}>Нет трансляций</div>;
                const grouped = {};
                broadcasts.forEach(b => {
                    if (!grouped[b.race_round]) grouped[b.race_round] = [];
                    grouped[b.race_round].push(b);
                });
                const rounds = Object.keys(grouped).map(Number).sort((a,b) => b - a);
                const sessionOrder = ['race','qualifying','sprint','sprint_qualifying','review'];
                return rounds.map(round => {
                    const items = grouped[round];
                    const raceName = schedule.find(r => r.round === round)?.name || ('\u0420\u0430\u0443\u043d\u0434 ' + round);
                    const hasLive = items.some(b => b.is_live);
                    const filledSessions = items.map(b => b.session_type);
                    return (
                        <div key={round} style={{marginBottom:10,background:'rgba(255,255,255,0.03)',borderRadius:10,border:'1px solid var(--f1-border)',overflow:'hidden'}}>
                            <div onClick={() => setExpandedRound(r => r === round ? null : round)} style={{padding:'10px 12px',cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>
                                {hasLive && <div style={{width:6,height:6,borderRadius:'50%',background:'var(--f1-red)',animation:'pulse 1.5s infinite',flexShrink:0}}/>}
                                <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:13,fontWeight:700}}>R{round} · {raceName}</div>
                                </div>
                                <div style={{display:'flex',gap:3,flexShrink:0}}>
                                    {sessionOrder.map(s => {
                                        const has = filledSessions.includes(s);
                                        const live = items.find(b => b.session_type === s && b.is_live);
                                        return <div key={s} style={{width:8,height:8,borderRadius:'50%',background:live?'var(--f1-red)':has?'#00C853':'rgba(255,255,255,0.12)'}} title={sessionLabels[s]}/>;
                                    })}
                                </div>
                                <span style={{fontSize:10,color:'var(--f1-text-muted)',flexShrink:0}}>{items.length}/{sessionOrder.length}</span>
                                <span style={{fontSize:11,color:'var(--f1-text-muted)',flexShrink:0}}>{expandedRound===round?'\u25b2':'\u25bc'}</span>
                            </div>
                            {expandedRound === round && (
                                <div style={{padding:'0 12px 10px',borderTop:'1px solid var(--f1-border)'}}>
                                    {sessionOrder.map(sType => {
                                        const b = items.find(x => x.session_type === sType);
                                        if (!b) return (
                                            <div key={sType} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0',opacity:0.4}}>
                                                <div style={{width:6,height:6,borderRadius:'50%',background:'rgba(255,255,255,0.15)'}}/>
                                                <span style={{fontSize:12,color:'var(--f1-text-muted)'}}>{sessionLabels[sType]}</span>
                                                <span style={{fontSize:10,color:'var(--f1-text-muted)',marginLeft:'auto'}}>{'\u2014'}</span>
                                            </div>
                                        );
                                        return (
                                            <div key={sType} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0'}}>
                                                <div style={{width:6,height:6,borderRadius:'50%',background:b.is_live?'var(--f1-red)':'#00C853',flexShrink:0}}/>
                                                <div style={{flex:1,minWidth:0}}>
                                                    <div style={{fontSize:12,fontWeight:600}}>{sessionLabels[sType]}{b.is_live ? ' \xb7 LIVE' : ''}</div>
                                                    <div style={{fontSize:10,color:'var(--f1-text-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.title || (b.video_url ? b.video_url.substring(0,40)+'...' : '')}</div>
                                                </div>
                                                {b.is_live && <button onClick={()=>handleEnd(b.id)} style={{padding:'3px 8px',borderRadius:5,border:'none',fontFamily:'inherit',fontSize:10,fontWeight:700,cursor:'pointer',background:'rgba(255,128,0,0.2)',color:'#FF8000'}}>{'\u0417\u0430\u0432\u0435\u0440\u0448\u0438\u0442\u044c'}</button>}
                                                <button onClick={()=>handleDelete(b.id)} style={{padding:'3px 6px',borderRadius:5,border:'none',fontFamily:'inherit',fontSize:10,cursor:'pointer',background:'rgba(255,0,0,0.1)',color:'var(--f1-red)'}}>{'\u2715'}</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                });
            })()}
        </div>
    );
};

// ==== RACE DETAIL PAGE ====
// ==== RACE PODIUM (app design) ====
const RacePodium = ({items}) => {
    if (!items || items.length < 3) return null;
    const METAL = ['#FFCB05','#C0C0C0','#CD7F32'];
    const Card = ({d, place, winner}) => {
        const portrait = d.card_photo_url || d.photo_url;
        const metal = METAL[place-1];
        return (
            <div style={{flex:winner?1.18:1,background:winner?'#1A1505':'var(--f1-card-solid)',borderRadius:20,
                         border:`1.5px solid ${metal}${winner?'CC':'55'}`,overflow:'hidden',
                         boxShadow:winner?`0 6px 22px ${metal}40`:`0 4px 12px ${metal}20`,alignSelf:'flex-end',minWidth:0}}>
                <div style={{width:'100%',height:winner?118:102,background:'#1A1A24',position:'relative'}}>
                    {portrait && <img src={hiResImg(portrait, 400)} alt="" loading="lazy" style={{width:'100%',height:'100%',objectFit:'cover',objectPosition:'top center'}} onError={e=>{e.target.style.display='none'}}/>}
                    <div style={{position:'absolute',top:0,left:10,fontSize:winner?40:32,fontWeight:800,letterSpacing:-1.5,color:'#FAFAFA',textShadow:'0 2px 8px rgba(0,0,0,0.75)'}}>{place}</div>
                </div>
                <div style={{padding:'9px 10px 11px'}}>
                    <div style={{fontSize:12,fontWeight:700,lineHeight:'14px'}}>{d.first_name || (d.name||'').split(' ')[0]}</div>
                    <div style={{fontSize:12,fontWeight:700,lineHeight:'14px'}}>{d.last_name || (d.name||'').split(' ').slice(1).join(' ') || d.code}</div>
                    <div style={{fontSize:10,fontWeight:700,color:d.team_color||'var(--f1-text-muted)',marginTop:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{(d.team||'').replace('F1 Team','').trim()}</div>
                    {d.points != null && d.points > 0 && <div style={{color:winner?'#FFCB05':'var(--f1-text)',fontWeight:800,fontSize:winner?16:14,marginTop:5}}>+{d.points}</div>}
                </div>
            </div>
        );
    };
    return (
        <div style={{display:'flex',gap:8,alignItems:'flex-end',margin:'2px 0 16px'}}>
            <Card d={items[1]} place={2}/>
            <Card d={items[0]} place={1} winner/>
            <Card d={items[2]} place={3}/>
        </div>
    );
};

// Погода на уикенд (Open-Meteo) — прямая подсказка для игры прогнозов:
// мокрая квала/гонка = хаос, значит стоит ставить осторожнее.
const weatherEmoji = (code) => {
    if (code == null) return '❓';
    if (code <= 1) return '☀️';
    if (code <= 3) return '⛅';
    if (code <= 48) return '🌫️';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '🌨️';
    if (code <= 82) return '🌧️';
    if (code >= 95) return '⛈️';
    return '⛅';
};
const RaceWeather = ({round, season}) => {
    const [wx, setWx] = useState(null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        if (round == null) return;
        let alive = true;
        api.get(`/api/weather/${round}?season=${season}`)
           .then(d => { if (alive) setWx(d || {}); })
           .catch(() => { if (alive) setFailed(true); });
        return () => { alive = false; };
    }, [round, season]);
    if (failed) return null;                       // погода не должна ломать карточку
    if (!wx) return <div className="skeleton" style={{height:120,borderRadius:16,marginBottom:16}}/>;
    const names = {fp1:'Практика 1',fp2:'Практика 2',fp3:'Практика 3',qualifying:'Квалификация',race:'Гонка',sprint:'Спринт',sprint_qualifying:'Спринт-квалификация'};
    const order = ['fp1','sprint_qualifying','fp2','fp3','sprint','qualifying','race'];
    const entries = order.filter(k => wx.sessions && wx.sessions[k]).map(k => [k, wx.sessions[k]]);
    if (!entries.length) return null;
    return (
        <div style={{marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',marginBottom:10,paddingLeft:2}}>
                <div style={{width:3,height:16,background:'var(--f1-red)',borderRadius:2,marginRight:10}}/>
                <div style={{fontSize:13,fontWeight:800,letterSpacing:2,textTransform:'uppercase'}}>Погода на уикенд</div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {entries.map(([key, s]) => {
                    if (!s || !s.available) return (
                        <div key={key} style={{display:'flex',alignItems:'center',padding:'12px 14px',borderRadius:14,background:'var(--f1-card-solid)',border:'1px solid var(--f1-border)',opacity:0.55}}>
                            <div style={{flex:1,fontSize:14,fontWeight:600}}>{names[key]||key}</div>
                            <div style={{fontSize:11,color:'var(--f1-text-muted)',textAlign:'right'}}>прогноз появится ближе к уикенду</div>
                        </div>
                    );
                    const wet = (key==='qualifying' || key==='race') && (s.rain_prob||0) >= 40;
                    return (
                        <div key={key} style={{padding:'12px 14px',borderRadius:14,background:wet?'rgba(90,130,255,0.10)':'var(--f1-card-solid)',border:'1px solid '+(wet?'rgba(100,140,255,0.5)':'var(--f1-border)')}}>
                            <div style={{display:'flex',alignItems:'center',gap:12}}>
                                <div style={{fontSize:26,lineHeight:'26px'}}>{weatherEmoji(s.code)}</div>
                                <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:14,fontWeight:700}}>{names[key]||key}</div>
                                    <div style={{fontSize:12,color:'var(--f1-text-secondary)',marginTop:2}}>
                                        {s.temp_c!=null?`${s.temp_c}° · `:''}дождь {s.rain_prob!=null?s.rain_prob:'—'}% · ветер {s.wind_kmh!=null?s.wind_kmh:'—'} км/ч
                                    </div>
                                </div>
                            </div>
                            {wet && <div style={{marginTop:8,fontSize:11,fontWeight:800,color:'#7C9BFF',letterSpacing:0.2}}>🌧️ Возможен дождь — непредсказуемая сессия!</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const RaceDetailPage = ({race, onBack, season, spoilerFree, allRaces, defaultTab}) => {
    const chartReady = useLib(ensureChart);
    const [revealed, setRevealed] = useState(false);
    const [qualiData, setQualiData] = useState(null);
    const [qualiSegment, setQualiSegment] = useState('q3');
    const [broadcastData, setBroadcastData] = useState(null);
    const [raceResults, setRaceResults] = useState(null);
    const [posChart, setPosChart] = useState(null);
    const posChartRef = useRef(null);
    const posChartInstance = useRef(null);
    const [raceTab, setRaceTab] = useState(defaultTab || 'race');
    const isPastRace = race && race.date && new Date(race.date) < new Date();
    useEffect(() => {
        setRevealed(false);
        setQualiData(null);
        setBroadcastData(null);
        setRaceResults(null);
        setPosChart(null);
        setRaceTab(defaultTab || 'race');
        // Fetch broadcasts for any race
        if (race?.round) {
            api.get(`/api/broadcasts?race_round=${race.round}&season=${season}`).then(d => {
                if (d?.broadcasts?.length > 0) setBroadcastData(d.broadcasts);
            });
        }
        if (race?.round && isPastRace) {
            api.get(`/api/race/${race.round}/qualifying?season=${season}`).then(d => {
                if (d?.results?.length > 0) setQualiData(d);
            });
            // Fetch position chart
            api.get(`/api/race/${race.round}/positions?season=${season}`).then(d => {
                if (d?.drivers?.length > 0) setPosChart(d);
            });
            // Fetch race results from API if not in hardcoded data
            if (!race.podium && !race.top_10) {
                api.get(`/api/race/${race.round}/results?season=${season}`).then(d => {
                    if (d?.results?.length > 0) setRaceResults(d.results);
                });
            }
        }
    }, [race?.round]);

    // Position chart rendering effect
    useEffect(() => {
        if (!chartReady) return;   // ждём ленивую библиотеку
        if (!posChart?.drivers?.length || !posChartRef.current || raceTab !== 'race') return;
        if (posChartInstance.current) posChartInstance.current.destroy();
        const ctx = posChartRef.current.getContext('2d');
        const datasets = posChart.drivers.map(d => ({
            label: d.code,
            data: d.laps.map((lap, i) => ({x: lap, y: d.positions[i]})),
            borderColor: d.team_color || '#888',
            backgroundColor: d.team_color || '#888',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.1,
            fill: false,
        }));
        const maxDrivers = posChart.drivers.length;
        posChartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {datasets},
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: {duration: 800, easing: 'easeOutQuart'},
                scales: {
                    x: {
                        type:'linear',
                        title:{display:true,text:'Круг',color:'#666',font:{size:10,weight:600}},
                        grid:{color:'rgba(255,255,255,0.04)',drawBorder:false},
                        ticks:{color:'#666',font:{size:9},maxTicksLimit:15}
                    },
                    y: {
                        reverse:true,min:1,max:maxDrivers,
                        title:{display:false},
                        grid:{color:'rgba(255,255,255,0.04)',drawBorder:false},
                        ticks:{color:'#666',font:{size:9},stepSize:1,callback:(v)=>'P'+v}
                    }
                },
                plugins: {
                    legend:{display:false},
                    tooltip:{
                        backgroundColor:'rgba(20,20,20,0.95)',
                        titleFont:{size:11,weight:700},
                        bodyFont:{size:11},
                        padding:10,
                        cornerRadius:8,
                        displayColors:true,
                        boxWidth:8,
                        boxHeight:3,
                        callbacks:{
                            title: (items) => `Круг ${items[0].parsed.x}`,
                            label: (item) => ` ${item.dataset.label}  P${item.parsed.y}`
                        }
                    }
                },
                interaction:{mode:'nearest',intersect:false,axis:'x'}
            }
        });
        return () => { if (posChartInstance.current) posChartInstance.current.destroy(); };
    }, [posChart, raceTab, chartReady]);

    if (!race) return <div className="page-container fade-in" style={{padding:16}}><F1Loader text="Загрузка гран-при..."/></div>;

    // Determine if this race should be spoiler-hidden (ONLY the last completed GP)
    const now = new Date();
    const lastCompletedRound = allRaces ? [...allRaces].filter(r => r.date && new Date(r.date) < now).pop()?.round : null;
    const isLastCompleted = lastCompletedRound && race.round === lastCompletedRound;
    const isSpoilerHidden = spoilerFree && !revealed && isPastRace;
    console.log("ANTISPOILER DEBUG:", {spoilerFree, revealed, isPastRace, isSpoilerHidden: spoilerFree && !revealed && isPastRace, raceDate: race?.date, raceName: race?.name});

    const flag = circuitFlags[race.circuit_id] || '🏁';
    const dateStr = new Date(race.date).toLocaleDateString('ru-RU', {day:'numeric', month:'long', year:'numeric'});
    const podiumColors = ['#FFD700','#C0C0C0','#CD7F32'];
    const podiumHeights = [130, 100, 85];
    const podiumSizes = [64, 48, 48];
    const tyreColors = {SOFT:'#FF3333',MEDIUM:'#FFD700',HARD:'#CCCCCC',INTERMEDIATE:'#39B54A',WET:'#0067FF',UNKNOWN:'#888'};

    return (
        <div className="page-container fade-in" style={{padding:'12px 16px'}}>
            <button onClick={onBack} style={{background:'none',border:'none',color:'var(--f1-text-muted)',fontSize:13,fontFamily:'inherit',cursor:'pointer',marginBottom:12,display:'flex',alignItems:'center',gap:4}}>← Расписание</button>

            {/* Race header — app design: city italic + circuit outline */}
            <div style={{marginBottom:16,background:'var(--f1-card-solid)',border:'1px solid rgba(225,6,0,0.18)',borderRadius:20,boxShadow:'0 6px 14px rgba(225,6,0,0.12)',display:'flex',alignItems:'center',padding:'18px 20px',minHeight:120}}>
                <div style={{flex:1,minWidth:0}}>
                    <div style={{marginBottom:8}}>{race.country_code ? <FlagImg code={race.country_code} size={26}/> : <span style={{fontSize:24}}>{flag}</span>}</div>
                    <div style={{fontSize:26,lineHeight:'28px',fontWeight:800,letterSpacing:-1,fontStyle:'italic',textTransform:'uppercase',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{(race.locality || (race.name||'').replace(/^Гран[- ]при\s+/i,'')).toUpperCase()}</div>
                    <div style={{fontSize:12,color:'var(--f1-text-muted)',marginTop:6}}>{dateStr}{race.laps ? ` · ${race.laps} кругов` : ''} · Раунд {race.round}</div>
                    {race.sprint && <div style={{marginTop:8,display:'inline-block',background:'rgba(255,128,0,0.18)',color:'#FF8000',fontSize:10,fontWeight:800,padding:'3px 10px',borderRadius:6,letterSpacing:1}}>СПРИНТ-УИКЕНД</div>}
                </div>
                {race.circuit_outline && <img src={race.circuit_outline} alt="" loading="lazy" style={{width:118,height:96,objectFit:'contain',flexShrink:0,filter:'drop-shadow(0 0 6px rgba(225,6,0,0.85))'}} onError={e=>{e.target.style.display='none'}}/>}
            </div>

            {isSpoilerHidden && isPastRace && (
                <div className="card" style={{padding:'28px 20px',textAlign:'center',marginBottom:16}}>
                    <img src="/static/antispoiler.webp?v=1" alt="" style={{width:56,height:56,borderRadius:16,objectFit:'cover',margin:'0 auto 10px',display:'block'}}/>
                    <div style={{fontSize:16,fontWeight:900,marginBottom:6}}>Антиспойлер включён</div>
                    <div style={{fontSize:13,color:'var(--f1-text-secondary)',marginBottom:16,lineHeight:1.5}}>Результаты этого гран-при скрыты.<br/>Вкладка «Запись» доступна без спойлеров.</div>
                    <div style={{display:'flex',gap:8,justifyContent:'center'}}>
                        <button onClick={()=>setRevealed(true)} style={{padding:'10px 24px',borderRadius:10,border:'none',fontFamily:'inherit',fontWeight:700,fontSize:14,cursor:'pointer',background:'var(--f1-red)',color:'white'}}>Показать результаты</button>
                        {broadcastData && <button onClick={()=>setRaceTab('broadcast')} style={{padding:'10px 24px',borderRadius:10,border:'none',fontFamily:'inherit',fontWeight:700,fontSize:14,cursor:'pointer',background:'rgba(255,255,255,0.08)',color:'var(--f1-text-secondary)'}}>Запись</button>}
                    </div>
                </div>
            )}

            {/* Race/Qualifying tabs for past races */}
            {isPastRace && !isSpoilerHidden && (race.podium || race.top_10 || raceResults || qualiData) && (
                <div style={{display:'flex',gap:6,marginBottom:16}}>
                    {[...(broadcastData?['race','qualifying','broadcast']:['race','qualifying'])].map(t=>(
                        <button key={t} onClick={()=>{if(t!=='broadcast'&&isSpoilerHidden)return;setRaceTab(t);}} style={{flex:1,padding:'10px 0',borderRadius:999,border:'none',fontFamily:'inherit',fontWeight:800,fontSize:12,letterSpacing:0.5,cursor:'pointer',transition:'all 0.2s',background:raceTab===t?'var(--f1-red)':'var(--f1-card-solid)',color:raceTab===t?'#fff':'var(--f1-text-muted)',boxShadow:raceTab===t?'0 3px 10px rgba(225,6,0,0.35)':'none'}}>
                            {t==='race'?'🏁 Гонка':t==='qualifying'?'⏱️ Квалификация':'📺 Запись'}
                        </button>
                    ))}
                </div>
            )}

            {/* Sessions schedule (for future races) */}
            {raceTab === 'broadcast' && broadcastData && (
                <BroadcastTab broadcasts={broadcastData} raceName={race.name} hasSprint={!!race.sprint}/>
            )}


            {!isPastRace && broadcastData && !isSpoilerHidden && (
                <div style={{display:'flex',gap:6,marginBottom:16}}>
                    <button onClick={()=>setRaceTab('broadcast')} style={{flex:1,padding:'10px 0',borderRadius:999,border:'none',fontFamily:'inherit',fontWeight:800,fontSize:12,letterSpacing:0.5,cursor:'pointer',transition:'all 0.2s',background:raceTab==='broadcast'?'var(--f1-red)':'var(--f1-card-solid)',color:raceTab==='broadcast'?'#fff':'var(--f1-text-muted)',boxShadow:raceTab==='broadcast'?'0 3px 10px rgba(225,6,0,0.35)':'none'}}>
                        📺 Запись
                    </button>
                </div>
            )}

            {race.sessions && !race.podium && !raceResults && (() => {
                const sessionNames = {fp1:'Практика 1',fp2:'Практика 2',fp3:'Практика 3',qualifying:'Квалификация',race:'Гонка',sprint:'Спринт',sprint_qualifying:'Спринт-квалификация'};
                const sessionShort = {fp1:'FP1',fp2:'FP2',fp3:'FP3',qualifying:'QUALI',race:'RACE',sprint:'SPRINT',sprint_qualifying:'SQ'};
                const sessionOrder = ['fp1','sprint_qualifying','fp2','fp3','sprint','qualifying','race'];
                const entries = typeof race.sessions === 'object' && !Array.isArray(race.sessions) ? Object.entries(race.sessions).sort((a,b)=>sessionOrder.indexOf(a[0])-sessionOrder.indexOf(b[0])) : [];
                if (!entries.length) return null;
                const days = {};
                entries.forEach(([key,val]) => {
                    if (!val.date) return;
                    const dt = new Date(val.date+'T'+(val.time||'00:00:00').replace('Z',''));
                    const msk = new Date(dt.getTime() + 3*3600000);
                    const dayKey = msk.toLocaleDateString('ru-RU',{weekday:'long',day:'numeric',month:'long'});
                    (days[dayKey] = days[dayKey] || []).push({key, time: msk.toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'})});
                });
                return (
                    <div style={{marginBottom:16}}>
                        {Object.entries(days).map(([day, list]) => (
                            <div key={day} style={{marginBottom:18}}>
                                <div style={{display:'flex',alignItems:'center',marginBottom:10,paddingLeft:2}}>
                                    <div style={{width:3,height:16,background:'var(--f1-red)',borderRadius:2,marginRight:10}}/>
                                    <div style={{fontSize:13,fontWeight:800,letterSpacing:2,textTransform:'uppercase'}}>{day}</div>
                                </div>
                                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                                    {list.map(({key,time}) => {
                                        const isRace = key==='race';
                                        return (
                                            <div key={key} style={{background:'var(--f1-card-solid)',borderRadius:16,border:'1px solid '+(isRace?'rgba(225,6,0,0.4)':'var(--f1-border)'),boxShadow:isRace?'0 4px 14px rgba(225,6,0,0.18)':'none',display:'flex',alignItems:'center',padding:'16px'}}>
                                                <div style={{color:'var(--f1-red)',fontSize:26,lineHeight:'28px',fontWeight:800,letterSpacing:-0.5,minWidth:76}}>{time}</div>
                                                <div style={{flex:1,marginLeft:10}}>
                                                    <div style={{fontSize:15,fontWeight:700}}>{sessionNames[key]||key}</div>
                                                    <span style={{display:'inline-block',marginTop:6,padding:'3px 8px',borderRadius:6,background:'rgba(255,255,255,0.06)',border:'1px solid var(--f1-border)',fontSize:10,fontWeight:800,letterSpacing:1.5,color:'var(--f1-text-secondary)'}}>{sessionShort[key]||key.toUpperCase()}</span>
                                                </div>
                                                <div style={{color:'var(--f1-text-muted)',fontSize:16}}>{'\u203a'}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            })()}

            {/* Прогноз погоды на сессии — только для предстоящих гонок */}
            {race.sessions && !race.podium && !raceResults && race.round != null && (
                <RaceWeather round={race.round} season={season}/>
            )}

            {/* Трасса — всё, что отдаёт API: схема как арт, факты, история */}
            <TrackSection race={race}/>

            {/* Spoiler reveal button */}
            {isSpoilerHidden && (
                <div className="card" onClick={()=>setRevealed(true)} style={{padding:'16px 20px',marginBottom:16,cursor:'pointer',textAlign:'center',background:'rgba(225,6,0,0.08)',border:'1px solid rgba(225,6,0,0.2)'}}>
                    <div style={{fontSize:24,marginBottom:6}}>🔒</div>
                    <div style={{fontSize:14,fontWeight:700,color:'var(--f1-red)'}}>Показать результаты</div>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',marginTop:4}}>Результаты скрыты режимом антиспойлера</div>
                </div>
            )}

            {raceTab === 'race' && !isSpoilerHidden && (<>
            {/* Podium (app design) */}
            {race.podium?.length >= 3 && <RacePodium items={race.podium.slice(0,3)}/>}

            {/* Full top-10 results */}
            {race.top_10?.length > 0 && (
                <div className="card" style={{padding:'16px 12px', marginBottom:16}}>
                    <div style={{filter:isSpoilerHidden?'blur(15px)':'none',transition:'filter 0.3s',pointerEvents:isSpoilerHidden?'none':'auto'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:10}}>📋 Топ-10 результаты</div>
                    {race.top_10.map((d, i) => (
                        <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:i<race.top_10.length-1?'1px solid var(--f1-border)':'none'}}>
                            <PosBadge pos={d.position}/>
                            <div style={{background:d.team_color,width:3,height:28,borderRadius:2}}/>
                            {d.photo_url && <DriverPhoto url={d.photo_url} size={28}/>}
                            <div style={{flex:1,minWidth:0}}>
                                <span style={{fontWeight:700,fontSize:13}}>{d.code}</span>
                                <span style={{fontSize:11,color:'var(--f1-text-muted)',marginLeft:6}}>{d.first_name} {d.last_name}</span>
                            </div>
                            <span style={{fontSize:11,color:d.team_color,fontWeight:600}}>{d.team?.split(' ')[0]}</span>
                        </div>
                    ))}
                    </div>{/* close blur wrapper */}
                </div>
            )}

            {/* Sprint top-10 results */}
            {race.sprint_top_10?.length > 0 && (
                <div className="card" style={{padding:'16px 12px', marginBottom:16}}>
                    <div style={{filter:isSpoilerHidden?'blur(15px)':'none',transition:'filter 0.3s',pointerEvents:isSpoilerHidden?'none':'auto'}}>
                    <div style={{fontSize:11,color:'#FF8000',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:10}}>Спринт · Топ-10</div>
                    {race.sprint_top_10.map((d, i) => (
                        <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:i<race.sprint_top_10.length-1?'1px solid var(--f1-border)':'none'}}>
                            <PosBadge pos={d.position}/>
                            <div style={{background:d.team_color,width:3,height:28,borderRadius:2}}/>
                            {d.photo_url && <DriverPhoto url={d.photo_url} size={28}/>}
                            <div style={{flex:1,minWidth:0}}>
                                <span style={{fontWeight:700,fontSize:13}}>{d.code}</span>
                                <span style={{fontSize:11,color:'var(--f1-text-muted)',marginLeft:6}}>{d.first_name} {d.last_name}</span>
                            </div>
                            <span style={{fontSize:11,color:d.team_color,fontWeight:600}}>{d.team?.split(' ')[0]}</span>
                        </div>
                    ))}
                    </div>{/* close blur wrapper */}
                </div>
            )}

            </>)}

            {/* API-loaded race results (2026+) */}
            {raceTab === 'race' && !race.podium && raceResults && !isSpoilerHidden && (
                <div>
                    <RacePodium items={raceResults.slice(0,3)}/>
                    <div className="card" style={{padding:'16px 12px',marginBottom:16}}>
                        <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:10}}>&#x1F4CB; Результаты гонки</div>
                        {raceResults.map((d,i) => (
                            <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:i<raceResults.length-1?'1px solid var(--f1-border)':'none'}}>
                                <div style={{width:28,fontWeight:900,fontSize:14,color:i<3?['#FFD700','#C0C0C0','#CD7F32'][i]:'var(--f1-text-muted)',textAlign:'center'}}>{d.position}</div>
                                <div style={{background:d.team_color,width:3,height:28,borderRadius:2}}/>
                                {d.photo_url && <img src={d.photo_url} alt="" style={{width:28,height:28,borderRadius:'50%',objectFit:'cover',background:'var(--f1-gray)'}} onError={e=>{e.target.style.display='none'}} loading="lazy"/>}
                                <div style={{flex:1,minWidth:0}}>
                                    <span style={{fontWeight:700,fontSize:13}}>{d.code}</span>
                                    <span style={{fontSize:11,color:'var(--f1-text-muted)',marginLeft:6}}>{d.first_name} {d.last_name}</span>
                                </div>
                                <div style={{textAlign:'right'}}>
                                    {d.is_dnf ? (
                                        <div><div style={{fontSize:11,color:'var(--f1-red)',fontWeight:700}}>{d.status==='Did not start'?'DNS':'DNF'}</div>{d.status&&d.status!=='Retired'&&d.status!=='Did not start'&&<div style={{fontSize:9,color:'var(--f1-text-muted)'}}>{d.status}</div>}</div>
                                    ) : (<>
                                        {d.time ? <div style={{fontSize:11,fontWeight:600,fontVariantNumeric:'tabular-nums'}}>{d.time}</div> : d.status && d.status!=='Finished' ? <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:600}}>{d.status}</div> : null}
                                        {d.points > 0 && <div style={{fontSize:10,color:'var(--f1-text-muted)'}}>{d.points} очк.</div>}
                                    </>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}



            {/* Qualifying Results */}
            {raceTab === 'qualifying' && !isSpoilerHidden && qualiData?.results?.length > 0 && (
                <div className="card" style={{padding:'16px 12px', marginBottom:16}}>
                    <div style={{filter:isSpoilerHidden?'blur(15px)':'none',transition:'filter 0.3s',pointerEvents:isSpoilerHidden?'none':'auto'}}>
                    <div style={{fontSize:11,color:'var(--f1-text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:12}}>КВАЛИФИКАЦИЯ · {qualiData.race_name || ''}</div>
                    {/* Q1/Q2/Q3 segment cards */}
                    <div style={{display:'flex',gap:8,marginBottom:16}}>
                        {['q1','q2','q3'].map(q=>(
                            <div key={q} onClick={()=>setQualiSegment(q)} style={{flex:1,textAlign:'center',padding:'10px 0',background:qualiSegment===q?'#e10600':'rgba(255,255,255,0.08)',borderRadius:10,fontWeight:700,fontSize:14,cursor:'pointer',transition:'all 0.2s'}}>
                                {q.toUpperCase()}
                                <div style={{fontSize:10,opacity:0.5,marginTop:2}}>{q==='q1'?'20 пилотов':q==='q2'?'15 пилотов':'10 пилотов'}</div>
                            </div>
                        ))}
                    </div>
                    {(() => {
                        const seg = qualiSegment;
                        const filtered = qualiData.results.filter(r => r[seg]).sort((a,b) => {
                            const tA = a[seg] || '99:99.999', tB = b[seg] || '99:99.999';
                            return tA.localeCompare(tB);
                        });
                        if (!filtered.length) return <div style={{textAlign:'center',padding:20,color:'var(--f1-text-muted)',fontSize:12}}>Нет данных {seg.toUpperCase()}</div>;
                        const bestTime = filtered[0]?.[seg] || '';
                        const parseTime = (t) => { if(!t) return null; const p = t.split(':'); return p.length===2 ? parseFloat(p[0])*60+parseFloat(p[1]) : parseFloat(t); };
                        const bestSec = parseTime(bestTime);
                        return filtered.map((r, i) => {
                            const time = r[seg];
                            const sec = parseTime(time);
                            const delta = bestSec && sec && i > 0 ? (sec - bestSec).toFixed(3) : null;
                            const isElim = (seg==='q1' && i >= 15) || (seg==='q2' && i >= 10);
                            return (
                                <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',marginBottom:4,background:isElim?'rgba(255,0,0,0.08)':'rgba(255,255,255,0.04)',borderRadius:10,borderLeft:`3px solid ${r.team_color||'#888'}`}}>
                                    <div style={{width:24,fontWeight:800,fontSize:16,textAlign:'center',color:i<3?['#e10600','#aaa','#cd7f32'][i]:'rgba(255,255,255,0.5)'}}>{i+1}</div>
                                    {r.photo_url && <img src={r.photo_url} alt="" style={{width:32,height:32,borderRadius:'50%',objectFit:'cover'}} onError={e=>{e.target.style.display='none'}}/>}
                                    <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontWeight:700,fontSize:14}}>{r.name||r.code}</div>
                                        <div style={{fontSize:11,color:'rgba(255,255,255,0.4)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.team}</div>
                                    </div>
                                    <div style={{textAlign:'right'}}>
                                        <div style={{fontWeight:700,fontSize:15,fontFamily:'monospace',color:i===0?'#a855f7':'white'}}>{time||'—'}</div>
                                        {delta && <div style={{fontSize:11,color:'rgba(255,255,255,0.4)',fontFamily:'monospace'}}>+{delta}</div>}
                                    </div>
                                    {isElim && <div style={{fontSize:9,color:'#ff4444',fontWeight:700,width:24,textAlign:'center'}}>OUT</div>}
                                </div>
                            );
                        });
                    })()}
                    </div>{/* close blur wrapper */}
                </div>
            )}

            {raceTab === 'qualifying' && !isSpoilerHidden && (!qualiData || !qualiData.results?.length) && (
                <div className="card" style={{padding:'30px 20px',marginBottom:16,textAlign:'center'}}>
                    <div style={{fontSize:36,marginBottom:8}}>⏱️</div>
                    <div style={{fontSize:14,fontWeight:600,color:'var(--f1-text-muted)'}}>Нет данных квалификации</div>
                </div>
            )}

            {raceTab === 'race' && !isSpoilerHidden && (<>
            <RacePitstops round={race.round} season={season}/>
            </>)}
        </div>
    );
};

// ==== MINI-GAMES ====
// Игры живут в src/games.jsx — один код для мини-аппа и сайта f1-hub.ru/games.
// build.js подставляет файл вместо строки ниже (правка игры — сразу везде).
// ==== MINI-GAMES ====
// Общий код игр: мини-апп (src/app.jsx через «// @include games.jsx») и сайт
// (src/site_games.jsx → static/site/games.min.js). От хозяина нужны: React-хуки,
// api, _f1media, _getAuthHeaders. Сайт дополнительно кладёт window.__gmHost:
//   onResult(body) → false — не сохранять (сайт без входа), afterSave(body),
//   needLogin() — онлайн-дурак только после входа, loginUrl, inviteLink(code).
// В мини-аппе __gmHost нет — всё работает как раньше.
const gmHost = () => window.__gmHost || {};

// Клавиатура на компьютере: обработчик всегда видит свежие функции компонента.
// Вернул true — клавиша «съедена» (пробел не прокручивает страницу).
const useGmKeys = (fn) => {
    const ref = useRef(fn);
    ref.current = fn;
    useEffect(() => {
        const on = (e) => {
            const t = e.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
            if (ref.current(e) === true) e.preventDefault();
        };
        window.addEventListener('keydown', on);
        return () => window.removeEventListener('keydown', on);
    }, []);
};
const gmKeyGo = (e) => e.code === 'Space' || e.key === ' ' || e.key === 'Enter';
// Три игры под телефон: пит-стоп, дурак на картах F1, реакция на стартовых
// огнях. Квиз убран (решение Егора 24.09.2026: вопросы повторялись).

// Вибро-отклик: в Telegram — его HapticFeedback, в RuStore-обёртке — vibrate.
const gHaptic = (kind = 'light') => {
    try {
        const h = window.Telegram?.WebApp?.HapticFeedback;
        if (h) {
            if (kind === 'success' || kind === 'error' || kind === 'warning') h.notificationOccurred(kind);
            else h.impactOccurred(kind);
        } else if (navigator.vibrate) {
            navigator.vibrate(kind === 'heavy' || kind === 'error' ? 30 : 12);
        }
    } catch (e) {}
};


// Отправка результата игры с повтором. Раньше при сбое (502 во время
// перезапуска сервера) результат молча терялся — так пропало 100 мс Даниила.
// Теперь: 3 попытки, а если связи нет — очередь в памяти телефона, отправим
// при следующем заходе в игры.
const GM_PENDING = 'gm_pending_results';
const gmPostResult = async (body) => {
    const host = gmHost();
    if (host.onResult && host.onResult(body) === false) return null;   // сайт без входа: играем, но не сохраняем
    for (let i = 0; i < 3; i++) {
        const r = await api.post('/api/games/result', body);
        if (r) { if (host.afterSave) host.afterSave(body, r); return r; }
        await new Promise(z => setTimeout(z, 1200 * (i + 1)));
    }
    try { const q = JSON.parse(localStorage.getItem(GM_PENDING) || '[]'); q.push(body); localStorage.setItem(GM_PENDING, JSON.stringify(q.slice(-20))); } catch (e) {}
    return null;
};
const gmFlushPending = async () => {
    let q = [];
    try { q = JSON.parse(localStorage.getItem(GM_PENDING) || '[]'); } catch (e) {}
    if (!q.length) return;
    const left = [];
    for (const body of q) { if (!(await api.post('/api/games/result', body))) left.push(body); }
    try { localStorage.setItem(GM_PENDING, JSON.stringify(left)); } catch (e) {}
};

const GameTop = ({title, onBack, right}) => (
    <div className="gm-top">
        <div className="gm-back" onClick={e => { e.stopPropagation(); onBack(); }}>← Назад</div>
        <div className="gm-title">{title}</div>
        <div className="gm-top-r">{right || null}</div>
    </div>
);

// Рекорды игры из /api/games/leaderboard (для пит-стопа и реакции — меньше = лучше)
const GameBoard = ({type, fmt, refreshKey}) => {
    const [d, setD] = useState(null);
    useEffect(() => { api.get('/api/games/leaderboard?game_type=' + type).then(setD); }, [type, refreshKey]);
    const rows = d?.leaderboard || [];
    if (!rows.length) return null;
    return (
        <div className="gm-board">
            <div className="gm-board-h">Лучшие в F1 Hub</div>
            {rows.slice(0, 5).map(r => (
                <div className="gm-board-r" key={r.user_id}>
                    <span className={'gm-board-p' + (r.rank === 1 ? ' gold' : '')}>{String(r.rank).padStart(2, '0')}</span>
                    <span className="gm-board-n">{r.first_name || 'Игрок'}</span>
                    <span className="gm-board-s">{fmt(r.best_score)}</span>
                </div>
            ))}
            {d.my_best != null && d.my_rank > 5 && (
                <div className="gm-board-r mine">
                    <span className="gm-board-p">{String(d.my_rank).padStart(2, '0')}</span>
                    <span className="gm-board-n">Ты</span>
                    <span className="gm-board-s">{fmt(d.my_best)}</span>
                </div>
            )}
            {d.my_best != null
                ? <div className="gm-board-me">Твоё место: <b>{d.my_rank || '—'}</b> из {d.players} · рекорд <b>{fmt(d.my_best)}</b></div>
                : <div className="gm-board-me">Сыграй — и попадёшь в таблицу ({d.players} {plural(d.players, 'игрок', 'игрока', 'игроков')})</div>}
        </div>
    );
};

// Секундомер на requestAnimationFrame — ровный, без setInterval
const LiveTimer = ({t0, offset = 0}) => {
    const [ms, setMs] = useState(0);
    const off = useRef(offset); off.current = offset;
    useEffect(() => {
        let raf;
        const tick = () => { setMs(performance.now() - t0 + off.current); raf = requestAnimationFrame(tick); };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [t0]);
    return <>{(ms / 1000).toFixed(2)}</>;
};

// ─────────────────────────── ПИТ-СТОП ───────────────────────────
const PIT_TEAMS = [
    {name: 'Ferrari', c: '#E8002D', c2: '#FFE200'},
    {name: 'McLaren', c: '#FF8000', c2: '#101010'},
    {name: 'Mercedes', c: '#1d1f22', c2: '#27F4D2'},
    {name: 'Red Bull', c: '#1B2A55', c2: '#E4002B'},
    {name: 'Aston Martin', c: '#0B5E45', c2: '#CEDC00'},
    {name: 'Williams', c: '#0A2D6E', c2: '#64C4FF'},
    {name: 'Alpine', c: '#0B1F3F', c2: '#FF87BC'},
];
const PIT_COMPOUNDS = [
    {name: 'SOFT', c: '#FF2A2A'}, {name: 'MEDIUM', c: '#FFD21F'}, {name: 'HARD', c: '#F2F2F2'},
];
const PIT_STEPS = ['Открути', 'Смени', 'Закрути', 'Готово'];

const PitCar = ({c, c2}) => { const gid = 'pb' + String(c).replace(/[^a-z0-9]/gi, ''); return (
    <svg className="pit-car-svg" viewBox="0 0 120 280" aria-hidden="true">
        <defs>
            <linearGradient id={gid} x1="0" x2="1">
                <stop offset="0" stopColor={c} stopOpacity=".78"/>
                <stop offset=".5" stopColor={c}/>
                <stop offset="1" stopColor={c} stopOpacity=".78"/>
            </linearGradient>
        </defs>
        {/* переднее крыло */}
        <path d="M8 22 H112 L108 34 H12 Z" fill="#111"/>
        <path d="M12 26 H108" stroke={c2} strokeWidth="3"/>
        {/* нос */}
        <path d="M52 12 Q60 4 68 12 L72 70 H48 Z" fill={`url(#${gid})`}/>
        {/* подвеска */}
        <path d="M30 62 L52 70 M90 62 L68 70 M30 216 L46 206 M90 216 L74 206" stroke="#2a2a2a" strokeWidth="3"/>
        {/* монокок и понтоны */}
        <path d="M46 68 H74 L78 104 Q100 112 100 138 L96 196 Q86 222 60 226 Q34 222 24 196 L20 138 Q20 112 42 104 Z" fill={`url(#${gid})`}/>
        <path d="M60 70 V226" stroke={c2} strokeWidth="3" opacity=".9"/>
        {/* кокпит + гало */}
        <ellipse cx="60" cy="122" rx="11" ry="18" fill="#07070a"/>
        <circle cx="60" cy="118" r="6" fill={c2}/>
        <path d="M47 108 Q60 92 73 108" stroke="#1a1a1a" strokeWidth="4" fill="none"/>
        {/* двигатель и заднее крыло */}
        <rect x="52" y="150" width="16" height="48" rx="6" fill="#0d0d0d" opacity=".55"/>
        <path d="M18 236 H102 V256 H18 Z" fill="#111"/>
        <path d="M18 240 H102" stroke={c2} strokeWidth="4"/>
        <rect x="56" y="226" width="8" height="12" fill="#111"/>
    </svg>
); };

// Пит-стоп v3 (правка Егора 24.09: «нереалистично»). Как в настоящем F1:
//  1) заезд — жмёшь «Тормоз», чтобы встать ровно на отметку (промах =
//     механики переставляют машину, плюс время);
//  2) колёса — четыре бригады работают параллельно: на каждом колесе
//     «отвернуть» → шину меняют → «завернуть»;
//  3) выпуск — если по быстрой полосе едет соперник, горит красный;
//     выпустишь на красный — штраф 5 с за небезопасный выпуск.
// Время считается с момента остановки машины и до выпуска, как на ТВ-графике.
const PIT_WHEELS = [
    {id: 'fl', short: 'ПЛ', label: 'Переднее левое'},
    {id: 'fr', short: 'ПП', label: 'Переднее правое'},
    {id: 'rl', short: 'ЗЛ', label: 'Заднее левое'},
    {id: 'rr', short: 'ЗП', label: 'Заднее правое'},
];
const PIT_SWAP_MS = 480;          // сколько бригада снимает старую шину и ставит новую
const PIT_UNSAFE = 5000;          // штраф за небезопасный выпуск

const PitStopGame = ({onBack}) => {
    const [teams, setTeams] = useState([]);
    const [phase, setPhase] = useState('intro');       // intro | approach | wheels | release | done
    const [team, setTeam] = useState(null);
    const [tyre, setTyre] = useState(PIT_COMPOUNDS[0]);
    const [wheels, setWheels] = useState({});
    const [tStop, setTStop] = useState(0);
    const [res, setRes] = useState(null);
    const [msg, setMsg] = useState(null);
    const [light, setLight] = useState('red');
    const [traffic, setTraffic] = useState(false);
    const [canLeave, setCanLeave] = useState(false);   // защита от «проскочившего» тапа на экране итога
    const [boardKey, setBoardKey] = useState(0);
    const [ach, setAch] = useState(false);
    const stageRef = useRef(null);
    const carRef = useRef(null);
    const st = useRef({});
    const timers = useRef([]);
    const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    useEffect(() => () => { timers.current.forEach(clearTimeout); cancelAnimationFrame(st.current.raf); }, []);

    useEffect(() => {
        api.get('/api/standings/constructors?season=2026').then(t => {
            const l = (t?.standings || t || []).filter(x => x.car_url);
            setTeams(l);
            if (l.length) setTeam(l[gmRand(l.length)]);
        });
    }, []);

    const say = (text, bad) => setMsg({text, bad, k: Date.now()});

    // ─ 1. заезд: машина едет к отметке, игрок тормозит ─
    const start = () => {
        timers.current.forEach(clearTimeout); timers.current = [];
        if (teams.length) setTeam(teams[gmRand(teams.length)]);
        setTyre(PIT_COMPOUNDS[gmRand(PIT_COMPOUNDS.length)]);
        setWheels(Object.fromEntries(PIT_WHEELS.map(w => [w.id, 0])));
        setRes(null); setMsg(null); setLight('red'); setTraffic(false); setCanLeave(false); setAch(false);
        st.current = {pen: 0, stopErr: 0, braking: false};
        setPhase('approach');
        later(() => {
            const W = stageRef.current?.clientWidth || 360;
            const carW = carRef.current?.clientWidth || W * 0.8;
            const target = (W - carW) / 2;               // отметка: машина ровно в боксе
            const s = st.current;
            s.W = W; s.carW = carW; s.target = target; s.x = -carW - 20; s.v = W * 1.05; s.t = performance.now();
            const tick = (now) => {
                const dt = Math.min(0.05, (now - s.t) / 1000); s.t = now;
                if (s.braking) s.v = Math.max(0, s.v - s.decel * dt);
                s.x += s.v * dt;
                // не нажал вовремя — механик ловит машину сам, с большим перелётом
                if (!s.braking && s.x > s.target + carW * 0.22) { s.braking = true; s.decel = s.v * s.v / (2 * 6); }
                if (carRef.current) carRef.current.style.transform = 'translateX(' + s.x + 'px)';
                if (s.v <= 0) { stopped(); return; }
                s.raf = requestAnimationFrame(tick);
            };
            s.raf = requestAnimationFrame(tick);
        }, 60);
    };
    const brake = () => {
        const s = st.current;
        if (phase !== 'approach' || s.braking) return;
        s.braking = true;
        s.decel = s.v * s.v / (2 * (s.carW * 0.09));    // тормозной путь ~ полметра
        gHaptic('medium');
    };
    const stopped = () => {
        const s = st.current;
        const meters = (s.x - s.target) / s.carW * 5.6;   // длина болида ~5.6 м
        s.stopErr = meters;
        const miss = Math.abs(meters);
        const pen = miss <= 0.15 ? 0 : Math.round(miss * 900);
        s.pen += pen;
        say(miss <= 0.15 ? 'Ровно на отметке!' : (meters > 0 ? 'Перелёт ' : 'Недоезд ') + miss.toFixed(2) + ' м · механики двигают машину +' + (pen / 1000).toFixed(1) + ' с', miss > 0.15);
        gHaptic(miss <= 0.15 ? 'success' : 'warning');
        const t = performance.now();
        s.tStop = t; setTStop(t);
        setPhase('wheels');
    };

    // ─ 2. колёса: 0 — стоит старая, 1 — меняют, 2 — новая, не затянута, 3 — готово ─
    const tapWheel = (id) => {
        if (phase !== 'wheels') return;
        const w = wheels[id];
        if (w === 0) {
            setWheels(p => ({...p, [id]: 1})); gHaptic('light');
            later(() => setWheels(p => ({...p, [id]: 2})), PIT_SWAP_MS);
        } else if (w === 1) {
            st.current.pen += 250; gHaptic('warning'); say('Рано — шину ещё не поставили · +0.25 с', true);
        } else if (w === 2) {
            const next = {...wheels, [id]: 3};
            setWheels(next); gHaptic('light');
            if (PIT_WHEELS.every(x => next[x.id] === 3)) release();
        }
    };

    // ─ 3. выпуск: следи за быстрой полосой ─
    const release = () => {
        setPhase('release');
        const busy = Math.random() < 0.5;
        setTraffic(busy);
        if (busy) {
            setLight('red');
            later(() => setLight('green'), 350 + Math.random() * 1100);
        } else later(() => setLight('green'), 200);
    };
    const go = () => {
        if (phase !== 'release') return;
        const s = st.current;
        const unsafe = light !== 'green';
        if (unsafe) { s.pen += PIT_UNSAFE; gHaptic('error'); } else gHaptic('success');
        const raw = performance.now() - s.tStop;
        const total = raw + s.pen;
        setRes({total, raw, pen: s.pen, unsafe, stopErr: s.stopErr});
        setPhase('done');
        later(() => setCanLeave(true), 700);
        gmPostResult({game_type: 'pit_real', score: Math.round(total)})
            .then(r => { if (r?.new_achievements?.length) setAch(true); setBoardKey(k => k + 1); });
    };

    useGmKeys(e => {
        if (gmKeyGo(e)) {
            if (phase === 'approach') brake();
            else if (phase === 'release') go();
            else if (phase === 'intro' || (phase === 'done' && canLeave)) start();
            else return;
            return true;
        }
        const n = '1234'.indexOf(e.key);
        if (n >= 0 && phase === 'wheels') { tapWheel(PIT_WHEELS[n].id); return true; }
    });

    const verdict = (ms) => ms < 2500 ? {t: 'Уровень McLaren', s: 'Рекорд F1 — 1.80 с, McLaren, Катар-2023'}
        : ms < 3200 ? {t: 'Топ-команда', s: 'Так меняют колёса в лидерах пелотона'}
        : ms < 4500 ? {t: 'Середина пелотона', s: 'Соперник уже прошёл под андеркатом'}
        : {t: 'Долгая остановка', s: 'В гонке это минус пара позиций'};

    const car = team?.car_url;
    const doneN = PIT_WHEELS.filter(w => wheels[w.id] === 3).length;

    return (
        <div className="page-container fade-in gm-page pit-page">
            <GameTop title="Пит-стоп" onBack={onBack}/>

            {phase === 'intro' && (
                <div className="gm-intro">
                    <div className="pit3-lane small">
                        {car && <img className="pit3-car" src={car} alt="" draggable="false" style={{transform: 'translateX(9%)'}}/>}
                    </div>
                    <div className="gm-h">Пит-стоп как в F1</div>
                    <div className="gm-p left">
                        <b>1. Заезд.</b> Жми «Тормоз», чтобы встать ровно на жёлтую отметку. Промах — механики двигают машину.<br/>
                        <b>2. Колёса.</b> На каждом: тап — отвернуть, шину меняют, второй тап — завернуть. Колёса в любом порядке.<br/>
                        <b>3. Выпуск.</b> Если по пит-лейну едет соперник, горит красный. Выпуск на красный — штраф 5 с.
                    </div>
                    <div className="gm-btn" onClick={start}>Машина в боксы</div>
                    <GameBoard type="pit_real" fmt={ms => (ms / 1000).toFixed(2) + ' с'} refreshKey={boardKey}/>
                </div>
            )}

            {phase !== 'intro' && phase !== 'done' && (
                <div className="pit3-stage">
                    <div className="pit-hud">
                        <div className={'pit-light ' + (phase === 'release' && light === 'green' ? 'go' : 'stop')}/>
                        <div className="pit-time">{phase === 'approach' ? '0.00' : <LiveTimer t0={tStop} offset={st.current.pen || 0}/>}<span>с</span></div>
                        <div className="pit-tyre"><i style={{background: tyre.c}}/>{tyre.name}</div>
                    </div>
                    {team && <div className="pit2-team">Бокс {team.team}</div>}
                    <div className="pit3-lane" ref={stageRef}>
                        <div className="pit3-fast">{phase === 'release' && traffic && light === 'red' && <div className="pit3-rival"/>}</div>
                        <div className="pit3-box" style={{'--tc': team?.team_color || '#E10600'}}><div className="pit3-mark"/></div>
                        {car && <img ref={carRef} className={'pit3-car' + (phase === 'wheels' ? ' up' : '') + (phase === 'release' ? ' down' : '')}
                                     src={car} alt="" draggable="false" style={{transform: 'translateX(-120%)'}}/>}
                    </div>
                    {msg && <div key={msg.k} className={'pit2-flash' + (msg.bad ? ' bad' : ' ok')}>{msg.text}</div>}

                    {phase === 'approach' && <div className="pit2-gun" onPointerDown={e => { e.preventDefault(); brake(); }}>ТОРМОЗ</div>}

                    {phase === 'wheels' && (
                        <div className="pit3-wheels">
                            {PIT_WHEELS.map(w => {
                                const s = wheels[w.id];
                                return (
                                    <div key={w.id} className={'pit3-w s' + s} style={{'--tc': tyre.c}} onPointerDown={e => { e.preventDefault(); tapWheel(w.id); }}>
                                        <div className="pit3-tyre"><i/></div>
                                        <b>{w.label}</b>
                                        <span>{s === 0 ? 'Отвернуть' : s === 1 ? 'Меняют шину…' : s === 2 ? 'Завернуть' : 'Готово ✓'}</span>
                                    </div>
                                );
                            })}
                            <div className="pit3-count">{doneN} / 4</div>
                        </div>
                    )}

                    {phase === 'release' && (
                        <div className={'pit3-go ' + light} onPointerDown={e => { e.preventDefault(); go(); }}>
                            {light === 'green' ? 'ВЫПУСК' : 'ЖДИ — СОПЕРНИК НА ПИТ-ЛЕЙНЕ'}
                        </div>
                    )}
                </div>
            )}

            {phase === 'done' && res && (() => { const v = verdict(res.total); return (
                <div className="gm-result">
                    <div className="gm-result-k">Время остановки</div>
                    <div className="gm-result-big">{(res.total / 1000).toFixed(2)}<span>с</span></div>
                    <div className="gm-result-t">{v.t}</div>
                    <div className="gm-result-s">{v.s}</div>
                    <div className="pit3-break">
                        <div><span>Работа бригады</span><b>{(res.raw / 1000).toFixed(2)} с</b></div>
                        <div><span>Остановка</span><b>{Math.abs(res.stopErr) <= 0.15 ? 'на отметке' : (res.stopErr > 0 ? 'перелёт ' : 'недоезд ') + Math.abs(res.stopErr).toFixed(2) + ' м'}</b></div>
                        {res.unsafe && <div className="bad"><span>Небезопасный выпуск</span><b>+5.0 с</b></div>}
                        {res.pen > 0 && <div><span>Штрафы всего</span><b>+{(res.pen / 1000).toFixed(2)} с</b></div>}
                    </div>
                    {ach && <div className="gm-ach">🏅 Новое достижение!</div>}
                    <div className={'gm-row' + (canLeave ? '' : ' wait')}>
                        <div className="gm-btn" onClick={() => canLeave && start()}>Ещё раз</div>
                        <div className="gm-btn ghost" onClick={() => canLeave && onBack()}>К играм</div>
                    </div>
                    <GameBoard type="pit_real" fmt={ms => (ms / 1000).toFixed(2) + ' с'} refreshKey={boardKey}/>
                </div>
            ); })()}
        </div>
    );
};

// ─────────────────────────── РЕАКЦИЯ ───────────────────────────
const REACTION_FACTS = [
    {max: 150, text: 'Быстрее моргания глаза. Ты вообще человек?'},
    {max: 150, text: 'Это уже рефлекс — мозг даже не успел подумать.'},
    {max: 220, text: 'Реакция уровня пилота F1 на стартовых огнях.'},
    {max: 220, text: 'Хэмилтон на старте реагирует примерно за 0.2 с. Ты рядом.'},
    {max: 220, text: 'Гепард реагирует на добычу за ~200 мс — ты на его уровне.'},
    {max: 300, text: 'Как вратарь на пенальти — прыжок начинается через ~250 мс.'},
    {max: 300, text: 'Средняя реакция человека — около 250 мс. Ты в норме.'},
    {max: 300, text: 'Киберспортсмены CS2 — 200–250 мс. Почти догнал.'},
    {max: 400, text: 'Для экзамена на права в Германии хватает 400 мс.'},
    {max: 400, text: 'После кофе реакция улучшается примерно на 10%.'},
    {max: 500, text: 'Водитель на светофоре реагирует за 400–500 мс.'},
    {max: 500, text: 'На старте Гран-при ты бы потерял пару позиций до первого поворота.'},
    {max: 99999, text: 'За {time} мс болид F1 на старте проезжает около {dist} м.'},
    {max: 99999, text: 'Попробуй ещё — первая попытка всегда разминка.'},
];

const ReactionGame = ({onBack}) => {
    const [phase, setPhase] = useState('ready');       // ready | lights | hold | go | done | false
    const [lit, setLit] = useState(0);
    const [rt, setRt] = useState(0);
    const [shown, setShown] = useState(0);
    const [fact, setFact] = useState('');
    const [boardKey, setBoardKey] = useState(0);
    const [ach, setAch] = useState(false);
    const goAt = useRef(0);
    const timers = useRef([]);
    const phaseRef = useRef('ready');
    const setP = (p) => { phaseRef.current = p; setPhase(p); };
    const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    useEffect(() => clear, []);

    const start = () => {
        clear(); setLit(0); setAch(false); setP('lights');
        for (let i = 1; i <= 5; i++) timers.current.push(setTimeout(() => { setLit(i); gHaptic('light'); }, i * 1000));
        // после пятого огня пауза 0.2–3 с — как у настоящей FIA-стартовой процедуры
        timers.current.push(setTimeout(() => setP('hold'), 5000));
        timers.current.push(setTimeout(() => {
            goAt.current = performance.now(); setLit(0); setP('go');
        }, 5000 + 200 + Math.random() * 2800));
    };

    const press = () => {
        const p = phaseRef.current;
        if (p === 'lights' || p === 'hold') { clear(); setP('false'); gHaptic('error'); return; }
        if (p !== 'go') return;
        const t = Math.round(performance.now() - goAt.current);
        setRt(t); setP('done'); gHaptic(t < 250 ? 'success' : 'medium');
        const pool = REACTION_FACTS.filter(f => t <= f.max);
        const f = pool[Math.floor(Math.random() * Math.min(4, pool.length))] || REACTION_FACTS[REACTION_FACTS.length - 1];
        setFact(f.text.replace('{time}', t).replace('{dist}', Math.max(1, Math.round(t * 0.012))));
        gmPostResult({game_type: 'reaction', score: t})
            .then(r => { if (r?.new_achievements?.length) setAch(true); setBoardKey(k => k + 1); });
    };
    // на компьютере: пробел/Enter — и «жми», и старт заново
    useGmKeys(e => {
        if (!gmKeyGo(e)) return;
        const p = phaseRef.current;
        if (p === 'lights' || p === 'hold' || p === 'go') press(); else start();
        return true;
    });

    // счётчик результата «набегает» до итоговых миллисекунд
    useEffect(() => {
        if (phase !== 'done') return;
        let raf; const s = performance.now();
        const tick = (n) => { const k = Math.min(1, (n - s) / 550); setShown(Math.round(rt * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(tick); };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [phase, rt]);

    const armed = phase === 'lights' || phase === 'hold' || phase === 'go';
    const pos = (ms) => Math.max(0, Math.min(100, (ms - 100) / 5)) + '%';   // шкала 100–600 мс

    return (
        <div className={'page-container fade-in gm-page rx-page' + (phase === 'go' ? ' rx-go' : '') + (phase === 'false' ? ' rx-false' : '')}
             onPointerDown={armed ? (e => { e.preventDefault(); press(); }) : undefined}>
            <GameTop title="Реакция" onBack={() => { clear(); onBack(); }}/>

            <div className="rx-gantry">
                {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="rx-pod">
                        <i/><i/>
                        <i className={phase !== 'go' && phase !== 'done' && i <= lit ? 'on' : ''}/>
                        <i className={phase !== 'go' && phase !== 'done' && i <= lit ? 'on' : ''}/>
                    </div>
                ))}
            </div>

            {phase === 'ready' && (
                <div className="gm-intro">
                    <div className="gm-h">Старт как в F1</div>
                    <div className="gm-p">Пять красных огней загораются по одному. Когда <b>все погаснут</b> — жми в любое место экрана.<br/>Нажмёшь раньше — фальстарт.</div>
                    <div className="gm-btn" onClick={start}>На стартовую решётку</div>
                    <GameBoard type="reaction" fmt={ms => ms + ' мс'} refreshKey={boardKey}/>
                </div>
            )}

            {armed && (
                <div className="rx-arena">
                    <div className="rx-call">{phase === 'go' ? 'ЖМИ!' : 'Жди, пока погаснут огни'}</div>
                    <div className="rx-sub">{phase === 'go' ? 'Lights out and away we go' : 'Палец на экране — не отпускай внимание'}</div>
                </div>
            )}

            {phase === 'false' && (
                <div className="gm-result">
                    <div className="gm-result-big bad">ФАЛЬСТАРТ</div>
                    <div className="gm-result-s">Ты сорвался раньше, чем погасли огни. В гонке за это дают штраф 5 секунд.</div>
                    <div className="gm-row">
                        <div className="gm-btn" onClick={start}>Ещё раз</div>
                        <div className="gm-btn ghost" onClick={onBack}>К играм</div>
                    </div>
                </div>
            )}

            {phase === 'done' && (
                <div className="gm-result">
                    <div className="gm-result-k">Время реакции</div>
                    <div className="gm-result-big">{shown}<span>мс</span></div>
                    <div className="gm-result-t">{rt < 200 ? 'Нечеловеческая реакция' : rt < 260 ? 'Реакция пилота' : rt < 350 ? 'Хороший старт' : 'Проспал старт'}</div>
                    {/* метки без подписей на самой шкале — подписи раньше налезали друг на друга; легенда ниже */}
                    <div className="rx-scale">
                        <div className="rx-scale-bar"/>
                        <div className="rx-mark pro" style={{left: pos(200)}}/>
                        <div className="rx-mark avg" style={{left: pos(270)}}/>
                        <div className="rx-you" style={{left: pos(rt)}}/>
                    </div>
                    <div className="rx-legend">
                        <div className="you"><i/><b>{shown} мс</b><span>Ты</span></div>
                        <div className="pro"><i/><b>~200 мс</b><span>Пилоты F1</span></div>
                        <div className="avg"><i/><b>~270 мс</b><span>Обычный человек</span></div>
                    </div>
                    <div className="gm-fact">{fact}</div>
                    {ach && <div className="gm-ach">🏅 Новое достижение!</div>}
                    <div className="gm-row">
                        <div className="gm-btn" onClick={start}>Ещё раз</div>
                        <div className="gm-btn ghost" onClick={onBack}>К играм</div>
                    </div>
                    <GameBoard type="reaction" fmt={ms => ms + ' мс'} refreshKey={boardKey}/>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────── ДУРАК ───────────────────────────
// Колода 36 карт. Сила карты = место в чемпионате:
//   тузы — четыре лучшие команды Кубка конструкторов (логотип),
//   короли…девятки — пилоты по местам (К = 1–4 место, Д = 5–8, …),
//   младшие — остальные пилоты и команды, в конце — машины безопасности.
// Масти идут по кругу ♣ ♥ ♠ ♦ — туз треф у лидера Кубка конструкторов.
// Режимы: против компьютера (всё в браузере) и онлайн с другом (комната на
// сервере, правила проверяет сервер — durak_online.py).
const DK_SUITS = ['♣', '♥', '♠', '♦'];
const DK_RED = {'♥': 1, '♦': 1};
const DK_RANK = {6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'В', 12: 'Д', 13: 'К', 14: 'Т'};
const DK_EXTRA = [
    {kind: 'sc', name: 'Safety Car', color: '#F5B301'},
    {kind: 'mc', name: 'Medical Car', color: '#2E7D32'},
    {kind: 'vsc', name: 'VSC', color: '#F5B301'},
    {kind: 'flag', name: 'Красный флаг', color: '#E10600'},
];
const DK_BOT = 'F1_egor_bot';

const dkBuildDeck = (drivers, teams) => {
    const teamCard = t => ({kind: 'team', name: t.team, color: t.team_color || '#555', logo: t.logo_url});
    const drvCard = d => ({kind: 'drv', name: d.last_name || d.name, color: d.team_color || '#555', img: d.photo_url || '', num: d.driver_number});
    const aces = teams.slice(0, 4).map(teamCard);
    const pool = [...drivers.map(drvCard), ...teams.slice(4).map(teamCard), ...DK_EXTRA].slice(0, 32);
    const cards = [];
    aces.forEach((c, i) => cards.push({...c, rank: 14, suit: DK_SUITS[i]}));
    [13, 12, 11, 10, 9, 8, 7, 6].forEach((rank, ri) => {
        pool.slice(ri * 4, ri * 4 + 4).forEach((c, i) => cards.push({...c, rank, suit: DK_SUITS[i]}));
    });
    return cards.map((c, i) => ({...c, id: 'c' + i}));
};

const dkShuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };
const dkBeats = (atk, def, trump) => (def.suit === atk.suit && def.rank > atk.rank) || (def.suit === trump && atk.suit !== trump);
const dkVal = (c, trump) => c.rank + (c.suit === trump ? 20 : 0);
const dkSort = (hand, trump) => [...hand].sort((x, y) => dkVal(x, trump) - dkVal(y, trump) || x.suit.localeCompare(y.suit));
const dkRanksOnTable = (table) => { const s = new Set(); table.forEach(p => { s.add(p.atk.card.rank); if (p.def) s.add(p.def.card.rank); }); return s; };
const dkCanThrow = (g, card) => {
    if (!g.table.length) return true;
    if (g.table.length >= g.limit) return false;
    return dkRanksOnTable(g.table).has(card.rank);
};

// <dk-ai> ─ Мозг компьютера в «Дураке» ─
// Блок без JSX: этот же текст гоняет симуляция (tools/durak_sim.js).
// Честная игра: пока колода не пуста, бот знает только свои карты, бито,
// стол, козырь под колодой и карты, которые соперник взял со стола.
// Колода пуста — карты соперника = все невышедшие, тогда перебор ходов.
const dkAI = (() => {
    const TB = 10;                                   // надбавка козырю
    // настройки подобраны симуляцией (tools/durak_sim.js)
    const P = {atkTrump: 6, twinLo: 2.2, twinHi: 1.2, atkNoBeatLate: 4, atkNoBeat: 1.5, thrBig: 4, thrMid: 6, thrPb: 0.3, thrNoBeat: 3,
        dumpBig: 11, dumpMid: 12, defTrump: 2, defTrumpDeck: 0.2, defSameRank: 1.5, defTwin: 0.8,
        tkBase: 2.2, tkCard: 1.3, tkDef: 1.5, tkLate: 3, tkBig: 0.8, trBase: 3, trRank: 1.1, trDeck: 0.12, hiRank: 1.2, takeOff: 4};
    const val = (c, T) => c.rank - 6 + (c.suit === T ? TB : 0);
    const beats = (a, d, T) => (d.suit === a.suit && d.rank > a.rank) || (d.suit === T && a.suit !== T);
    const clock = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());
    const tableCards = (s) => s.table.flatMap(p => p.def ? [p.atk.card, p.def.card] : [p.atk.card]);
    const ranksOn = (s) => { const r = new Set(); s.table.forEach(p => { r.add(p.atk.card.rank); if (p.def) r.add(p.def.card.rank); }); return r; };

    // Что бот знает о соперничьей руке
    const view = (s, me) => {
        const op = me === 'a' ? 'p' : 'a';
        const tc = tableCards(s);
        const gone = s.gone || [];
        const kn = new Set((s.known && s.known[op]) || []);
        // известные карты соперника: он их взял со стола или вытянул открытый козырь
        const oppKnown = s.hands[op].filter(c => kn.has(c.id));
        const seen = new Set([...s.hands[me], ...tc, ...gone, ...oppKnown].map(c => c.id));
        if (s.deck.length) seen.add(s.trumpCard.id);
        // все 36 карт: перечисляем, но берём только невиданные — это «тёмный» пул
        const all = [...s.hands.p, ...s.hands.a, ...s.deck, ...tc, ...gone];
        const pool = all.filter(c => !seen.has(c.id));
        return {op, oppKnown, pool, oppN: s.hands[op].length, unk: s.hands[op].length - oppKnown.length};
    };

    // Вероятность, что соперник сможет побить карту x
    const pBeat = (x, V, T) => {
        if (V.oppKnown.some(b => beats(x, b, T))) return 1;
        const n = V.pool.length, k = Math.min(V.unk, n);
        const good = V.pool.filter(b => beats(x, b, T)).length;
        if (!good || !k) return 0;
        let miss = 1;
        for (let i = 0; i < k; i++) miss *= Math.max(0, n - good - i) / (n - i);
        return 1 - miss;
    };

    // ─── Перебор в эндшпиле ───
    // карта → код: масть*16 + ранг; руки — отсортированные массивы кодов
    const solve = (s, me, budget) => {
        const T = s.trump;
        const suits = [T];
        const sc = (c) => { let i = suits.indexOf(c.suit); if (i < 0) { suits.push(c.suit); i = suits.length - 1; } return i * 16 + c.rank; };
        const byCode = new Map();
        const enc = (c) => { const k = sc(c); byCode.set(k, c); return k; };
        const V = view(s, me);
        const A = s.hands[me].map(enc).sort((x, y) => x - y);
        const B = [...V.pool, ...V.oppKnown].map(enc).sort((x, y) => x - y);
        const tb = [];
        s.table.forEach(p => { tb.push(enc(p.atk.card), p.def ? enc(p.def.card) : -1); });
        const root = {h: [A, B], att: s.attacker === me ? 0 : 1, tb, lim: s.limit, tk: !!s.taking};

        // козырь — масть 0
        const bt = (a, d) => ((d >> 4) === (a >> 4) && (d & 15) > (a & 15)) || ((d >> 4) === 0 && (a >> 4) !== 0);
        const cv = (c) => (c & 15) + ((c >> 4) === 0 ? TB : 0);
        const rk = (tb) => { let m = 0; for (const c of tb) if (c >= 0) m |= 1 << (c & 15); return m; };
        const ins = (h, c) => { const r = h.slice(); let i = r.length; r.push(c); while (i > 0 && r[i - 1] > c) { r[i] = r[i - 1]; i--; } r[i] = c; return r; };
        const rem = (h, c) => { const i = h.indexOf(c); const r = h.slice(); r.splice(i, 1); return r; };
        const canThrow = (h, tb, lim) => { if (tb.length / 2 >= lim) return false; const m = rk(tb); return h.some(c => m & (1 << (c & 15))); };
        const fin = (st) => {
            const a = st.h[0].length, b = st.h[1].length;
            st.end = !a && !b ? 0 : !a ? 1 : !b ? -1 : null;
            return st;
        };
        const bito = (st) => fin({h: st.h, att: 1 - st.att, tb: [], lim: 6, tk: false});
        const take = (st) => {
            const d = 1 - st.att;
            const h = st.h.slice();
            h[d] = st.h[d].concat(st.tb.filter(c => c >= 0)).sort((x, y) => x - y);
            return fin({h, att: st.att, tb: [], lim: 6, tk: false});
        };
        const moves = (st) => {
            const att = st.att, def = 1 - att, H = st.h, tb = st.tb, n = tb.length / 2;
            if (st.tk) {
                const m = rk(tb), out = [];
                if (n < st.lim) for (const c of H[att]) if (m & (1 << (c & 15))) out.push(c);
                out.push(-1);
                return [att, out];
            }
            let u = -1;
            for (let i = 1; i < tb.length; i += 2) if (tb[i] < 0) { u = i - 1; break; }
            if (u >= 0) {
                const out = H[def].filter(c => bt(tb[u], c));
                out.sort((x, y) => cv(x) - cv(y));
                out.push(-1);
                return [def, out];
            }
            if (!n) return [att, H[att].slice().sort((x, y) => cv(x) - cv(y))];
            const m = rk(tb), out = [];
            if (n < st.lim) for (const c of H[att]) if (m & (1 << (c & 15))) out.push(c);
            out.sort((x, y) => cv(x) - cv(y));
            out.push(-1);
            return [att, out];
        };
        const play = (st, mv) => {
            const att = st.att, def = 1 - att, n = st.tb.length / 2;
            if (st.tk) {
                if (mv < 0) return take(st);
                const h = st.h.slice(); h[att] = rem(h[att], mv);
                const ns = {h, att, tb: st.tb.concat([mv, -1]), lim: st.lim, tk: true};
                if (!h[att].length || !canThrow(h[att], ns.tb, ns.lim)) return take(ns);
                return ns;
            }
            let u = -1;
            for (let i = 1; i < st.tb.length; i += 2) if (st.tb[i] < 0) { u = i - 1; break; }
            if (u >= 0) {
                if (mv < 0) {
                    const ns = {h: st.h, att, tb: st.tb, lim: st.lim, tk: true};
                    if (!st.h[att].length || !canThrow(st.h[att], st.tb, st.lim)) return take(ns);
                    return ns;
                }
                const h = st.h.slice(); h[def] = rem(h[def], mv);
                const tb = st.tb.slice(); tb[u + 1] = mv;
                const ns = {h, att, tb, lim: st.lim, tk: false};
                if (tb.length / 2 >= st.lim || !h[att].length || !h[def].length) return bito(ns);
                return ns;
            }
            if (mv < 0) return bito(st);
            const h = st.h.slice(); h[att] = rem(h[att], mv);
            const lim = n ? st.lim : Math.min(6, st.h[def].length);
            return {h, att, tb: st.tb.concat([mv, -1]), lim, tk: false};
        };
        const heur = (st) => {
            const [a, b] = st.h;
            let sa = 0, sb = 0;
            for (const c of a) sa += cv(c);
            for (const c of b) sb += cv(c);
            const x = (b.length - a.length) * 0.07 + (sa / Math.max(1, a.length) - sb / Math.max(1, b.length)) * 0.025 + (st.att === 0 ? 0.03 : -0.03);
            return Math.max(-0.9, Math.min(0.9, x));
        };
        const key = (st) => st.h[0].join(',') + '|' + st.h[1].join(',') + '|' + st.att + '|' + st.tb.join(',') + '|' + st.lim + '|' + (st.tk ? 1 : 0);

        const deadline = clock() + budget;
        const TT = new Map();
        const ABORT = {};
        let nodes = 0, cuts = 0;
        const node = (st, depth, alpha, beta) => {
            if (st.end !== undefined && st.end !== null) return st.end;
            if ((++nodes & 1023) === 0 && clock() > deadline) throw ABORT;
            if (depth <= 0) { cuts++; return heur(st); }
            const k = key(st);
            const e = TT.get(k);
            if (e && e.d >= depth) {
                if (e.d !== 1e9) cuts++;
                if (e.f === 0) return e.v;
                if (e.f === 1 && e.v >= beta) return e.v;
                if (e.f === 2 && e.v <= alpha) return e.v;
            }
            const a0 = alpha, c0 = cuts;
            const [who, ms] = moves(st);
            let best = who === 0 ? -2 : 2;
            for (const mv of ms) {
                const v = node(play(st, mv), depth - 1, alpha, beta);
                if (who === 0) { if (v > best) best = v; if (best > alpha) alpha = best; }
                else { if (v < best) best = v; if (best < beta) beta = best; }
                if (alpha >= beta) break;
            }
            const f = best <= a0 ? 2 : best >= beta ? 1 : 0;
            TT.set(k, {d: cuts === c0 ? 1e9 : depth, v: best, f});
            return best;
        };
        const [who0, ms0] = moves(root);
        if (who0 !== 0) return null;
        let bestMv = ms0[0], bestV = null, order = ms0.slice();
        try {
            for (let depth = 2; depth <= 80; depth += 2) {
                const c0 = cuts;
                let bv = -2, bm = order[0], alpha = -2;
                for (const mv of order) {
                    const v = node(play(root, mv), depth - 1, alpha, 2);
                    if (v > bv) { bv = v; bm = mv; }
                    if (bv > alpha) alpha = bv;
                    if (bv >= 1) break;
                }
                bestMv = bm; bestV = bv;
                order = [bm, ...order.filter(x => x !== bm)];
                if (cuts === c0 || bv >= 1) break;      // всё просчитано до конца
            }
        } catch (e) { if (e !== ABORT) throw e; }
        return {card: bestMv < 0 ? null : byCode.get(bestMv), v: bestV, nodes};
    };

    // ─── Миттельшпиль: эвристики ───
    const attackCard = (s, me, V) => {
        const T = s.trump, H = s.hands[me], d = s.deck.length;
        let best = null, bc = 1e9;
        const allTrump = H.every(c => c.suit === T);
        for (const c of H) {
            let cost = val(c, T);
            if (c.suit === T && !allTrump) cost += P.atkTrump;
            // пара/тройка одного ранга — можно подкинуть следом, это выгодно
            const twins = H.filter(x => x !== c && x.rank === c.rank && x.suit !== T).length;
            cost -= twins * (c.rank <= 11 ? P.twinLo : P.twinHi);
            // соперник вряд ли побьёт — заставим брать или тратить козырь
            const pb = pBeat(c, V, T);
            cost -= (1 - pb) * (d <= 6 ? P.atkNoBeatLate : P.atkNoBeat);
            if (cost < bc) { bc = cost; best = c; }
        }
        return best;
    };
    const throwCard = (s, me, V) => {
        const T = s.trump, H = s.hands[me], d = s.deck.length;
        if (s.table.length >= s.limit) return null;
        const r = ranksOn(s);
        const thr = d >= 14 ? P.thrBig : d >= 7 ? P.thrMid : 8;    // до какого ранга подкидывать (над шестёркой)
        let best = null, bc = 1e9;
        for (const c of H) {
            if (!r.has(c.rank)) continue;
            const pb = pBeat(c, V, T);
            if (c.suit === T) {
                // козырь подкидываем только в самом конце колоды и если его вряд ли перебьют
                if (d > 2 || pb > 0.35 || c.rank >= 12) continue;
            } else if (c.rank - 6 > thr && pb > P.thrPb) continue;
            const cost = val(c, T) - (1 - pb) * P.thrNoBeat;
            if (cost < bc) { bc = cost; best = c; }
        }
        return best;
    };
    const dumpCards = (s, me) => {
        const T = s.trump, H = s.hands[me], d = s.deck.length;
        const r = ranksOn(s);
        const room = Math.max(0, s.limit - s.table.length);
        const maxRank = d >= 10 ? P.dumpBig : d >= 4 ? P.dumpMid : 14;
        return H.filter(c => r.has(c.rank) && c.suit !== T && c.rank <= maxRank)
            .sort((x, y) => val(x, T) - val(y, T)).slice(0, room);
    };
    const defendMove = (s, me, V) => {
        const T = s.trump, H = s.hands[me], d = s.deck.length;
        const u = s.table.find(p => !p.def);
        const x = u.atk.card;
        const r = ranksOn(s);
        const bs = H.filter(b => beats(x, b, T));
        if (!bs.length) return {kind: 'take'};
        let best = null, bc = 1e9;
        for (const b of bs) {
            let cost = val(b, T) - val(x, T) * 0.3;
            if (b.suit === T && x.suit !== T) cost += P.defTrump + d * P.defTrumpDeck;
            if (r.has(b.rank)) cost -= P.defSameRank;               // не даём новый ранг для подкидывания
            const twins = H.filter(y => y !== b && y.rank === b.rank).length;
            if (twins && b.suit !== T) cost += P.defTwin;       // пару бережём для атаки
            if (cost < bc) { bc = cost; best = b; }
        }
        // брать или биться: оцениваем «цену» карты, которой бьём
        if (d >= P.takeOff) {
            const onTable = tableCards(s);
            const defended = s.table.filter(p => p.def).length;
            const tCost = best.suit === T && x.suit !== T ? P.trBase + (best.rank - 6) * P.trRank + d * P.trDeck : Math.max(0, best.rank - 11) * P.hiRank;
            const takeCost = P.tkBase + onTable.length * P.tkCard + defended * P.tkDef
                + onTable.reduce((a, c) => a + (c.suit === T ? -0.8 : c.rank <= 9 ? 0.6 : 0), 0)
                + (d <= 8 ? P.tkLate : 0) + Math.max(0, H.length + onTable.length - 8) * P.tkBig;
            if (tCost > takeCost) return {kind: 'take'};
        }
        return {kind: 'defend', card: best};
    };

    // ─── Миттельшпиль: оценка хода быстрыми партиями до конца (Монте-Карло) ───
    // Неизвестные карты соперника и порядок колоды каждый раз раскладываются
    // случайно (из того, что бот честно не видел), дальше обе стороны играют
    // простыми правилами. Ход с лучшим средним итогом — наш.
    const xbt = (a, d) => ((d >> 4) === (a >> 4) && (d & 15) > (a & 15)) || ((d >> 4) === 0 && (a >> 4) !== 0);
    const xcv = (c) => (c & 15) + ((c >> 4) === 0 ? TB : 0);
    const xDraw = (st, x) => { const h = st.H[x]; while (h.length < 6 && st.di < st.dk.length) h.push(st.dk[st.di++]); };
    const xEnd = (st) => {
        if (st.di < st.dk.length) return;
        const a = st.H[0].length, b = st.H[1].length;
        if (!a && !b) st.res = 0; else if (!a) st.res = 1; else if (!b) st.res = -1;
    };
    const xBito = (st) => { const at = st.att; st.tb = []; xDraw(st, at); xDraw(st, 1 - at); st.att = 1 - at; st.lim = 6; xEnd(st); };
    const xTake = (st) => {
        const at = st.att, df = 1 - at;
        for (const c of st.tb) if (c >= 0) st.H[df].push(c);
        st.tb = []; st.tk = false; xDraw(st, at); xDraw(st, df); st.lim = 6; xEnd(st);
    };
    const xRm = (h, c) => { h.splice(h.indexOf(c), 1); };
    const xRanks = (tb) => { let m = 0; for (const c of tb) if (c >= 0) m |= 1 << (c & 15); return m; };
    const xAttack = (st, c) => { if (!st.tb.length) st.lim = Math.min(6, st.H[1 - st.att].length); xRm(st.H[st.att], c); st.tb.push(c, -1); };
    const xDefend = (st, c) => {
        const at = st.att, df = 1 - at;
        xRm(st.H[df], c); st.tb[st.tb.indexOf(-1)] = c;
        if (st.tb.indexOf(-1) < 0 && (st.tb.length / 2 >= st.lim || !st.H[at].length || !st.H[df].length)) xBito(st);
    };
    const xDump = (st, cards) => { for (const c of cards) { xRm(st.H[st.att], c); st.tb.push(c, -1); } xTake(st); };
    const xApply = (st, a) => {
        if (a.k === 'take') { st.tk = true; return; }
        if (a.k === 'bito') { xBito(st); return; }
        if (a.k === 'dump') { xDump(st, a.c); return; }
        if (a.k === 'defend') { xDefend(st, a.c); return; }
        xAttack(st, a.c);
    };
    // простые правила для доигрывания
    const xPolicy = (st) => {
        const at = st.att, df = 1 - at, deckLeft = st.dk.length - st.di;
        const tb = st.tb, n = tb.length / 2;
        if (st.tk) {
            const m = xRanks(tb), out = [];
            let room = st.lim - n;
            const h = st.H[at].slice().sort((x, y) => xcv(x) - xcv(y));
            for (const c of h) if (room > 0 && (m & (1 << (c & 15))) && ((c >> 4) !== 0 || !deckLeft) && (!deckLeft || (c & 15) <= 12)) { out.push(c); room--; }
            xDump(st, out); return;
        }
        const ui = tb.indexOf(-1);
        if (ui >= 0) {
            const x = tb[ui - 1];
            let best = -1;
            for (const c of st.H[df]) if (xbt(x, c) && (best < 0 || xcv(c) < xcv(best))) best = c;
            if (best >= 0 && (best >> 4) === 0 && (x >> 4) !== 0 && deckLeft >= 8 && (best & 15) >= 13 && (x & 15) <= 10) best = -1;
            if (best < 0) st.tk = true; else xDefend(st, best);
            return;
        }
        const h = st.H[at];
        if (!n) {
            let best = h[0];
            for (const c of h) if (xcv(c) < xcv(best)) best = c;
            xAttack(st, best); return;
        }
        if (n < st.lim) {
            const m = xRanks(tb);
            let best = -1;
            for (const c of h) if ((m & (1 << (c & 15))) && (!deckLeft || ((c >> 4) !== 0 && (c & 15) <= 11)) && (best < 0 || xcv(c) < xcv(best))) best = c;
            if (best >= 0) { xAttack(st, best); return; }
        }
        xBito(st);
    };
    const mc = (s, me, V, cands, budget, prior) => {
        const T = s.trump, suits = [T];
        const sc = (c) => { let i = suits.indexOf(c.suit); if (i < 0) { suits.push(c.suit); i = suits.length - 1; } return i * 16 + c.rank; };
        const A = s.hands[me].map(sc);
        const known = V.oppKnown.map(sc);
        const pool = V.pool.map(sc);
        const trumpC = sc(s.trumpCard);
        const tb = [];
        s.table.forEach(p => { tb.push(sc(p.atk.card), p.def ? sc(p.def.card) : -1); });
        const acts = cands.map(a => ({k: a.kind, c: a.cards ? a.cards.map(sc) : a.card ? sc(a.card) : null}));
        const sum = acts.map(() => 0), cnt = acts.map(() => 0);
        const deadline = clock() + budget;
        const deckN = s.deck.length;
        let it = 0;
        while (true) {
            // расклад неизвестного
            const p = pool.slice();
            for (let i = p.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); const t = p[i]; p[i] = p[j]; p[j] = t; }
            const B = known.concat(p.slice(0, V.unk));
            const dk = p.slice(V.unk, V.unk + Math.max(0, deckN - 1));
            if (deckN) dk.push(trumpC);
            for (let i = 0; i < acts.length; i++) {
                const st = {H: [A.slice(), B.slice()], dk, di: 0, att: s.attacker === me ? 0 : 1, tb: tb.slice(), lim: s.limit, tk: !!s.taking, res: null};
                xApply(st, acts[i]);
                for (let g = 0; g < 600 && st.res === null; g++) xPolicy(st);
                sum[i] += st.res || 0; cnt[i]++;
            }
            if ((++it & 3) === 0 && clock() > deadline) break;
        }
        let bi = prior, bv = -9;
        for (let i = 0; i < acts.length; i++) {
            const v = sum[i] / cnt[i] + (i === prior ? 0.02 : 0);
            if (v > bv) { bv = v; bi = i; }
        }
        return {i: bi, it};
    };

    // эвристика даёт «предпочтительный» ход, Монте-Карло проверяет все варианты
    const same = (a, b) => a.kind === b.kind && (a.card ? b.card && a.card.id === b.card.id : true)
        && (a.cards ? b.cards && a.cards.length === b.cards.length && a.cards.every((c, i) => c.id === b.cards[i].id) : true);
    const pick = (s, me, V, cands, h, budget) => {
        let prior = cands.findIndex(c => same(c, h));
        if (prior < 0) { cands = [h, ...cands]; prior = 0; }
        if (cands.length < 2 || !budget || P.noMC) return h;
        return cands[mc(s, me, V, cands, budget, prior).i];
    };

    // Главная функция: что делать боту me ('a' или 'p') в состоянии s.
    // Ответ: {kind:'defend',card} | {kind:'take'} | {kind:'attack',card} | {kind:'bito'} | {kind:'dump',cards} | {kind:'wait'}
    const decide = (s, me, budget = 70) => {
        const op = me === 'a' ? 'p' : 'a';
        const u = s.table.find(p => !p.def);
        const V = view(s, me);
        const endgame = !s.deck.length;
        if (s.attacker === op) {
            if (!u || s.taking) return {kind: 'wait'};
            if (endgame) {
                const r = solve(s, me, budget);
                if (r) return r.card ? {kind: 'defend', card: r.card} : {kind: 'take'};
            }
            const h = defendMove(s, me, V);
            const T = s.trump, x = u.atk.card;
            const cands = [...s.hands[me].filter(b => beats(x, b, T)).map(card => ({kind: 'defend', card})), {kind: 'take'}];
            return pick(s, me, V, cands, h, budget);
        }
        if (s.taking === op) {
            if (endgame) {
                // подкидываем по одной, каждый раз пересчитывая
                const cards = [];
                let cur = s;
                for (let i = 0; i < 6; i++) {
                    if (cur.table.length >= cur.limit) break;
                    const r = solve(cur, me, Math.max(15, budget / 2));
                    if (!r || !r.card) break;
                    cards.push(r.card);
                    cur = {...cur, hands: {...cur.hands, [me]: cur.hands[me].filter(c => c.id !== r.card.id)},
                        table: [...cur.table, {atk: {card: r.card, by: me}, def: null}]};
                    if (!cur.hands[me].length) break;
                }
                return {kind: 'dump', cards};
            }
            const h = {kind: 'dump', cards: dumpCards(s, me)};
            const r = ranksOn(s), room = Math.max(0, s.limit - s.table.length);
            const all = s.hands[me].filter(c => r.has(c.rank)).sort((x, y) => val(x, s.trump) - val(y, s.trump));
            const cands = [];
            for (let k = 0; k <= Math.min(room, all.length); k++) cands.push({kind: 'dump', cards: all.slice(0, k)});
            return pick(s, me, V, cands, h, budget);
        }
        if (u) return {kind: 'wait'};
        if (endgame && s.hands[me].length) {
            const r = solve(s, me, budget);
            if (r) {
                if (r.card) return {kind: 'attack', card: r.card};
                return s.table.length ? {kind: 'bito'} : {kind: 'attack', card: attackCard(s, me, V)};
            }
        }
        if (!s.table.length) {
            const h = {kind: 'attack', card: attackCard(s, me, V)};
            return pick(s, me, V, s.hands[me].map(card => ({kind: 'attack', card})), h, budget);
        }
        const c = throwCard(s, me, V);
        const h = c ? {kind: 'attack', card: c} : {kind: 'bito'};
        const r = ranksOn(s);
        const thr = s.table.length < s.limit ? s.hands[me].filter(x => r.has(x.rank)) : [];
        return pick(s, me, V, [...thr.map(card => ({kind: 'attack', card})), {kind: 'bito'}], h, budget);
    };

    return {decide, solve, pBeat, view, P};
})();
// </dk-ai>

// Фото пилота — квадрат «голова и плечи» целиком, без обрезки (правка 24.09:
// вытянутый кадр вылезал за рамку и резал голову)
const dkDriverImg = (url) => (url || '').replace(/ar_\d+:\d+,w_\d+/, 'ar_1:1,w_300');
const dkPreload = (cards) => cards.forEach(c => {
    const u = c.kind === 'drv' ? dkDriverImg(c.img) : c.logo;
    if (u) { const im = new Image(); im.src = u; }
});

const DkCard = ({c, size = 'm', back, dim, sel, anim, style, ...rest}) => {
    if (back) return <div className={'dk-card back ' + size + (anim ? ' ' + anim : '')} style={style}><div className="dk-back-in"><b>F1</b><span>HUB</span></div></div>;
    const red = DK_RED[c.suit];
    return (
        <div className={'dk-card ' + size + (dim ? ' dim' : '') + (sel ? ' sel' : '') + (anim ? ' ' + anim : '')} style={style} {...rest}>
            <div className={'dk-idx' + (red ? ' red' : '')}><b>{DK_RANK[c.rank]}</b><i>{c.suit}</i></div>
            <div className="dk-face">
                {c.kind === 'drv' && c.img && <img className="dk-driver" src={dkDriverImg(c.img)} alt="" draggable="false" onError={e => { e.target.style.display = 'none'; }}/>}
                {c.kind === 'team' && c.logo && <img className="dk-logo" src={c.logo} alt="" draggable="false" onError={e => { e.target.style.display = 'none'; }}/>}
                {(c.kind === 'sc' || c.kind === 'vsc') && <div className="dk-emoji">🚨</div>}
                {c.kind === 'mc' && <div className="dk-emoji">🚑</div>}
                {c.kind === 'flag' && <div className="dk-emoji">🚩</div>}
            </div>
            <div className="dk-label" style={{'--tc': c.color}}><span>{c.name}</span></div>
        </div>
    );
};

// Запросы к комнатам: нужен текст ошибки сервера, а api.post его глотает.
const dkReq = async (method, url, body) => {
    try {
        const res = await fetch(url, {method, headers: {'Content-Type': 'application/json', ..._getAuthHeaders()},
            body: body ? JSON.stringify(body) : undefined});
        const data = await res.json().catch(() => ({}));
        return res.ok ? {ok: true, data: _f1media(data)} : {ok: false, error: data?.detail || ('Ошибка ' + res.status), status: res.status};
    } catch (e) { return {ok: false, error: 'Нет связи с сервером'}; }
};

// Код комнаты из приглашения: ?durak=КОД (кнопка бота) или start_param durak_КОД
const DURAK_INVITE = (() => {
    try {
        const q = new URLSearchParams(location.search).get('durak');
        const sp = window.Telegram?.WebApp?.initDataUnsafe?.start_param || '';
        const c = q || (sp.startsWith('durak_') ? sp.slice(6) : '');
        return c ? c.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) : null;
    } catch (e) { return null; }
})();
let _dkInviteUsed = false;

const dkInviteLink = (code) => gmHost().inviteLink ? gmHost().inviteLink(code) : 'https://t.me/' + DK_BOT + '?start=durak_' + code;
const dkNeedLogin = () => !!(gmHost().needLogin && gmHost().needLogin());
const dkShare = async (code) => {
    const link = dkInviteLink(code);
    const text = 'Го в дурака на картах F1! Комната ' + code;
    const tgw = window.Telegram?.WebApp;
    if (tgw?.openTelegramLink && tgw.initData) {
        tgw.openTelegramLink('https://t.me/share/url?url=' + encodeURIComponent(link) + '&text=' + encodeURIComponent(text));
        return 'shared';
    }
    try { if (navigator.share) { await navigator.share({text: text + '\n' + link}); return 'shared'; } } catch (e) {}
    try { await navigator.clipboard.writeText(text + '\n' + link); return 'copied'; } catch (e) { return 'fail'; }
};

// ─ Стикеры-реакции: 22 пилота, у каждого своя фраза ─
// Картинки — Pixar-чибики в /static/stickers/<k>.webp; пока их нет, стикер
// рисуется из фото пилота. Когда арт готов — добавить ключ в DK_STK_ART.
const DK_STK = [
    {k: 'verstappen', t: 'Ту-ту-ту-ту!'}, {k: 'hamilton', t: 'Боно, шины всё'}, {k: 'leclerc', t: 'Я тупой, я тупой'},
    {k: 'norris', t: 'Ха-ха-ха'}, {k: 'piastri', t: 'Спокойно'}, {k: 'russell', t: 'Молодцы, ребята'},
    {k: 'antonelli', t: 'Мне всего 19!'}, {k: 'alonso', t: 'Мотор как в GP2!'}, {k: 'stroll', t: 'Всё под контролем'},
    {k: 'sainz', t: 'Smooth operator'}, {k: 'albon', t: 'Мяу'}, {k: 'gasly', t: 'ПЬЕР ГАСЛИИИ!'},
    {k: 'ocon', t: 'ЭСТЕБАН ОКОН!'}, {k: 'hulkenberg', t: 'Наконец-то подиум!'}, {k: 'bottas', t: 'Валттери, это Джеймс'},
    {k: 'perez', t: 'Министр обороны'}, {k: 'hadjar', t: 'Не плачу я!'}, {k: 'colapinto', t: '¡Vamos!'},
    {k: 'bortoleto', t: 'Бора!'}, {k: 'lawson', t: 'Подвинься!'}, {k: 'bearman', t: 'Олли в деле'}, {k: 'lindblad', t: 'Я новенький'},
];
const DK_STK_ART = DK_STK.map(s => s.k);   // все 22 нарисованы (24.09)
const DK_STK_ART_V = 3;
// для бота: над чем он смеётся и из-за чего расстраивается
const DK_STK_MOCK = ['norris', 'verstappen', 'albon', 'gasly', 'lawson'];
const DK_STK_SAD = ['hamilton', 'alonso', 'leclerc', 'hadjar', 'bottas'];
const DK_STK_WIN = ['ocon', 'gasly', 'colapinto', 'hulkenberg', 'verstappen'];
const dkStkKey = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]/g, '');
const dkStkRecent = () => gmLS.get('dk_stk_recent', []);

const useDkStickers = () => {
    const [grid] = useGm([gmGrid]);
    return useMemo(() => {
        const photo = {};
        (grid?.[0]?.drivers || []).forEach(d => { photo[dkStkKey(d.last_name)] = {src: d.photo_url, color: d.team_color, name: d.last_name}; });
        return DK_STK.map(s => ({...s, ...(photo[s.k] || {name: s.k}), art: DK_STK_ART.includes(s.k) ? '/static/stickers/' + s.k + '.webp?v=' + DK_STK_ART_V : null}));
    }, [grid]);
};

const DkStkImg = ({s}) => s.art
    ? <img className="art" src={s.art} alt={s.name}/>
    : <div className="ph" style={{'--tc': s.color || '#555'}}>{s.src ? <img src={s.src} alt={s.name}/> : <b>{(s.name || '?')[0]}</b>}</div>;

// всплывает у того, кто отправил, не закрывает стол и исчезает через 2 с
const DkEmote = ({e, list}) => {
    const s = list.find(x => x.k === e.k);
    if (!s) return null;
    return (
        <div className={'dk-emote ' + e.by}>
            <DkStkImg s={s}/>
            <span>{s.t}</span>
        </div>
    );
};

const DkStkTray = ({list, onPick, onClose}) => {
    const recent = dkStkRecent();
    const sorted = [...list].sort((a, b) => {
        const ia = recent.indexOf(a.k), ib = recent.indexOf(b.k);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    return (
        <>
            <div className="dk-tray-bg" onClick={onClose}/>
            <div className="dk-tray">
                {sorted.map(s => (
                    <div key={s.k} className="dk-tray-i" onClick={() => {
                        gmLS.set('dk_stk_recent', [s.k, ...recent.filter(x => x !== s.k)].slice(0, 8));
                        onPick(s.k);
                    }}>
                        <DkStkImg s={s}/>
                        <span>{s.t}</span>
                    </div>
                ))}
            </div>
        </>
    );
};

// Таймер хода в онлайне: 15 с считает сервер, здесь только полоска
const DkTimer = ({t}) => {
    const [left, setLeft] = useState(t.total);
    const warned = useRef(false);
    useEffect(() => {
        warned.current = false;
        let raf;
        const tick = () => {
            const l = Math.max(0, (t.deadline - performance.now()) / 1000);
            setLeft(l);
            if (t.by === 'p' && l < 5 && !warned.current) { warned.current = true; gHaptic('warning'); }
            if (l > 0) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [t.deadline, t.by]);
    const hot = left < 5;
    return (
        <div className={'dk-timer ' + t.by + (hot ? ' hot' : '')}>
            <i style={{transform: 'scaleX(' + (left / t.total) + ')'}}/>
            <span>{t.by === 'p' ? 'Твой ход' : 'Ход соперника'} · {Math.ceil(left)} с</span>
        </div>
    );
};

// ─ Игровой стол: общий для игры с компьютером и онлайн ─
// g: {phase, trump, trumpCard, deckCount, discard, hand, oppCount, table,
//     attacker 'p'|'a', taking 'p'|'a'|null, limit, result}
const DurakBoard = ({g, anim, say, locked, onPlay, onBito, onTake, onDone, onBack, oppName, oppOnline, waitText, over, toast, emote, onEmote, timer}) => {
    const [selId, setSelId] = useState(null);
    const [tray, setTray] = useState(false);
    const stk = useDkStickers();
    const stkAt = useRef(0);
    const sendStk = (k) => {
        setTray(false);
        if (Date.now() - stkAt.current < 2000) { gHaptic('warning'); return; }
        stkAt.current = Date.now(); gHaptic('light'); onEmote(k);
    };
    const drag = useRef(null);

    const undefended = g.table.find(p => !p.def);
    const pTurnAtk = g.attacker === 'p' && (!undefended || g.taking === 'a');
    const pTurnDef = g.attacker === 'a' && !!undefended && !g.taking;
    const playable = (c) => {
        if (g.phase !== 'play' || anim || locked) return false;
        if (pTurnAtk) return dkCanThrow(g, c);
        if (pTurnDef) return dkBeats(undefended.atk.card, c, g.trump);
        return false;
    };
    const hand = dkSort(g.hand, g.trump);
    const handW = Math.min(window.innerWidth, 480) - 64;
    const cw = 84;
    const step = hand.length > 1 ? Math.min(cw * 0.78, (handW - cw) / (hand.length - 1)) : 0;
    const handTotal = cw + step * Math.max(0, hand.length - 1);
    const mid = (hand.length - 1) / 2;
    const selCard = hand.find(c => c.id === selId && playable(c));

    const play = (c) => { setSelId(null); onPlay(c); };
    // тап — поднять карту, второй тап или тап по столу — сходить; можно смахнуть вверх
    const onCardDown = (c, e) => {
        if (!playable(c)) { gHaptic('warning'); return; }
        drag.current = {id: c.id, x0: e.clientX, y0: e.clientY, el: e.currentTarget, moved: false, dx: 0, dy: 0};
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    };
    const onCardMove = (e) => {
        const d = drag.current; if (!d) return;
        d.dx = e.clientX - d.x0; d.dy = e.clientY - d.y0;
        if (!d.moved && Math.hypot(d.dx, d.dy) > 8) d.moved = true;
        if (d.moved) { d.el.style.transition = 'none'; d.el.style.transform = 'translate(' + d.dx + 'px,' + d.dy + 'px) scale(1.1)'; d.el.style.zIndex = 99; }
    };
    const onCardUp = (c) => {
        const d = drag.current; drag.current = null;
        if (!d) return;
        d.el.style.transition = ''; d.el.style.transform = ''; d.el.style.zIndex = '';
        // тап — карта сразу ложится на стол (так быстрее, правка Егора 24.09);
        // смахивание вверх тоже работает
        if (d.moved) { if (d.dy < -60) play(c); return; }
        play(c);
    };

    let msg = '';
    if (g.phase !== 'play') msg = '';
    else if (g.taking === 'a') msg = 'Соперник берёт — можешь подкинуть';
    else if (g.taking === 'p') msg = 'Забираешь карты…';
    else if (pTurnAtk) msg = g.table.length ? 'Подкинь карту или скажи «Бито»' : 'Твой ход — нажми на карту';
    else if (pTurnDef) msg = 'Отбейся старшей картой той же масти или козырем';
    else msg = waitText || 'Соперник думает…';

    return (
        <div className="page-container flex-layout gm-page dk-page dk-table">
            <GameTop title="Дурак" onBack={onBack} right={<span className="dk-trump-chip">Козырь <b className={DK_RED[g.trump] ? 'red' : ''}>{g.trump}</b></span>}/>

            <div className="dk-opp">
                <div className="dk-opp-hand">
                    {Array.from({length: Math.min(12, g.oppCount)}).map((_, i) => <DkCard key={i} back size="s" style={{marginLeft: i ? -30 : 0, '--r': ((i - g.oppCount / 2) * 3) + 'deg'}}/>)}
                </div>
                <div className="dk-opp-cap">
                    {oppOnline !== undefined && <i className={'dk-dot' + (oppOnline ? ' on' : '')}/>}
                    {oppName || 'Соперник'} · {g.oppCount}
                </div>
                {say && <div key={say.k} className="dk-say">{say.text}</div>}
            </div>

            <div className="dk-mid">
                <div className="dk-deck">
                    {g.deckCount > 0 ? <>
                        {/* козырь лежит поперёк под колодой, как в настоящей игре;
                            повёрнут так, что угол с достоинством и мастью смотрит наружу */}
                        <DkCard c={g.trumpCard} size="t" style={{position: 'absolute', left: 14, top: 30, '--r': '90deg'}}/>
                        {g.deckCount > 1 && <DkCard back size="s" style={{position: 'absolute', left: 26, top: 0}}/>}
                        <div className="dk-deck-n">{g.deckCount}</div>
                    </> : <div className={'dk-deck-empty' + (DK_RED[g.trump] ? ' red' : '')}>{g.trump}</div>}
                    {g.discard > 0 && <div className="dk-bito">бито {g.discard}</div>}
                </div>
                <div className={'dk-field' + (anim ? ' ' + anim : '') + (selCard ? ' drop' : '')} onClick={() => { if (selCard) play(selCard); }}>
                    {g.table.map((p, i) => (
                        <div key={p.atk.card.id} className="dk-pair">
                            <DkCard c={p.atk.card} size="t" anim={p.atk.by === 'p' ? 'in-up' : 'in-down'} style={{'--r': ((i % 3) - 1) * 4 + 'deg'}}/>
                            {p.def && <DkCard c={p.def.card} size="t" anim={'def ' + (p.def.by === 'p' ? 'in-up' : 'in-down')} style={{'--r': '14deg'}}/>}
                        </div>
                    ))}
                    {!g.table.length && g.phase === 'play' && <div className="dk-field-empty">{g.attacker === 'p' ? 'Твой ход' : 'Ход соперника'}</div>}
                </div>
            </div>

            {timer && g.phase === 'play' && <DkTimer t={timer}/>}
            <div className="dk-bar">
                {onEmote && <div className="dk-emo-btn" onClick={() => setTray(t => !t)} aria-label="Стикеры">💬</div>}
                <div className="dk-msg">{msg}</div>
                <div className="dk-actions">
                    {!locked && pTurnAtk && !g.taking && g.table.length > 0 && !undefended && <div className="gm-btn sm" onClick={onBito}>Бито</div>}
                    {!locked && pTurnDef && <div className="gm-btn sm ghost" onClick={onTake}>Беру</div>}
                    {!locked && g.taking === 'a' && <div className="gm-btn sm" onClick={onDone}>Забирай</div>}
                </div>
            </div>

            <div className="dk-hand" style={{width: handTotal}}>
                {hand.map((c, i) => {
                    const ok = playable(c);
                    const off = i - mid;
                    return <DkCard key={c.id} c={c} size="m" anim="deal"
                                   dim={!ok && g.phase === 'play' && (pTurnAtk || pTurnDef)}
                                   sel={ok && selId === c.id}
                                   onPointerDown={e => onCardDown(c, e)} onPointerMove={onCardMove}
                                   onPointerUp={() => onCardUp(c)} onPointerCancel={() => onCardUp(c)}
                                   style={{left: i * step, zIndex: i, '--r': (off * Math.min(4, 28 / hand.length)) + 'deg', '--y': (off * off * 1.6) + 'px'}}/>;
                })}
            </div>

            {emote && <DkEmote key={emote.key} e={emote} list={stk}/>}
            {tray && <DkStkTray list={stk} onPick={sendStk} onClose={() => setTray(false)}/>}
            {toast && <div key={toast.k} className="dk-toast">{toast.text}</div>}

            {over && (
                <div className="dk-over">
                    <div className="dk-over-card">
                        <div className="dk-over-emoji">{over.emoji}</div>
                        <div className="gm-result-big small">{over.title}</div>
                        <div className="gm-result-s">{over.text}</div>
                        <div className="gm-row">
                            {over.primary && <div className={'gm-btn' + (over.primary.off ? ' off' : '')} onClick={over.primary.on}>{over.primary.label}</div>}
                            <div className="gm-btn ghost" onClick={over.secondary.on}>{over.secondary.label}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const dkOverText = (result, opts = {}) => ({
    emoji: result === 'win' ? '🏆' : result === 'draw' ? '🤝' : '🃏',
    title: result === 'win' ? 'ПОБЕДА' : result === 'draw' ? 'НИЧЬЯ' : 'ТЫ — ДУРАК',
    text: opts.timeout === 'a' ? 'Соперник трижды не успел походить — победа твоя.'
        : opts.timeout === 'p' ? 'Три хода подряд без ответа — засчитано поражение.'
        : opts.left ? 'Соперник вышел из партии — победа засчитана тебе.'
        : result === 'win' ? 'Скинул все карты первым. Чистый выезд с поула.'
        : result === 'draw' ? 'Оба вышли одновременно — фото-финиш.' : 'Соперник вышел раньше. Реванш?',
});

// ─ Против компьютера ─
const DK_AI_MS = 60;   // сколько бот думает над ходом, мс (на слабом телефоне — столько же, просто считает меньше вариантов)
const dkDraw = (s, who) => {
    const hands = {...s.hands};
    const deck = [...s.deck];
    const h = [...hands[who]];
    let known = s.known;
    while (h.length < 6 && deck.length) {
        const c = deck.shift();
        // открытый козырь под колодой: все видели, кому он достался
        if (known && s.trumpCard && c.id === s.trumpCard.id) known = {...known, [who]: [...known[who], c.id]};
        h.push(c);
    }
    hands[who] = h;
    return {...s, hands, deck, known};
};
const dkOver = (s) => {
    if (s.deck.length) return s;
    const pe = !s.hands.p.length, ae = !s.hands.a.length;
    if (pe && ae) return {...s, phase: 'over', result: 'draw'};
    if (pe) return {...s, phase: 'over', result: 'win'};
    if (ae) return {...s, phase: 'over', result: 'lose'};
    return s;
};

const DurakLocal = ({deckSrc, onExit}) => {
    const [g, setG] = useState(null);
    const [anim, setAnim] = useState(null);
    const [say, setSay] = useState(null);
    const [emote, setEmote] = useState(null);
    const [sent, setSent] = useState(false);
    const gRef = useRef(null);
    const busy = useRef(false);
    const commit = (s) => { gRef.current = s; setG(s); };
    const speak = (text) => setSay({text, k: Date.now()});
    // бот иногда отвечает стикером — чтобы реакции было видно и без друга
    const botStk = (pool, chance = 0.35, delay = 500) => {
        if (Math.random() > chance) return;
        setTimeout(() => setEmote({k: pool[gmRand(pool.length)], by: 'a', key: Date.now()}), delay);
    };
    const myEmote = (k) => {
        setEmote({k, by: 'p', key: Date.now()});
        botStk(DK_STK.map(x => x.k), 0.4, 1600);
    };

    const deal = () => {
        const deck = dkShuffle(deckSrc);
        const hp = deck.splice(0, 6), ha = deck.splice(0, 6);
        const trumpCard = deck[deck.length - 1];
        const trump = trumpCard.suit;
        const low = h => Math.min(...h.filter(c => c.suit === trump).map(c => c.rank), 99);
        const attacker = low(hp) <= low(ha) ? 'p' : 'a';
        setSent(false); setAnim(null); setSay(null); busy.current = false;
        commit({deck, trump, trumpCard, hands: {p: hp, a: ha}, table: [], attacker, phase: 'play', limit: 6, taking: null, discard: 0, result: null,
            gone: [], known: {p: [], a: []}});
        gHaptic('medium');
    };
    useEffect(deal, []);

    const endBout = (kind) => {
        const s = gRef.current;
        if (!s || busy.current) return;
        busy.current = true;
        const defender = s.attacker === 'p' ? 'a' : 'p';
        setAnim(kind === 'bito' ? 'bito' : ('take-' + defender));
        setTimeout(() => {
            let ns = {...gRef.current};
            const cards = ns.table.flatMap(p => p.def ? [p.atk.card, p.def.card] : [p.atk.card]);
            if (kind === 'bito') {
                ns = {...ns, discard: ns.discard + cards.length, gone: [...(ns.gone || []), ...cards], table: []};
                ns = dkDraw(ns, ns.attacker); ns = dkDraw(ns, defender);
                ns.attacker = defender;
            } else {
                const kn = ns.known || {p: [], a: []};
                ns = {...ns, hands: {...ns.hands, [defender]: [...ns.hands[defender], ...cards]}, table: [],
                    known: {...kn, [defender]: [...kn[defender], ...cards.map(c => c.id)]}};
                ns = dkDraw(ns, ns.attacker); ns = dkDraw(ns, defender);
            }
            ns.taking = null; ns.dumped = false; ns.limit = 6;
            ns = dkOver(ns);
            setAnim(null); busy.current = false;
            commit(ns);
        }, 420);
    };

    const afterDefend = (s) => {
        const defender = s.attacker === 'p' ? 'a' : 'p';
        const allDef = s.table.every(p => p.def);
        const cap = s.table.length >= s.limit;
        const noMoreCards = !s.hands[s.attacker].length || !s.hands[defender].length;
        if (allDef && (cap || noMoreCards)) setTimeout(() => endBout('bito'), 450);
    };

    const playerTap = (card) => {
        const s = gRef.current;
        if (!s || s.phase !== 'play' || busy.current || anim) return;
        const undefended = s.table.find(p => !p.def);
        if (s.attacker === 'p') {
            if (undefended && !s.taking) return;
            if (!dkCanThrow(s, card)) { gHaptic('warning'); return; }
            const limit = s.table.length ? s.limit : Math.min(6, s.hands.a.length);
            const ns = {...s, limit, hands: {...s.hands, p: s.hands.p.filter(c => c.id !== card.id)},
                table: [...s.table, {atk: {card, by: 'p'}, def: null}]};
            commit(ns); gHaptic('light');
            if (ns.taking === 'a' && (ns.table.length >= ns.limit || !ns.hands.p.length)) setTimeout(() => endBout('take'), 500);
        } else {
            if (!undefended) return;
            if (!dkBeats(undefended.atk.card, card, s.trump)) { gHaptic('warning'); return; }
            const table = s.table.map(p => p === undefended ? {...p, def: {card, by: 'p'}} : p);
            const ns = {...s, table, hands: {...s.hands, p: s.hands.p.filter(c => c.id !== card.id)}};
            commit(ns); gHaptic('light');
            afterDefend(ns);
        }
    };
    const playerBito = () => { const s = gRef.current; if (s && s.attacker === 'p' && s.table.length && s.table.every(p => p.def)) endBout('bito'); };
    const playerTake = () => {
        const s = gRef.current;
        if (!s || s.attacker !== 'a' || !s.table.some(p => !p.def) || s.taking) return;
        commit({...s, taking: 'p'}); speak('Подкидываю'); gHaptic('medium'); botStk(DK_STK_MOCK, 0.3, 900);
    };
    const playerGiveUp = () => { const s = gRef.current; if (s && s.taking === 'a') endBout('take'); };

    // ход компьютера
    useEffect(() => {
        const s = g;
        if (!s || s.phase !== 'play' || busy.current || anim) return;
        const undefended = s.table.find(p => !p.def);
        let job = null;
        if (s.attacker === 'p' && undefended && !s.taking) {
            job = setTimeout(() => {
                const cur = gRef.current; if (cur !== s || busy.current) return;
                const mv = dkAI.decide(s, 'a', DK_AI_MS);
                const pick = mv.kind === 'defend' ? mv.card : null;
                if (!pick) { commit({...s, taking: 'a'}); speak('Беру'); gHaptic('medium'); botStk(DK_STK_SAD, 0.3, 900); return; }
                const table = s.table.map(p => p === undefended ? {...p, def: {card: pick, by: 'a'}} : p);
                const ns = {...s, table, hands: {...s.hands, a: s.hands.a.filter(c => c.id !== pick.id)}};
                commit(ns); afterDefend(ns);
            }, 750);
        } else if (s.attacker === 'a' && !undefended && s.taking !== 'a') {
            job = setTimeout(() => {
                const cur = gRef.current; if (cur !== s || busy.current) return;
                const mv = dkAI.decide(s, 'a', DK_AI_MS);
                const pick = mv.kind === 'attack' ? mv.card : null;
                if (!pick) { if (s.table.length) { speak('Бито'); endBout('bito'); } return; }
                const limit = s.table.length ? s.limit : Math.min(6, s.hands.p.length);
                commit({...s, limit, hands: {...s.hands, a: s.hands.a.filter(c => c.id !== pick.id)},
                    table: [...s.table, {atk: {card: pick, by: 'a'}, def: null}]});
            }, s.table.length ? 650 : 900);
        } else if (s.attacker === 'a' && s.taking === 'p') {
            job = setTimeout(() => {
                const cur = gRef.current; if (cur !== s || busy.current) return;
                if (s.dumped) { endBout('take'); return; }
                const mv = dkAI.decide(s, 'a', DK_AI_MS);
                const extra = (mv.cards || []).slice(0, Math.max(0, s.limit - s.table.length));
                if (!extra.length) { endBout('take'); return; }
                const ids = new Set(extra.map(c => c.id));
                commit({...s, dumped: true, hands: {...s.hands, a: s.hands.a.filter(c => !ids.has(c.id))},
                    table: [...s.table, ...extra.map(card => ({atk: {card, by: 'a'}, def: null}))]});
            }, s.dumped ? 650 : 700);
        }
        return () => job && clearTimeout(job);
    }, [g, anim]);

    useEffect(() => {
        if (g?.phase === 'over' && !sent) {
            setSent(true);
            gHaptic(g.result === 'win' ? 'success' : 'error');
            gmPostResult({game_type: 'durak', score: g.result === 'win' ? 1 : 0, details: g.result});
            botStk(g.result === 'win' ? DK_STK_SAD : DK_STK_WIN, 0.8, 300);
        }
    }, [g, sent]);
    useEffect(() => { if (!say) return; const t = setTimeout(() => setSay(null), 1400); return () => clearTimeout(t); }, [say]);
    useEffect(() => { if (!emote) return; const t = setTimeout(() => setEmote(null), 2000); return () => clearTimeout(t); }, [emote]);

    if (!g) return null;
    const board = {phase: g.phase, trump: g.trump, trumpCard: g.trumpCard, deckCount: g.deck.length, discard: g.discard,
        hand: g.hands.p, oppCount: g.hands.a.length, table: g.table, attacker: g.attacker, taking: g.taking, limit: g.limit, result: g.result};
    const over = g.phase === 'over' ? {...dkOverText(g.result),
        primary: {label: 'Ещё партию', on: deal}, secondary: {label: 'Выйти', on: onExit}} : null;
    return <DurakBoard g={board} anim={anim} say={say} onPlay={playerTap} onBito={playerBito} onTake={playerTake}
                       onDone={playerGiveUp} onBack={onExit} oppName="F1 Hub" waitText="F1 Hub думает…" over={over}
                       emote={emote} onEmote={myEmote}/>;
};

// ─ Онлайн: комната на сервере ─
const DurakOnline = ({initial, onExit}) => {
    const [v, setV] = useState(initial);
    const [anim, setAnim] = useState(null);
    const [say, setSay] = useState(null);
    const [toast, setToast] = useState(null);
    const [emote, setEmote] = useState(null);
    const [timer, setTimer] = useState(null);
    const [locked, setLocked] = useState(false);
    const [shareMsg, setShareMsg] = useState('');
    const vRef = useRef(initial);
    const alive = useRef(true);
    const code = initial.code;
    const note = (text) => setToast({text, k: Date.now()});

    // новое состояние с сервера: если стол опустел — сначала анимация улёта карт
    const apply = (nv) => {
        const prev = vRef.current;
        if (!nv || (prev && nv.v <= prev.v && nv.phase === prev.phase)) return;
        vRef.current = nv;
        if (nv.turn) setTimer({by: nv.turn.by, total: nv.turn.total, deadline: performance.now() + nv.turn.left * 1000});
        else setTimer(null);
        if (prev && nv.auto && nv.v !== prev.v) note(nv.auto === 'p' ? 'Время вышло — сходили за тебя' : 'Соперник не успел — ход сделан за него');
        // свой стикер уже показан сразу по нажатию — тут только стикеры соперника
        if (prev && nv.emote && nv.emote.by === 'a' && nv.emote.n !== prev.emote?.n) { setEmote({...nv.emote, key: Date.now()}); gHaptic('light'); }
        if (nv.last?.by === 'a') {
            if (nv.last.kind === 'taking') setSay({text: 'Беру', k: Date.now()});
            if (nv.last.kind === 'bito') setSay({text: 'Бито', k: Date.now()});
        }
        if (prev?.phase === 'waiting' && nv.phase === 'play') gHaptic('success');
        if (prev?.table?.length && nv.table && !nv.table.length && (nv.last?.kind === 'bito' || nv.last?.kind === 'take')) {
            setAnim(nv.last.kind === 'bito' ? 'bito' : ('take-' + nv.last.by));
            setTimeout(() => { setAnim(null); setV(vRef.current); }, 420);
        } else {
            setV(nv);
        }
        if (nv.phase === 'play' && prev?.phase === 'play' && nv.attacker !== prev.attacker && nv.attacker === 'p') gHaptic('light');
    };

    // long-poll: сервер держит запрос до 20 с, пока не случится ход
    useEffect(() => {
        alive.current = true;
        (async () => {
            while (alive.current) {
                const cur = vRef.current;
                const r = await dkReq('GET', '/api/durak/rooms/' + code + '?v=' + (cur?.v || 0));
                if (!alive.current) break;
                if (r.ok) apply(r.data);
                else if (r.status === 404) { note('Комната закрыта'); await new Promise(z => setTimeout(z, 3000)); }
                else await new Promise(z => setTimeout(z, 1500));
            }
        })();
        return () => { alive.current = false; };
    }, [code]);

    useEffect(() => { if (!say) return; const t = setTimeout(() => setSay(null), 1400); return () => clearTimeout(t); }, [say]);
    useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 2200); return () => clearTimeout(t); }, [toast]);
    useEffect(() => { if (!emote) return; const t = setTimeout(() => setEmote(null), 2000); return () => clearTimeout(t); }, [emote]);
    // стикер не блокирует ходы: отдельный запрос без locked
    const sendEmote = async (k) => {
        setEmote({k, by: 'p', key: Date.now()});
        const r = await dkReq('POST', '/api/durak/rooms/' + code + '/action', {type: 'emote', card: k});
        if (r.ok) apply(r.data);
    };
    useEffect(() => { if (v.phase === 'over' && v.result) gHaptic(v.result === 'win' ? 'success' : 'error'); }, [v.phase]);

    const send = async (type, card) => {
        if (locked) return;
        setLocked(true);
        const r = await dkReq('POST', '/api/durak/rooms/' + code + '/action', {type, card: card?.id});
        setLocked(false);
        if (r.ok) { apply(r.data); if (type === 'play') gHaptic('light'); }
        else { note(r.error); gHaptic('warning'); }
    };

    const leave = async () => {
        if (v.phase === 'play') {
            const ok = window.confirm('Выйти из партии? Соперник получит победу.');
            if (!ok) return;
        }
        alive.current = false;
        dkReq('POST', '/api/durak/rooms/' + code + '/action', {type: 'leave'});
        onExit();
    };

    const share = async () => {
        const r = await dkShare(code);
        setShareMsg(r === 'copied' ? 'Ссылка скопирована — отправь другу' : r === 'fail' ? 'Отправь другу код комнаты' : '');
    };

    if (v.phase === 'waiting' || v.phase === 'closed') return (
        <div className="page-container fade-in gm-page dk-page">
            <GameTop title="Дурак онлайн" onBack={leave}/>
            <div className="gm-intro">
                <div className="gm-h">Комната создана</div>
                <div className="gm-p">Отправь другу приглашение — он откроет его в Telegram и сразу окажется за столом. Или пусть введёт код в игре.</div>
                <div className="dk-code">{code.split('').map((ch, i) => <span key={i}>{ch}</span>)}</div>
                <div className="gm-row">
                    <div className="gm-btn" onClick={share}>Пригласить друга</div>
                </div>
                {shareMsg && <div className="gm-hint">{shareMsg}</div>}
                <div className="dk-wait"><i/><i/><i/> Ждём соперника</div>
            </div>
            {toast && <div key={toast.k} className="dk-toast">{toast.text}</div>}
        </div>
    );

    const board = {phase: v.phase, trump: v.trump, trumpCard: v.trump_card, deckCount: v.deck_count, discard: v.discard,
        hand: v.hand || [], oppCount: v.opp_count || 0, table: v.table || [], attacker: v.attacker, taking: v.taking,
        limit: v.limit, result: v.result};
    const over = v.phase === 'over' ? {...dkOverText(v.result, {left: v.left, timeout: v.timeout}),
        primary: v.left ? null : {label: v.rematch_me ? (v.rematch_opp ? 'Раздаём…' : 'Ждём соперника…') : (v.rematch_opp ? 'Принять реванш' : 'Реванш'),
            off: v.rematch_me, on: () => send('rematch')},
        secondary: {label: 'Выйти', on: () => { alive.current = false; onExit(); }}} : null;
    return <DurakBoard g={board} anim={anim} say={say} locked={locked} toast={toast}
                       onPlay={c => send('play', c)} onBito={() => send('bito')} onTake={() => send('take')} onDone={() => send('done')}
                       onBack={leave} oppName={v.opp_name || 'Соперник'} oppOnline={v.opp_online}
                       waitText={v.opp_online ? 'Ход соперника…' : 'Соперник не в сети — ждём'} over={over}
                       emote={emote} onEmote={sendEmote} timer={timer}/>;
};

// ─ Меню: с компьютером / с другом / по коду ─
const DurakGame = ({onBack}) => {
    const [deckSrc, setDeckSrc] = useState(null);
    const [err, setErr] = useState(false);
    const [mode, setMode] = useState(null);          // null | 'local' | 'online'
    const [room, setRoom] = useState(null);
    const [joinCode, setJoinCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    const load = () => {
        setErr(false);
        Promise.all([api.get('/api/standings/drivers?season=2026'), api.get('/api/standings/constructors?season=2026')])
            .then(([d, t]) => {
                const drivers = (d?.standings || d || []).slice().sort((a, b) => (+a.position || 99) - (+b.position || 99));
                const teams = (t?.standings || t || []).slice().sort((a, b) => (+a.position || 99) - (+b.position || 99));
                if (drivers.length < 10 || teams.length < 4) { setErr(true); return; }
                const deck = dkBuildDeck(drivers, teams);
                dkPreload(deck);          // фото — заранее, пока человек читает меню
                setDeckSrc(deck);
            });
    };
    useEffect(load, []);

    const create = async () => {
        if (dkNeedLogin()) { setMsg('Игра с другом — после входа'); return; }
        setBusy(true); setMsg('');
        const r = await dkReq('POST', '/api/durak/rooms');
        setBusy(false);
        if (r.ok) { setRoom(r.data); setMode('online'); } else setMsg(r.error);
    };
    const join = async (codeIn) => {
        const c = String(codeIn || joinCode).toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (c.length !== 6) { setMsg('Код комнаты — 6 символов'); return; }
        if (dkNeedLogin()) { setMsg('Чтобы сесть за стол с другом, войдите'); return; }
        setBusy(true); setMsg('');
        const r = await dkReq('POST', '/api/durak/rooms/' + c + '/join');
        setBusy(false);
        if (r.ok) { if (r.data.hand) dkPreload(r.data.hand); setRoom(r.data); setMode('online'); } else setMsg(r.error);
    };
    // пришли по приглашению — сразу садимся за стол
    useEffect(() => { if (DURAK_INVITE && !_dkInviteUsed) { _dkInviteUsed = true; join(DURAK_INVITE); } }, []);

    const exit = () => { setMode(null); setRoom(null); };
    if (mode === 'local' && deckSrc) return <DurakLocal deckSrc={deckSrc} onExit={exit}/>;
    if (mode === 'online' && room) return <DurakOnline initial={room} onExit={exit}/>;

    return (
        <div className="page-container fade-in gm-page dk-page">
            <GameTop title="Дурак" onBack={onBack}/>
            <div className="gm-intro">
                <div className="dk-hero">
                    {deckSrc ? [deckSrc[0], deckSrc[4], deckSrc[8]].map((c, i) => <DkCard key={c.id} c={c} size="l" style={{'--r': (i - 1) * 10 + 'deg', '--x': (i - 1) * 74 + 'px', '--hy': (i === 1 ? 0 : 14) + 'px'}}/>)
                        : <div className="dk-hero-empty">{err ? 'Не удалось загрузить карты' : 'Раздаём…'}</div>}
                </div>
                <div className="gm-h">Подкидной дурак</div>
                <div className="gm-p">36 карт, сила карты — место в чемпионате: <b>тузы</b> — лучшие команды, <b>короли</b> — лидеры личного зачёта, дальше по местам.</div>
                {err ? <div className="gm-btn" onClick={load}>Попробовать ещё раз</div> : (
                    <div className="dk-menu">
                        <div className={'gm-btn' + (deckSrc ? '' : ' off')} onClick={() => setMode('local')}>Играть с компьютером</div>
                        {dkNeedLogin() ? (
                            <div className="dk-login">Игра с другом по коду комнаты — после входа.
                                <a className="gm-btn sm ghost" href={gmHost().loginUrl || '/login'}>Войти</a></div>
                        ) : <>
                        <div className={'gm-btn dk-friend' + (busy ? ' off' : '')} onClick={create}>Играть с другом</div>
                        <div className="dk-join">
                            <input value={joinCode} maxLength={6} placeholder="КОД КОМНАТЫ" autoCapitalize="characters"
                                   onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Za-z0-9]/g, ''))}
                                   onKeyDown={e => { if (e.key === 'Enter') join(); }}/>
                            <div className={'gm-btn sm ghost' + (busy ? ' off' : '')} onClick={() => join()}>Войти</div>
                        </div>
                        </>}
                        {msg && <div className="dk-err">{msg}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};


// ═══════════════════ ИГРЫ-УГАДАЙКИ И СТРАТЕГИЯ (24.09.2026) ═══════════════════
// Пилот дня, карьера, трасса, больше/меньше, пит-уолл, менеджер, квиз.
// История сезонов 1980–2025 — static/games/f1hist.json (build_f1hist.py,
// источник Jolpica); текущий сезон — живой API.

// склонение: plural(3, 'круг', 'круга', 'кругов')
const plural = (n, one, few, many) => { const a = Math.abs(n) % 100, b = a % 10; return a > 10 && a < 20 ? many : b > 1 && b < 5 ? few : b === 1 ? one : many; };

const GM_HIST_URL = '/static/games/f1hist.json?v=20260924';
let _gmHist = null, _gmGrid = null, _gmSched = null;
const gmHist = () => _gmHist || (_gmHist = fetch(GM_HIST_URL).then(r => r.ok ? r.json() : null).catch(() => null)
    .then(x => { if (!x) _gmHist = null; return x; }));
const gmByPos = (a, b) => (+a.position || 99) - (+b.position || 99);
const gmGrid = () => _gmGrid || (_gmGrid = Promise.all([api.get('/api/standings/drivers?season=2026'), api.get('/api/standings/constructors?season=2026')])
    .then(([d, t]) => {
        const drivers = (d?.standings || []).slice().sort(gmByPos);
        const teams = (t?.standings || t || []).slice().sort(gmByPos);
        if (drivers.length < 10) { _gmGrid = null; return null; }
        return {drivers, teams};
    }));
const gmSched = () => _gmSched || (_gmSched = api.get('/api/schedule').then(d => { const r = (d?.races || []).filter(x => x.round); if (!r.length) _gmSched = null; return r.length ? r : null; }));

// загрузка данных игры: undefined — грузится, null — ошибка
const useGm = (loaders) => {
    const [v, setV] = useState(undefined);
    const [k, setK] = useState(0);
    useEffect(() => {
        let alive = true;
        setV(undefined);
        Promise.all(loaders.map(f => f())).then(r => { if (alive) setV(r.some(x => !x) ? null : r); });
        return () => { alive = false; };
    }, [k]);
    return [v, () => setK(x => x + 1)];
};

const GmLoading = ({title, onBack, data, retry}) => (
    <div className="page-container fade-in gm-page">
        <GameTop title={title} onBack={onBack}/>
        <div className="gm-intro">
            {data === null
                ? <><div className="gm-p">Не удалось загрузить данные для игры.</div><div className="gm-btn" onClick={retry}>Попробовать ещё раз</div></>
                : <div className="gm-loading"><i/><i/><i/></div>}
        </div>
    </div>
);

const GM_NAT = {British: 'Великобритания', German: 'Германия', Spanish: 'Испания', French: 'Франция', Italian: 'Италия', Dutch: 'Нидерланды',
    Finnish: 'Финляндия', Brazilian: 'Бразилия', Australian: 'Австралия', Mexican: 'Мексика', Canadian: 'Канада', Monegasque: 'Монако',
    Thai: 'Таиланд', Japanese: 'Япония', Danish: 'Дания', 'New Zealander': 'Новая Зеландия', American: 'США', Argentine: 'Аргентина',
    Austrian: 'Австрия', Belgian: 'Бельгия', Swiss: 'Швейцария', Swedish: 'Швеция', Polish: 'Польша', Russian: 'Россия', Chinese: 'Китай',
    Colombian: 'Колумбия', Venezuelan: 'Венесуэла', Indonesian: 'Индонезия', Indian: 'Индия', Portuguese: 'Португалия', Irish: 'Ирландия',
    Hungarian: 'Венгрия', Czech: 'Чехия', 'South African': 'ЮАР', Chilean: 'Чили', Malaysian: 'Малайзия', Estonian: 'Эстония'};
const gmNat = (n) => GM_NAT[n] || n || '—';
// в узкой клетке длинные названия переносились по буквам
const GM_NAT_SHORT = {British: 'Британия', 'New Zealander': 'Н. Зеландия', Dutch: 'Нидерл.', Venezuelan: 'Венесуэла', Indonesian: 'Индонезия', 'South African': 'ЮАР', American: 'США'};
const gmNatShort = (n) => GM_NAT_SHORT[n] || gmNat(n);
const gmTeam = (n) => String(n || '').replace(/ F1 Team$/, '').replace(/^Scuderia /, '').replace(/ Racing$/, '').trim();
const GM_CITY = {albert_park: 'Мельбурн', shanghai: 'Шанхай', suzuka: 'Судзука', miami: 'Майами', villeneuve: 'Монреаль', monaco: 'Монако',
    catalunya: 'Барселона', red_bull_ring: 'Шпильберг', silverstone: 'Сильверстоун', spa: 'Спа', hungaroring: 'Будапешт', zandvoort: 'Зандвоорт',
    monza: 'Монца', madring: 'Мадрид', baku: 'Баку', sepang: 'Куала-Лумпур', marina_bay: 'Сингапур', americas: 'Остин', rodriguez: 'Мехико',
    interlagos: 'Сан-Паулу', vegas: 'Лас-Вегас', losail: 'Лусаил', yas_marina: 'Абу-Даби', bahrain: 'Сахир', jeddah: 'Джидда', imola: 'Имола'};
const gmRand = (n) => Math.floor(Math.random() * n);
const gmShuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = gmRand(i + 1); [b[i], b[j]] = [b[j], b[i]]; } return b; };
const gmPickN = (a, n, not = []) => gmShuffle(a.filter(x => !not.includes(x))).slice(0, n);
// номер дня по Москве — одна загадка на всех
const gmDay = () => Math.floor((Date.now() + 3 * 3600e3) / 864e5);
const gmUntilTomorrow = () => { const ms = 864e5 - ((Date.now() + 3 * 3600e3) % 864e5); const h = Math.floor(ms / 36e5), m = Math.floor(ms % 36e5 / 6e4); return h + ' ч ' + m + ' мин'; };
const gmLS = {
    get: (k, d) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
    set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
};

// ─ Пилоты: история 1980–2025 + текущий сезон ─
const gmDrivers = (hist, grid) => {
    const by = {};
    Object.entries(hist.seasons).forEach(([y, s]) => s.drivers.forEach(r => {
        const b = hist.drivers[r.id] || {};
        const d = by[r.id] || (by[r.id] = {id: r.id, first: b.first, last: b.last, nat: b.nat, seasons: [], wins: 0, best: 99});
        d.seasons.push({y: +y, teams: r.teams.map(gmTeam)});
        d.wins += r.wins || 0;
        if (r.pos && r.pos < d.best) d.best = r.pos;
        if (r.pos === 1) d.titles = (d.titles || 0) + 1;
    }));
    (grid?.drivers || []).forEach(g => {
        const id = g.ergast_id || ('g' + g.driver_number);
        const d = by[id] || (by[id] = {id, first: g.first_name, last: g.last_name, nat: g.nationality, seasons: [], wins: 0, best: 99});
        d.seasons.push({y: 2026, teams: [gmTeam(g.team)]});
        d.wins += +g.wins || 0;
        d.onGrid = true; d.photo = g.photo_url; d.color = g.team_color; d.num = g.driver_number;
    });
    Object.values(by).forEach(d => {
        d.seasons.sort((a, b) => a.y - b.y);
        d.debut = d.seasons[0]?.y;
        const last = d.seasons[d.seasons.length - 1];
        d.team = last ? last.teams[last.teams.length - 1] : '';
        d.teamsAll = [...new Set(d.seasons.flatMap(s => s.teams))];
        d.name = ((d.first || '') + ' ' + (d.last || '')).trim();
    });
    return by;
};

// Поиск пилота: латиница или кириллица («хэмилтон» найдёт Hamilton)
const GM_TR = {а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p',
    р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya'};
const gmSkel = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .split('').map(ch => GM_TR[ch] ?? ch).join('')
    .replace(/[^a-z]/g, '').replace(/[aeiouy]/g, '').replace(/v/g, 'f').replace(/[cq]/g, 'k').replace(/z/g, 's').replace(/(.)\1+/g, '$1');
const gmMatch = (d, q) => {
    const low = q.trim().toLowerCase();
    if (!low) return false;
    if (d.name.toLowerCase().includes(low)) return true;
    const sq = gmSkel(low);
    return sq.length >= 2 && (gmSkel(d.last).startsWith(sq) || gmSkel(d.name).includes(sq));
};

const gmStats = (key, day, win, tries) => {
    const s = gmLS.get(key, {played: 0, won: 0, streak: 0, best: 0, last: null, dist: {}});
    if (s.last === day) return s;
    s.played += 1;
    if (win) { s.won += 1; s.streak = s.last === day - 1 ? s.streak + 1 : 1; s.best = Math.max(s.best, s.streak); s.dist[tries] = (s.dist[tries] || 0) + 1; }
    else s.streak = 0;
    s.last = day;
    gmLS.set(key, s);
    return s;
};
const GmStatsView = ({s}) => (
    <div className="gm-stats">
        <div><b>{s.played}</b><span>сыграно</span></div>
        <div><b>{s.played ? Math.round(s.won / s.played * 100) : 0}%</b><span>побед</span></div>
        <div><b>{s.streak}</b><span>серия</span></div>
        <div><b>{s.best}</b><span>лучшая</span></div>
    </div>
);

// Поле поиска стоит НАВЕРХУ экрана, подсказки выпадают поверх контента прямо
// под ним — раньше список рос вниз и уезжал под клавиатуру (жалоба Егора 24.09).
const DriverPicker = ({pool, exclude = [], onPick, placeholder = 'Начни вводить фамилию'}) => {
    const [q, setQ] = useState('');
    const list = q.trim().length >= 2 ? pool.filter(d => !exclude.includes(d.id) && gmMatch(d, q))
        .sort((a, b) => (b.onGrid ? 1 : 0) - (a.onGrid ? 1 : 0) || (b.debut || 0) - (a.debut || 0)).slice(0, 7) : [];
    return (
        <div className="gm-pick">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} autoComplete="off" spellCheck="false"
                   onFocus={e => { const sc = e.target.closest('.page-container'); if (sc) sc.scrollTo({top: 0, behavior: 'smooth'}); }}/>
            {q && <div className="gm-pick-x" onClick={() => setQ('')}>✕</div>}
            {list.length > 0 && (
                <div className="gm-pick-list">
                    {list.map(d => (
                        <div key={d.id} className="gm-pick-row" onClick={() => { setQ(''); onPick(d); }}>
                            {d.photo ? <img src={d.photo} alt=""/> : <i style={{background: d.color || '#2a2a36'}}>{(d.last || '?')[0]}</i>}
                            <div><b>{d.name}</b><span>{d.team} · {d.debut}{d.seasons.length > 1 ? '–' + d.seasons[d.seasons.length - 1].y : ''}</span></div>
                        </div>
                    ))}
                </div>
            )}
            {q.trim().length >= 2 && !list.length && <div className="gm-pick-list empty">Не нашли — попробуй латиницей</div>}
        </div>
    );
};

// Переключатель «Загадка дня / Бесконечно»
const GmModes = ({mode, setMode}) => (
    <div className="gm-modes">
        <div className={mode === 'daily' ? 'on' : ''} onClick={() => setMode('daily')}>Загадка дня</div>
        <div className={mode === 'endless' ? 'on' : ''} onClick={() => setMode('endless')}>Бесконечно</div>
    </div>
);

// Случайный ответ без повторов последних 25
const gmNextRandom = (list, key) => {
    const recent = gmLS.get(key, []);
    const fresh = list.filter(d => !recent.includes(d.id));
    const pick = (fresh.length ? fresh : list)[gmRand((fresh.length ? fresh : list).length)];
    gmLS.set(key, [...recent, pick.id].slice(-25));
    return pick.id;
};

// Общая логика «угадай пилота»: день + бесконечный режим
const useGuessGame = ({answers, keyDay, keyStats, keyRecent, salt, gameType}) => {
    const day = gmDay();
    const [mode, setModeRaw] = useState('daily');
    const [endlessId, setEndlessId] = useState(null);
    const [guesses, setGuesses] = useState([]);
    const [stats, setStats] = useState(null);
    const [endless, setEndless] = useState(() => gmLS.get(keyStats + '_endless', {streak: 0, best: 0, played: 0, won: 0}));
    const dailyAnswer = answers.length ? answers[(day * 9301 + salt) % answers.length] : null;
    const answer = mode === 'daily' ? dailyAnswer : answers.find(a => a.id === endlessId);
    useEffect(() => { if (answers.length && mode === 'daily') setGuesses(gmLS.get(keyDay + day, [])); }, [answers.length]);

    const setMode = (m) => {
        setModeRaw(m);
        if (m === 'daily') setGuesses(gmLS.get(keyDay + day, []));
        else { if (!endlessId || guesses.includes(endlessId)) setEndlessId(gmNextRandom(answers, keyRecent)); setGuesses([]); }
    };
    const nextEndless = () => { setEndlessId(gmNextRandom(answers, keyRecent)); setGuesses([]); setModeRaw('endless'); };
    const solved = !!answer && guesses.includes(answer.id);
    const over = solved || guesses.length >= 6;
    const guess = (d) => {
        if (over || !answer) return;
        const g = [...guesses, d.id];
        setGuesses(g);
        const win = d.id === answer.id;
        gHaptic(win ? 'success' : 'light');
        const done = win || g.length >= 6;
        if (mode === 'daily') {
            gmLS.set(keyDay + day, g);
            if (done) {
                setStats(gmStats(keyStats, day, win, g.length));
                if (!gmLS.get(keyDay + day + '_sent', false)) { gmLS.set(keyDay + day + '_sent', true); gmPostResult({game_type: gameType, score: win ? g.length : 7}); }
            }
        } else if (done) {
            const e = {...endless, played: endless.played + 1, won: endless.won + (win ? 1 : 0), streak: win ? endless.streak + 1 : 0};
            e.best = Math.max(e.best, e.streak);
            setEndless(e); gmLS.set(keyStats + '_endless', e);
        }
    };
    const st = stats || gmLS.get(keyStats, null);
    return {mode, setMode, answer, guesses, guess, solved, over, st, endless, nextEndless};
};

// ─────────────── 1. ПИЛОТ ДНЯ ───────────────
const DLE_COLS = [
    {k: 'nat', label: 'Страна'}, {k: 'team', label: 'Команда'}, {k: 'debut', label: 'Дебют'},
    {k: 'wins', label: 'Победы'}, {k: 'best', label: 'Лучшее место'},
];
const dleCell = (g, a, k) => {
    if (k === 'nat') return {t: gmNatShort(g.nat), c: g.nat === a.nat ? 'ok' : ''};
    if (k === 'team') return {t: g.team, c: g.team === a.team ? 'ok' : (a.teamsAll.includes(g.team) ? 'near' : '')};
    const gv = k === 'best' ? (g.best === 99 ? null : g.best) : g[k];
    const av = k === 'best' ? (a.best === 99 ? null : a.best) : a[k];
    if (gv == null || av == null) return {t: gv == null ? '—' : String(gv), c: gv === av ? 'ok' : ''};
    if (gv === av) return {t: String(gv), c: 'ok'};
    const near = k === 'debut' ? Math.abs(gv - av) <= 2 : k === 'wins' ? Math.abs(gv - av) <= 3 : Math.abs(gv - av) <= 2;
    // у «лучшего места» стрелка про таблицу: ↑ — загаданный был выше (число меньше)
    const up = k === 'best' ? av < gv : av > gv;
    return {t: gv + (up ? ' ↑' : ' ↓'), c: near ? 'near' : ''};
};

const GuessResult = ({solved, guesses, answer, mode, st, endless, onNext, onBack, sub, word}) => (
    <div className="gm-result gm-result-top">
        <div className="gm-result-k">{solved ? 'Угадал с ' + guesses.length + '-й попытки' : 'Загадан был'}</div>
        <div className="dle-answer">
            {answer.photo ? <img src={answer.photo} alt=""/> : <i className="dle-ph">{(answer.last || '?')[0]}</i>}
            <div><b>{answer.name}</b><span>{sub}</span></div>
        </div>
        {mode === 'daily' && st && <GmStatsView s={st}/>}
        {mode === 'endless' && <div className="gm-stats three"><div><b>{endless.streak}</b><span>серия</span></div><div><b>{endless.best}</b><span>лучшая</span></div><div><b>{endless.won}</b><span>угадано</span></div></div>}
        {mode === 'daily' && <div className="gm-hint">Новая загадка дня через {gmUntilTomorrow()}</div>}
        <div className="gm-row">
            <div className="gm-btn" onClick={onNext}>{mode === 'daily' ? 'Играть бесконечно' : 'Следующий ' + word}</div>
            <div className="gm-btn ghost" onClick={onBack}>К играм</div>
        </div>
    </div>
);

const DriverDle = ({onBack}) => {
    const [data, retry] = useGm([gmHist, gmGrid]);
    const db = useMemo(() => data ? gmDrivers(data[0], data[1]) : null, [data]);
    const pool = useMemo(() => db ? Object.values(db).filter(d => d.debut >= 1995 || d.onGrid) : [], [db]);
    const answers = useMemo(() => db ? Object.values(db).filter(d => d.onGrid || d.seasons.some(s => s.y >= 2016)).sort((a, b) => a.id.localeCompare(b.id)) : [], [db]);
    const G = useGuessGame({answers, keyDay: 'gm_dle_', keyStats: 'gm_dle_stats', keyRecent: 'gm_dle_recent', salt: 17, gameType: 'f1dle'});
    if (!db || !G.answer) return <GmLoading title="Пилот дня" onBack={onBack} data={data} retry={retry}/>;
    const a = G.answer;
    return (
        <div className="page-container fade-in gm-page">
            <GameTop title="Пилот дня" onBack={onBack} right={<span className="gm-chip">{G.guesses.length} / 6</span>}/>
            <GmModes mode={G.mode} setMode={G.setMode}/>
            {!G.over && <DriverPicker pool={pool} exclude={G.guesses} onPick={G.guess}/>}
            {G.over && <GuessResult {...G} onNext={G.nextEndless} onBack={onBack} word="пилот" sub={a.team + ' · ' + gmNat(a.nat) + ' · побед: ' + a.wins}/>}
            {!G.guesses.length && !G.over && <div className="gm-p left dle-help"><b className="ok-t">Зелёный</b> — совпало, <b className="near-t">жёлтый</b> — близко или пилот ездил за эту команду, стрелка — у загаданного больше или меньше (в «лучшем месте» ↑ — он был выше в таблице).</div>}
            {G.guesses.length > 0 && (
                <div className="dle-grid">
                    <div className="dle-row head">{DLE_COLS.map(c => <div key={c.k}>{c.label}</div>)}</div>
                    {[...G.guesses].reverse().map((id) => { const g = pool.find(x => x.id === id) || db[id]; return (
                        <div key={id} className={'dle-guess' + (id === a.id ? ' win' : '')}>
                            <div className="dle-name">{g.name}</div>
                            <div className="dle-row">
                                {DLE_COLS.map((c, i) => { const cell = dleCell(g, a, c.k); return (
                                    <div key={c.k} className={'dle-cell ' + cell.c} style={{animationDelay: (i * 0.08) + 's'}}>{cell.t}</div>
                                ); })}
                            </div>
                        </div>
                    ); })}
                </div>
            )}
        </div>
    );
};

// ─────────────── 2. КАРЬЕРА ───────────────
const gmStints = (d) => {
    const out = [];
    d.seasons.forEach(s => s.teams.forEach(t => {
        const last = out[out.length - 1];
        if (last && last.team === t && s.y - last.to <= 1) last.to = s.y; else out.push({team: t, from: s.y, to: s.y});
    }));
    return out;
};

const CareerGuess = ({onBack}) => {
    const [data, retry] = useGm([gmHist, gmGrid]);
    const db = useMemo(() => data ? gmDrivers(data[0], data[1]) : null, [data]);
    const pool = useMemo(() => db ? Object.values(db) : [], [db]);
    // загадываем узнаваемых: 5+ сезонов, 2+ команды и победа или место в первой восьмёрке
    const answers = useMemo(() => db ? Object.values(db).filter(d => d.debut >= 1990 && d.seasons.length >= 5 && (d.wins > 0 || d.best <= 8) && new Set(gmStints(d).map(s => s.team)).size >= 2)
        .sort((a, b) => a.id.localeCompare(b.id)) : [], [db]);
    const G = useGuessGame({answers, keyDay: 'gm_car_', keyStats: 'gm_car_stats', keyRecent: 'gm_car_recent', salt: 101, gameType: 'journeyman'});
    if (!db || !G.answer) return <GmLoading title="Карьера" onBack={onBack} data={data} retry={retry}/>;
    const a = G.answer;
    const stints = gmStints(a);
    const shown = G.over ? stints.length : Math.min(stints.length, 1 + G.guesses.length);
    const extra = G.over ? 2 : Math.max(0, 1 + G.guesses.length - stints.length);
    const wrong = G.guesses.filter(id => id !== a.id);
    return (
        <div className="page-container fade-in gm-page">
            <GameTop title="Карьера" onBack={onBack} right={<span className="gm-chip">{G.guesses.length} / 6</span>}/>
            <GmModes mode={G.mode} setMode={G.setMode}/>
            {!G.over && <DriverPicker pool={pool} exclude={G.guesses} onPick={G.guess} placeholder="Чья это карьера? Введи фамилию"/>}
            {G.over && <GuessResult {...G} onNext={G.nextEndless} onBack={onBack} word="пилот" sub={gmNat(a.nat) + ' · ' + a.seasons.length + ' ' + plural(a.seasons.length, 'сезон', 'сезона', 'сезонов') + ' · побед: ' + a.wins}/>}
            {wrong.length > 0 && <div className="car-wrong">{wrong.map(id => <span key={id}>{db[id]?.last}</span>)}</div>}
            <div className="car-list">
                {stints.map((s, i) => (
                    <div key={a.id + i} className={'car-stint' + (i < shown ? ' on' : '')}>
                        <span className="car-y">{i < shown ? (s.from === s.to ? s.from : s.from + '–' + s.to) : '????'}</span>
                        <span className="car-t">{i < shown ? s.team : '• • •'}</span>
                    </div>
                ))}
                {extra >= 1 && <div className="car-hint">Страна: <b>{gmNat(a.nat)}</b></div>}
                {extra >= 2 && !G.over && <div className="car-hint">Фамилия начинается на <b>{(a.last || '?')[0]}</b></div>}
            </div>
            {!G.guesses.length && !G.over && <div className="gm-p left">Каждая ошибка открывает следующую команду в карьере.</div>}
        </div>
    );
};

// ─────────────── 3. УГАДАЙ ТРАССУ ───────────────
const gmTrackPath = (pts) => {
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const pad = Math.max(x1 - x0, y1 - y0) * 0.07;
    return {vb: (x0 - pad) + ' ' + (y0 - pad) + ' ' + (x1 - x0 + pad * 2) + ' ' + (y1 - y0 + pad * 2),
        d: pts.map((p, i) => (i ? 'L' : 'M') + p.x.toFixed(1) + ' ' + p.y.toFixed(1)).join(' ') + ' Z',
        w: (Math.max(x1 - x0, y1 - y0) / 34).toFixed(1)};
};
const CIRCUIT_ROUNDS = 10, CIRCUIT_TIME = 10;

const CircuitGuess = ({onBack}) => {
    const [sched, retry] = useGm([gmSched]);
    const [tracks, setTracks] = useState(null);
    const [phase, setPhase] = useState('intro');
    const [qs, setQs] = useState([]);
    const [i, setI] = useState(0);
    const [pick, setPick] = useState(null);
    const [score, setScore] = useState(0);
    const [right, setRight] = useState(0);
    const [left, setLeft] = useState(CIRCUIT_TIME);
    const [boardKey, setBoardKey] = useState(0);
    const t0 = useRef(0);
    const lock = useRef(false);

    useEffect(() => {
        if (!sched) return;
        const races = sched[0].filter(r => GM_CITY[r.circuit_id]);
        Promise.all(races.map(r => api.get('/api/circuit/' + r.circuit_id + '/track').then(t => (t?.points?.length > 10 ? {id: r.circuit_id, city: GM_CITY[r.circuit_id], gp: r.name, path: gmTrackPath(t.points)} : null))))
            .then(l => setTracks(l.filter(Boolean)));
    }, [sched]);

    const next = (sc, rt, idx) => {
        if (idx + 1 >= qs.length) {
            setPhase('done');
            gmPostResult({game_type: 'circuit', score: sc}).then(() => setBoardKey(k => k + 1));
            return;
        }
        setI(idx + 1); setPick(null); lock.current = false; t0.current = performance.now(); setLeft(CIRCUIT_TIME);
    };
    const answer = (opt) => {
        if (lock.current || phase !== 'play') return;
        lock.current = true;
        const q = qs[i];
        const ok = opt === q.t.city;
        const secs = Math.max(0, CIRCUIT_TIME - (performance.now() - t0.current) / 1000);
        const sc = score + (ok ? 100 + Math.round(secs * 10) : 0);
        setPick(opt || '—'); setScore(sc); if (ok) setRight(r => r + 1);
        gHaptic(ok ? 'success' : 'error');
        setTimeout(() => next(sc, right + (ok ? 1 : 0), i), 950);
    };
    // таймер вопроса
    useEffect(() => {
        if (phase !== 'play') return;
        let raf;
        const tick = () => {
            const l = CIRCUIT_TIME - (performance.now() - t0.current) / 1000;
            setLeft(Math.max(0, l));
            if (l <= 0 && !lock.current) { answer(null); return; }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [phase, i]);

    const start = () => {
        const pool = gmShuffle(tracks).slice(0, Math.min(CIRCUIT_ROUNDS, tracks.length));
        const cities = tracks.map(t => t.city);
        setQs(pool.map(t => ({t, opts: gmShuffle([t.city, ...gmPickN(cities, 3, [t.city])])})));
        setI(0); setPick(null); setScore(0); setRight(0); lock.current = false;
        t0.current = performance.now(); setLeft(CIRCUIT_TIME); setPhase('play');
    };

    if (!sched || !tracks) return <GmLoading title="Угадай трассу" onBack={onBack} data={sched === null ? null : undefined} retry={retry}/>;
    const q = qs[i];
    return (
        <div className="page-container fade-in gm-page">
            <GameTop title="Угадай трассу" onBack={onBack} right={phase === 'play' ? <span className="gm-chip">{i + 1} / {qs.length}</span> : null}/>
            {phase === 'intro' && (
                <div className="gm-intro">
                    <div className="trk-hero"><svg viewBox={tracks[0].path.vb}><path d={tracks[0].path.d} strokeWidth={tracks[0].path.w} className="trk-line draw" pathLength="1"/></svg></div>
                    <div className="gm-h">Узнай по силуэту</div>
                    <div className="gm-p">{CIRCUIT_ROUNDS} трасс календаря, на каждую — {CIRCUIT_TIME} секунд. Чем быстрее ответ, тем больше очков.</div>
                    <div className="gm-btn" onClick={start}>Старт</div>
                    <GameBoard type="circuit" fmt={s => s + ' очк.'} refreshKey={boardKey}/>
                </div>
            )}
            {phase === 'play' && q && (
                <div className="trk-play">
                    <div className="trk-timer"><i style={{transform: 'scaleX(' + (left / CIRCUIT_TIME) + ')'}} className={left < 3 ? 'hot' : ''}/></div>
                    <div className="trk-score">{score}<span>очков</span></div>
                    <div className="trk-box" key={i}><svg viewBox={q.t.path.vb}><path d={q.t.path.d} strokeWidth={q.t.path.w} className="trk-line draw" pathLength="1"/></svg></div>
                    <div className="trk-opts">
                        {q.opts.map(o => (
                            <div key={o} className={'trk-opt' + (pick ? (o === q.t.city ? ' ok' : o === pick ? ' bad' : ' dim') : '')} onClick={() => answer(o)}>{o}</div>
                        ))}
                    </div>
                    {pick && <div className="gm-hint">{q.t.gp}</div>}
                </div>
            )}
            {phase === 'done' && (
                <div className="gm-result">
                    <div className="gm-result-k">Угадано {right} из {qs.length}</div>
                    <div className="gm-result-big">{score}<span>очков</span></div>
                    <div className="gm-result-t">{right >= 9 ? 'Ходячий атлас F1' : right >= 6 ? 'Знаешь календарь' : right >= 3 ? 'Неплохо для начала' : 'Пора пересмотреть гонки'}</div>
                    <div className="gm-row"><div className="gm-btn" onClick={start}>Ещё раз</div><div className="gm-btn ghost" onClick={onBack}>К играм</div></div>
                    <GameBoard type="circuit" fmt={s => s + ' очк.'} refreshKey={boardKey}/>
                </div>
            )}
        </div>
    );
};

// ─────────────── 4. БОЛЬШЕ ИЛИ МЕНЬШЕ ───────────────
// 24.09: пары подбираются по близости значений (Егору попадался «Хэмилтон
// против новичка»). С каждым верным ответом разница сужается. Кроме текущей
// решётки — сезоны 1980–2025: очки и победы соперников в одном чемпионате.
const gmCareer = (c) => { if (typeof c === 'string') { try { return JSON.parse(c.replace(/'/g, '"')); } catch (e) { return {}; } } return c || {}; };
// окно относительной разницы |a-b|/max по длине серии
const hlWindow = (s) => s < 3 ? [0.25, 0.6] : s < 6 ? [0.12, 0.4] : s < 10 ? [0.05, 0.25] : [0.01, 0.15];

const hlSets = (db, hist, careers) => {
    const sets = [];
    const all = Object.values(db);
    const card = (d, extra = {}) => ({id: d.id, name: d.name, last: d.last, team: d.team, color: d.color, photo: d.photo, ...extra});
    sets.push({w: 3, label: 'побед в карьере', items: all.filter(d => d.wins > 0).map(d => card(d, {v: d.wins}))});
    sets.push({w: 2, label: 'сезонов в F1', items: all.filter(d => d.seasons.length >= 3 && d.debut >= 1990).map(d => card(d, {v: d.seasons.length}))});
    const cs = Object.values(careers);
    if (cs.length) {
        sets.push({w: 2, label: 'подиумов в карьере', items: cs.filter(c => c.podiums > 0).map(c => card(db[c.id] || c, {v: c.podiums}))});
        sets.push({w: 2, label: 'поулов в карьере', items: cs.filter(c => c.poles > 0).map(c => card(db[c.id] || c, {v: c.poles}))});
        sets.push({w: 1, label: 'стартов в F1', items: cs.filter(c => c.races > 0).map(c => card(db[c.id] || c, {v: c.races}))});
    }
    // сезонные: оба пилота из одного чемпионата
    Object.entries(hist.seasons).forEach(([y, s]) => {
        if (+y < 1990) return;
        const top = s.drivers.filter(r => r.pts > 0 && r.pos && r.pos <= 16);
        const mk = (r, v) => { const d = db[r.id]; return d && card(d, {v, team: gmTeam(r.teams[r.teams.length - 1]), color: null, photo: null}); };
        sets.push({w: 0.25, label: 'очков в сезоне ' + y, items: top.map(r => mk(r, Math.round(r.pts))).filter(Boolean)});
        const wn = s.drivers.filter(r => r.wins > 0);
        if (wn.length >= 3) sets.push({w: 0.12, label: 'побед в сезоне ' + y, items: wn.map(r => mk(r, r.wins)).filter(Boolean)});
    });
    return sets.filter(s => s.items.length >= 4);
};

const hlPair = (sets, streak, recent) => {
    const tot = sets.reduce((s, x) => s + x.w, 0);
    const [lo, hi] = hlWindow(streak);
    for (let tries = 0; tries < 40; tries++) {
        let r = Math.random() * tot, set = sets[0];
        for (const s of sets) { r -= s.w; if (r <= 0) { set = s; break; } }
        const a = set.items[gmRand(set.items.length)];
        const rel = (b) => Math.abs(a.v - b.v) / Math.max(a.v, b.v, 1);
        const cand = set.items.filter(b => b.id !== a.id && b.v !== a.v && rel(b) >= lo && rel(b) <= hi && !recent.includes(a.id + b.id));
        if (!cand.length) continue;
        const b = cand[gmRand(cand.length)];
        return {set, a, b};
    }
    return null;
};

const HLPhoto = ({c}) => c.photo ? <img src={c.photo} alt=""/> : <div className="hl-mono">{(c.last || c.name || '?').slice(0, 3).toUpperCase()}</div>;

const HigherLower = ({onBack}) => {
    const [data, retry] = useGm([gmHist, gmGrid]);
    const [sets, setSets] = useState(null);
    const [phase, setPhase] = useState('intro');
    const [q, setQ] = useState(null);
    const [streak, setStreak] = useState(0);
    const [shown, setShown] = useState(null);
    const [boardKey, setBoardKey] = useState(0);
    const recent = useRef([]);

    useEffect(() => {
        if (!data) return;
        const [hist, grid] = data;
        const db = gmDrivers(hist, grid);
        const ids = grid.drivers.map(d => d.ergast_id).filter(Boolean);
        api.get('/api/drivers/career/batch?ids=' + ids.join(',')).then(b => {
            const careers = {};
            grid.drivers.forEach(d => {
                const c = gmCareer(b?.drivers?.[d.ergast_id]?.career);
                if (!d.ergast_id) return;
                careers[d.ergast_id] = {id: d.ergast_id, name: d.name, last: d.last_name, team: d.team, color: d.team_color, photo: d.photo_url,
                    podiums: +c.podiums || 0, poles: +c.poles || 0, races: +c.races || 0};
            });
            setSets(hlSets(db, hist, careers));
        });
    }, [data]);

    const next = (s) => {
        const p = hlPair(sets, s, recent.current);
        if (!p) return;
        recent.current = [...recent.current, p.a.id + p.b.id, p.b.id + p.a.id].slice(-60);
        setQ(p); setShown(null);
    };
    const start = () => { setStreak(0); recent.current = []; next(0); setPhase('guess'); };
    const choose = (dir) => {
        if (phase !== 'guess') return;
        const {a, b} = q;
        const ok = dir === 'up' ? b.v > a.v : b.v < a.v;
        setPhase('reveal');
        const s0 = performance.now();
        const tick = (n) => { const k = Math.min(1, (n - s0) / 650); setShown(Math.round(b.v * (1 - Math.pow(1 - k, 3)))); if (k < 1) requestAnimationFrame(tick); };
        requestAnimationFrame(tick);
        setTimeout(() => {
            if (ok) { gHaptic('success'); const s = streak + 1; setStreak(s); next(s); setPhase('guess'); }
            else {
                gHaptic('error'); setPhase('over');
                gmPostResult({game_type: 'higher_lower', score: streak}).then(() => setBoardKey(k => k + 1));
            }
        }, 1400);
    };

    if (!data || !sets) return <GmLoading title="Больше/меньше" onBack={onBack} data={data === null ? null : undefined} retry={retry}/>;
    const Card = ({c, val, hidden}) => (
        <div className="hl-card" style={{'--tc': c.color || '#3a3a4a'}}>
            <HLPhoto c={c}/>
            <div className="hl-tx">
                <div className="hl-name">{c.last}</div>
                <div className="hl-team">{c.team}</div>
                <div className="hl-val">{hidden ? '?' : val}</div>
                <div className="hl-lab">{q.set.label}</div>
            </div>
        </div>
    );
    const [lo, hi] = hlWindow(streak);
    return (
        <div className="page-container fade-in gm-page">
            <GameTop title="Больше/меньше" onBack={onBack} right={phase !== 'intro' ? <span className="gm-chip">Серия {streak}</span> : null}/>
            {phase === 'intro' && (
                <div className="gm-intro">
                    <div className="gm-h">У кого больше?</div>
                    <div className="gm-p">Пилоты с 1990 года и текущая решётка: победы, подиумы, поулы, очки за сезон. Соперники подобраны по уровню, и с каждым верным ответом разница всё меньше.</div>
                    <div className="gm-btn" onClick={start}>Начать</div>
                    <GameBoard type="higher_lower" fmt={s => 'серия ' + s} refreshKey={boardKey}/>
                </div>
            )}
            {(phase === 'guess' || phase === 'reveal') && q && (
                <div className="hl-play">
                    <div className="hl-q">У кого больше <b>{q.set.label}</b>?</div>
                    <Card c={q.a} val={q.a.v}/>
                    <div className="hl-vs">VS</div>
                    <Card c={q.b} val={shown ?? q.b.v} hidden={phase === 'guess'}/>
                    {phase === 'guess' && (
                        <div className="hl-btns">
                            <div className="gm-btn" onClick={() => choose('up')}>Больше ▲</div>
                            <div className="gm-btn ghost" onClick={() => choose('down')}>Меньше ▼</div>
                        </div>
                    )}
                    <div className="hl-diff">Сложность: разница {Math.round(lo * 100)}–{Math.round(hi * 100)}%</div>
                </div>
            )}
            {phase === 'over' && q && (
                <div className="gm-result">
                    <div className="gm-result-k">Серия</div>
                    <div className="gm-result-big">{streak}</div>
                    <div className="gm-result-s">{q.b.last}: {q.b.v}, а у {q.a.last} — {q.a.v} ({q.set.label}).</div>
                    <div className="gm-row"><div className="gm-btn" onClick={start}>Ещё раз</div><div className="gm-btn ghost" onClick={onBack}>К играм</div></div>
                    <GameBoard type="higher_lower" fmt={s => 'серия ' + s} refreshKey={boardKey}/>
                </div>
            )}
        </div>
    );
};

// ─────────────── 5. ПИТ-УОЛЛ: стратегия гонки ───────────────
// Упрощённая модель: время круга = база + класс пилота + состав шин + износ,
// после «обрыва» шины сильно теряют. Пит-стоп −20 с, под сейфти-каром −10 с.
// Обязательны два разных состава, иначе +10 с.
// 24.09: этап выбирается из календаря, стартовая позиция — жребий, круг 0.9 с,
// на сейфти-каре и в меню пит-стопа гонка встаёт на паузу (Егор не успевал).
const STR_TYRES = {
    S: {name: 'Soft', c: '#FF2A2A', d: -0.75, deg: 0.085, life: 15},
    M: {name: 'Medium', c: '#FFD21F', d: 0, deg: 0.05, life: 25},
    H: {name: 'Hard', c: '#F2F2F2', d: 0.45, deg: 0.03, life: 38},
};
const STR_PTS = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
const STR_LAP_MS = 900;
const gmNoise = (a) => (Math.random() * 2 - 1) * a;

const StrategyGame = ({onBack}) => {
    const [data, retry] = useGm([gmSched, gmGrid]);
    const [phase, setPhase] = useState('intro');       // intro | race | done
    const [round, setRound] = useState(null);
    const [gridPos, setGridPos] = useState(() => 4 + gmRand(11));
    const [view, setView] = useState(null);
    const [pitOpen, setPitOpen] = useState(false);
    const [scAsk, setScAsk] = useState(false);
    const [paused, setPaused] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [boardKey, setBoardKey] = useState(0);
    const sim = useRef(null);
    const timer = useRef(null);

    const races = data ? data[0] : null;
    const selIdx = races ? (round != null ? races.findIndex(r => r.round === round) : Math.max(0, races.findIndex(x => x.is_next))) : 0;
    const gp = useMemo(() => {
        if (!races) return null;
        const r = races[selIdx] || races[0];
        return {name: r.name, city: GM_CITY[r.circuit_id] || r.locality, laps: Math.min(60, +r.laps || 55), round: r.round};
    }, [races, selIdx]);

    const snap = () => {
        const s = sim.current;
        const sorted = [...s.cars].sort((a, b) => a.total - b.total);
        return {lap: s.lap, laps: s.laps, sc: s.sc, rows: sorted.map((c, i) => ({...c, pos: i + 1, gap: i ? c.total - sorted[i - 1].total : 0})), log: s.log.slice(-2)};
    };

    const start = (tyre) => {
        const ds = data[1].drivers.slice(0, 21);
        const maxPts = Math.max(...ds.map(d => +d.points || 0), 1);
        const laps = gp.laps, sc = laps / 57;
        const cars = ds.map(d => {
            const st = Math.random() < 0.3 ? 'S' : Math.random() < 0.7 ? 'M' : 'H';
            const next = st === 'H' ? 'M' : 'H';
            const pitAt = Math.round(STR_TYRES[st].life * sc * (0.85 + Math.random() * 0.3));
            return {id: d.ergast_id || String(d.driver_number), name: d.last_name, color: d.team_color, skill: (1 - (+d.points || 0) / maxPts) * 1.5,
                tyre: st, age: 0, used: [st], plan: [{lap: pitAt, tyre: next}], total: 0, me: false};
        });
        // класс игрока — как у соседей по решётке, чтобы стартовая позиция имела смысл
        const order = cars.sort((a, b) => a.skill + gmNoise(0.3) - (b.skill + gmNoise(0.3)));
        const nb = order.slice(Math.max(0, gridPos - 2), gridPos + 1);
        const me = {id: 'me', name: 'ТЫ', color: '#E10600', skill: nb.reduce((s, c) => s + c.skill, 0) / nb.length - 0.1, tyre, age: 0, used: [tyre], plan: [], total: 0, me: true};
        order.splice(gridPos - 1, 0, me);
        order.forEach((c, i) => { c.total = i * 0.4; });
        // 0–2 сейфти-кара за гонку
        const scLaps = [];
        if (Math.random() < 0.6) scLaps.push(Math.round(laps * (0.2 + Math.random() * 0.3)));
        if (Math.random() < 0.3) scLaps.push(Math.round(laps * (0.55 + Math.random() * 0.3)));
        sim.current = {cars: order, lap: 0, laps, sc: 0, scLaps, lifeScale: sc, pitReq: null, log: ['Старт с P' + gridPos + ' на ' + STR_TYRES[tyre].name]};
        setPhase('race'); setPitOpen(false); setScAsk(false); setPaused(false);
        setView(snap());
    };

    const lapStep = () => {
        const s = sim.current;
        if (!s || s.lap >= s.laps) return;
        s.lap += 1;
        let newSC = false;
        if (s.scLaps.includes(s.lap)) { s.sc = 5; newSC = true; s.log.push('Круг ' + s.lap + ': сейфти-кар на трассе'); gHaptic('warning'); }
        const underSC = s.sc > 0;
        const meBefore = [...s.cars].sort((a, b) => a.total - b.total).findIndex(c => c.me) + 1;
        let iPitted = null;
        s.cars.forEach(c => {
            const t = STR_TYRES[c.tyre];
            const life = t.life * s.lifeScale;
            let lt = 95 + c.skill + t.d + t.deg * c.age + (c.age > life ? (c.age - life) * 0.4 : 0) + gmNoise(0.2);
            if (underSC) lt = 125;
            let pitTo = null;
            if (c.me && s.pitReq) { pitTo = s.pitReq; s.pitReq = null; }
            if (!c.me) {
                if (c.plan.length && c.plan[0].lap <= s.lap) pitTo = c.plan.shift().tyre;
                else if (underSC && c.age > life * 0.45 && c.used.length < 2) { pitTo = c.tyre === 'H' ? 'M' : 'H'; c.plan = []; }
            }
            if (pitTo) {
                lt += underSC ? 10 : 20;
                c.tyre = pitTo; c.age = 0; if (!c.used.includes(pitTo)) c.used.push(pitTo);
                if (c.me) iPitted = pitTo;
            } else c.age += 1;
            c.total += lt;
        });
        if (underSC) {
            const sorted = [...s.cars].sort((a, b) => a.total - b.total);
            sorted.forEach((c, i) => { c.total = sorted[0].total + i * 0.6; });
            s.sc -= 1;
            if (!s.sc) s.log.push('Круг ' + s.lap + ': рестарт!');
        }
        if (iPitted) {
            const after = [...s.cars].sort((a, b) => a.total - b.total).findIndex(c => c.me) + 1;
            s.log.push('Круг ' + s.lap + ': пит-стоп, ' + STR_TYRES[iPitted].name + '. P' + meBefore + ' → P' + after + (after > meBefore ? ' — на свежих шинах отыграешь' : ''));
        }
        if (s.lap >= s.laps) { finish(); return; }
        setView(snap());
        // решение под сейфти-каром — гонка ждёт игрока
        if (newSC) { clearInterval(timer.current); setScAsk(true); }
    };

    const finish = () => {
        clearInterval(timer.current);
        const s = sim.current;
        const me = s.cars.find(c => c.me);
        let pen = 0;
        if (me.used.length < 2) { pen = 10; me.total += 10; }
        const v = snap();
        const pos = v.rows.find(r => r.me).pos;
        setView({...v, pen, pos, start: gridPos});
        setPhase('done');
        gHaptic(pos <= 3 ? 'success' : 'medium');
        gmPostResult({game_type: 'strategy', score: pos}).then(() => setBoardKey(k => k + 1));
    };

    const running = phase === 'race' && !pitOpen && !scAsk && !paused;
    useEffect(() => {
        if (!running) return;
        timer.current = setInterval(lapStep, speed === 2 ? STR_LAP_MS / 2 : STR_LAP_MS);
        return () => clearInterval(timer.current);
    }, [running, speed]);
    useEffect(() => () => clearInterval(timer.current), []);

    const pit = (t) => { if (sim.current) { sim.current.pitReq = t; setPitOpen(false); setScAsk(false); gHaptic('medium'); } };
    // где выедешь после пит-стопа: +20 с (под SC +10) к своему времени
    const predict = () => {
        const s = sim.current; if (!s) return null;
        const me = s.cars.find(c => c.me);
        const loss = s.sc > 0 ? 10 : 20;
        return 1 + s.cars.filter(c => !c.me && c.total < me.total + loss).length;
    };
    const newGame = () => { setGridPos(4 + gmRand(11)); setPhase('intro'); };

    if (!gp) return <GmLoading title="Пит-уолл" onBack={onBack} data={data} retry={retry}/>;
    const me = view?.rows?.find(r => r.me);
    const meIdx = view?.rows ? view.rows.findIndex(r => r.me) : 0;
    const tower = view?.rows ? view.rows.filter((r, i) => i < 3 || Math.abs(i - meIdx) <= 2) : [];
    const life = me ? STR_TYRES[me.tyre].life * (sim.current?.lifeScale || 1) : 1;
    const TyrePick = ({onPick}) => (
        <div className="str-tyres small">{Object.entries(STR_TYRES).map(([k, t]) => (
            <div key={k} className={'str-tyre' + (me && me.tyre === k ? ' cur' : '')} onClick={() => onPick(k)}><i style={{'--tc': t.c}}/><b>{t.name}</b></div>
        ))}</div>
    );

    return (
        <div className="page-container fade-in gm-page">
            <GameTop title="Пит-уолл" onBack={() => { clearInterval(timer.current); onBack(); }}/>
            {phase === 'intro' && (
                <div className="gm-intro">
                    <div className="str-pick-h">Этап</div>
                    <div className="str-gps">
                        {races.map((r, i) => (
                            <div key={r.round} className={'str-gp' + (i === selIdx ? ' on' : '') + (r.is_next ? ' next' : '')} onClick={() => setRound(r.round)}
                                 ref={i === selIdx ? (el => { if (el && !el.dataset.sc) { el.dataset.sc = 1; el.scrollIntoView({inline: 'center', block: 'nearest'}); } }) : undefined}>
                                <span>{r.is_next ? 'Следующий' : 'Этап ' + r.round}</span><b>{GM_CITY[r.circuit_id] || r.locality}</b>
                            </div>
                        ))}
                    </div>
                    <div className="gm-h">{gp.name}</div>
                    <div className="str-grid-pos"><span>Квалификация</span><b>P{gridPos}</b><em onClick={() => setGridPos(4 + gmRand(11))}>перебросить</em></div>
                    <div className="gm-p">{gp.laps} {plural(gp.laps, 'круг', 'круга', 'кругов')}. Ты решаешь, когда ехать в боксы и какие шины ставить. Пит-стоп стоит ~20 с — сначала потеряешь места, потом отыграешь их на свежих шинах. На сейфти-каре гонка встанет на паузу: там пит-стоп в два раза дешевле. Нужны <b>два разных состава</b>, иначе +10 с.</div>
                    <div className="str-pick-h">Шины на старт</div>
                    <div className="str-tyres">
                        {Object.entries(STR_TYRES).map(([k, t]) => (
                            <div key={k} className="str-tyre" onClick={() => start(k)}>
                                <i style={{'--tc': t.c}}/><b>{t.name}</b>
                                <span>{k === 'S' ? 'быстрые, живут мало' : k === 'M' ? 'золотая середина' : 'медленные, но долго'}</span>
                            </div>
                        ))}
                    </div>
                    <GameBoard type="strategy" fmt={p => 'P' + p} refreshKey={boardKey}/>
                </div>
            )}
            {phase === 'race' && view && me && (
                <div className="str-race">
                    <div className="str-top">
                        <div className="str-lap">Круг <b>{view.lap}</b> / {view.laps}</div>
                        {view.sc > 0 && <div className="str-sc">SAFETY CAR · {view.sc}</div>}
                        <div className="str-speed" onClick={() => setPaused(p => !p)}>{paused ? '▶' : '❚❚'}</div>
                        <div className="str-speed" onClick={() => setSpeed(s => s === 1 ? 2 : 1)}>×{speed}</div>
                    </div>
                    <div className="str-tower">
                        {tower.map(r => (
                            <div key={r.id} className={'str-row' + (r.me ? ' me' : '')}>
                                <span className="p">{r.pos}</span><i style={{background: r.color}}/><span className="n">{r.name}</span>
                                <span className="ty" style={{'--tc': STR_TYRES[r.tyre].c}}>{r.tyre}</span>
                                <span className="g">{r.pos === 1 ? 'Лидер' : '+' + r.gap.toFixed(1)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="str-me">
                        <div className="str-me-row"><span>Шины</span><b style={{color: STR_TYRES[me.tyre].c}}>{STR_TYRES[me.tyre].name}</b><span>{me.age} {plural(me.age, 'круг', 'круга', 'кругов')}</span></div>
                        <div className="str-wear"><i style={{width: Math.min(100, me.age / life * 100) + '%'}} className={me.age > life ? 'dead' : me.age > life * 0.8 ? 'warn' : ''}/></div>
                        <div className="str-hint">{me.age > life ? 'Шины «кончились» — теряешь по полсекунды и больше с каждым кругом' : me.used.length < 2 ? 'Ещё не было пит-стопа: нужен второй состав' : ' '}</div>
                    </div>
                    {view.log.map((l, i) => <div key={i} className="str-log">{l}</div>)}
                    {scAsk ? (
                        <div className="str-ask sc">
                            <div className="str-ask-h">🟡 Сейфти-кар! Гонка на паузе</div>
                            <div className="str-ask-p">Пит-стоп сейчас стоит ~10 с вместо 20. Выедешь примерно на <b>P{predict()}</b>.</div>
                            <TyrePick onPick={pit}/>
                            <div className="gm-btn ghost" onClick={() => setScAsk(false)}>Остаёмся на трассе</div>
                        </div>
                    ) : pitOpen ? (
                        <div className="str-ask">
                            <div className="str-ask-h">Пит-стоп на этом круге</div>
                            <div className="str-ask-p">Потеряешь ~{view.sc > 0 ? 10 : 20} с и выедешь примерно на <b>P{predict()}</b>. Какие шины?</div>
                            <TyrePick onPick={pit}/>
                            <div className="gm-btn ghost" onClick={() => setPitOpen(false)}>Отмена</div>
                        </div>
                    ) : (
                        <div className={'pit2-gun' + (sim.current?.pitReq ? ' off' : '') + (me.age > life * 0.9 ? ' hot' : '')} onClick={() => !sim.current?.pitReq && setPitOpen(true)}>
                            {sim.current?.pitReq ? 'Заезжаем на этом круге…' : 'В БОКСЫ'}
                        </div>
                    )}
                </div>
            )}
            {phase === 'done' && view && (
                <div className="gm-result">
                    <div className="gm-result-k">{gp.city} · финиш</div>
                    <div className="gm-result-big">P{view.pos}</div>
                    <div className="gm-result-t">{view.pos === 1 ? 'Победа стратегией!' : view.pos <= 3 ? 'Подиум' : view.pos <= 10 ? '+' + STR_PTS[view.pos - 1] + ' ' + plural(STR_PTS[view.pos - 1], 'очко', 'очка', 'очков') : 'Без очков'}</div>
                    <div className="gm-result-s">Старт P{view.start} → финиш P{view.pos}{view.start > view.pos ? ' · +' + (view.start - view.pos) + ' ' + plural(view.start - view.pos, 'позиция', 'позиции', 'позиций') : ''}</div>
                    {view.pen > 0 && <div className="pit2-pen">+10 с штрафа: не использовал два состава шин</div>}
                    <div className="gm-row"><div className="gm-btn" onClick={newGame}>Ещё гонку</div><div className="gm-btn ghost" onClick={onBack}>К играм</div></div>
                    <GameBoard type="strategy" fmt={p => 'P' + p} refreshKey={boardKey}/>
                </div>
            )}
        </div>
    );
};

// ─────────────── 6. МЕНЕДЖЕР: сезон руководителя команды ───────────────
// Сезон за реальную команду: спонсор и цель от совета директоров, на каждом
// этапе — крыло под тип трассы и погоду, тактика и число пит-стопов каждому
// пилоту, доработки на призовые. Квалификация Q1–Q3 с временами, гонка с
// хроникой событий, личный зачёт и Кубок конструкторов. Форма пилотов
// меняется от результатов. У слабых команд денег на старте больше.
const MGR_RACES = 8;
const MGR_TRACK = {
    monaco: 'street', baku: 'street', marina_bay: 'street', jeddah: 'street', vegas: 'street', madring: 'street', miami: 'street', albert_park: 'street',
    monza: 'fast', spa: 'fast', silverstone: 'fast', red_bull_ring: 'fast', villeneuve: 'fast', losail: 'fast',
    hungaroring: 'tech', zandvoort: 'tech', catalunya: 'tech', suzuka: 'tech', imola: 'tech',
};
const MGR_TYPES = {
    street: {name: 'Городская', hint: 'медленные повороты у стен — нужна прижимная сила', ideal: 'high'},
    fast: {name: 'Скоростная', hint: 'длинные прямые — срезай крыло', ideal: 'low'},
    tech: {name: 'Техничная', hint: 'связки поворотов — нужна прижимная сила', ideal: 'high'},
    mixed: {name: 'Сбалансированная', hint: 'всего понемногу — лучше баланс', ideal: 'mid'},
};
const MGR_RAIN = {spa: 45, silverstone: 35, interlagos: 35, suzuka: 30, marina_bay: 25, villeneuve: 25, shanghai: 20, hungaroring: 20, zandvoort: 25,
    bahrain: 2, losail: 2, yas_marina: 2, jeddah: 2, vegas: 3};
// износ шин: на «высоком» выгоднее два пит-стопа, на «низком» — один
const MGR_WEAR = {bahrain: 'high', silverstone: 'high', suzuka: 'high', losail: 'high', catalunya: 'high', shanghai: 'high', zandvoort: 'high',
    monaco: 'low', monza: 'low', vegas: 'low', baku: 'low', madring: 'low', miami: 'low', villeneuve: 'low'};
const MGR_WEAR_TXT = {high: 'высокий', mid: 'средний', low: 'низкий'};
const MGR_SETUPS = [
    {k: 'low', name: 'Низкое крыло', sub: 'скорость на прямых'},
    {k: 'mid', name: 'Баланс', sub: 'универсально'},
    {k: 'high', name: 'Высокое крыло', sub: 'сцепление в поворотах'},
];
const MGR_TACTICS = [
    {k: 'safe', name: 'Беречь', d: -0.03, risk: -0.02},
    {k: 'std', name: 'Обычно', d: 0, risk: 0},
    {k: 'push', name: 'Атака', d: 0.045, risk: 0.05},
];
const MGR_UPG = [
    {k: 'eng', name: 'Мотор', sub: 'быстрее на прямых', icon: '⚙️'},
    {k: 'aero', name: 'Аэродинамика', sub: 'быстрее в поворотах', icon: '🪽'},
    {k: 'rel', name: 'Надёжность', sub: 'меньше сходов', icon: '🛡️'},
    {k: 'crew', name: 'Пит-экипаж', sub: 'быстрые пит-стопы', icon: '🔧'},
];
const MGR_UPG_COST = [6, 10, 15];
const MGR_SPONSORS = [
    {k: 'energy', icon: '⚡', name: 'Энергетик', sub: '+0,6 млн за каждое очко команды'},
    {k: 'bank', icon: '🏦', name: 'Банк', sub: '+4 млн за этап, если оба пилота финишировали'},
    {k: 'tel', icon: '📡', name: 'Телеком', sub: '22 млн сразу, дальше ничего'},
];
const MGR_DNF = {push: ['авария', 'вылет в гравий', 'столкновение'], any: ['отказ мотора', 'гидравлика', 'коробка передач', 'перегрев тормозов', 'столкновение']};

const mgrSetupFit = (setup, type, wet) => {
    const ideal = wet ? 'high' : MGR_TYPES[type].ideal;
    if (setup === ideal) return 0.05;
    if (setup === 'mid' || ideal === 'mid') return 0;
    return -0.06;
};
const mgrStopFit = (stops, wear) => wear === 'high' ? (stops === 2 ? 0.03 : -0.03) : wear === 'low' ? (stops === 1 ? 0.03 : -0.02) : (stops === 1 ? 0.01 : 0);
const mgrUpgBonus = (u, type) => (u.eng || 0) * (type === 'fast' ? 0.04 : 0.02) + (u.aero || 0) * (type === 'fast' ? 0.02 : 0.04);
const mgrLap = (s) => { const m = Math.floor(s / 60); const r = (s - m * 60).toFixed(3).padStart(6, '0'); return m + ':' + r; };
const mgrPlace = (n) => n + '-е';
const mgrRaceTime = (s) => { const h = Math.floor(s / 3600), mm = Math.floor(s % 3600 / 60), ss = Math.floor(s % 60); return h + ':' + String(mm).padStart(2, '0') + ':' + String(ss).padStart(2, '0'); };

const ManagerGame = ({onBack}) => {
    const [data, retry] = useGm([gmGrid, gmSched]);
    const [phase, setPhase] = useState('team');     // team | sponsor | prep | quali | race | done
    const [me, setMe] = useState(null);
    const [st, setSt] = useState(null);
    const [setup, setSetup] = useState('mid');
    const [tac, setTac] = useState({});
    const [stops, setStops] = useState({});
    const [tab, setTab] = useState('report');
    const [boardKey, setBoardKey] = useState(0);

    const m = useMemo(() => {
        if (!data) return null;
        const [grid, races] = data;
        const maxT = Math.max(...grid.teams.map(t => +t.points || 0), 1);
        const teams = grid.teams.map(t => {
            const ds = grid.drivers.filter(d => d.team === t.team);
            const tp = Math.max(1, ds.reduce((s, d) => s + (+d.points || 0), 0));
            return {name: t.team, color: t.team_color, logo: t.logo_url, power: Math.pow((+t.points || 0) / maxT, 0.6),
                drivers: ds.slice(0, 2).map(d => ({id: d.ergast_id || String(d.driver_number), name: d.last_name, full: d.name, photo: d.photo_url, color: d.team_color, team: t.team,
                    skill: 0.35 + 0.65 * ((+d.points || 0) / tp)}))};
        }).filter(t => t.drivers.length === 2);
        teams.forEach((t, i) => { t.rank = i + 1; });
        const next = Math.max(0, races.findIndex(r => r.is_next));
        const cal = [...races.slice(next), ...races.slice(0, next)].slice(0, MGR_RACES).map(r => ({
            city: GM_CITY[r.circuit_id] || r.locality, name: r.name, type: MGR_TRACK[r.circuit_id] || 'mixed', rain: MGR_RAIN[r.circuit_id] ?? 12,
            wear: MGR_WEAR[r.circuit_id] || 'mid', laps: +r.laps || 57}));
        const names = {}; teams.forEach(t => t.drivers.forEach(d => { names[d.id] = d; }));
        return {teams, cal, names};
    }, [data]);

    const pickTeam = (t) => { setMe(t.name); setPhase('sponsor'); gHaptic('light'); };
    const pickSponsor = (sp) => {
        const t = m.teams.find(x => x.name === me);
        const cash = 8 + Math.round((1 - t.power) * 22) + (sp === 'tel' ? 22 : 0);
        const cal = m.cal.map(r => {
            const fc = Math.max(0, Math.min(90, r.rain + gmRand(21) - 10));
            const ev = Math.random() < 0.3 ? 'bonus' : Math.random() < 0.2 ? 'penalty' : null;
            return {...r, forecast: fc, event: ev, penaltyIdx: gmRand(2), qt: 76 + gmRand(18) + Math.random()};
        });
        // цель совета: реальное место команды в чемпионате с запасом в одну позицию (лидеру — только победа)
        const goal = t.rank === 1 ? 1 : Math.min(m.teams.length, t.rank + 1);
        setSt({cash, upg: {}, ai: {}, i: 0, pts: {}, dpts: {}, form: {}, cal, history: [], last: null, sponsor: sp, goal, best: null});
        setSetup('mid'); setTac({}); setStops({});
        setPhase('prep'); gHaptic('medium');
    };

    if (!m) return <GmLoading title="Менеджер" onBack={onBack} data={data} retry={retry}/>;
    const my = me && m.teams.find(t => t.name === me);
    const race = st && st.cal[st.i];

    const buy = (k) => {
        const lvl = st.upg[k] || 0;
        if (lvl >= 3 || st.cash < MGR_UPG_COST[lvl]) { gHaptic('warning'); return; }
        setSt({...st, cash: st.cash - MGR_UPG_COST[lvl], upg: {...st.upg, [k]: lvl + 1}}); gHaptic('success');
    };

    const simulate = () => {
        const wet = Math.random() * 100 < race.forecast;
        const best = race.wear === 'high' ? 2 : 1;
        const field = [];
        m.teams.forEach(t => {
            const mine = t.name === me;
            const aiSetup = Math.random() < 0.6 ? (wet ? 'high' : MGR_TYPES[race.type].ideal) : MGR_SETUPS[gmRand(3)].k;
            const aiStops = Math.random() < 0.6 ? best : 1 + gmRand(2);
            const car = t.power * 0.55 + (mine ? mgrUpgBonus(st.upg, race.type) : (st.ai[t.name] || 0)) + mgrSetupFit(mine ? setup : aiSetup, race.type, wet);
            t.drivers.forEach((d, j) => {
                const tk = mine ? tac[d.id] || 'std' : 'std';
                const tc = MGR_TACTICS.find(x => x.k === tk);
                const ns = mine ? stops[d.id] || 1 : aiStops;
                const sFit = wet ? 0 : mgrStopFit(ns, race.wear);
                const pace = car + d.skill * (wet ? 0.45 : 0.3) + (st.form[d.id] || 0);
                const dnfP = 0.05 + tc.risk + (wet ? 0.04 : 0) - (mine ? (st.upg.rel || 0) * 0.015 : 0);
                field.push({...d, mine, tactic: tk, stops: ns, q: pace + gmNoise(0.05), base: pace + tc.d + sFit,
                    pen: mine && race.event === 'penalty' && race.penaltyIdx === j, dnf: Math.random() < dnfP});
            });
        });
        // квалификация: Q1 и Q2 отсекают по шесть машин
        const qs = [...field].sort((a, b) => b.q - a.q);
        const qTop = qs[0].q;
        qs.forEach((x, i) => { x.qpos = i + 1; x.qtime = race.qt + (wet ? 9 : 0) + (qTop - x.q) * 4.5; x.qout = i >= qs.length - 6 ? 'Q1' : i >= 10 ? 'Q2' : null; });
        const grid = [...qs];
        grid.filter(x => x.pen).forEach(p => { grid.splice(grid.indexOf(p), 1); grid.push(p); });
        grid.forEach((x, i) => { x.grid = i + 1; });

        const laps = race.laps;
        const sc = Math.random() < (wet ? 0.6 : 0.35);
        const scLap = 5 + gmRand(Math.max(1, laps - 15));
        const noise = (wet ? 0.16 : 0.1) + (sc ? 0.04 : 0);
        const ev = [];
        grid.forEach(x => {
            x.r = x.base + gmNoise(noise) - (x.grid - 1) * 0.006;
            // пит-стопы: круги и время на месте
            x.pits = [];
            const at = x.stops === 2 ? [0.3, 0.64] : [0.42];
            const slowP = 0.12 - (x.mine ? (st.upg.crew || 0) * 0.035 : 0);
            at.forEach(f => {
                const slow = Math.random() < slowP;
                x.pits.push({lap: Math.round(laps * (f + gmNoise(0.06))), t: slow ? 5 + Math.random() * 3 : 2.1 + Math.random() * 0.9, slow});
            });
            x.lost = x.pits.reduce((s, p) => s + Math.max(0, p.t - 2.5), 0);
            if (x.dnf) x.dnfLap = 1 + gmRand(laps - 1);
        });
        const rTop = Math.max(...grid.map(x => x.r));
        const live = grid.filter(x => !x.dnf).map(x => ({x, t: (rTop - x.r) * laps * 0.9 * (sc ? 0.55 : 1) + x.lost})).sort((a, b) => a.t - b.t);
        const t0 = live[0].t;
        const fin = [...live.map((o, i) => ({...o.x, pos: i + 1, gap: o.t - t0})), ...grid.filter(x => x.dnf).sort((a, b) => b.dnfLap - a.dnfLap).map(x => ({...x, pos: null}))];
        fin.forEach((x, i) => { x.p = x.pos ? (STR_PTS[i] || 0) : 0; });
        const winTime = laps * (race.qt + (wet ? 12 : 4));

        // хроника гонки
        fin.filter(x => x.mine).forEach(x => {
            const g = Math.max(-3, Math.min(3, Math.round(gmNoise(2.2) + (x.tactic === 'push' ? 0.8 : x.tactic === 'safe' ? -0.5 : 0))));
            if (g) ev.push({lap: 1, mine: true, t: x.name + (g > 0 ? ' отыгрывает на старте ' + g + ' ' + plural(g, 'позицию', 'позиции', 'позиций') : ' теряет на старте ' + -g + ' ' + plural(-g, 'позицию', 'позиции', 'позиций'))});
            x.pits.filter(p => !x.dnf || p.lap < x.dnfLap).forEach((p, k) => ev.push({lap: p.lap, mine: true, bad: p.slow,
                t: x.name + ': пит-стоп ' + p.t.toFixed(1) + ' с' + (p.slow ? ' — заело колесо' : '') + (x.stops === 2 ? (k ? ', Medium → Hard' : ', Soft → Medium') : ', Medium → Hard')}));
        });
        fin.filter(x => x.dnf).forEach(x => {
            const why = x.tactic === 'push' && Math.random() < 0.6 ? MGR_DNF.push[gmRand(3)] : MGR_DNF.any[gmRand(MGR_DNF.any.length)];
            x.why = why;
            ev.push({lap: x.dnfLap, mine: x.mine, bad: true, t: 'Сход: ' + x.name + ' — ' + why});
        });
        if (sc) ev.push({lap: scLap, t: 'Сейфти-кар — отрывы обнулились'});
        if (wet) ev.push({lap: 0, t: 'Гонка под дождём: все на промежуточной резине'});
        fin.filter(x => x.mine && x.pos && x.pos < x.grid).forEach(x => {
            const passed = fin.filter(o => o.pos && o.pos > x.pos && o.grid < x.grid);
            if (passed.length) { const o = passed[gmRand(passed.length)]; ev.push({lap: 8 + gmRand(Math.max(1, laps - 12)), mine: true, good: true, t: x.name + ' обгоняет ' + o.name}); }
        });
        const fl = fin.filter(x => x.pos && x.pos <= 8)[gmRand(Math.min(8, live.length))];
        const flTime = race.qt + (wet ? 11 : 1.3) + Math.random() * 0.8;
        ev.push({lap: Math.round(laps * (0.6 + Math.random() * 0.35)), t: 'Лучший круг: ' + fl.name + ' — ' + mgrLap(flTime), mine: fl.mine});
        ev.push({lap: laps, t: 'Финиш: побеждает ' + fin[0].name + ' (' + fin[0].team + ')', good: fin[0].mine});
        ev.sort((a, b) => a.lap - b.lap);

        // очки, деньги, форма
        const myFin = fin.filter(x => x.mine);
        const myPts = myFin.reduce((s, x) => s + x.p, 0);
        const both = myFin.every(x => x.pos);
        const bothPts = myFin.every(x => x.p > 0);
        const spMoney = st.sponsor === 'energy' ? Math.round(myPts * 0.6) : st.sponsor === 'bank' && both ? 4 : 0;
        const bonus = race.event === 'bonus' && bothPts ? 6 : 0;
        const prize = 3 + Math.round(myPts * 0.35) + spMoney + bonus;
        const pts = {...st.pts}, dpts = {...st.dpts}, form = {...st.form};
        fin.forEach(x => {
            pts[x.team] = (pts[x.team] || 0) + x.p; dpts[x.id] = (dpts[x.id] || 0) + x.p;
            const f = (form[x.id] || 0) + (x.pos ? Math.max(-0.01, Math.min(0.01, (x.grid - x.pos) * 0.002)) : -0.005);
            form[x.id] = Math.max(-0.04, Math.min(0.04, f));
        });
        const ai = {...st.ai}; m.teams.forEach(t => { if (t.name !== me) ai[t.name] = (ai[t.name] || 0) + Math.random() * 0.012; });
        const bestPos = Math.min(...myFin.map(x => x.pos || 99), st.best || 99);
        const last = {wet, sc, qs, grid, fin, ev, myPts, prize, spMoney, bonus, bothPts, winTime};
        setSt({...st, last, pts, dpts, form, ai, cash: st.cash + prize, best: bestPos, history: [...st.history, {city: race.city, pts: myPts}]});
        setTab('report'); setPhase('quali'); gHaptic('medium');
    };

    const nextRace = () => {
        if (st.i + 1 >= MGR_RACES) {
            setPhase('done');
            gmPostResult({game_type: 'manager', score: st.pts[me] || 0}).then(() => setBoardKey(k => k + 1));
        } else { setSt({...st, i: st.i + 1, last: null}); setSetup('mid'); setPhase('prep'); }
        gHaptic('light');
    };

    const standings = st ? m.teams.map(t => ({name: t.name, color: t.color, pts: st.pts[t.name] || 0, mine: t.name === me})).sort((a, b) => b.pts - a.pts) : [];
    const myPlace = standings.findIndex(t => t.name === me) + 1;
    const drvSt = st ? Object.values(m.names).map(d => ({...d, pts: st.dpts[d.id] || 0, mine: d.team === me})).sort((a, b) => b.pts - a.pts) : [];
    const goalTxt = st ? (st.goal === 1 ? 'выиграть Кубок' : 'место не ниже ' + st.goal + '-го') : '';
    const Form = ({id}) => { const f = st.form[id] || 0; return <em className={'mgr-form' + (f > 0.005 ? ' up' : f < -0.005 ? ' down' : '')}>{f > 0.005 ? '▲ в форме' : f < -0.005 ? '▼ спад' : 'форма ровная'}</em>; };
    const Table = ({rows, limit = 10, val}) => {
        const top = rows.slice(0, limit);
        const extra = rows.map((x, i) => ({x, i})).filter(o => o.i >= limit && o.x.mine);
        return (
            <div className="mgr-last">
                {top.map((x, i) => <div key={x.id || x.name} className={'mgr-res' + (x.mine ? ' mine' : '')}><span>{i + 1}</span><i style={{background: x.color}}/><em>{x.name}</em><small>{val(x)}</small></div>)}
                {extra.map(({x, i}) => <div key={x.id || x.name} className="mgr-res mine"><span>{i + 1}</span><i style={{background: x.color}}/><em>{x.name}</em><small>{val(x)}</small></div>)}
            </div>
        );
    };

    return (
        <div className="page-container fade-in gm-page">
            <GameTop title="Менеджер" onBack={onBack} right={st ? <span className="gm-chip">{st.cash} млн</span> : null}/>

            {phase === 'team' && (
                <div>
                    <div className="gm-h left">Ты — руководитель команды</div>
                    <div className="gm-p left">Сезон из {MGR_RACES} этапов по реальному календарю. Выбери команду и спонсора — совет директоров поставит цель. Перед каждой гонкой: крыло под трассу и погоду, тактика и пит-стопы пилотам, доработки на призовые.</div>
                    <div className="mgr-teams">
                        {m.teams.map(t => (
                            <div key={t.name} className="mgr-team" style={{'--tc': t.color}} onClick={() => pickTeam(t)}>
                                {t.logo ? <img src={t.logo} alt=""/> : <i/>}
                                <div className="mgr-team-tx"><b>{t.name}</b><span>{t.drivers.map(d => d.name).join(' · ')}</span></div>
                                <div className="mgr-team-r"><div className="mgr-bar"><i style={{width: Math.round(20 + t.power * 80) + '%'}}/></div><span>{8 + Math.round((1 - t.power) * 22)} млн</span></div>
                            </div>
                        ))}
                    </div>
                    <GameBoard type="manager" fmt={s => s + ' очк.'} refreshKey={boardKey}/>
                </div>
            )}

            {phase === 'sponsor' && my && (
                <div>
                    <div className="mgr-stage" style={{'--tc': my.color}}>
                        <span>Твоя команда</span><b>{my.name}</b>
                        <div className="mgr-stage-h">Совет директоров ждёт: <b>{my.rank === 1 ? 'выиграть Кубок конструкторов' : 'место в Кубке не ниже ' + Math.min(m.teams.length, my.rank + 1) + '-го'}</b></div>
                    </div>
                    <div className="mgr-h">Титульный спонсор</div>
                    <div className="mgr-spons">
                        {MGR_SPONSORS.map(s => (
                            <div key={s.k} className="mgr-spon" onClick={() => pickSponsor(s.k)}>
                                <div className="ic">{s.icon}</div><div><b>{s.name}</b><span>{s.sub}</span></div>
                            </div>
                        ))}
                    </div>
                    <div className="gm-p left small">Энергетик выгоден сильным командам, банк — надёжным, телеком — если хочешь сразу вложиться в доработки.</div>
                </div>
            )}

            {phase === 'prep' && my && race && (
                <div>
                    <div className="mgr-stage" style={{'--tc': my.color}}>
                        <span>Этап {st.i + 1} из {MGR_RACES} · {st.i > 0 ? 'ты ' + myPlace + '-й в Кубке' : 'старт сезона'} · цель: {goalTxt}</span>
                        <b>{race.city}</b>
                        <div className="mgr-tags">
                            <em>{MGR_TYPES[race.type].name}</em>
                            <em>{race.laps} {plural(race.laps, 'круг', 'круга', 'кругов')}</em>
                            <em>износ шин: {MGR_WEAR_TXT[race.wear]}</em>
                            <em className={race.forecast >= 40 ? 'wet' : ''}>{race.forecast >= 40 ? '🌧 скорее всего дождь · ' + race.forecast + '%' : race.forecast >= 15 ? '⛅ возможен дождь · ' + race.forecast + '%' : '☀️ сухо'}</em>
                        </div>
                        <div className="mgr-stage-h">{MGR_TYPES[race.type].hint}{race.forecast >= 40 ? '; в дождь выручает высокое крыло' : ''}. {race.wear === 'high' ? 'Шины горят — два пит-стопа выгоднее.' : race.wear === 'low' ? 'Шины живут долго — хватит одного пит-стопа.' : 'Обычно хватает одного пит-стопа.'}</div>
                    </div>
                    {race.event === 'bonus' && <div className="mgr-event">💰 Партнёр: +6 млн, если оба пилота привезут очки</div>}
                    {race.event === 'penalty' && <div className="mgr-event bad">🔧 {my.drivers[race.penaltyIdx].name}: замена мотора — старт с последнего ряда</div>}

                    <div className="mgr-h">Настройка машины</div>
                    <div className="mgr-seg three">
                        {MGR_SETUPS.map(s => <div key={s.k} className={setup === s.k ? 'on' : ''} onClick={() => setSetup(s.k)}><b>{s.name}</b><span>{s.sub}</span></div>)}
                    </div>

                    <div className="mgr-h">Пилоты</div>
                    {my.drivers.map(d => (
                        <div key={d.id} className="mgr-drv">
                            <div className="mgr-drv-n">{d.photo ? <img src={d.photo} alt=""/> : <i style={{background: d.color}}/>}<b>{d.name}</b><Form id={d.id}/><span>{st.dpts[d.id] || 0} очк.</span></div>
                            <div className="mgr-seg">
                                {MGR_TACTICS.map(t => <div key={t.k} className={(tac[d.id] || 'std') === t.k ? 'on' : ''} onClick={() => setTac({...tac, [d.id]: t.k})}>{t.name}</div>)}
                            </div>
                            <div className="mgr-seg two">
                                {[1, 2].map(n => <div key={n} className={(stops[d.id] || 1) === n ? 'on' : ''} onClick={() => setStops({...stops, [d.id]: n})}>{n === 1 ? '1 пит-стоп' : '2 пит-стопа'}</div>)}
                            </div>
                        </div>
                    ))}
                    <div className="gm-p left small">«Атака» — быстрее, но чаще сходы. «Беречь» — медленнее, зато почти всегда финиш. Можно разделить стратегии: одному пилоту один стоп, другому два.</div>

                    <div className="mgr-h">Доработки · {st.cash} млн</div>
                    <div className="mgr-upgs">
                        {MGR_UPG.map(u => { const lvl = st.upg[u.k] || 0; const cost = MGR_UPG_COST[lvl]; return (
                            <div key={u.k} className={'mgr-upg2' + (lvl >= 3 || st.cash < cost ? ' off' : '')} onClick={() => buy(u.k)}>
                                <div className="ic">{u.icon}</div><b>{u.name}</b><span>{u.sub}</span>
                                <div className="lv">{[0, 1, 2].map(i => <i key={i} className={i < lvl ? 'on' : ''}/>)}</div>
                                <em>{lvl >= 3 ? 'максимум' : cost + ' млн'}</em>
                            </div>
                        ); })}
                    </div>
                    <div className="mgr-go"><div className="gm-btn" onClick={simulate}>Квалификация</div></div>
                </div>
            )}

            {phase === 'quali' && st.last && (
                <div>
                    <div className="mgr-race-h">{race.city} · <b>Квалификация</b>{st.last.wet ? ' · 🌧 пошёл дождь' : ' · ☀️ сухо'}</div>
                    <div className="mgr-mine">
                        {st.last.qs.filter(x => x.mine).map(x => (
                            <div key={x.id}><b>{x.name}</b><span>{x.qout ? 'вылет в ' + x.qout : 'в Q3'}{x.pen ? ' · штраф' : ''}</span><em>P{x.grid}</em></div>
                        ))}
                    </div>
                    <div className="mgr-last">
                        {st.last.qs.map((x, i) => (
                            <React.Fragment key={x.id}>
                                {(i === 10 || i === st.last.qs.length - 6) && <div className="mgr-cut">{i === 10 ? 'выбыли в Q2' : 'выбыли в Q1'}</div>}
                                <div className={'mgr-res' + (x.mine ? ' mine' : '')}><span>{i + 1}</span><i style={{background: x.color}}/><em>{x.name}</em>
                                    <small>{i === 0 ? mgrLap(x.qtime) : '+' + (x.qtime - st.last.qs[0].qtime).toFixed(3)}</small></div>
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="mgr-go"><div className="gm-btn" onClick={() => { setPhase('race'); gHaptic('medium'); }}>Старт гонки</div></div>
                </div>
            )}

            {phase === 'race' && st.last && (
                <div>
                    <div className="mgr-race-h">{race.city} · <b>Финиш</b>{st.last.wet ? ' · 🌧 дождевая гонка' : ''}{st.last.sc ? ' · был сейфти-кар' : ''}</div>
                    <div className="mgr-mine">
                        {st.last.fin.filter(x => x.mine).map(x => (
                            <div key={x.id} className={x.dnf ? 'dnf' : ''}><b>{x.name}</b><span>P{x.grid} → {x.dnf ? 'сход, ' + x.dnfLap + ' круг' : 'P' + x.pos}</span><em>+{x.p}</em></div>
                        ))}
                    </div>
                    <div className="mgr-tabs">
                        {[['report', 'Хроника'], ['result', 'Протокол'], ['cup', 'Кубок'], ['drv', 'Пилоты']].map(([k, n]) => <div key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{n}</div>)}
                    </div>
                    {tab === 'report' && (
                        <div className="mgr-ev">
                            {st.last.ev.map((e, i) => (
                                <div key={i} className={(e.mine ? 'mine' : '') + (e.bad ? ' bad' : '') + (e.good ? ' good' : '')}><span>{e.lap ? 'Круг ' + e.lap : 'Старт'}</span><p>{e.t}</p></div>
                            ))}
                        </div>
                    )}
                    {tab === 'result' && <Table rows={st.last.fin.map(x => ({...x}))} limit={22} val={x => x.dnf ? 'сход' : x.pos === 1 ? mgrRaceTime(st.last.winTime) : '+' + x.gap.toFixed(1) + ' с'}/>}
                    {tab === 'cup' && <Table rows={standings} limit={11} val={x => x.pts}/>}
                    {tab === 'drv' && <Table rows={drvSt} limit={10} val={x => x.pts}/>}
                    <div className="mgr-money">Призовые: <b>+{st.last.prize} млн</b>
                        {st.last.spMoney > 0 && <> · спонсор +{st.last.spMoney}</>}
                        {race.event === 'bonus' && (st.last.bonus ? <> · партнёр +6</> : <> · бонус партнёра не взяли</>)}
                    </div>
                    <div className="mgr-go"><div className="gm-btn" onClick={nextRace}>{st.i + 1 >= MGR_RACES ? 'Итоги сезона' : 'Следующий этап'}</div></div>
                </div>
            )}

            {phase === 'done' && (
                <div className="gm-result">
                    <div className="gm-result-k">{me} · итог сезона</div>
                    <div className="gm-result-big">{st.pts[me] || 0}<span>очков</span></div>
                    <div className="gm-result-t">{myPlace === 1 ? 'Кубок конструкторов твой!' : mgrPlace(myPlace) + ' место в Кубке'}</div>
                    <div className={'mgr-verdict' + (myPlace <= st.goal ? ' ok' : '')}>{myPlace <= st.goal ? '✅ Совет директоров доволен: цель «' + goalTxt + '» выполнена' : '❌ Цель «' + goalTxt + '» не выполнена — совет недоволен'}</div>
                    <div className="mgr-bars">
                        {st.history.map((r, i) => { const mx = Math.max(...st.history.map(h => h.pts), 1); return (
                            <div key={i}><b>{r.pts}</b><i style={{height: Math.max(3, r.pts / mx * 70) + 'px'}}/><span>{r.city}</span></div>
                        ); })}
                    </div>
                    <div className="mgr-sum">
                        <div><span>Лучший финиш</span><b>{st.best && st.best < 99 ? 'P' + st.best : '—'}</b></div>
                        <div><span>Лучший пилот команды</span><b>{my.drivers.map(d => ({d, p: st.dpts[d.id] || 0})).sort((a, b) => b.p - a.p)[0].d.name}</b></div>
                        <div><span>Чемпион среди пилотов</span><b>{drvSt[0]?.name} · {drvSt[0]?.pts}</b></div>
                    </div>
                    <div className="gm-row"><div className="gm-btn" onClick={() => { setSt(null); setMe(null); setPhase('team'); }}>Новый сезон</div><div className="gm-btn ghost" onClick={onBack}>К играм</div></div>
                    <GameBoard type="manager" fmt={s => s + ' очк.'} refreshKey={boardKey}/>
                </div>
            )}
        </div>
    );
};

// ─────────────── 7. КВИЗ ───────────────
// Вопросы собираются из данных (сезоны 1980–2025, решётка и календарь 2026) —
// это больше тысячи разных вопросов, плюс ручной набор фактов. Последние
// 300 заданных вопросов запоминаются и не повторяются.
const QUIZ_FIXED = [
    {q: 'Кто выиграл первый чемпионат мира F1 в 1950 году?', o: ['Джузеппе Фарина', 'Хуан Мануэль Фанхио', 'Альберто Аскари', 'Стирлинг Мосс'], a: 0},
    {q: 'Какая команда выиграла больше всего Кубков конструкторов?', o: ['Ferrari', 'McLaren', 'Mercedes', 'Williams'], a: 0},
    {q: 'Кто самый молодой чемпион мира F1?', o: ['Себастьян Феттель', 'Макс Ферстаппен', 'Льюис Хэмилтон', 'Фернандо Алонсо'], a: 0},
    {q: 'Что такое DRS?', o: ['Подвижный элемент заднего крыла', 'Система торможения', 'Система рекуперации', 'Ограничитель скорости'], a: 0},
    {q: 'Что означает синий флаг?', o: ['Пропусти более быструю машину', 'Опасность на трассе', 'Конец сессии', 'Мокрая трасса'], a: 0},
    {q: 'Сколько очков получает победитель гонки?', o: ['25', '20', '30', '10'], a: 0},
    {q: 'Сколько очков дают за второе место?', o: ['18', '20', '15', '19'], a: 0},
    {q: 'Сколько очков дают за десятое место?', o: ['1', '2', '0', '3'], a: 0},
    {q: 'Сколько цилиндров в моторе F1 с 2014 года?', o: ['6', '8', '10', '4'], a: 0},
    {q: 'Кто единственный стал чемпионом посмертно?', o: ['Йохен Риндт', 'Айртон Сенна', 'Джим Кларк', 'Жиль Вильнёв'], a: 0},
    {q: 'В каком году появилась защита «гало»?', o: ['2018', '2016', '2020', '2014'], a: 0},
    {q: 'Сколько побед одержал Ферстаппен в сезоне-2023?', o: ['19', '15', '17', '21'], a: 0},
    {q: 'Сколько передач в коробке болида F1?', o: ['8', '6', '7', '10'], a: 0},
    {q: 'Где базируется Ferrari?', o: ['Маранелло', 'Монца', 'Турин', 'Имола'], a: 0},
    {q: 'Где базируется Red Bull Racing?', o: ['Милтон-Кинс', 'Зальцбург', 'Брэкли', 'Энстоун'], a: 0},
    {q: 'На какой трассе проходит Гран-при Италии?', o: ['Монца', 'Имола', 'Муджелло', 'Имола и Монца по очереди'], a: 0},
    {q: 'Какой цвет боковины у мягких шин Pirelli?', o: ['Красный', 'Жёлтый', 'Белый', 'Зелёный'], a: 0},
    {q: 'Какой цвет у шин для сильного дождя?', o: ['Синий', 'Зелёный', 'Белый', 'Оранжевый'], a: 0},
    {q: 'Какого цвета промежуточные шины (intermediate)?', o: ['Зелёные', 'Синие', 'Жёлтые', 'Фиолетовые'], a: 0},
    {q: 'Что такое андеркат?', o: ['Ранний пит-стоп, чтобы обогнать соперника на свежих шинах', 'Обгон по внутренней траектории', 'Срез шиканы', 'Старт с пит-лейн'], a: 0},
    {q: 'Что означает жёлтый флаг?', o: ['Опасность, обгоны запрещены', 'Пропусти быструю машину', 'Скользкая трасса', 'Последний круг'], a: 0},
    {q: 'Что означает красный флаг?', o: ['Сессия остановлена', 'Опасность впереди', 'Дисквалификация', 'Пит-лейн закрыт'], a: 0},
    {q: 'Что такое «закрытый парк» (parc fermé)?', o: ['Запрет менять настройки машины после квалификации', 'Стоянка болидов в боксах', 'Зона для болельщиков', 'Штрафная зона'], a: 0},
    {q: 'Что такое поул-позиция?', o: ['Первое место на старте', 'Лучший круг гонки', 'Место в боксах', 'Позиция на подиуме'], a: 0},
    {q: 'Какой автодром называют «Храмом скорости»?', o: ['Монца', 'Спа', 'Сильверстоун', 'Сузука'], a: 0},
    {q: 'На какой трассе знаменитая связка Eau Rouge?', o: ['Спа-Франкоршам', 'Монца', 'Сузука', 'Интерлагос'], a: 0},
    {q: 'Как называют Северную петлю Нюрбургринга?', o: ['Зелёный ад', 'Храм скорости', 'Красный ад', 'Королевская трасса'], a: 0},
    {q: 'В какой стране трасса Интерлагос?', o: ['Бразилия', 'Аргентина', 'Мексика', 'Португалия'], a: 0},
    {q: 'Какая трасса самая короткая в календаре?', o: ['Монако', 'Зандворт', 'Баку', 'Сингапур'], a: 0},
    {q: 'Где с 2008 года проходит первая ночная гонка F1?', o: ['Сингапур', 'Бахрейн', 'Абу-Даби', 'Лас-Вегас'], a: 0},
    {q: 'Кто основал Ferrari?', o: ['Энцо Феррари', 'Фрэнк Уильямс', 'Брюс Макларен', 'Колин Чепмен'], a: 0},
    {q: 'Кто основал McLaren?', o: ['Брюс Макларен', 'Рон Деннис', 'Колин Чепмен', 'Кен Тиррелл'], a: 0},
    {q: 'Какое прозвище было у Алена Проста?', o: ['Профессор', 'Барон', 'Летучий финн', 'Ледяной'], a: 0},
    {q: 'Кого называли «Ледяным» (Iceman)?', o: ['Кими Райкконена', 'Мику Хаккинена', 'Нико Росберга', 'Дженсона Баттона'], a: 0},
    {q: 'Кто первый российский пилот F1?', o: ['Виталий Петров', 'Даниил Квят', 'Сергей Сироткин', 'Никита Мазепин'], a: 0},
    {q: 'Кто выиграл первый Гран-при России в Сочи (2014)?', o: ['Льюис Хэмилтон', 'Нико Росберг', 'Себастьян Феттель', 'Фернандо Алонсо'], a: 0},
    {q: 'Сколько титулов у Михаэля Шумахера?', o: ['7', '5', '6', '4'], a: 0},
    {q: 'Сколько титулов у Айртона Сенны?', o: ['3', '2', '4', '5'], a: 0},
    {q: 'Сколько титулов у Алена Проста?', o: ['4', '3', '2', '5'], a: 0},
    {q: 'Сколько титулов у Фернандо Алонсо?', o: ['2', '1', '3', '4'], a: 0},
    {q: 'Кто выиграл больше всего Гран-при Монако?', o: ['Айртон Сенна', 'Михаэль Шумахер', 'Грэм Хилл', 'Ален Прост'], a: 0},
    {q: 'Кто стал чемпионом 2008 года с отрывом в одно очко?', o: ['Льюис Хэмилтон', 'Фелипе Масса', 'Кими Райкконен', 'Роберт Кубица'], a: 0},
    {q: 'На каком Гран-при Ферстаппен одержал первую победу?', o: ['Испания 2016', 'Малайзия 2016', 'Бразилия 2016', 'Австрия 2018'], a: 0},
    {q: 'В какой команде Шумахер вернулся в F1 в 2010 году?', o: ['Mercedes', 'Ferrari', 'Benetton', 'Sauber'], a: 0},
    {q: 'Какая команда дебютирует в F1 в 2026 году?', o: ['Cadillac', 'Andretti', 'Porsche', 'Toyota'], a: 0},
    {q: 'Кто поставляет шины для F1?', o: ['Pirelli', 'Bridgestone', 'Michelin', 'Goodyear'], a: 0},
    {q: 'Сколько длится гонка максимум (без остановок)?', o: ['2 часа', '1,5 часа', '3 часа', '1 час'], a: 0},
    {q: 'Под каким номером может выступать действующий чемпион?', o: ['1', '0', '100', '99'], a: 0},
    {q: 'Как в 1988 году выступила McLaren?', o: ['Выиграла 15 гонок из 16', 'Не набрала очков', 'Выиграла все гонки', 'Пропустила сезон'], a: 0},
    {q: 'Кто выиграл первую гонку для Red Bull (Китай-2009)?', o: ['Себастьян Феттель', 'Марк Уэббер', 'Дэвид Култхард', 'Макс Ферстаппен'], a: 0},
];

const QUIZ_SEEN = 'gm_quiz_seen';
const quizMake = (hist, grid, races) => {
    const yrs = Object.keys(hist.seasons).filter(y => +y >= 1980);
    const nm = (id) => { const d = hist.drivers[id] || {}; return ((d.first || '') + ' ' + (d.last || '')).trim(); };
    const ry = () => yrs[gmRand(yrs.length)];
    const debut = {};
    yrs.forEach(y => hist.seasons[y].drivers.forEach(r => { if (!debut[r.id]) debut[r.id] = +y; }));
    // чемпионы: id → годы титулов
    const titles = {};
    yrs.forEach(y => { const c = hist.seasons[y].drivers.find(x => x.pos === 1); if (c) (titles[c.id] = titles[c.id] || []).push(+y); });
    // только те, чья карьера целиком после 1980 — иначе «первый титул» соврёт (Лауда)
    const champs = Object.keys(titles).filter(id => debut[id] > 1980);
    const numOpts = (v, spread) => { const o = []; for (let x = v - spread; x <= v + spread; x++) if (x >= 0 && x !== v) o.push(String(x)); return o; };
    const gens = [
        () => { const y = ry(); const s = hist.seasons[y].drivers; const ch = s.find(x => x.pos === 1); if (!ch) return null;
            return {q: 'Кто стал чемпионом мира в ' + y + ' году?', right: nm(ch.id), opts: s.filter(x => x.pos > 1 && x.pos <= 8).map(x => nm(x.id))}; },
        () => { const y = ry(); const s = hist.seasons[y].drivers; const v = s.find(x => x.pos === 2); if (!v) return null;
            return {q: 'Кто стал вице-чемпионом в ' + y + ' году?', right: nm(v.id), opts: s.filter(x => x.pos !== 2 && x.pos <= 8).map(x => nm(x.id))}; },
        () => { const y = ry(); const c = hist.seasons[y].constructors; if (c.length < 4) return null;
            return {q: 'Кто выиграл Кубок конструкторов в ' + y + ' году?', right: gmTeam(c[0].name), opts: c.slice(1, 8).map(x => gmTeam(x.name))}; },
        () => { const y = ry(); const s = hist.seasons[y]; const d = gmShuffle(s.drivers.filter(x => x.pos && x.pos <= 10 && x.teams.length === 1))[0]; if (!d) return null;
            return {q: 'За какую команду выступал ' + nm(d.id) + ' в ' + y + ' году?', right: gmTeam(d.teams[0]), opts: s.constructors.map(x => gmTeam(x.name))}; },
        () => { const y = ry(); const s = hist.seasons[y].drivers; const d = gmShuffle(s.filter(x => x.pos && x.pos <= 8 && x.teams.length === 1))[0]; if (!d) return null;
            const mate = s.filter(x => x.id !== d.id && x.teams.includes(d.teams[0])); if (mate.length !== 1) return null;
            return {q: 'Кто был напарником ' + nm(d.id) + ' в ' + y + ' году?', right: nm(mate[0].id), opts: s.filter(x => !x.teams.includes(d.teams[0])).map(x => nm(x.id))}; },
        () => { const y = ry(); const s = hist.seasons[y].drivers; const mx = Math.max(...s.map(x => x.wins)); const top = s.filter(x => x.wins === mx); if (top.length !== 1 || !mx) return null;
            return {q: 'Кто выиграл больше всего гонок в сезоне-' + y + '?', right: nm(top[0].id), opts: s.filter(x => x.id !== top[0].id && x.pos <= 8).map(x => nm(x.id))}; },
        () => { const y = ry(); const ch = hist.seasons[y].drivers.find(x => x.pos === 1); if (!ch || !ch.wins) return null;
            return {q: 'Сколько гонок выиграл чемпион ' + y + ' года ' + nm(ch.id) + '?', right: String(ch.wins), opts: numOpts(ch.wins, 4).filter(x => +x > 0)}; },
        () => { const id = champs[gmRand(champs.length)];
            return {q: 'В каком году ' + nm(id) + ' впервые стал чемпионом мира?', right: String(titles[id][0]), opts: numOpts(titles[id][0], 5).filter(x => +x <= 2025)}; },
        () => { const id = champs[gmRand(champs.length)]; const n = titles[id].length;
            return {q: 'Сколько чемпионских титулов у ' + nm(id) + '?', right: String(n), opts: numOpts(n, 3).filter(x => +x > 0)}; },
        () => { const y = ry(); const s = hist.seasons[y].drivers.filter(x => x.pos && x.pos <= 6 && debut[x.id] === +y && +y > 1980); if (!s.length) return null; const d = s[0];
            return {q: 'Кто из этих пилотов дебютировал в F1 в ' + y + ' году?', right: nm(d.id), opts: hist.seasons[y].drivers.filter(x => debut[x.id] < +y - 2 && x.pos <= 12).map(x => nm(x.id))}; },
        () => { const d = grid.drivers[gmRand(grid.drivers.length)]; if (!d.nationality) return null;
            return {q: 'Из какой страны ' + d.name + '?', right: gmNat(d.nationality), opts: [...new Set(grid.drivers.map(x => gmNat(x.nationality)))]}; },
        () => { const d = grid.drivers[gmRand(grid.drivers.length)];
            return {q: 'Под каким номером выступает ' + d.name + '?', right: String(d.driver_number), opts: grid.drivers.map(x => String(x.driver_number))}; },
        () => { const d = grid.drivers[gmRand(grid.drivers.length)];
            return {q: 'За какую команду ' + d.name + ' выступает в 2026 году?', right: d.team, opts: grid.teams.map(t => t.team)}; },
        () => { const r = races[gmRand(races.length)]; const c = GM_CITY[r.circuit_id]; if (!c) return null;
            return {q: 'Где проходит ' + r.name + ' в 2026 году?', right: c, opts: races.map(x => GM_CITY[x.circuit_id]).filter(Boolean)}; },
    ];
    const seen = new Set(gmLS.get(QUIZ_SEEN, []));
    const out = [], used = new Set();
    let guard = 0;
    while (out.length < 7 && guard++ < 300) {
        const q = gens[gmRand(gens.length)]();
        if (!q || used.has(q.q) || (seen.has(q.q) && guard < 200)) continue;
        const wrong = gmPickN([...new Set(q.opts)].filter(Boolean), 3, [q.right]);
        if (wrong.length < 3) continue;
        used.add(q.q);
        const o = gmShuffle([q.right, ...wrong]);
        out.push({q: q.q, o, a: o.indexOf(q.right)});
    }
    const fresh = QUIZ_FIXED.filter(f => !seen.has(f.q));
    gmPickN(fresh.length >= 3 ? fresh : QUIZ_FIXED, 3).forEach(f => { used.add(f.q); const o = gmShuffle(f.o.map((t, i) => ({t, i}))); out.push({q: f.q, o: o.map(x => x.t), a: o.findIndex(x => x.i === f.a)}); });
    gmLS.set(QUIZ_SEEN, [...seen, ...used].slice(-300));
    return gmShuffle(out);
};

const QUIZ_TIME = 12;
const QuizGame2 = ({onBack}) => {
    const [data, retry] = useGm([gmHist, gmGrid, gmSched]);
    const [phase, setPhase] = useState('intro');
    const [qs, setQs] = useState([]);
    const [i, setI] = useState(0);
    const [pick, setPick] = useState(null);
    const [score, setScore] = useState(0);
    const [right, setRight] = useState(0);
    const [left, setLeft] = useState(QUIZ_TIME);
    const [boardKey, setBoardKey] = useState(0);
    const t0 = useRef(0);
    const lock = useRef(false);

    const answer = (idx) => {
        if (lock.current || phase !== 'play') return;
        lock.current = true;
        const ok = idx === qs[i].a;
        const secs = Math.max(0, QUIZ_TIME - (performance.now() - t0.current) / 1000);
        const sc = score + (ok ? 100 + Math.round(secs * 5) : 0);
        setPick(idx === null ? -1 : idx); setScore(sc); if (ok) setRight(r => r + 1);
        gHaptic(ok ? 'success' : 'error');
        setTimeout(() => {
            if (i + 1 >= qs.length) {
                setPhase('done');
                gmPostResult({game_type: 'quiz2', score: sc}).then(() => setBoardKey(k => k + 1));
            } else { setI(i + 1); setPick(null); lock.current = false; t0.current = performance.now(); setLeft(QUIZ_TIME); }
        }, 1100);
    };
    useEffect(() => {
        if (phase !== 'play') return;
        let raf;
        const tick = () => {
            const l = QUIZ_TIME - (performance.now() - t0.current) / 1000;
            setLeft(Math.max(0, l));
            if (l <= 0 && !lock.current) { answer(null); return; }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [phase, i]);

    useGmKeys(e => {
        const k = '1234'.indexOf(e.key);
        if (k >= 0 && phase === 'play' && qs[i] && k < qs[i].o.length) { answer(k); return true; }
    });
    if (!data) return <GmLoading title="Квиз" onBack={onBack} data={data} retry={retry}/>;
    const start = () => {
        setQs(quizMake(data[0], data[1], data[2])); setI(0); setPick(null); setScore(0); setRight(0);
        lock.current = false; t0.current = performance.now(); setLeft(QUIZ_TIME); setPhase('play');
    };
    const q = qs[i];
    return (
        <div className="page-container fade-in gm-page">
            <GameTop title="Квиз" onBack={onBack} right={phase === 'play' ? <span className="gm-chip">{i + 1} / {qs.length}</span> : null}/>
            {phase === 'intro' && (
                <div className="gm-intro">
                    <div className="gm-h">10 вопросов</div>
                    <div className="gm-p">Больше тысячи вопросов: чемпионы и напарники с 1980 года, рекорды, правила, трассы, решётка 2026. Недавние вопросы не повторяются. На ответ — {QUIZ_TIME} секунд.</div>
                    <div className="gm-btn" onClick={start}>Старт</div>
                    <GameBoard type="quiz2" fmt={s => s + ' очк.'} refreshKey={boardKey}/>
                </div>
            )}
            {phase === 'play' && q && (
                <div className="qz-play" key={i}>
                    <div className="trk-timer"><i style={{transform: 'scaleX(' + (left / QUIZ_TIME) + ')'}} className={left < 4 ? 'hot' : ''}/></div>
                    <div className="trk-score">{score}<span>очков</span></div>
                    <div className="qz-q">{q.q}</div>
                    <div className="qz-opts">
                        {q.o.map((o, k) => (
                            <div key={k} className={'trk-opt' + (pick !== null ? (k === q.a ? ' ok' : k === pick ? ' bad' : ' dim') : '')} onClick={() => answer(k)}>
                                <span className="qz-l">{'ABCD'[k]}</span>{o}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {phase === 'done' && (
                <div className="gm-result">
                    <div className="gm-result-k">Верно {right} из {qs.length}</div>
                    <div className="gm-result-big">{score}<span>очков</span></div>
                    <div className="gm-result-t">{right >= 9 ? 'Энциклопедия F1' : right >= 6 ? 'Настоящий болельщик' : right >= 3 ? 'Неплохо' : 'Есть куда расти'}</div>
                    <div className="gm-row"><div className="gm-btn" onClick={start}>Ещё раз</div><div className="gm-btn ghost" onClick={onBack}>К играм</div></div>
                    <GameBoard type="quiz2" fmt={s => s + ' очк.'} refreshKey={boardKey}/>
                </div>
            )}
        </div>
    );
};

const GamesPage = ({onChange}) => {
    // по приглашению в дурака открываем игру сразу
    const [activeGame, setActiveGame] = useState(DURAK_INVITE ? 'durak' : null);
    const goBack = () => setActiveGame(null);

    useEffect(() => { gmFlushPending(); }, []);
    const G = {pit_stop: PitStopGame, durak: DurakGame, reaction: ReactionGame, f1dle: DriverDle, career: CareerGuess,
        circuit: CircuitGuess, hl: HigherLower, strategy: StrategyGame, manager: ManagerGame, quiz: QuizGame2};
    if (activeGame && G[activeGame]) { const C = G[activeGame]; return <C onBack={goBack}/>; }

    const day = gmDay();
    const dleDone = gmLS.get('gm_dle_' + day, []).length > 0;
    const carDone = gmLS.get('gm_car_' + day, []).length > 0;
    const sections = [
        {h: 'Каждый день', items: [
            {id: 'f1dle', name: 'Пилот дня', desc: 'Загадка дня и бесконечный режим', cls: 'g-dle', badge: dleDone ? null : 'новый'},
            {id: 'career', name: 'Карьера', desc: 'Чья карьера — загадка дня и бесконечно', cls: 'g-car', badge: carDone ? null : 'новый'},
        ]},
        {h: 'Карты', items: [{id: 'durak', name: 'Дурак', desc: 'Подкидной на картах F1 — с компьютером или с другом', cls: 'g-durak', big: true}]},
        {h: 'Угадайки', items: [
            {id: 'circuit', name: 'Угадай трассу', desc: 'По силуэту, на время', cls: 'g-trk'},
            {id: 'hl', name: 'Больше / меньше', desc: 'Пилоты одного уровня, всё сложнее', cls: 'g-hl'},
            {id: 'quiz', name: 'Квиз', desc: 'Больше 1000 вопросов', cls: 'g-qz'},
        ]},
        {h: 'Стратегия', items: [
            {id: 'strategy', name: 'Пит-уолл', desc: 'Любой этап, шины и пит-стопы', cls: 'g-str'},
            {id: 'manager', name: 'Менеджер', desc: 'Сезон руководителя команды: стратегия, доработки, спонсоры', cls: 'g-mgr'},
        ]},
        {h: 'На реакцию', items: [
            {id: 'pit_stop', name: 'Пит-стоп', desc: 'Гайковёрт на время', cls: 'g-pit'},
            {id: 'reaction', name: 'Реакция', desc: 'Старт по огням F1', cls: 'g-rx'},
        ]},
    ];

    return (
        <div className="page-container fade-in gm-page">
            <div className="gm-list-head">
                <h2>Мини-игры</h2>
                {onChange && <div className="gm-back" onClick={() => onChange('home')}>← Главная</div>}
            </div>
            {sections.map(s => (
                <div key={s.h} className="gm-sec">
                    <div className="gm-sec-h">{s.h}</div>
                    <div className="gm-grid">
                        {s.items.map(g => (
                            <div key={g.id} className={'gm-card2 ' + g.cls + (g.big ? ' big' : '')} onClick={() => setActiveGame(g.id)}>
                                {g.badge && <span className={'gm-badge' + (g.badge === 'новый' ? ' hot' : '')}>{g.badge}</span>}
                                <div className="gm-card2-art" aria-hidden="true"/>
                                <div className="gm-card2-name">{g.name}</div>
                                <div className="gm-card2-desc">{g.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};



// ==== PUBLIC LOGIN (Login Widget) ====
const _TgIcon = ({size=20}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><path d="M21.9 4.3 18.6 20c-.25 1.1-.9 1.37-1.82.85l-5.04-3.72-2.43 2.34c-.27.27-.5.5-1 .5l.36-5.13L18 6.34c.4-.36-.09-.56-.62-.2L6.9 13.03l-4.98-1.56c-1.08-.34-1.1-1.08.23-1.6L20.5 2.6c.9-.33 1.68.2 1.4 1.7Z" fill="#fff"/></svg>
);

const PublicLoginPage = ({ onLogin }) => {
    const [step, setStep] = useState('start');   // start | code
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const submitCode = async () => {
        const c = code.trim();
        if (!c || busy) return;
        setBusy(true); setErr('');
        try {
            const res = await api.post('/api/auth/code', { code: c });
            if (res && res.token) {
                localStorage.setItem('f1hub_auth_token', res.token);
                onLogin(res.user || null);
            } else { setErr('Неверный или просроченный код'); }
        } catch (e) { setErr('Неверный или просроченный код'); }
        setBusy(false);
    };

    const box = {width:'100%',maxWidth:340,display:'flex',flexDirection:'column',gap:12,position:'relative'};
    return (
        <div style={{minHeight:'100dvh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'32px 24px',position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',top:'-12%',left:'50%',transform:'translateX(-50%)',width:'160%',height:'48%',background:'radial-gradient(60% 100% at 50% 0%, rgba(225,6,0,0.24), transparent 72%)',pointerEvents:'none'}}/>
            <img src="/static/logo-f1hub-720.webp?v=1" alt="F1 Hub" style={{width:'min(80%,300px)',marginBottom:24,position:'relative',filter:'drop-shadow(0 10px 30px rgba(0,0,0,0.55))'}} onError={e=>{e.target.style.display='none'}}/>
            <div style={{fontSize:14.5,color:'var(--f1-text-secondary)',textAlign:'center',maxWidth:330,lineHeight:1.55,marginBottom:26,position:'relative'}}>
                Прогнозы, игры, статистика и Зал славы.<br/>Вход через Telegram — 10 секунд.
            </div>

            {step === 'start' && (
                <div style={box}>
                    <a href="https://t.me/F1_egor_bot?start=code" target="_blank" rel="noopener" onClick={()=>setStep('code')}
                       style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,background:'#229ED9',color:'#fff',borderRadius:14,padding:'15px',fontSize:15,fontWeight:800,textDecoration:'none',boxShadow:'0 8px 26px rgba(34,158,217,0.4)'}}>
                        <_TgIcon/> Войти через Telegram
                    </a>
                    <button onClick={()=>setStep('code')} style={{background:'transparent',border:'1px solid var(--f1-border)',borderRadius:14,padding:'13px',color:'var(--f1-text-secondary)',fontSize:13.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                        У меня уже есть код
                    </button>
                    <button onClick={()=>onLogin(null)} style={{background:'none',border:'none',color:'var(--f1-text-muted)',fontSize:13,marginTop:6,cursor:'pointer',fontFamily:'inherit'}}>Продолжить без входа</button>
                </div>
            )}

            {step === 'code' && (
                <div style={box}>
                    <div style={{fontSize:12.5,color:'var(--f1-text-muted)',textAlign:'center',lineHeight:1.5}}>
                        Бот <b style={{color:'var(--f1-text)'}}>@F1_egor_bot</b> прислал код в Telegram — введи его:
                    </div>
                    <input value={code} onChange={e=>{setCode(e.target.value.replace(/\s/g,''));setErr('');}} onKeyDown={e=>e.key==='Enter'&&submitCode()}
                        inputMode="numeric" autoFocus placeholder="Код"
                        style={{background:'var(--f1-card-solid)',border:'1px solid '+(err?'var(--f1-red)':'var(--f1-border)'),borderRadius:14,padding:'15px',color:'#fff',fontSize:22,fontWeight:800,textAlign:'center',letterSpacing:8,fontFamily:'inherit',outline:'none'}}/>
                    {err && <div style={{fontSize:12.5,color:'var(--f1-red)',textAlign:'center'}}>{err}</div>}
                    <button onClick={submitCode} disabled={busy||!code.trim()}
                        style={{background:code.trim()?'var(--f1-red)':'var(--f1-gray)',border:'none',borderRadius:14,padding:'15px',color:'#fff',fontSize:15,fontWeight:800,cursor:code.trim()?'pointer':'default',fontFamily:'inherit',opacity:busy?0.6:1,transition:'background .2s'}}>
                        {busy?'Проверяем…':'Войти'}
                    </button>
                    <a href="https://t.me/F1_egor_bot?start=code" target="_blank" rel="noopener" style={{textAlign:'center',color:'#229ED9',fontSize:13,fontWeight:700,textDecoration:'none'}}>Получить код в боте →</a>
                    <button onClick={()=>{setStep('start');setErr('');}} style={{background:'none',border:'none',color:'var(--f1-text-muted)',fontSize:13,marginTop:2,cursor:'pointer',fontFamily:'inherit'}}>← Назад</button>
                </div>
            )}
        </div>
    );
};

// ==== MAIN APP ====
const App = () => {
    const [tab, setTab] = useState(DURAK_INVITE ? 'games' : 'home');
    const [currentSeason, setCurrentSeason] = useState(2026);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [showLogin, setShowLogin] = useState(false);
    const [nextRace, setNextRace] = useState(null);
    const [lastRace, setLastRace] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [driversStandings, setDriversStandings] = useState(null);
    const [constructorsStandings, setConstructorsStandings] = useState(null);
    const [session, setSession] = useState(null);
    const [positions, setPositions] = useState(null);
    const [timing, setTiming] = useState(null);
    const [weather, setWeather] = useState(null);
    const [raceControl, setRaceControl] = useState(null);
    const [streams, setStreams] = useState(null);
    const [liveBroadcasts, setLiveBroadcasts] = useState([]);
    const [seasonResults, setSeasonResults] = useState(null);
    const [selectedRound, setSelectedRound] = useState(null);
    const [raceDetailTab, setRaceDetailTab] = useState('race');
    const [selectedBroadcast, setSelectedBroadcast] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [spoilerFree, setSpoilerFree] = useState(() => localStorage.getItem('f1hub_spoiler_free') === 'true');
    const isLive = session?.is_live;
    // системная «назад» закрывает экран, а не приложение (нативная обёртка)
    useHistoryBack(tab === 'article', () => { setSelectedArticle(null); setTab('news'); });
    useHistoryBack(tab === 'broadcastView', () => setTab('videos'));
    useHistoryBack(tab === 'raceDetail', () => setTab('schedule'));
    const toggleSpoiler = () => { const v = !spoilerFree; setSpoilerFree(v); localStorage.setItem('f1hub_spoiler_free', String(v)); };

    useEffect(() => {
        (async () => {
            if (IS_WEBAPP) {
                const [u, s] = await Promise.all([api.get('/api/user/me'), api.get('/api/live/session')]);
                setUser(u); setSession(s); setLoading(false);
                if (u?.user_id) {
                    api.get('/api/user/is-admin').then(d => {
                        if (d?.is_admin) window.__F1_ADMIN_IDS = [u.user_id];
                    }).catch(()=>{});
                }
            } else {
                // Public site: try stored auth, otherwise load anonymously
                const token = localStorage.getItem('f1hub_auth_token');
                const s = await api.get('/api/live/session');
                setSession(s);
                if (token) {
                    const u = await api.get('/api/user/me');
                    if (u) {
                        setUser(u); setLoading(false);
                        // админка доступна и вне Telegram (RuStore-обёртка / сайт)
                        api.get('/api/user/is-admin').then(d => { if (d?.is_admin) window.__F1_ADMIN_IDS = [u.user_id]; }).catch(()=>{});
                        return;
                    }
                    // Token expired, clear it
                    localStorage.removeItem('f1hub_auth_token');
                }
                setLoading(false);
            }
        })();
    }, []);

    // Public site: listen for auth-expired events
    useEffect(() => {
        if (IS_WEBAPP) return;
        const handler = () => {
            localStorage.removeItem('f1hub_auth_token');
            setUser(null);
            setShowLogin(true);
        };
        window.addEventListener('f1:auth-expired', handler);
        return () => window.removeEventListener('f1:auth-expired', handler);
    }, []);

    useEffect(() => {
        if (loading) return;
        const s = currentSeason;
        setNextRace(null); setLastRace(null); setDriversStandings(null); setConstructorsStandings(null); setSchedule(null); setSeasonResults(null);
        Promise.all([api.get(`/api/home?season=${s}`),api.get(`/api/standings/drivers?season=${s}`),api.get(`/api/standings/constructors?season=${s}`),api.get(`/api/schedule?season=${s}`),api.get('/api/streams'),api.get(`/api/season/${s}/results`),api.get('/api/broadcasts/live')]).then(([home,ds,cs,sched,str,sr,lb])=>{
            if(home){setNextRace(home.next_race);setLastRace(home.last_race);}
            setDriversStandings(ds); setConstructorsStandings(cs); setSchedule(sched); setStreams(str); setSeasonResults(sr); if(lb?.broadcasts) setLiveBroadcasts(lb.broadcasts);
        });
    }, [loading, currentSeason]);

    useEffect(() => {
        if (tab !== 'live') return;
        const fetchLive = async () => { const d=await api.get('/api/live/dashboard'); if(d){setSession(d.session || {is_live:false, is_demo:false, offline:true, message:d.message});setPositions(d.positions);setTiming(d.timing);setWeather(d.weather);setRaceControl(d.race_control);} };
        fetchLive();
        const iv = setInterval(fetchLive, isLive ? 10000 : 30000);
        return () => clearInterval(iv);
    }, [tab, isLive]);

    useEffect(() => {
        if (!tg) return;
        if (tab === 'article') {
            tg.BackButton?.show();
            const h = () => { setSelectedArticle(null); setTab('news'); };
            tg.BackButton?.onClick(h);
            return () => tg.BackButton?.offClick(h);
        } else if (tab === 'raceDetail') {
            tg.BackButton?.show();
            const h = () => setTab('schedule');
            tg.BackButton?.onClick(h);
            return () => tg.BackButton?.offClick(h);
        } else if (tab !== 'home') {
            tg.BackButton?.show();
            const h = () => setTab('home');
            tg.BackButton?.onClick(h);
            return () => tg.BackButton?.offClick(h);
        } else {
            tg.BackButton?.hide();
        }
    }, [tab]);

    if (loading) return (
        <div style={{height:'100dvh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16}}>
            <div style={{fontSize:22,fontWeight:900}}>F1 <span style={{color:'var(--f1-red)'}}>Hub</span></div>
            <div className="f1-lights"><div className="f1-light"/><div className="f1-light"/><div className="f1-light"/><div className="f1-light"/><div className="f1-light"/></div>
        </div>
    );

    // Public site: show login page when requested or for auth-required tabs
    if (!IS_WEBAPP && showLogin) {
        return <PublicLoginPage onLogin={(tgUser) => {
            setShowLogin(false);
            if (tgUser) {
                api.get('/api/user/me').then(u => {
                    if (u) {
                        setUser(u);
                        api.get('/api/user/is-admin').then(d => { if (d?.is_admin) window.__F1_ADMIN_IDS = [u.user_id]; }).catch(()=>{});
                    }
                });
            }
        }} />;
    }

    // Public: tabs requiring auth → prompt login
    const needsAuth = !IS_WEBAPP && !user && (tab === 'predict' || tab === 'profile' || tab === 'games');
    if (needsAuth) {
        return <PublicLoginPage onLogin={(tgUser) => {
            if (tgUser) {
                api.get('/api/user/me').then(u => {
                    if (u) {
                        setUser(u);
                        api.get('/api/user/is-admin').then(d => { if (d?.is_admin) window.__F1_ADMIN_IDS = [u.user_id]; }).catch(()=>{});
                    }
                });
            } else {
                setTab('home');
            }
        }} />;
    }

    const renderTab = () => {
        switch(tab) {
            case 'home': return <HomePage nextRace={nextRace} lastRace={lastRace} standings={driversStandings} user={user} streams={streams} seasonResults={seasonResults} schedule={schedule} onChange={setTab} season={currentSeason} onSeasonChange={setCurrentSeason} spoilerFree={spoilerFree && currentSeason === 2026} onToggleSpoiler={toggleSpoiler} liveBroadcasts={liveBroadcasts} onRaceClick={(round,dtab)=>{setSelectedRound(round);setRaceDetailTab(dtab||'race');setTab('raceDetail');}} onRefresh={async()=>{const[home,ds]=await Promise.all([api.get(`/api/home?season=${currentSeason}`),api.get(`/api/standings/drivers?season=${currentSeason}`)]);if(home){setNextRace(home.next_race);setLastRace(home.last_race);}if(ds)setDriversStandings(ds);}}/>;
            case 'live': return <LivePage session={session} positions={positions} timing={timing} weather={weather} raceControl={raceControl} onChange={setTab} onRefresh={async()=>{const d=await api.get('/api/live/dashboard');if(d){setSession(d.session || {is_live:false, is_demo:false, offline:true, message:d.message});setPositions(d.positions);setTiming(d.timing);setWeather(d.weather);setRaceControl(d.race_control);}}}/>;
            case 'news': return <NewsPage onArticleClick={(url)=>{setSelectedArticle(url);setTab('article');}}/>;
            case 'article': return <ArticlePage articleUrl={selectedArticle} onBack={()=>{setSelectedArticle(null);setTab('news');}}/>;
            case 'schedule': return <SchedulePage seasonResults={seasonResults} schedule={schedule} season={currentSeason} onRaceClick={(round,dtab)=>{setSelectedRound(round);setRaceDetailTab(dtab||'race');setTab('raceDetail');}} spoilerFree={spoilerFree}/>;
            case 'raceDetail': return <RaceDetailPage race={seasonResults?.races?.find(r=>r.round===selectedRound) || schedule?.races?.find(r=>r.round===selectedRound)} onBack={()=>setTab('schedule')} season={currentSeason} spoilerFree={spoilerFree && currentSeason === 2026} allRaces={seasonResults?.races} defaultTab={raceDetailTab}/>;
            case 'standings': return <StandingsPage driversStandings={driversStandings} constructorsStandings={constructorsStandings} season={currentSeason} spoilerFree={spoilerFree && currentSeason === 2026} onBack={()=>setTab('home')} onRefresh={async()=>{const[ds,cs]=await Promise.all([api.get(`/api/standings/drivers?season=${currentSeason}`),api.get(`/api/standings/constructors?season=${currentSeason}`)]);if(ds)setDriversStandings(ds);if(cs)setConstructorsStandings(cs);}}/>;
            case 'predict': return <PredictionsPage user={user}/>;
            case 'videos': return <VideosPage schedule={schedule} onOpen={(b,rn)=>{setSelectedBroadcast({b,rn});setTab('broadcastView');}}/>;
            case 'broadcastView': return selectedBroadcast ? <BroadcastViewPage broadcast={selectedBroadcast.b} raceName={selectedBroadcast.rn} onBack={()=>setTab('videos')} user={user} spoilerFree={spoilerFree}/> : null;
            case 'profile': return <ProfilePage user={user} onNavigate={setTab} spoilerFree={spoilerFree} onToggleSpoiler={toggleSpoiler} onLogout={IS_WEBAPP ? null : ()=>{try{localStorage.removeItem('f1hub_auth_token');}catch(e){} setUser(null); setTab('home');}}/>;
            case 'analytics': return <AnalyticsPage/>;
            case 'games': return <GamesPage onChange={setTab}/>;
            default: return null;
        }
    };

    return (<>{renderTab()}{tab !== 'broadcastView' && <BottomNav active={tab} onChange={setTab} isLive={isLive}/>}</>);
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App/>);
